import { isString, isNil, isPureObject, isNumber } from '@worksolutions/utils';
import { IsSorting, IsNumberFromString, IsDate, IsOptional, IsInstancesOf, applyDecorators, BaseEntity, plainToClass, wrapToArray, withDefaultValue } from '@app/kit';
import { IsString, Matches, IsBoolean, IsEnum, Equals, IsEmail, IsMobilePhone, IsNumber, Min, Max, IsUrl, IsDefined, IsArray, ArrayNotEmpty, IsObject, ValidateNested, MinLength, MaxLength, isArray } from 'class-validator';
import { Expose, Type } from 'class-transformer';

function createClassValidatorErrorMessage({ code, message = "Invalid value" } = {}) {
    return function(validationArguments) {
        const resultMessage = isString(message) ? message : message(validationArguments);
        if (isNil(code)) return `${validationArguments.property};; ${resultMessage}`;
        return `${code};; ${resultMessage}`;
    };
}

const isStringMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть строкой"
});
const isStringArrayMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть массивом строк"
});
// format — человекочитаемый вид значения: его видит клиент в ответе 400, регулярного выражения он не увидит.
const matchesMessage = (format)=>createClassValidatorErrorMessage({
        message: `Поле должно быть вида ${format}`
    });
const matchesArrayMessage = (format)=>createClassValidatorErrorMessage({
        message: `Каждый элемент должен быть вида ${format}`
    });

const isBooleanMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть boolean"
});
const isBooleanArrayMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть массивом boolean"
});

const isNumberMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть числом"
});
const isNumberStringrMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть числом, но передано как строка"
});
const isNumberArrayMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть массивом чисел"
});
const minNumberMessage = createClassValidatorErrorMessage({
    message: ({ constraints })=>`Минимальное значение [${constraints[0]}]`
});
const maxNumberMessage = createClassValidatorErrorMessage({
    message: ({ constraints })=>`Максимальное значение [${constraints[0]}]`
});

const enumMessage = createClassValidatorErrorMessage({
    message: ({ constraints })=>`Поле должно содержать одно из значений [${constraints[1].join(", ")}]`
});
const enumArrayMessage = createClassValidatorErrorMessage({
    message: ({ constraints })=>`Поле должно быть массивов из значений [${constraints[1].join(", ")}]`
});

const isDateMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть датой в формате ISO"
});

const isDefinedMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть передано"
});

const isArrayMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть массивом"
});
const arrayNotEmptyMessage = createClassValidatorErrorMessage({
    message: "Массив не должен быть пустым"
});

const isInstanceOfMessage = createClassValidatorErrorMessage({
    message: "Поле должно соответствовать одному из типов"
});
const isValidObjectMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть валидным объектом"
});

const emailMessage = createClassValidatorErrorMessage({
    message: "Поле должно содержать электронную почту"
});
const emailArrayMessage = createClassValidatorErrorMessage({
    message: "Поле должно содержать массив электронных почт"
});

const phoneMessage = createClassValidatorErrorMessage({
    message: "Поле должно содержать номер телефона"
});
const phoneArrayMessage = createClassValidatorErrorMessage({
    message: "Поле должно содержать массив номеров телефонов"
});

const isUrlMessage = createClassValidatorErrorMessage({
    message: "Поле должно быть URL адресом"
});

const sortingMessage = createClassValidatorErrorMessage({
    message: ({ constraints })=>`Поле должно содержать одно из значений [${constraints[0].join(", ")}]`
});

const isOptionalMessage = createClassValidatorErrorMessage({
    message: "Неверный тип"
});

function dto(options) {
    const decorators = [
        Expose({
            name: options.name
        })
    ];
    const isArray = !!options.array;
    if (options.string) {
        decorators.push(IsString({
            message: isArray ? isStringArrayMessage : isStringMessage,
            each: isArray
        }));
        if (options.string !== true && options.string.pattern) {
            const { regexp, format } = options.string.pattern;
            decorators.push(Matches(regexp, {
                message: isArray ? matchesArrayMessage(format) : matchesMessage(format),
                each: isArray
            }));
        }
    }
    if (options.boolean) decorators.push(IsBoolean({
        message: isArray ? isBooleanArrayMessage : isBooleanMessage,
        each: isArray
    }));
    if (options.enum) decorators.push(IsEnum(options.enum, {
        message: isArray ? enumArrayMessage : enumMessage,
        each: isArray
    }));
    if (options.sorting) {
        if (isArray) throw new Error("Нельзя одновременно использовать sorting и array");
        decorators.push(IsSorting(options.sorting, {
            message: sortingMessage,
            each: isArray
        }));
    }
    if (options.equals) {
        if (isArray) throw new Error("Нельзя одновременно использовать equals и array");
        if (options.enum) throw new Error("Нельзя одновременно использовать equals и enum");
        decorators.push(Equals(options.equals));
    }
    if (options.email) {
        decorators.push(IsEmail(undefined, {
            message: isArray ? emailArrayMessage : emailMessage,
            each: isArray
        }));
    }
    if (options.phone) decorators.push(IsMobilePhone(undefined, undefined, {
        message: isArray ? phoneArrayMessage : phoneMessage,
        each: isArray
    }));
    if (options.number) {
        if (options.number === true) {
            decorators.push(IsNumber(undefined, {
                message: isArray ? isNumberArrayMessage : isNumberMessage,
                each: isArray
            }));
        } else {
            if (options.number.asString) {
                decorators.push(IsNumberFromString({
                    maxDecimalPlaces: options.number.maxDecimalPlaces
                }, {
                    message: isArray ? isNumberArrayMessage : isNumberStringrMessage,
                    each: isArray
                }));
            } else {
                decorators.push(IsNumber({
                    maxDecimalPlaces: options.number.maxDecimalPlaces
                }, {
                    message: isArray ? isNumberArrayMessage : isNumberMessage,
                    each: isArray
                }));
            }
            if (options.number.min !== undefined) decorators.push(Min(options.number.min, {
                message: minNumberMessage,
                each: isArray
            }));
            if (options.number.max !== undefined) decorators.push(Max(options.number.max, {
                message: maxNumberMessage,
                each: isArray
            }));
        }
    }
    if (options.date) decorators.push(IsDate({
        message: isDateMessage
    }));
    if (options.url) {
        if (options.url === true) {
            decorators.push(IsUrl(undefined, {
                message: isUrlMessage,
                each: isArray
            }));
        } else {
            decorators.push(IsUrl(Object.assign({}, options.url.allowAnyProtocol ? {
                require_valid_protocol: false,
                require_tld: false
            } : undefined), {
                message: isUrlMessage,
                each: isArray
            }));
        }
    }
    if (options.optional) {
        decorators.push(IsOptional(options.optional === true ? undefined : options.optional, {
            message: isOptionalMessage
        }));
    } else {
        decorators.push(IsDefined({
            message: isDefinedMessage
        }));
    }
    if (isArray && options.date) throw new Error("Not implemented");
    if (isArray) {
        decorators.push(IsArray({
            message: isArrayMessage
        }));
        if (options.array !== true && options.array?.notEmpty) decorators.push(ArrayNotEmpty({
            message: arrayNotEmptyMessage
        }));
    }
    // Произвольный JSON без схемы: валидируем только «это объект», содержимое не трогаем.
    if (options.rawObject) decorators.push(IsObject({
        message: isValidObjectMessage,
        each: isArray
    }));
    if (options.object) {
        // IsObject обязателен рядом с ValidateNested: в class-validator он отсеивает массив, а без него
        // массив проходит валидацию с нулём ошибок (вложенного объекта нет — проверять нечего), поля
        // разобранного объекта становятся undefined, и дальше запрос едет как валидный. В TypeORM 0.3
        // условие со значением undefined молча выбрасывается, то есть findOne по такому полю вырождается
        // в выборку первой попавшейся строки.
        decorators.push(IsObject({
            message: isValidObjectMessage,
            each: isArray
        }));
        decorators.push(ValidateNested({
            each: isArray,
            message: isValidObjectMessage
        }));
        if ("type" in options.object) {
            const type = options.object.type;
            decorators.push(Type(()=>type));
        }
        if ("discriminator" in options.object) {
            decorators.push(Type(options.object.transformer ?? (()=>undefined), {
                keepDiscriminatorProperty: true,
                discriminator: options.object.discriminator
            }));
            decorators.push(IsInstancesOf(options.object.discriminator.subTypes.map(({ value })=>value), {
                message: isInstanceOfMessage,
                each: isArray
            }));
        }
    }
    return applyDecorators(...decorators);
}

const dtoMessageIsDefined = createClassValidatorErrorMessage({
    message: "Должно быть определено"
});
const dtoMessageIsValidValue = createClassValidatorErrorMessage({
    message: "Должно содержать валидное значение"
});
const dtoMessageIsPhone = createClassValidatorErrorMessage({
    message: "Должно содержать валидный номер телефона"
});
// Для @MinLength(1): без своего сообщения class-validator кладёт в ключ отказа весь текст, а не имя поля
const dtoMessageIsNotEmptyString = createClassValidatorErrorMessage({
    message: "Поле не должно быть пустым"
});

function _define_property$61(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$61(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$61(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AdminCreateMerchantRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$61(this, "name", void 0);
    }
}
_ts_decorate$61([
    dto({
        string: true
    }),
    _ts_metadata$61("design:type", String)
], AdminCreateMerchantRequestDTO.prototype, "name", void 0);

function _define_property$60(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$60(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$60(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$60(this, "id", void 0), _define_property$60(this, "name", void 0);
    }
}
_ts_decorate$60([
    dto({
        string: true
    }),
    _ts_metadata$60("design:type", String)
], MerchantResponseDTO.prototype, "id", void 0);
_ts_decorate$60([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$60("design:type", Object)
], MerchantResponseDTO.prototype, "name", void 0);

var MerchantIntegrationTypes = /*#__PURE__*/ function(MerchantIntegrationTypes) {
    MerchantIntegrationTypes["MAXMA_API_KEY"] = "maxma_api_key";
    MerchantIntegrationTypes["DATA_PROVIDER_BACKEND_KEY"] = "data_provider_backend_key";
    MerchantIntegrationTypes["DATA_PROVIDER_INTEGRATION_KEY"] = "data_provider_integration_key";
    MerchantIntegrationTypes["DATA_PROVIDER_INTEGRATION_URL"] = "data_provider_integration_url";
    MerchantIntegrationTypes["TELEGRAM_BOT_TOKEN"] = "telegram_bot_token";
    MerchantIntegrationTypes["TELEGRAM_BOT_SUPPORT_LINK"] = "telegram_bot_support_link";
    MerchantIntegrationTypes["SMS_PROVIDER"] = "sms_provider";
    MerchantIntegrationTypes["PAYMENT_YOOKASSA"] = "payment_yookassa";
    MerchantIntegrationTypes["PAYMENT_CDEK_PAY"] = "payment_cdek_pay";
    MerchantIntegrationTypes["DELIVERY_CDEK"] = "delivery_cdek";
    MerchantIntegrationTypes["YANDEX_MAPS"] = "yandex_maps";
    MerchantIntegrationTypes["FIREBASE"] = "firebase";
    return MerchantIntegrationTypes;
}({});
// Значения, по которым ищут мерчанта (getMerchantByValue), — одно на всех.
// Url и ключ к 1С сюда не входят: по ним никого не ищут, и несколько мерчантов могут жить на одной 1С.
const MERCHANT_INTEGRATION_UNIQUE_TYPES = [
    "data_provider_backend_key"
];
// Типы, которые внешняя интеграция (1С) настраивает себе сама.
// Связь бэка с самой 1С (её урл, ключ к ней и её ключ доступа к бэку) сюда не входит: сменив их, 1С потеряет связь.
const MERCHANT_INTEGRATION_NOT_SELF_SETTABLE_TYPES = [
    "data_provider_backend_key",
    "data_provider_integration_key",
    "data_provider_integration_url"
];
const notSelfSettableTypes = new Set(MERCHANT_INTEGRATION_NOT_SELF_SETTABLE_TYPES);
const MERCHANT_INTEGRATION_SELF_SETTABLE_TYPES = Object.fromEntries(Object.entries(MerchantIntegrationTypes).filter(([, type])=>!notSelfSettableTypes.has(type)));

function _define_property$5$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5$(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5$(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationCdekPayValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5$(this, "type", void 0), _define_property$5$(this, "login", void 0), _define_property$5$(this, "secretKey", void 0), // Тестовый режим CDEK — пара «тестовый ключ магазина + валюта TST», деньги не списываются. Это свойство
        // учётных данных, а не окружения: один образ крутится на всех стендах. Не передан — боевой режим, RUR.
        // Ключ необязательный, а не `!: boolean | undefined`: значения интеграций собирают литералами, и
        // обязательный ключ пришлось бы дописывать в каждый.
        _define_property$5$(this, "testMode", void 0);
    }
}
_ts_decorate$5$([
    dto({
        equals: MerchantIntegrationTypes.PAYMENT_CDEK_PAY
    }),
    _ts_metadata$5$("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.PAYMENT_CDEK_PAY === "undefined" ? Object : MerchantIntegrationTypes.PAYMENT_CDEK_PAY)
], MerchantIntegrationCdekPayValueDTO.prototype, "type", void 0);
_ts_decorate$5$([
    dto({
        string: true
    }),
    _ts_metadata$5$("design:type", String)
], MerchantIntegrationCdekPayValueDTO.prototype, "login", void 0);
_ts_decorate$5$([
    dto({
        string: true
    }),
    _ts_metadata$5$("design:type", String)
], MerchantIntegrationCdekPayValueDTO.prototype, "secretKey", void 0);
_ts_decorate$5$([
    dto({
        boolean: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$5$("design:type", Boolean)
], MerchantIntegrationCdekPayValueDTO.prototype, "testMode", void 0);

function _define_property$5_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5_(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5_(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationCdekValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5_(this, "type", void 0), _define_property$5_(this, "login", void 0), _define_property$5_(this, "password", void 0);
    }
}
_ts_decorate$5_([
    dto({
        equals: MerchantIntegrationTypes.DELIVERY_CDEK
    }),
    _ts_metadata$5_("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.DELIVERY_CDEK === "undefined" ? Object : MerchantIntegrationTypes.DELIVERY_CDEK)
], MerchantIntegrationCdekValueDTO.prototype, "type", void 0);
_ts_decorate$5_([
    dto({
        string: true
    }),
    _ts_metadata$5_("design:type", String)
], MerchantIntegrationCdekValueDTO.prototype, "login", void 0);
_ts_decorate$5_([
    dto({
        string: true
    }),
    _ts_metadata$5_("design:type", String)
], MerchantIntegrationCdekValueDTO.prototype, "password", void 0);

function _define_property$5Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5Z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5Z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationDataProviderBackendKeyValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5Z(this, "type", void 0), _define_property$5Z(this, "key", void 0);
    }
}
_ts_decorate$5Z([
    dto({
        equals: MerchantIntegrationTypes.DATA_PROVIDER_BACKEND_KEY
    }),
    _ts_metadata$5Z("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.DATA_PROVIDER_BACKEND_KEY === "undefined" ? Object : MerchantIntegrationTypes.DATA_PROVIDER_BACKEND_KEY)
], MerchantIntegrationDataProviderBackendKeyValueDTO.prototype, "type", void 0);
_ts_decorate$5Z([
    dto({
        string: true
    }),
    _ts_metadata$5Z("design:type", String)
], MerchantIntegrationDataProviderBackendKeyValueDTO.prototype, "key", void 0);

function _define_property$5Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5Y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5Y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var MerchantIntegrationDataProviderAuthType = /*#__PURE__*/ function(MerchantIntegrationDataProviderAuthType) {
    MerchantIntegrationDataProviderAuthType["CREDENTIALS"] = "credentials";
    MerchantIntegrationDataProviderAuthType["TOKEN"] = "token";
    return MerchantIntegrationDataProviderAuthType;
}({});
class MerchantIntegrationDataProviderCredentialsAuthDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5Y(this, "authType", void 0), _define_property$5Y(this, "username", void 0), _define_property$5Y(this, "password", void 0);
    }
}
_ts_decorate$5Y([
    dto({
        equals: "credentials"
    }),
    _ts_metadata$5Y("design:type", typeof MerchantIntegrationDataProviderAuthType === "undefined" || "string" === "undefined" ? Object : "credentials")
], MerchantIntegrationDataProviderCredentialsAuthDTO.prototype, "authType", void 0);
_ts_decorate$5Y([
    dto({
        string: true
    }),
    _ts_metadata$5Y("design:type", String)
], MerchantIntegrationDataProviderCredentialsAuthDTO.prototype, "username", void 0);
_ts_decorate$5Y([
    dto({
        string: true
    }),
    _ts_metadata$5Y("design:type", String)
], MerchantIntegrationDataProviderCredentialsAuthDTO.prototype, "password", void 0);
class MerchantIntegrationDataProviderTokenAuthDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5Y(this, "authType", void 0), _define_property$5Y(this, "token", void 0);
    }
}
_ts_decorate$5Y([
    dto({
        equals: "token"
    }),
    _ts_metadata$5Y("design:type", typeof MerchantIntegrationDataProviderAuthType === "undefined" || "string" === "undefined" ? Object : "token")
], MerchantIntegrationDataProviderTokenAuthDTO.prototype, "authType", void 0);
_ts_decorate$5Y([
    dto({
        string: true
    }),
    _ts_metadata$5Y("design:type", String)
], MerchantIntegrationDataProviderTokenAuthDTO.prototype, "token", void 0);
class MerchantIntegrationDataProviderKeyValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5Y(this, "type", void 0), _define_property$5Y(this, "auth", void 0);
    }
}
_ts_decorate$5Y([
    dto({
        equals: MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_KEY
    }),
    _ts_metadata$5Y("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_KEY === "undefined" ? Object : MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_KEY)
], MerchantIntegrationDataProviderKeyValueDTO.prototype, "type", void 0);
_ts_decorate$5Y([
    dto({
        object: {
            discriminator: {
                property: "authType",
                subTypes: [
                    {
                        name: "credentials",
                        value: MerchantIntegrationDataProviderCredentialsAuthDTO
                    },
                    {
                        name: "token",
                        value: MerchantIntegrationDataProviderTokenAuthDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5Y("design:type", Object)
], MerchantIntegrationDataProviderKeyValueDTO.prototype, "auth", void 0);

function _define_property$5X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5X(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5X(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationDataProviderUrlValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5X(this, "type", void 0), _define_property$5X(this, "url", void 0);
    }
}
_ts_decorate$5X([
    dto({
        equals: MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_URL
    }),
    _ts_metadata$5X("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_URL === "undefined" ? Object : MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_URL)
], MerchantIntegrationDataProviderUrlValueDTO.prototype, "type", void 0);
_ts_decorate$5X([
    dto({
        url: true
    }),
    _ts_metadata$5X("design:type", String)
], MerchantIntegrationDataProviderUrlValueDTO.prototype, "url", void 0);

function _define_property$5W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5W(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5W(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationFirebaseValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5W(this, "type", void 0), _define_property$5W(this, "projectId", void 0), _define_property$5W(this, "privateKey", void 0), _define_property$5W(this, "clientEmail", void 0);
    }
}
_ts_decorate$5W([
    dto({
        equals: MerchantIntegrationTypes.FIREBASE
    }),
    _ts_metadata$5W("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.FIREBASE === "undefined" ? Object : MerchantIntegrationTypes.FIREBASE)
], MerchantIntegrationFirebaseValueDTO.prototype, "type", void 0);
_ts_decorate$5W([
    dto({
        string: true
    }),
    _ts_metadata$5W("design:type", String)
], MerchantIntegrationFirebaseValueDTO.prototype, "projectId", void 0);
_ts_decorate$5W([
    dto({
        string: true
    }),
    _ts_metadata$5W("design:type", String)
], MerchantIntegrationFirebaseValueDTO.prototype, "privateKey", void 0);
_ts_decorate$5W([
    dto({
        email: true
    }),
    _ts_metadata$5W("design:type", String)
], MerchantIntegrationFirebaseValueDTO.prototype, "clientEmail", void 0);

function _define_property$5V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5V(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5V(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationMaxmaValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5V(this, "type", void 0), _define_property$5V(this, "apiKey", void 0);
    }
}
_ts_decorate$5V([
    dto({
        equals: MerchantIntegrationTypes.MAXMA_API_KEY
    }),
    _ts_metadata$5V("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.MAXMA_API_KEY === "undefined" ? Object : MerchantIntegrationTypes.MAXMA_API_KEY)
], MerchantIntegrationMaxmaValueDTO.prototype, "type", void 0);
_ts_decorate$5V([
    dto({
        string: true
    }),
    _ts_metadata$5V("design:type", String)
], MerchantIntegrationMaxmaValueDTO.prototype, "apiKey", void 0);

function _define_property$5U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5U(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5U(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var MerchantIntegrationSmsProviderName = /*#__PURE__*/ function(MerchantIntegrationSmsProviderName) {
    MerchantIntegrationSmsProviderName["SMSC_RU"] = "smsc_ru";
    MerchantIntegrationSmsProviderName["SMSINT_RU"] = "smsint_ru";
    MerchantIntegrationSmsProviderName["SMSUSLUGI_RU"] = "smsuslugi_ru";
    MerchantIntegrationSmsProviderName["SMS_RU"] = "sms_ru";
    MerchantIntegrationSmsProviderName["PROSTOR_SMS"] = "prostor_sms";
    return MerchantIntegrationSmsProviderName;
}({});
class MerchantIntegrationSmsProviderSmscRuDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5U(this, "provider", void 0), _define_property$5U(this, "login", void 0), _define_property$5U(this, "password", void 0);
    }
}
_ts_decorate$5U([
    dto({
        equals: "smsc_ru"
    }),
    _ts_metadata$5U("design:type", typeof MerchantIntegrationSmsProviderName === "undefined" || "string" === "undefined" ? Object : "smsc_ru")
], MerchantIntegrationSmsProviderSmscRuDTO.prototype, "provider", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderSmscRuDTO.prototype, "login", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderSmscRuDTO.prototype, "password", void 0);
class MerchantIntegrationSmsProviderSmsIntRuDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5U(this, "provider", void 0), _define_property$5U(this, "apiToken", void 0);
    }
}
_ts_decorate$5U([
    dto({
        equals: "smsint_ru"
    }),
    _ts_metadata$5U("design:type", typeof MerchantIntegrationSmsProviderName === "undefined" || "string" === "undefined" ? Object : "smsint_ru")
], MerchantIntegrationSmsProviderSmsIntRuDTO.prototype, "provider", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderSmsIntRuDTO.prototype, "apiToken", void 0);
class MerchantIntegrationSmsProviderSmsUslugiRuDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5U(this, "provider", void 0), _define_property$5U(this, "apiToken", void 0);
    }
}
_ts_decorate$5U([
    dto({
        equals: "smsuslugi_ru"
    }),
    _ts_metadata$5U("design:type", typeof MerchantIntegrationSmsProviderName === "undefined" || "string" === "undefined" ? Object : "smsuslugi_ru")
], MerchantIntegrationSmsProviderSmsUslugiRuDTO.prototype, "provider", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderSmsUslugiRuDTO.prototype, "apiToken", void 0);
class MerchantIntegrationSmsProviderSmsRuDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5U(this, "provider", void 0), _define_property$5U(this, "apiToken", void 0);
    }
}
_ts_decorate$5U([
    dto({
        equals: "sms_ru"
    }),
    _ts_metadata$5U("design:type", typeof MerchantIntegrationSmsProviderName === "undefined" || "string" === "undefined" ? Object : "sms_ru")
], MerchantIntegrationSmsProviderSmsRuDTO.prototype, "provider", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderSmsRuDTO.prototype, "apiToken", void 0);
class MerchantIntegrationSmsProviderProstorSmsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5U(this, "provider", void 0), _define_property$5U(this, "login", void 0), _define_property$5U(this, "password", void 0), // Подпись отправителя из кабинета Простора. Не задана — Простор выбирает подпись сам.
        _define_property$5U(this, "sender", void 0);
    }
}
_ts_decorate$5U([
    dto({
        equals: "prostor_sms"
    }),
    _ts_metadata$5U("design:type", typeof MerchantIntegrationSmsProviderName === "undefined" || "string" === "undefined" ? Object : "prostor_sms")
], MerchantIntegrationSmsProviderProstorSmsDTO.prototype, "provider", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderProstorSmsDTO.prototype, "login", void 0);
_ts_decorate$5U([
    dto({
        string: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderProstorSmsDTO.prototype, "password", void 0);
_ts_decorate$5U([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$5U("design:type", String)
], MerchantIntegrationSmsProviderProstorSmsDTO.prototype, "sender", void 0);
class MerchantIntegrationSmsProviderValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5U(this, "type", void 0), _define_property$5U(this, "settings", void 0);
    }
}
_ts_decorate$5U([
    dto({
        equals: MerchantIntegrationTypes.SMS_PROVIDER
    }),
    _ts_metadata$5U("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.SMS_PROVIDER === "undefined" ? Object : MerchantIntegrationTypes.SMS_PROVIDER)
], MerchantIntegrationSmsProviderValueDTO.prototype, "type", void 0);
_ts_decorate$5U([
    dto({
        object: {
            discriminator: {
                property: "provider",
                subTypes: [
                    {
                        name: "smsc_ru",
                        value: MerchantIntegrationSmsProviderSmscRuDTO
                    },
                    {
                        name: "smsint_ru",
                        value: MerchantIntegrationSmsProviderSmsIntRuDTO
                    },
                    {
                        name: "smsuslugi_ru",
                        value: MerchantIntegrationSmsProviderSmsUslugiRuDTO
                    },
                    {
                        name: "sms_ru",
                        value: MerchantIntegrationSmsProviderSmsRuDTO
                    },
                    {
                        name: "prostor_sms",
                        value: MerchantIntegrationSmsProviderProstorSmsDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5U("design:type", Object)
], MerchantIntegrationSmsProviderValueDTO.prototype, "settings", void 0);

function _define_property$5T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5T(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5T(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationTelegramBotSupportLinkValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5T(this, "type", void 0), _define_property$5T(this, "link", void 0);
    }
}
_ts_decorate$5T([
    dto({
        equals: MerchantIntegrationTypes.TELEGRAM_BOT_SUPPORT_LINK
    }),
    _ts_metadata$5T("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.TELEGRAM_BOT_SUPPORT_LINK === "undefined" ? Object : MerchantIntegrationTypes.TELEGRAM_BOT_SUPPORT_LINK)
], MerchantIntegrationTelegramBotSupportLinkValueDTO.prototype, "type", void 0);
_ts_decorate$5T([
    dto({
        url: true
    }),
    _ts_metadata$5T("design:type", String)
], MerchantIntegrationTelegramBotSupportLinkValueDTO.prototype, "link", void 0);

function _define_property$5S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5S(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5S(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationTelegramBotTokenValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5S(this, "type", void 0), _define_property$5S(this, "token", void 0);
    }
}
_ts_decorate$5S([
    dto({
        equals: MerchantIntegrationTypes.TELEGRAM_BOT_TOKEN
    }),
    _ts_metadata$5S("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.TELEGRAM_BOT_TOKEN === "undefined" ? Object : MerchantIntegrationTypes.TELEGRAM_BOT_TOKEN)
], MerchantIntegrationTelegramBotTokenValueDTO.prototype, "type", void 0);
_ts_decorate$5S([
    dto({
        string: true
    }),
    _ts_metadata$5S("design:type", String)
], MerchantIntegrationTelegramBotTokenValueDTO.prototype, "token", void 0);

function _define_property$5R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5R(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5R(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationYandexMapsValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5R(this, "type", void 0), _define_property$5R(this, "token", void 0);
    }
}
_ts_decorate$5R([
    dto({
        equals: MerchantIntegrationTypes.YANDEX_MAPS
    }),
    _ts_metadata$5R("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.YANDEX_MAPS === "undefined" ? Object : MerchantIntegrationTypes.YANDEX_MAPS)
], MerchantIntegrationYandexMapsValueDTO.prototype, "type", void 0);
_ts_decorate$5R([
    dto({
        string: true
    }),
    _ts_metadata$5R("design:type", String)
], MerchantIntegrationYandexMapsValueDTO.prototype, "token", void 0);

function _define_property$5Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5Q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5Q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantIntegrationYooKassaValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5Q(this, "type", void 0), _define_property$5Q(this, "shopId", void 0), _define_property$5Q(this, "secretKey", void 0);
    }
}
_ts_decorate$5Q([
    dto({
        equals: MerchantIntegrationTypes.PAYMENT_YOOKASSA
    }),
    _ts_metadata$5Q("design:type", typeof MerchantIntegrationTypes === "undefined" || typeof MerchantIntegrationTypes.PAYMENT_YOOKASSA === "undefined" ? Object : MerchantIntegrationTypes.PAYMENT_YOOKASSA)
], MerchantIntegrationYooKassaValueDTO.prototype, "type", void 0);
_ts_decorate$5Q([
    dto({
        string: true
    }),
    _ts_metadata$5Q("design:type", String)
], MerchantIntegrationYooKassaValueDTO.prototype, "shopId", void 0);
_ts_decorate$5Q([
    dto({
        string: true
    }),
    _ts_metadata$5Q("design:type", String)
], MerchantIntegrationYooKassaValueDTO.prototype, "secretKey", void 0);

const MERCHANT_INTEGRATION_VALUE_SUB_TYPES = [
    {
        name: MerchantIntegrationTypes.MAXMA_API_KEY,
        value: MerchantIntegrationMaxmaValueDTO
    },
    {
        name: MerchantIntegrationTypes.DATA_PROVIDER_BACKEND_KEY,
        value: MerchantIntegrationDataProviderBackendKeyValueDTO
    },
    {
        name: MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_KEY,
        value: MerchantIntegrationDataProviderKeyValueDTO
    },
    {
        name: MerchantIntegrationTypes.DATA_PROVIDER_INTEGRATION_URL,
        value: MerchantIntegrationDataProviderUrlValueDTO
    },
    {
        name: MerchantIntegrationTypes.TELEGRAM_BOT_TOKEN,
        value: MerchantIntegrationTelegramBotTokenValueDTO
    },
    {
        name: MerchantIntegrationTypes.TELEGRAM_BOT_SUPPORT_LINK,
        value: MerchantIntegrationTelegramBotSupportLinkValueDTO
    },
    {
        name: MerchantIntegrationTypes.SMS_PROVIDER,
        value: MerchantIntegrationSmsProviderValueDTO
    },
    {
        name: MerchantIntegrationTypes.PAYMENT_YOOKASSA,
        value: MerchantIntegrationYooKassaValueDTO
    },
    {
        name: MerchantIntegrationTypes.PAYMENT_CDEK_PAY,
        value: MerchantIntegrationCdekPayValueDTO
    },
    {
        name: MerchantIntegrationTypes.DELIVERY_CDEK,
        value: MerchantIntegrationCdekValueDTO
    },
    {
        name: MerchantIntegrationTypes.YANDEX_MAPS,
        value: MerchantIntegrationYandexMapsValueDTO
    },
    {
        name: MerchantIntegrationTypes.FIREBASE,
        value: MerchantIntegrationFirebaseValueDTO
    }
];
const selfSettableTypes = new Set(Object.values(MERCHANT_INTEGRATION_SELF_SETTABLE_TYPES));
const merchantIntegrationValueDiscriminator = {
    property: "type",
    subTypes: MERCHANT_INTEGRATION_VALUE_SUB_TYPES
};
const merchantIntegrationSelfSettableValueDiscriminator = {
    property: "type",
    subTypes: MERCHANT_INTEGRATION_VALUE_SUB_TYPES.filter(({ name })=>selfSettableTypes.has(name))
};

function _define_property$5P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5P(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5P(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var StorageFileProcessingStatus = /*#__PURE__*/ function(StorageFileProcessingStatus) {
    StorageFileProcessingStatus["COMPLETED"] = "completed";
    StorageFileProcessingStatus["PROCESSING"] = "processing";
    StorageFileProcessingStatus["FAILED"] = "failed";
    return StorageFileProcessingStatus;
}({});
class StorageFileDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5P(this, "id", void 0), _define_property$5P(this, "bucket", void 0), _define_property$5P(this, "folder", void 0), _define_property$5P(this, "fileName", void 0), _define_property$5P(this, "size", void 0), _define_property$5P(this, "processingStatus", void 0);
    }
}
_ts_decorate$5P([
    dto({
        string: true
    }),
    _ts_metadata$5P("design:type", String)
], StorageFileDTO.prototype, "id", void 0);
_ts_decorate$5P([
    dto({
        string: true
    }),
    _ts_metadata$5P("design:type", String)
], StorageFileDTO.prototype, "bucket", void 0);
_ts_decorate$5P([
    dto({
        string: true
    }),
    _ts_metadata$5P("design:type", String)
], StorageFileDTO.prototype, "folder", void 0);
_ts_decorate$5P([
    dto({
        string: true
    }),
    _ts_metadata$5P("design:type", String)
], StorageFileDTO.prototype, "fileName", void 0);
_ts_decorate$5P([
    dto({
        number: true
    }),
    _ts_metadata$5P("design:type", Number)
], StorageFileDTO.prototype, "size", void 0);
_ts_decorate$5P([
    dto({
        enum: StorageFileProcessingStatus
    }),
    _ts_metadata$5P("design:type", String)
], StorageFileDTO.prototype, "processingStatus", void 0);

function _define_property$5O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5O(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5O(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var MerchantMetaLegalFileType = /*#__PURE__*/ function(MerchantMetaLegalFileType) {
    MerchantMetaLegalFileType["TERMS_OF_USE"] = "terms_of_use";
    MerchantMetaLegalFileType["PRIVACY_POLICY"] = "privacy_policy";
    MerchantMetaLegalFileType["LOYALTY_SYSTEM_POLICY"] = "loyalty_system_policy";
    MerchantMetaLegalFileType["PERSONAL_DATA_PROCESSING_POLICY"] = "personal_data_processing_policy";
    MerchantMetaLegalFileType["OFFER"] = "offer";
    MerchantMetaLegalFileType["DELETE_ACCOUNT_OFFER"] = "delete_account_offer";
    MerchantMetaLegalFileType["DELIVERY"] = "delivery";
    MerchantMetaLegalFileType["GUARANTEE"] = "guarantee";
    MerchantMetaLegalFileType["RETURN"] = "return";
    return MerchantMetaLegalFileType;
}({});
var MerchantMetaLegalFilePlace = /*#__PURE__*/ function(MerchantMetaLegalFilePlace) {
    MerchantMetaLegalFilePlace["PROFILE"] = "profile";
    MerchantMetaLegalFilePlace["DOCS"] = "docs";
    return MerchantMetaLegalFilePlace;
}({});
class MerchantMetaLegalFileResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5O(this, "type", void 0), _define_property$5O(this, "place", void 0), _define_property$5O(this, "name", void 0), _define_property$5O(this, "date", void 0), _define_property$5O(this, "file", void 0);
    }
}
_ts_decorate$5O([
    dto({
        enum: MerchantMetaLegalFileType
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaLegalFileResponseDTO.prototype, "type", void 0);
_ts_decorate$5O([
    dto({
        enum: MerchantMetaLegalFilePlace
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaLegalFileResponseDTO.prototype, "place", void 0);
_ts_decorate$5O([
    dto({
        string: true
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaLegalFileResponseDTO.prototype, "name", void 0);
_ts_decorate$5O([
    dto({
        date: true
    }),
    _ts_metadata$5O("design:type", typeof Date === "undefined" ? Object : Date)
], MerchantMetaLegalFileResponseDTO.prototype, "date", void 0);
_ts_decorate$5O([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5O("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], MerchantMetaLegalFileResponseDTO.prototype, "file", void 0);
var MerchantMetaLinkType = /*#__PURE__*/ function(MerchantMetaLinkType) {
    MerchantMetaLinkType["VK"] = "vk";
    MerchantMetaLinkType["INSTAGRAM"] = "instagram";
    MerchantMetaLinkType["YOUTUBE"] = "youtube";
    MerchantMetaLinkType["TELEGRAM"] = "telegram";
    MerchantMetaLinkType["MAX"] = "max";
    MerchantMetaLinkType["DZEN"] = "dzen";
    MerchantMetaLinkType["SITE"] = "site";
    MerchantMetaLinkType["OTHER"] = "other";
    return MerchantMetaLinkType;
}({});
class MerchantMetaLinkResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5O(this, "type", void 0), _define_property$5O(this, "text", void 0), _define_property$5O(this, "url", void 0);
    }
}
_ts_decorate$5O([
    dto({
        enum: MerchantMetaLinkType
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaLinkResponseDTO.prototype, "type", void 0);
_ts_decorate$5O([
    dto({
        string: true
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaLinkResponseDTO.prototype, "text", void 0);
_ts_decorate$5O([
    dto({
        url: true
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaLinkResponseDTO.prototype, "url", void 0);
class MerchantMetaPhoneResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5O(this, "phone", void 0);
    }
}
_ts_decorate$5O([
    dto({
        string: true
    }),
    _ts_metadata$5O("design:type", String)
], MerchantMetaPhoneResponseDTO.prototype, "phone", void 0);
class MerchantMetaResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5O(this, "legalFiles", void 0), _define_property$5O(this, "phones", void 0), _define_property$5O(this, "links", void 0), _define_property$5O(this, "descriptionFile", void 0);
    }
}
_ts_decorate$5O([
    dto({
        array: true,
        object: {
            type: MerchantMetaLegalFileResponseDTO
        }
    }),
    _ts_metadata$5O("design:type", Array)
], MerchantMetaResponseDTO.prototype, "legalFiles", void 0);
_ts_decorate$5O([
    dto({
        array: true,
        object: {
            type: MerchantMetaPhoneResponseDTO
        }
    }),
    _ts_metadata$5O("design:type", Array)
], MerchantMetaResponseDTO.prototype, "phones", void 0);
_ts_decorate$5O([
    dto({
        array: true,
        object: {
            type: MerchantMetaLinkResponseDTO
        }
    }),
    _ts_metadata$5O("design:type", Array)
], MerchantMetaResponseDTO.prototype, "links", void 0);
_ts_decorate$5O([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5O("design:type", Object)
], MerchantMetaResponseDTO.prototype, "descriptionFile", void 0);

function _define_property$5N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5N(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5N(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantMetaCustomerAuthRequiredLegalFilesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5N(this, "legalFiles", void 0);
    }
}
_ts_decorate$5N([
    dto({
        array: true,
        object: {
            type: MerchantMetaLegalFileResponseDTO
        }
    }),
    _ts_metadata$5N("design:type", Array)
], MerchantMetaCustomerAuthRequiredLegalFilesResponseDTO.prototype, "legalFiles", void 0);

function _define_property$5M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5M(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5M(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AdminEditMerchantRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5M(this, "name", void 0), // Slug уезжает в путь публичной ссылки {apiHost}/r/<slug>/<code>, поэтому набор символов
        // ограничен тем, что не требует экранирования и читается в мессенджере. Поле optional:
        // ADM-05 и ADM-14 патчат мерчанта, не присылая slug, и обязательное поле их бы сломало —
        // а PATCH и не должен требовать поля, которое клиент не трогает. allowNull: false запрещает
        // именно null: колонка NOT NULL, а семантики «обнулить slug» не существует — иначе null
        // проходил бы мимо MinLength/MaxLength/Matches (IsOptional гасит их и для null) и падал бы
        // в save отказом ограничения базы вместо внятной валидационной ошибки.
        _define_property$5M(this, "slug", void 0), _define_property$5M(this, "integrations", void 0), // Список интеграций сервер патчит, а не заменяет: чего в нём нет — остаётся нетронутым, иначе
        // неполное тело гасит мерчанту канал 1С. Поэтому удаление интеграции — отдельное явное поле.
        _define_property$5M(this, "deleteIntegrationTypes", void 0);
    }
}
_ts_decorate$5M([
    dto({
        string: true
    }),
    _ts_metadata$5M("design:type", String)
], AdminEditMerchantRequestDTO.prototype, "name", void 0);
_ts_decorate$5M([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    MinLength(1),
    MaxLength(64),
    Matches(/^[a-z0-9-]+$/),
    _ts_metadata$5M("design:type", Object)
], AdminEditMerchantRequestDTO.prototype, "slug", void 0);
_ts_decorate$5M([
    dto({
        array: true,
        object: {
            discriminator: merchantIntegrationValueDiscriminator
        }
    }),
    _ts_metadata$5M("design:type", Array)
], AdminEditMerchantRequestDTO.prototype, "integrations", void 0);
_ts_decorate$5M([
    dto({
        optional: true,
        array: true,
        enum: MerchantIntegrationTypes
    }),
    _ts_metadata$5M("design:type", Object)
], AdminEditMerchantRequestDTO.prototype, "deleteIntegrationTypes", void 0);

function _define_property$5L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5L(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5L(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AdminGetMerchantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5L(this, "id", void 0), _define_property$5L(this, "name", void 0), _define_property$5L(this, "slug", void 0), _define_property$5L(this, "integrations", void 0);
    }
}
_ts_decorate$5L([
    dto({
        string: true
    }),
    _ts_metadata$5L("design:type", String)
], AdminGetMerchantResponseDTO.prototype, "id", void 0);
_ts_decorate$5L([
    dto({
        string: true
    }),
    _ts_metadata$5L("design:type", String)
], AdminGetMerchantResponseDTO.prototype, "name", void 0);
_ts_decorate$5L([
    dto({
        string: true
    }),
    _ts_metadata$5L("design:type", String)
], AdminGetMerchantResponseDTO.prototype, "slug", void 0);
_ts_decorate$5L([
    dto({
        array: true,
        object: {
            discriminator: merchantIntegrationValueDiscriminator
        }
    }),
    _ts_metadata$5L("design:type", Array)
], AdminGetMerchantResponseDTO.prototype, "integrations", void 0);

function _define_property$5K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5K(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5K(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AppConfigRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5K(this, "merchantId", void 0);
    }
}
_ts_decorate$5K([
    dto({
        string: true
    }),
    _ts_metadata$5K("design:type", String)
], AppConfigRequestDTO.prototype, "merchantId", void 0);
class AppConfigOtelResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5K(this, "endpoint", void 0), _define_property$5K(this, "key", void 0);
    }
}
_ts_decorate$5K([
    dto({
        string: true
    }),
    _ts_metadata$5K("design:type", String)
], AppConfigOtelResponseDTO.prototype, "endpoint", void 0);
_ts_decorate$5K([
    dto({
        string: true
    }),
    _ts_metadata$5K("design:type", String)
], AppConfigOtelResponseDTO.prototype, "key", void 0);
class AppConfigCaptchaResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5K(this, "yandexCaptchaPageUrl", void 0);
    }
}
_ts_decorate$5K([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$5K("design:type", Object)
], AppConfigCaptchaResponseDTO.prototype, "yandexCaptchaPageUrl", void 0);
class AppConfigResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5K(this, "otel", void 0), _define_property$5K(this, "captcha", void 0);
    }
}
_ts_decorate$5K([
    dto({
        object: {
            type: AppConfigOtelResponseDTO
        }
    }),
    _ts_metadata$5K("design:type", typeof AppConfigOtelResponseDTO === "undefined" ? Object : AppConfigOtelResponseDTO)
], AppConfigResponseDTO.prototype, "otel", void 0);
_ts_decorate$5K([
    dto({
        object: {
            type: AppConfigCaptchaResponseDTO
        }
    }),
    _ts_metadata$5K("design:type", typeof AppConfigCaptchaResponseDTO === "undefined" ? Object : AppConfigCaptchaResponseDTO)
], AppConfigResponseDTO.prototype, "captcha", void 0);

function _define_property$5J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5J(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5J(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AppVersionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5J(this, "items", void 0);
    }
}
_ts_decorate$5J([
    dto({
        string: true,
        array: true
    }),
    _ts_metadata$5J("design:type", Array)
], AppVersionDTO.prototype, "items", void 0);

function _define_property$5I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5I(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5I(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthAdminEmailRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5I(this, "email", void 0), _define_property$5I(this, "password", void 0);
    }
}
_ts_decorate$5I([
    dto({
        email: true
    }),
    _ts_metadata$5I("design:type", String)
], AuthAdminEmailRequestDTO.prototype, "email", void 0);
_ts_decorate$5I([
    dto({
        string: true
    }),
    _ts_metadata$5I("design:type", String)
], AuthAdminEmailRequestDTO.prototype, "password", void 0);

function _define_property$5H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5H(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5H(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthAnonymousRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5H(this, "merchantId", void 0), _define_property$5H(this, "customAccessTokenExpiresInMs", void 0), _define_property$5H(this, "customRefreshTokenExpiresInMs", void 0);
    }
}
_ts_decorate$5H([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$5H("design:type", Object)
], AuthAnonymousRequestDTO.prototype, "merchantId", void 0);
_ts_decorate$5H([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$5H("design:type", Object)
], AuthAnonymousRequestDTO.prototype, "customAccessTokenExpiresInMs", void 0);
_ts_decorate$5H([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$5H("design:type", Object)
], AuthAnonymousRequestDTO.prototype, "customRefreshTokenExpiresInMs", void 0);

function _define_property$5G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5G(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5G(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthAnonymousResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5G(this, "token", void 0), _define_property$5G(this, "refreshToken", void 0), _define_property$5G(this, "accessTokenExpiresAt", void 0), _define_property$5G(this, "refreshTokenExpiresAt", void 0);
    }
}
_ts_decorate$5G([
    dto({
        string: true
    }),
    _ts_metadata$5G("design:type", String)
], AuthAnonymousResponseDTO.prototype, "token", void 0);
_ts_decorate$5G([
    dto({
        string: true
    }),
    _ts_metadata$5G("design:type", String)
], AuthAnonymousResponseDTO.prototype, "refreshToken", void 0);
_ts_decorate$5G([
    dto({
        date: true
    }),
    _ts_metadata$5G("design:type", typeof Date === "undefined" ? Object : Date)
], AuthAnonymousResponseDTO.prototype, "accessTokenExpiresAt", void 0);
_ts_decorate$5G([
    dto({
        date: true
    }),
    _ts_metadata$5G("design:type", typeof Date === "undefined" ? Object : Date)
], AuthAnonymousResponseDTO.prototype, "refreshTokenExpiresAt", void 0);

function _define_property$5F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5F(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5F(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthMerchantEmailRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5F(this, "merchantId", void 0), _define_property$5F(this, "email", void 0), _define_property$5F(this, "password", void 0);
    }
}
_ts_decorate$5F([
    dto({
        string: true
    }),
    _ts_metadata$5F("design:type", String)
], AuthMerchantEmailRequestDTO.prototype, "merchantId", void 0);
_ts_decorate$5F([
    dto({
        email: true
    }),
    _ts_metadata$5F("design:type", String)
], AuthMerchantEmailRequestDTO.prototype, "email", void 0);
_ts_decorate$5F([
    dto({
        string: true
    }),
    _ts_metadata$5F("design:type", String)
], AuthMerchantEmailRequestDTO.prototype, "password", void 0);

function _define_property$5E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5E(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5E(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthMerchantEmployeeByKeyRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), /** Ключ из externalInterfaces профиля. Отвечает 302 на интерфейс продавца */ _define_property$5E(this, "key", void 0);
    }
}
_ts_decorate$5E([
    dto({
        string: true
    }),
    _ts_metadata$5E("design:type", String)
], AuthMerchantEmployeeByKeyRequestDTO.prototype, "key", void 0);

function _define_property$5D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5D(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5D(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthPhoneFinishRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5D(this, "phone", void 0), _define_property$5D(this, "code", void 0), _define_property$5D(this, "customAccessTokenExpiresInMs", void 0), _define_property$5D(this, "customRefreshTokenExpiresInMs", void 0);
    }
}
_ts_decorate$5D([
    dto({
        phone: true
    }),
    _ts_metadata$5D("design:type", String)
], AuthPhoneFinishRequestDTO.prototype, "phone", void 0);
_ts_decorate$5D([
    dto({
        string: true
    }),
    _ts_metadata$5D("design:type", String)
], AuthPhoneFinishRequestDTO.prototype, "code", void 0);
_ts_decorate$5D([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$5D("design:type", Object)
], AuthPhoneFinishRequestDTO.prototype, "customAccessTokenExpiresInMs", void 0);
_ts_decorate$5D([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$5D("design:type", Object)
], AuthPhoneFinishRequestDTO.prototype, "customRefreshTokenExpiresInMs", void 0);

function _define_property$5C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5C(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5C(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthPhoneFinishResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5C(this, "token", void 0), _define_property$5C(this, "refreshToken", void 0), _define_property$5C(this, "accessTokenExpiresAt", void 0), _define_property$5C(this, "refreshTokenExpiresAt", void 0), _define_property$5C(this, "showTrackingRequest", void 0);
    }
}
_ts_decorate$5C([
    dto({
        string: true
    }),
    _ts_metadata$5C("design:type", String)
], AuthPhoneFinishResponseDTO.prototype, "token", void 0);
_ts_decorate$5C([
    dto({
        string: true
    }),
    _ts_metadata$5C("design:type", String)
], AuthPhoneFinishResponseDTO.prototype, "refreshToken", void 0);
_ts_decorate$5C([
    dto({
        date: true
    }),
    _ts_metadata$5C("design:type", typeof Date === "undefined" ? Object : Date)
], AuthPhoneFinishResponseDTO.prototype, "accessTokenExpiresAt", void 0);
_ts_decorate$5C([
    dto({
        date: true
    }),
    _ts_metadata$5C("design:type", typeof Date === "undefined" ? Object : Date)
], AuthPhoneFinishResponseDTO.prototype, "refreshTokenExpiresAt", void 0);
_ts_decorate$5C([
    dto({
        boolean: true
    }),
    _ts_metadata$5C("design:type", Boolean)
], AuthPhoneFinishResponseDTO.prototype, "showTrackingRequest", void 0);
class AuthPhoneFinishResponse401DTO extends BaseEntity {
    static async buildAndValidate(planeObject) {
        const dto = plainToClass(AuthPhoneFinishResponse401DTO, planeObject);
        await dto.validateAsyncAndThrowFirstError();
        return dto;
    }
    constructor(...args){
        super(...args), _define_property$5C(this, "nextRequestCodeAt", void 0), _define_property$5C(this, "codeReset", void 0), _define_property$5C(this, "attemptsLeft", void 0);
    }
}
_ts_decorate$5C([
    dto({
        date: true
    }),
    _ts_metadata$5C("design:type", typeof Date === "undefined" ? Object : Date)
], AuthPhoneFinishResponse401DTO.prototype, "nextRequestCodeAt", void 0);
_ts_decorate$5C([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$5C("design:type", Object)
], AuthPhoneFinishResponse401DTO.prototype, "codeReset", void 0);
_ts_decorate$5C([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$5C("design:type", Object)
], AuthPhoneFinishResponse401DTO.prototype, "attemptsLeft", void 0);

function _define_property$5B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5B(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5B(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthPhoneStartRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5B(this, "phone", void 0), /** В V1 токен обязателен. В V2 обязателен только для номеров, которым нужна капча. */ _define_property$5B(this, "captchaToken", void 0);
    }
}
_ts_decorate$5B([
    dto({
        phone: true
    }),
    _ts_metadata$5B("design:type", String)
], AuthPhoneStartRequestDTO.prototype, "phone", void 0);
_ts_decorate$5B([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$5B("design:type", Object)
], AuthPhoneStartRequestDTO.prototype, "captchaToken", void 0);

function _define_property$5A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5A(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5A(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthPhoneStartResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5A(this, "nextRequestCodeAt", void 0);
    }
}
_ts_decorate$5A([
    dto({
        date: true
    }),
    _ts_metadata$5A("design:type", typeof Date === "undefined" ? Object : Date)
], AuthPhoneStartResponseDTO.prototype, "nextRequestCodeAt", void 0);
class AuthPhoneStartV2ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5A(this, "captchaRequired", void 0), /** Срок повтора запроса кода. Присутствует только при captchaRequired: false. */ _define_property$5A(this, "nextRequestCodeAt", void 0);
    }
}
_ts_decorate$5A([
    dto({
        boolean: true
    }),
    _ts_metadata$5A("design:type", Boolean)
], AuthPhoneStartV2ResponseDTO.prototype, "captchaRequired", void 0);
_ts_decorate$5A([
    dto({
        date: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$5A("design:type", typeof Date === "undefined" ? Object : Date)
], AuthPhoneStartV2ResponseDTO.prototype, "nextRequestCodeAt", void 0);
class AuthPhoneStartResponse429DTO extends BaseEntity {
    static async buildAndValidate(planeObject) {
        const dto = plainToClass(AuthPhoneStartResponse429DTO, planeObject);
        await dto.validateAsyncAndThrowFirstError();
        return dto;
    }
    constructor(...args){
        super(...args), _define_property$5A(this, "nextRequestCodeAt", void 0);
    }
}
_ts_decorate$5A([
    dto({
        date: true
    }),
    _ts_metadata$5A("design:type", typeof Date === "undefined" ? Object : Date)
], AuthPhoneStartResponse429DTO.prototype, "nextRequestCodeAt", void 0);

function _define_property$5z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class AuthRefreshResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5z(this, "token", void 0), _define_property$5z(this, "refreshToken", void 0), _define_property$5z(this, "accessTokenExpiresAt", void 0), _define_property$5z(this, "refreshTokenExpiresAt", void 0);
    }
}
_ts_decorate$5z([
    dto({
        string: true
    }),
    _ts_metadata$5z("design:type", String)
], AuthRefreshResponseDTO.prototype, "token", void 0);
_ts_decorate$5z([
    dto({
        string: true
    }),
    _ts_metadata$5z("design:type", String)
], AuthRefreshResponseDTO.prototype, "refreshToken", void 0);
_ts_decorate$5z([
    dto({
        date: true
    }),
    _ts_metadata$5z("design:type", typeof Date === "undefined" ? Object : Date)
], AuthRefreshResponseDTO.prototype, "accessTokenExpiresAt", void 0);
_ts_decorate$5z([
    dto({
        date: true
    }),
    _ts_metadata$5z("design:type", typeof Date === "undefined" ? Object : Date)
], AuthRefreshResponseDTO.prototype, "refreshTokenExpiresAt", void 0);

function _define_property$5y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var BannerMediaType = /*#__PURE__*/ function(BannerMediaType) {
    BannerMediaType["IMAGE"] = "image";
    BannerMediaType["VIDEO"] = "video";
    return BannerMediaType;
}({});
class BannerMediaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5y(this, "type", void 0), _define_property$5y(this, "file", void 0);
    }
}
_ts_decorate$5y([
    dto({
        enum: BannerMediaType
    }),
    _ts_metadata$5y("design:type", String)
], BannerMediaDTO.prototype, "type", void 0);
_ts_decorate$5y([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5y("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerMediaDTO.prototype, "file", void 0);

function _define_property$5x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5x(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5x(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var BannerGroupPlace = /*#__PURE__*/ function(BannerGroupPlace) {
    BannerGroupPlace["HOME_MAIN"] = "home_main";
    BannerGroupPlace["HOME_BIG"] = "home_big";
    BannerGroupPlace["CATALOG_BOTTOM"] = "catalog_bottom";
    return BannerGroupPlace;
}({});
var BannerActionType = /*#__PURE__*/ function(BannerActionType) {
    BannerActionType["PRODUCT"] = "product";
    BannerActionType["PRODUCTS_CATALOG"] = "products_catalog";
    BannerActionType["HTML"] = "html";
    return BannerActionType;
}({});
class BannerActionForProductResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5x(this, "type", void 0);
    }
}
_ts_decorate$5x([
    dto({
        equals: "product"
    }),
    _ts_metadata$5x("design:type", typeof BannerActionType === "undefined" || "string" === "undefined" ? Object : "product")
], BannerActionForProductResponseDTO.prototype, "type", void 0);
class BannerActionForProductsCatalogResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5x(this, "type", void 0);
    }
}
_ts_decorate$5x([
    dto({
        equals: "products_catalog"
    }),
    _ts_metadata$5x("design:type", typeof BannerActionType === "undefined" || "string" === "undefined" ? Object : "products_catalog")
], BannerActionForProductsCatalogResponseDTO.prototype, "type", void 0);
class BannerActionForHtmlResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5x(this, "type", void 0);
    }
}
_ts_decorate$5x([
    dto({
        equals: "html"
    }),
    _ts_metadata$5x("design:type", typeof BannerActionType === "undefined" || "string" === "undefined" ? Object : "html")
], BannerActionForHtmlResponseDTO.prototype, "type", void 0);
class BannerInGroupResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5x(this, "id", void 0), _define_property$5x(this, "name", void 0), _define_property$5x(this, "media", void 0), _define_property$5x(this, "startDate", void 0), _define_property$5x(this, "endDate", void 0), _define_property$5x(this, "action", void 0);
    }
}
_ts_decorate$5x([
    dto({
        string: true
    }),
    _ts_metadata$5x("design:type", String)
], BannerInGroupResponseDTO.prototype, "id", void 0);
_ts_decorate$5x([
    dto({
        string: true
    }),
    _ts_metadata$5x("design:type", String)
], BannerInGroupResponseDTO.prototype, "name", void 0);
_ts_decorate$5x([
    dto({
        object: {
            type: BannerMediaDTO
        }
    }),
    _ts_metadata$5x("design:type", typeof BannerMediaDTO === "undefined" ? Object : BannerMediaDTO)
], BannerInGroupResponseDTO.prototype, "media", void 0);
_ts_decorate$5x([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5x("design:type", Object)
], BannerInGroupResponseDTO.prototype, "startDate", void 0);
_ts_decorate$5x([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5x("design:type", Object)
], BannerInGroupResponseDTO.prototype, "endDate", void 0);
_ts_decorate$5x([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "product",
                        value: BannerActionForProductResponseDTO
                    },
                    {
                        name: "products_catalog",
                        value: BannerActionForProductsCatalogResponseDTO
                    },
                    {
                        name: "html",
                        value: BannerActionForHtmlResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5x("design:type", Object)
], BannerInGroupResponseDTO.prototype, "action", void 0);
class BannerGroupResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5x(this, "place", void 0), _define_property$5x(this, "banners", void 0);
    }
}
_ts_decorate$5x([
    dto({
        enum: BannerGroupPlace
    }),
    _ts_metadata$5x("design:type", String)
], BannerGroupResponseDTO.prototype, "place", void 0);
_ts_decorate$5x([
    dto({
        array: true,
        object: {
            type: BannerInGroupResponseDTO
        }
    }),
    _ts_metadata$5x("design:type", Array)
], BannerGroupResponseDTO.prototype, "banners", void 0);
class BannerGroupListResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5x(this, "groups", void 0);
    }
}
_ts_decorate$5x([
    dto({
        array: true,
        object: {
            type: BannerGroupResponseDTO
        }
    }),
    _ts_metadata$5x("design:type", Array)
], BannerGroupListResponseDTO.prototype, "groups", void 0);

function _define_property$5w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5w(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5w(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class BannerGroupRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5w(this, "places", void 0);
    }
}
_ts_decorate$5w([
    dto({
        array: true,
        enum: BannerGroupPlace
    }),
    wrapToArray,
    _ts_metadata$5w("design:type", Array)
], BannerGroupRequestDTO.prototype, "places", void 0);

function _define_property$5v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5v(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5v(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class BannerDetailActionForProductResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5v(this, "type", void 0), _define_property$5v(this, "productId", void 0);
    }
}
_ts_decorate$5v([
    dto({
        equals: BannerActionType.PRODUCT
    }),
    _ts_metadata$5v("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCT === "undefined" ? Object : BannerActionType.PRODUCT)
], BannerDetailActionForProductResponseDTO.prototype, "type", void 0);
_ts_decorate$5v([
    dto({
        string: true
    }),
    _ts_metadata$5v("design:type", String)
], BannerDetailActionForProductResponseDTO.prototype, "productId", void 0);
class BannerDetailActionForProductsCatalogResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5v(this, "type", void 0), _define_property$5v(this, "description", void 0);
    }
}
_ts_decorate$5v([
    dto({
        equals: BannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$5v("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : BannerActionType.PRODUCTS_CATALOG)
], BannerDetailActionForProductsCatalogResponseDTO.prototype, "type", void 0);
_ts_decorate$5v([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$5v("design:type", Object)
], BannerDetailActionForProductsCatalogResponseDTO.prototype, "description", void 0);
class BannerDetailActionForHtmlResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5v(this, "type", void 0), _define_property$5v(this, "file", void 0);
    }
}
_ts_decorate$5v([
    dto({
        equals: BannerActionType.HTML
    }),
    _ts_metadata$5v("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.HTML === "undefined" ? Object : BannerActionType.HTML)
], BannerDetailActionForHtmlResponseDTO.prototype, "type", void 0);
_ts_decorate$5v([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5v("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerDetailActionForHtmlResponseDTO.prototype, "file", void 0);
class BannerDetailResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5v(this, "id", void 0), _define_property$5v(this, "name", void 0), _define_property$5v(this, "media", void 0), _define_property$5v(this, "startDate", void 0), _define_property$5v(this, "endDate", void 0), _define_property$5v(this, "action", void 0);
    }
}
_ts_decorate$5v([
    dto({
        string: true
    }),
    _ts_metadata$5v("design:type", String)
], BannerDetailResponseDTO.prototype, "id", void 0);
_ts_decorate$5v([
    dto({
        string: true
    }),
    _ts_metadata$5v("design:type", String)
], BannerDetailResponseDTO.prototype, "name", void 0);
_ts_decorate$5v([
    dto({
        object: {
            type: BannerMediaDTO
        }
    }),
    _ts_metadata$5v("design:type", typeof BannerMediaDTO === "undefined" ? Object : BannerMediaDTO)
], BannerDetailResponseDTO.prototype, "media", void 0);
_ts_decorate$5v([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5v("design:type", Object)
], BannerDetailResponseDTO.prototype, "startDate", void 0);
_ts_decorate$5v([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5v("design:type", Object)
], BannerDetailResponseDTO.prototype, "endDate", void 0);
_ts_decorate$5v([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: BannerActionType.PRODUCT,
                        value: BannerDetailActionForProductResponseDTO
                    },
                    {
                        name: BannerActionType.PRODUCTS_CATALOG,
                        value: BannerDetailActionForProductsCatalogResponseDTO
                    },
                    {
                        name: BannerActionType.HTML,
                        value: BannerDetailActionForHtmlResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5v("design:type", Object)
], BannerDetailResponseDTO.prototype, "action", void 0);

function _define_property$5u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5u(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5u(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var BannerGroupPlaceV1 = /*#__PURE__*/ function(BannerGroupPlaceV1) {
    BannerGroupPlaceV1["HOME_MAIN"] = "home_main";
    BannerGroupPlaceV1["HOME_BIG"] = "home_big";
    BannerGroupPlaceV1["CATALOG_BOTTOM"] = "catalog_bottom";
    return BannerGroupPlaceV1;
}({});
var BannerActionTypeV1 = /*#__PURE__*/ function(BannerActionTypeV1) {
    BannerActionTypeV1["PRODUCT"] = "product";
    BannerActionTypeV1["PRODUCTS_CATALOG"] = "products_catalog";
    return BannerActionTypeV1;
}({});
class BannerActionForProductResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5u(this, "type", void 0), _define_property$5u(this, "productId", void 0);
    }
}
_ts_decorate$5u([
    dto({
        equals: "product"
    }),
    _ts_metadata$5u("design:type", typeof BannerActionTypeV1 === "undefined" || "string" === "undefined" ? Object : "product")
], BannerActionForProductResponseV1DTO.prototype, "type", void 0);
_ts_decorate$5u([
    dto({
        string: true
    }),
    _ts_metadata$5u("design:type", String)
], BannerActionForProductResponseV1DTO.prototype, "productId", void 0);
class BannerActionForProductsCatalogResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5u(this, "type", void 0);
    }
}
_ts_decorate$5u([
    dto({
        equals: "products_catalog"
    }),
    _ts_metadata$5u("design:type", typeof BannerActionTypeV1 === "undefined" || "string" === "undefined" ? Object : "products_catalog")
], BannerActionForProductsCatalogResponseV1DTO.prototype, "type", void 0);
class BannerInGroupResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5u(this, "id", void 0), _define_property$5u(this, "name", void 0), _define_property$5u(this, "image", void 0), _define_property$5u(this, "startDate", void 0), _define_property$5u(this, "endDate", void 0), _define_property$5u(this, "action", void 0);
    }
}
_ts_decorate$5u([
    dto({
        string: true
    }),
    _ts_metadata$5u("design:type", String)
], BannerInGroupResponseV1DTO.prototype, "id", void 0);
_ts_decorate$5u([
    dto({
        string: true
    }),
    _ts_metadata$5u("design:type", String)
], BannerInGroupResponseV1DTO.prototype, "name", void 0);
_ts_decorate$5u([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5u("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerInGroupResponseV1DTO.prototype, "image", void 0);
_ts_decorate$5u([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5u("design:type", Object)
], BannerInGroupResponseV1DTO.prototype, "startDate", void 0);
_ts_decorate$5u([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5u("design:type", Object)
], BannerInGroupResponseV1DTO.prototype, "endDate", void 0);
_ts_decorate$5u([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "product",
                        value: BannerActionForProductResponseV1DTO
                    },
                    {
                        name: "products_catalog",
                        value: BannerActionForProductsCatalogResponseV1DTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5u("design:type", Object)
], BannerInGroupResponseV1DTO.prototype, "action", void 0);
class BannerGroupResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5u(this, "place", void 0), _define_property$5u(this, "banners", void 0);
    }
}
_ts_decorate$5u([
    dto({
        enum: BannerGroupPlaceV1
    }),
    _ts_metadata$5u("design:type", String)
], BannerGroupResponseV1DTO.prototype, "place", void 0);
_ts_decorate$5u([
    dto({
        array: true,
        object: {
            type: BannerInGroupResponseV1DTO
        }
    }),
    _ts_metadata$5u("design:type", Array)
], BannerGroupResponseV1DTO.prototype, "banners", void 0);
class BannerGroupListResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5u(this, "groups", void 0);
    }
}
_ts_decorate$5u([
    dto({
        array: true,
        object: {
            type: BannerGroupResponseV1DTO
        }
    }),
    _ts_metadata$5u("design:type", Array)
], BannerGroupListResponseV1DTO.prototype, "groups", void 0);

function _define_property$5t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5t(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5t(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class BannerGroupRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5t(this, "places", void 0);
    }
}
_ts_decorate$5t([
    dto({
        array: true,
        enum: BannerGroupPlaceV1
    }),
    wrapToArray,
    _ts_metadata$5t("design:type", Array)
], BannerGroupRequestV1DTO.prototype, "places", void 0);

function _define_property$5s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5s(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5s(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class BannerDetailActionForProductResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5s(this, "type", void 0), _define_property$5s(this, "productId", void 0);
    }
}
_ts_decorate$5s([
    dto({
        equals: BannerActionTypeV1.PRODUCT
    }),
    _ts_metadata$5s("design:type", typeof BannerActionTypeV1 === "undefined" || typeof BannerActionTypeV1.PRODUCT === "undefined" ? Object : BannerActionTypeV1.PRODUCT)
], BannerDetailActionForProductResponseV1DTO.prototype, "type", void 0);
_ts_decorate$5s([
    dto({
        string: true
    }),
    _ts_metadata$5s("design:type", String)
], BannerDetailActionForProductResponseV1DTO.prototype, "productId", void 0);
class BannerDetailActionForProductsCatalogResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5s(this, "type", void 0), _define_property$5s(this, "description", void 0);
    }
}
_ts_decorate$5s([
    dto({
        equals: BannerActionTypeV1.PRODUCTS_CATALOG
    }),
    _ts_metadata$5s("design:type", typeof BannerActionTypeV1 === "undefined" || typeof BannerActionTypeV1.PRODUCTS_CATALOG === "undefined" ? Object : BannerActionTypeV1.PRODUCTS_CATALOG)
], BannerDetailActionForProductsCatalogResponseV1DTO.prototype, "type", void 0);
_ts_decorate$5s([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$5s("design:type", Object)
], BannerDetailActionForProductsCatalogResponseV1DTO.prototype, "description", void 0);
class BannerDetailResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5s(this, "id", void 0), _define_property$5s(this, "name", void 0), _define_property$5s(this, "image", void 0), _define_property$5s(this, "startDate", void 0), _define_property$5s(this, "endDate", void 0), _define_property$5s(this, "action", void 0);
    }
}
_ts_decorate$5s([
    dto({
        string: true
    }),
    _ts_metadata$5s("design:type", String)
], BannerDetailResponseV1DTO.prototype, "id", void 0);
_ts_decorate$5s([
    dto({
        string: true
    }),
    _ts_metadata$5s("design:type", String)
], BannerDetailResponseV1DTO.prototype, "name", void 0);
_ts_decorate$5s([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5s("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerDetailResponseV1DTO.prototype, "image", void 0);
_ts_decorate$5s([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5s("design:type", Object)
], BannerDetailResponseV1DTO.prototype, "startDate", void 0);
_ts_decorate$5s([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5s("design:type", Object)
], BannerDetailResponseV1DTO.prototype, "endDate", void 0);
_ts_decorate$5s([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: BannerActionTypeV1.PRODUCT,
                        value: BannerDetailActionForProductResponseV1DTO
                    },
                    {
                        name: BannerActionTypeV1.PRODUCTS_CATALOG,
                        value: BannerDetailActionForProductsCatalogResponseV1DTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5s("design:type", Object)
], BannerDetailResponseV1DTO.prototype, "action", void 0);

function _define_property$5r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5r(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5r(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class BannerDetailActionForProductResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5r(this, "type", void 0), _define_property$5r(this, "productId", void 0);
    }
}
_ts_decorate$5r([
    dto({
        equals: BannerActionType.PRODUCT
    }),
    _ts_metadata$5r("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCT === "undefined" ? Object : BannerActionType.PRODUCT)
], BannerDetailActionForProductResponseV2DTO.prototype, "type", void 0);
_ts_decorate$5r([
    dto({
        string: true
    }),
    _ts_metadata$5r("design:type", String)
], BannerDetailActionForProductResponseV2DTO.prototype, "productId", void 0);
class BannerDetailActionForProductsCatalogResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5r(this, "type", void 0), _define_property$5r(this, "description", void 0);
    }
}
_ts_decorate$5r([
    dto({
        equals: BannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$5r("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : BannerActionType.PRODUCTS_CATALOG)
], BannerDetailActionForProductsCatalogResponseV2DTO.prototype, "type", void 0);
_ts_decorate$5r([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$5r("design:type", Object)
], BannerDetailActionForProductsCatalogResponseV2DTO.prototype, "description", void 0);
class BannerDetailActionForHtmlResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5r(this, "type", void 0), _define_property$5r(this, "file", void 0);
    }
}
_ts_decorate$5r([
    dto({
        equals: BannerActionType.HTML
    }),
    _ts_metadata$5r("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.HTML === "undefined" ? Object : BannerActionType.HTML)
], BannerDetailActionForHtmlResponseV2DTO.prototype, "type", void 0);
_ts_decorate$5r([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5r("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerDetailActionForHtmlResponseV2DTO.prototype, "file", void 0);
class BannerDetailResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5r(this, "id", void 0), _define_property$5r(this, "name", void 0), _define_property$5r(this, "image", void 0), _define_property$5r(this, "startDate", void 0), _define_property$5r(this, "endDate", void 0), _define_property$5r(this, "action", void 0);
    }
}
_ts_decorate$5r([
    dto({
        string: true
    }),
    _ts_metadata$5r("design:type", String)
], BannerDetailResponseV2DTO.prototype, "id", void 0);
_ts_decorate$5r([
    dto({
        string: true
    }),
    _ts_metadata$5r("design:type", String)
], BannerDetailResponseV2DTO.prototype, "name", void 0);
_ts_decorate$5r([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5r("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerDetailResponseV2DTO.prototype, "image", void 0);
_ts_decorate$5r([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5r("design:type", Object)
], BannerDetailResponseV2DTO.prototype, "startDate", void 0);
_ts_decorate$5r([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5r("design:type", Object)
], BannerDetailResponseV2DTO.prototype, "endDate", void 0);
_ts_decorate$5r([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: BannerActionType.PRODUCT,
                        value: BannerDetailActionForProductResponseV2DTO
                    },
                    {
                        name: BannerActionType.PRODUCTS_CATALOG,
                        value: BannerDetailActionForProductsCatalogResponseV2DTO
                    },
                    {
                        name: BannerActionType.HTML,
                        value: BannerDetailActionForHtmlResponseV2DTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5r("design:type", Object)
], BannerDetailResponseV2DTO.prototype, "action", void 0);

function _define_property$5q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class BannerActionForProductResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5q(this, "type", void 0);
    }
}
_ts_decorate$5q([
    dto({
        equals: BannerActionType.PRODUCT
    }),
    _ts_metadata$5q("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCT === "undefined" ? Object : BannerActionType.PRODUCT)
], BannerActionForProductResponseV2DTO.prototype, "type", void 0);
class BannerActionForProductsCatalogResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5q(this, "type", void 0);
    }
}
_ts_decorate$5q([
    dto({
        equals: BannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$5q("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : BannerActionType.PRODUCTS_CATALOG)
], BannerActionForProductsCatalogResponseV2DTO.prototype, "type", void 0);
class BannerActionForHtmlResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5q(this, "type", void 0);
    }
}
_ts_decorate$5q([
    dto({
        equals: BannerActionType.HTML
    }),
    _ts_metadata$5q("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.HTML === "undefined" ? Object : BannerActionType.HTML)
], BannerActionForHtmlResponseV2DTO.prototype, "type", void 0);
class BannerInGroupResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5q(this, "id", void 0), _define_property$5q(this, "name", void 0), _define_property$5q(this, "image", void 0), _define_property$5q(this, "startDate", void 0), _define_property$5q(this, "endDate", void 0), _define_property$5q(this, "action", void 0);
    }
}
_ts_decorate$5q([
    dto({
        string: true
    }),
    _ts_metadata$5q("design:type", String)
], BannerInGroupResponseV2DTO.prototype, "id", void 0);
_ts_decorate$5q([
    dto({
        string: true
    }),
    _ts_metadata$5q("design:type", String)
], BannerInGroupResponseV2DTO.prototype, "name", void 0);
_ts_decorate$5q([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5q("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], BannerInGroupResponseV2DTO.prototype, "image", void 0);
_ts_decorate$5q([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5q("design:type", Object)
], BannerInGroupResponseV2DTO.prototype, "startDate", void 0);
_ts_decorate$5q([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$5q("design:type", Object)
], BannerInGroupResponseV2DTO.prototype, "endDate", void 0);
_ts_decorate$5q([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: BannerActionType.PRODUCT,
                        value: BannerActionForProductResponseV2DTO
                    },
                    {
                        name: BannerActionType.PRODUCTS_CATALOG,
                        value: BannerActionForProductsCatalogResponseV2DTO
                    },
                    {
                        name: BannerActionType.HTML,
                        value: BannerActionForHtmlResponseV2DTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5q("design:type", Object)
], BannerInGroupResponseV2DTO.prototype, "action", void 0);
class BannerGroupResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5q(this, "place", void 0), _define_property$5q(this, "banners", void 0);
    }
}
_ts_decorate$5q([
    dto({
        enum: BannerGroupPlace
    }),
    _ts_metadata$5q("design:type", typeof BannerGroupPlace === "undefined" ? Object : BannerGroupPlace)
], BannerGroupResponseV2DTO.prototype, "place", void 0);
_ts_decorate$5q([
    dto({
        array: true,
        object: {
            type: BannerInGroupResponseV2DTO
        }
    }),
    _ts_metadata$5q("design:type", Array)
], BannerGroupResponseV2DTO.prototype, "banners", void 0);
class BannerGroupListResponseV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5q(this, "groups", void 0);
    }
}
_ts_decorate$5q([
    dto({
        array: true,
        object: {
            type: BannerGroupResponseV2DTO
        }
    }),
    _ts_metadata$5q("design:type", Array)
], BannerGroupListResponseV2DTO.prototype, "groups", void 0);

function _define_property$5p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5p(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5p(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Бейдж товара: хранится в jsonb и проходит passthrough-ом через все слои —
// приём из интеграции и отдача в деталке/списках.
var ProductBadgeIconType = /*#__PURE__*/ function(ProductBadgeIconType) {
    ProductBadgeIconType["INTERNAL"] = "internal";
    return ProductBadgeIconType;
}({});
class ProductBadgeIconDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5p(this, "type", void 0), _define_property$5p(this, "icon", void 0);
    }
}
_ts_decorate$5p([
    dto({
        enum: ProductBadgeIconType
    }),
    _ts_metadata$5p("design:type", String)
], ProductBadgeIconDTO.prototype, "type", void 0);
_ts_decorate$5p([
    dto({
        string: true
    }),
    _ts_metadata$5p("design:type", String)
], ProductBadgeIconDTO.prototype, "icon", void 0);
class ProductBadgeColorsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5p(this, "icon", void 0), _define_property$5p(this, "text", void 0), _define_property$5p(this, "background", void 0);
    }
}
_ts_decorate$5p([
    dto({
        string: true
    }),
    _ts_metadata$5p("design:type", String)
], ProductBadgeColorsDTO.prototype, "icon", void 0);
_ts_decorate$5p([
    dto({
        string: true
    }),
    _ts_metadata$5p("design:type", String)
], ProductBadgeColorsDTO.prototype, "text", void 0);
_ts_decorate$5p([
    dto({
        string: true
    }),
    _ts_metadata$5p("design:type", String)
], ProductBadgeColorsDTO.prototype, "background", void 0);
class ProductBadgeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5p(this, "text", void 0), _define_property$5p(this, "icon", void 0), _define_property$5p(this, "colors", void 0);
    }
}
_ts_decorate$5p([
    dto({
        string: true
    }),
    _ts_metadata$5p("design:type", String)
], ProductBadgeDTO.prototype, "text", void 0);
_ts_decorate$5p([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: ProductBadgeIconDTO
        }
    }),
    _ts_metadata$5p("design:type", Object)
], ProductBadgeDTO.prototype, "icon", void 0);
_ts_decorate$5p([
    dto({
        object: {
            type: ProductBadgeColorsDTO
        }
    }),
    _ts_metadata$5p("design:type", typeof ProductBadgeColorsDTO === "undefined" ? Object : ProductBadgeColorsDTO)
], ProductBadgeDTO.prototype, "colors", void 0);

function _define_property$5o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5o(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5o(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class SetProductFavouriteRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5o(this, "id", void 0), _define_property$5o(this, "favourite", void 0);
    }
}
_ts_decorate$5o([
    dto({
        string: true
    }),
    _ts_metadata$5o("design:type", String)
], SetProductFavouriteRequestDTO.prototype, "id", void 0);
_ts_decorate$5o([
    dto({
        boolean: true
    }),
    _ts_metadata$5o("design:type", Boolean)
], SetProductFavouriteRequestDTO.prototype, "favourite", void 0);

function _define_property$5n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5n(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5n(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ProductFiltersBaseSortingDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5n(this, "code", void 0), _define_property$5n(this, "name", void 0);
    }
}
_ts_decorate$5n([
    dto({
        string: true
    }),
    _ts_metadata$5n("design:type", String)
], ProductFiltersBaseSortingDTO.prototype, "code", void 0);
_ts_decorate$5n([
    dto({
        string: true
    }),
    _ts_metadata$5n("design:type", String)
], ProductFiltersBaseSortingDTO.prototype, "name", void 0);
class ProductFiltersBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5n(this, "sortingFields", void 0);
    }
}
_ts_decorate$5n([
    dto({
        array: true,
        object: {
            type: ProductFiltersBaseSortingDTO
        }
    }),
    _ts_metadata$5n("design:type", Array)
], ProductFiltersBaseDTO.prototype, "sortingFields", void 0);

function _define_property$5m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5m(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5m(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetProductsListFiltersByCategoryRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5m(this, "categoryId", void 0);
    }
}
_ts_decorate$5m([
    dto({
        string: true
    }),
    _ts_metadata$5m("design:type", String)
], GetProductsListFiltersByCategoryRequestDTO.prototype, "categoryId", void 0);

var ProductAttributeType = /*#__PURE__*/ function(ProductAttributeType) {
    ProductAttributeType["STATIC"] = "static";
    ProductAttributeType["DYNAMIC"] = "dynamic";
    return ProductAttributeType;
}({});
var ProductAttributeValueType = /*#__PURE__*/ function(ProductAttributeValueType) {
    ProductAttributeValueType["STRING"] = "string";
    ProductAttributeValueType["STRING_ARRAY"] = "string_array";
    ProductAttributeValueType["BOOLEAN"] = "boolean";
    ProductAttributeValueType["BOOLEAN_TRUE"] = "boolean_true";
    ProductAttributeValueType["NUMBER"] = "number";
    return ProductAttributeValueType;
}({});
var ProductAttributeRequiredToOrderMode = /*#__PURE__*/ function(ProductAttributeRequiredToOrderMode) {
    ProductAttributeRequiredToOrderMode["SINGLE"] = "single";
    ProductAttributeRequiredToOrderMode["MULTI"] = "multi";
    return ProductAttributeRequiredToOrderMode;
}({});

function _define_property$5l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5l(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5l(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ProductAttributeValueStringDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "valueType", void 0), _define_property$5l(this, "id", void 0), _define_property$5l(this, "name", void 0);
    }
}
_ts_decorate$5l([
    dto({
        equals: ProductAttributeValueType.STRING
    }),
    _ts_metadata$5l("design:type", typeof ProductAttributeValueType === "undefined" || typeof ProductAttributeValueType.STRING === "undefined" ? Object : ProductAttributeValueType.STRING)
], ProductAttributeValueStringDTO.prototype, "valueType", void 0);
_ts_decorate$5l([
    dto({
        string: true
    }),
    _ts_metadata$5l("design:type", String)
], ProductAttributeValueStringDTO.prototype, "id", void 0);
_ts_decorate$5l([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5l("design:type", Object)
], ProductAttributeValueStringDTO.prototype, "name", void 0);
class ProductAttributeValueStringArrayItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "id", void 0), _define_property$5l(this, "name", void 0);
    }
}
_ts_decorate$5l([
    dto({
        string: true
    }),
    _ts_metadata$5l("design:type", String)
], ProductAttributeValueStringArrayItemDTO.prototype, "id", void 0);
_ts_decorate$5l([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5l("design:type", Object)
], ProductAttributeValueStringArrayItemDTO.prototype, "name", void 0);
class ProductAttributeValueStringArrayDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "valueType", void 0), _define_property$5l(this, "items", void 0);
    }
}
_ts_decorate$5l([
    dto({
        equals: ProductAttributeValueType.STRING_ARRAY
    }),
    _ts_metadata$5l("design:type", typeof ProductAttributeValueType === "undefined" || typeof ProductAttributeValueType.STRING_ARRAY === "undefined" ? Object : ProductAttributeValueType.STRING_ARRAY)
], ProductAttributeValueStringArrayDTO.prototype, "valueType", void 0);
_ts_decorate$5l([
    dto({
        array: true,
        object: {
            type: ProductAttributeValueStringArrayItemDTO
        }
    }),
    _ts_metadata$5l("design:type", Array)
], ProductAttributeValueStringArrayDTO.prototype, "items", void 0);
class ProductAttributeValueBooleanDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "valueType", void 0), _define_property$5l(this, "value", void 0);
    }
}
_ts_decorate$5l([
    dto({
        equals: ProductAttributeValueType.BOOLEAN
    }),
    _ts_metadata$5l("design:type", typeof ProductAttributeValueType === "undefined" || typeof ProductAttributeValueType.BOOLEAN === "undefined" ? Object : ProductAttributeValueType.BOOLEAN)
], ProductAttributeValueBooleanDTO.prototype, "valueType", void 0);
_ts_decorate$5l([
    dto({
        boolean: true
    }),
    _ts_metadata$5l("design:type", Boolean)
], ProductAttributeValueBooleanDTO.prototype, "value", void 0);
class ProductAttributeValueBooleanTrueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "valueType", void 0);
    }
}
_ts_decorate$5l([
    dto({
        equals: ProductAttributeValueType.BOOLEAN_TRUE
    }),
    _ts_metadata$5l("design:type", typeof ProductAttributeValueType === "undefined" || typeof ProductAttributeValueType.BOOLEAN_TRUE === "undefined" ? Object : ProductAttributeValueType.BOOLEAN_TRUE)
], ProductAttributeValueBooleanTrueDTO.prototype, "valueType", void 0);
class ProductAttributeValueNumberDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "valueType", void 0), _define_property$5l(this, "value", void 0);
    }
}
_ts_decorate$5l([
    dto({
        equals: ProductAttributeValueType.NUMBER
    }),
    _ts_metadata$5l("design:type", typeof ProductAttributeValueType === "undefined" || typeof ProductAttributeValueType.NUMBER === "undefined" ? Object : ProductAttributeValueType.NUMBER)
], ProductAttributeValueNumberDTO.prototype, "valueType", void 0);
_ts_decorate$5l([
    dto({
        number: true
    }),
    _ts_metadata$5l("design:type", Number)
], ProductAttributeValueNumberDTO.prototype, "value", void 0);
class ProductAttributeRequiredToOrderDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "mode", void 0);
    }
}
_ts_decorate$5l([
    dto({
        enum: ProductAttributeRequiredToOrderMode
    }),
    _ts_metadata$5l("design:type", typeof ProductAttributeRequiredToOrderMode === "undefined" ? Object : ProductAttributeRequiredToOrderMode)
], ProductAttributeRequiredToOrderDTO.prototype, "mode", void 0);
class ProductAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5l(this, "id", void 0), _define_property$5l(this, "name", void 0), _define_property$5l(this, "measure", void 0), _define_property$5l(this, "requiredToOrder", void 0), _define_property$5l(this, "value", void 0);
    }
}
_ts_decorate$5l([
    dto({
        string: true
    }),
    _ts_metadata$5l("design:type", String)
], ProductAttributeDTO.prototype, "id", void 0);
_ts_decorate$5l([
    dto({
        string: true
    }),
    _ts_metadata$5l("design:type", String)
], ProductAttributeDTO.prototype, "name", void 0);
_ts_decorate$5l([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5l("design:type", Object)
], ProductAttributeDTO.prototype, "measure", void 0);
_ts_decorate$5l([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: ProductAttributeRequiredToOrderDTO
        }
    }),
    _ts_metadata$5l("design:type", Object)
], ProductAttributeDTO.prototype, "requiredToOrder", void 0);
_ts_decorate$5l([
    dto({
        object: {
            discriminator: {
                property: "valueType",
                subTypes: [
                    {
                        name: ProductAttributeValueType.STRING,
                        value: ProductAttributeValueStringDTO
                    },
                    {
                        name: ProductAttributeValueType.STRING_ARRAY,
                        value: ProductAttributeValueStringArrayDTO
                    },
                    {
                        name: ProductAttributeValueType.BOOLEAN,
                        value: ProductAttributeValueBooleanDTO
                    },
                    {
                        name: ProductAttributeValueType.BOOLEAN_TRUE,
                        value: ProductAttributeValueBooleanTrueDTO
                    },
                    {
                        name: ProductAttributeValueType.NUMBER,
                        value: ProductAttributeValueNumberDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5l("design:type", Object)
], ProductAttributeDTO.prototype, "value", void 0);

function _define_property$5k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5k(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5k(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ProductFilterByCategoryVariantDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "id", void 0), _define_property$5k(this, "name", void 0);
    }
}
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFilterByCategoryVariantDTO.prototype, "id", void 0);
_ts_decorate$5k([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5k("design:type", Object)
], ProductFilterByCategoryVariantDTO.prototype, "name", void 0);
class ProductFilterByCategoryRangeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "min", void 0), _define_property$5k(this, "max", void 0);
    }
}
_ts_decorate$5k([
    dto({
        number: true
    }),
    _ts_metadata$5k("design:type", Number)
], ProductFilterByCategoryRangeDTO.prototype, "min", void 0);
_ts_decorate$5k([
    dto({
        number: true
    }),
    _ts_metadata$5k("design:type", Number)
], ProductFilterByCategoryRangeDTO.prototype, "max", void 0);
class ProductFilterByCategoryAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "valueType", void 0), _define_property$5k(this, "id", void 0), _define_property$5k(this, "name", void 0), _define_property$5k(this, "measure", void 0), _define_property$5k(this, "variants", void 0), // Заполняется только у number: границы всех числовых значений категории для ползунка.
        // Отсутствует не только у остальных типов, но и в кеше фильтров, посчитанном до появления поля.
        _define_property$5k(this, "range", void 0);
    }
}
_ts_decorate$5k([
    dto({
        enum: ProductAttributeValueType
    }),
    _ts_metadata$5k("design:type", typeof ProductAttributeValueType === "undefined" ? Object : ProductAttributeValueType)
], ProductFilterByCategoryAttributeDTO.prototype, "valueType", void 0);
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFilterByCategoryAttributeDTO.prototype, "id", void 0);
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFilterByCategoryAttributeDTO.prototype, "name", void 0);
_ts_decorate$5k([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5k("design:type", Object)
], ProductFilterByCategoryAttributeDTO.prototype, "measure", void 0);
_ts_decorate$5k([
    dto({
        optional: {
            allowUndefined: false
        },
        array: true,
        object: {
            type: ProductFilterByCategoryVariantDTO
        }
    }),
    _ts_metadata$5k("design:type", Object)
], ProductFilterByCategoryAttributeDTO.prototype, "variants", void 0);
_ts_decorate$5k([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: ProductFilterByCategoryRangeDTO
        }
    }),
    _ts_metadata$5k("design:type", Object)
], ProductFilterByCategoryAttributeDTO.prototype, "range", void 0);
class ProductFilterByCategoryPartDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "id", void 0), _define_property$5k(this, "name", void 0);
    }
}
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFilterByCategoryPartDTO.prototype, "id", void 0);
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFilterByCategoryPartDTO.prototype, "name", void 0);
class ProductFiltersByCategorySortingDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "code", void 0), _define_property$5k(this, "name", void 0);
    }
}
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFiltersByCategorySortingDTO.prototype, "code", void 0);
_ts_decorate$5k([
    dto({
        string: true
    }),
    _ts_metadata$5k("design:type", String)
], ProductFiltersByCategorySortingDTO.prototype, "name", void 0);
class ProductFiltersByCategoryPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "min", void 0), _define_property$5k(this, "max", void 0);
    }
}
_ts_decorate$5k([
    dto({
        number: true
    }),
    _ts_metadata$5k("design:type", Number)
], ProductFiltersByCategoryPriceDTO.prototype, "min", void 0);
_ts_decorate$5k([
    dto({
        number: true
    }),
    _ts_metadata$5k("design:type", Number)
], ProductFiltersByCategoryPriceDTO.prototype, "max", void 0);
class ProductFiltersByCategoryDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5k(this, "attributes", void 0), _define_property$5k(this, "parts", void 0), _define_property$5k(this, "partAttributes", void 0), _define_property$5k(this, "sortingFields", void 0), _define_property$5k(this, "price", void 0);
    }
}
_ts_decorate$5k([
    dto({
        array: true,
        object: {
            type: ProductFilterByCategoryAttributeDTO
        }
    }),
    _ts_metadata$5k("design:type", Array)
], ProductFiltersByCategoryDTO.prototype, "attributes", void 0);
_ts_decorate$5k([
    dto({
        array: true,
        object: {
            type: ProductFilterByCategoryPartDTO
        }
    }),
    _ts_metadata$5k("design:type", Array)
], ProductFiltersByCategoryDTO.prototype, "parts", void 0);
_ts_decorate$5k([
    dto({
        array: true,
        object: {
            type: ProductFilterByCategoryAttributeDTO
        }
    }),
    _ts_metadata$5k("design:type", Array)
], ProductFiltersByCategoryDTO.prototype, "partAttributes", void 0);
_ts_decorate$5k([
    dto({
        array: true,
        object: {
            type: ProductFiltersByCategorySortingDTO
        }
    }),
    _ts_metadata$5k("design:type", Array)
], ProductFiltersByCategoryDTO.prototype, "sortingFields", void 0);
_ts_decorate$5k([
    dto({
        object: {
            type: ProductFiltersByCategoryPriceDTO
        }
    }),
    _ts_metadata$5k("design:type", typeof ProductFiltersByCategoryPriceDTO === "undefined" ? Object : ProductFiltersByCategoryPriceDTO)
], ProductFiltersByCategoryDTO.prototype, "price", void 0);

function _define_property$5j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5j(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5j(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ProductInfoSectionType = /*#__PURE__*/ function(ProductInfoSectionType) {
    ProductInfoSectionType["TEXT"] = "text";
    ProductInfoSectionType["LINK"] = "link";
    return ProductInfoSectionType;
}({});
class ProductInfoSectionTextDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5j(this, "type", void 0);
    }
}
_ts_decorate$5j([
    dto({
        equals: "text"
    }),
    _ts_metadata$5j("design:type", typeof ProductInfoSectionType === "undefined" || "string" === "undefined" ? Object : "text")
], ProductInfoSectionTextDTO.prototype, "type", void 0);
class ProductInfoSectionLinkDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5j(this, "type", void 0), _define_property$5j(this, "link", void 0);
    }
}
_ts_decorate$5j([
    dto({
        equals: "link"
    }),
    _ts_metadata$5j("design:type", typeof ProductInfoSectionType === "undefined" || "string" === "undefined" ? Object : "link")
], ProductInfoSectionLinkDTO.prototype, "type", void 0);
_ts_decorate$5j([
    dto({
        url: {
            allowAnyProtocol: true
        }
    }),
    _ts_metadata$5j("design:type", String)
], ProductInfoSectionLinkDTO.prototype, "link", void 0);
class ProductInfoSectionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5j(this, "id", void 0), _define_property$5j(this, "code", void 0), _define_property$5j(this, "index", void 0), _define_property$5j(this, "text", void 0), _define_property$5j(this, "image", void 0), _define_property$5j(this, "data", void 0);
    }
}
_ts_decorate$5j([
    dto({
        string: true
    }),
    _ts_metadata$5j("design:type", String)
], ProductInfoSectionDTO.prototype, "id", void 0);
_ts_decorate$5j([
    dto({
        string: true
    }),
    _ts_metadata$5j("design:type", String)
], ProductInfoSectionDTO.prototype, "code", void 0);
_ts_decorate$5j([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$5j("design:type", Number)
], ProductInfoSectionDTO.prototype, "index", void 0);
_ts_decorate$5j([
    dto({
        string: true
    }),
    _ts_metadata$5j("design:type", String)
], ProductInfoSectionDTO.prototype, "text", void 0);
_ts_decorate$5j([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5j("design:type", Object)
], ProductInfoSectionDTO.prototype, "image", void 0);
_ts_decorate$5j([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "text",
                        value: ProductInfoSectionTextDTO
                    },
                    {
                        name: "link",
                        value: ProductInfoSectionLinkDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$5j("design:type", Object)
], ProductInfoSectionDTO.prototype, "data", void 0);

function _define_property$5i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5i(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5i(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ProductMediaType = /*#__PURE__*/ function(ProductMediaType) {
    ProductMediaType["IMAGE"] = "image";
    ProductMediaType["VIDEO"] = "video";
    return ProductMediaType;
}({});
class ProductMediaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5i(this, "type", void 0), _define_property$5i(this, "file", void 0);
    }
}
_ts_decorate$5i([
    dto({
        enum: ProductMediaType
    }),
    _ts_metadata$5i("design:type", String)
], ProductMediaDTO.prototype, "type", void 0);
_ts_decorate$5i([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$5i("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], ProductMediaDTO.prototype, "file", void 0);

function _define_property$5h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5h(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5h(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ProductPartDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5h(this, "id", void 0), _define_property$5h(this, "name", void 0), _define_property$5h(this, "attributes", void 0);
    }
}
_ts_decorate$5h([
    dto({
        string: true
    }),
    _ts_metadata$5h("design:type", String)
], ProductPartDTO.prototype, "id", void 0);
_ts_decorate$5h([
    dto({
        string: true
    }),
    _ts_metadata$5h("design:type", String)
], ProductPartDTO.prototype, "name", void 0);
_ts_decorate$5h([
    dto({
        array: true,
        object: {
            type: ProductAttributeDTO
        }
    }),
    _ts_metadata$5h("design:type", Array)
], ProductPartDTO.prototype, "attributes", void 0);

function _define_property$5g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5g(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5g(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5g(this, "base", void 0), _define_property$5g(this, "result", void 0), _define_property$5g(this, "discountPercent", void 0);
    }
}
_ts_decorate$5g([
    dto({
        number: true
    }),
    _ts_metadata$5g("design:type", Number)
], ProductPriceDTO.prototype, "base", void 0);
_ts_decorate$5g([
    dto({
        number: true
    }),
    _ts_metadata$5g("design:type", Number)
], ProductPriceDTO.prototype, "result", void 0);
_ts_decorate$5g([
    dto({
        number: true
    }),
    _ts_metadata$5g("design:type", Number)
], ProductPriceDTO.prototype, "discountPercent", void 0);
class ProductBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5g(this, "id", void 0), _define_property$5g(this, "name", void 0), _define_property$5g(this, "favourite", void 0), _define_property$5g(this, "description", void 0), _define_property$5g(this, "articleNumber", void 0), _define_property$5g(this, "media", void 0), _define_property$5g(this, "badges", void 0);
    }
}
_ts_decorate$5g([
    dto({
        string: true
    }),
    _ts_metadata$5g("design:type", String)
], ProductBaseDTO.prototype, "id", void 0);
_ts_decorate$5g([
    dto({
        string: true
    }),
    _ts_metadata$5g("design:type", String)
], ProductBaseDTO.prototype, "name", void 0);
_ts_decorate$5g([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$5g("design:type", Object)
], ProductBaseDTO.prototype, "favourite", void 0);
_ts_decorate$5g([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$5g("design:type", Object)
], ProductBaseDTO.prototype, "description", void 0);
_ts_decorate$5g([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$5g("design:type", Object)
], ProductBaseDTO.prototype, "articleNumber", void 0);
_ts_decorate$5g([
    dto({
        array: true,
        object: {
            type: ProductMediaDTO
        }
    }),
    withDefaultValue([]),
    _ts_metadata$5g("design:type", Array)
], ProductBaseDTO.prototype, "media", void 0);
_ts_decorate$5g([
    dto({
        array: true,
        object: {
            type: ProductBadgeDTO
        }
    }),
    withDefaultValue([]),
    _ts_metadata$5g("design:type", Array)
], ProductBaseDTO.prototype, "badges", void 0);
class ProductDTO extends ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$5g(this, "price", void 0);
    }
}
_ts_decorate$5g([
    dto({
        object: {
            type: ProductPriceDTO
        }
    }),
    _ts_metadata$5g("design:type", typeof ProductPriceDTO === "undefined" ? Object : ProductPriceDTO)
], ProductDTO.prototype, "price", void 0);

function _define_property$5f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5f(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5f(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ProductDetailPriceForUserDependencyAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5f(this, "id", void 0), _define_property$5f(this, "value", void 0);
    }
}
_ts_decorate$5f([
    dto({
        string: true
    }),
    _ts_metadata$5f("design:type", String)
], ProductDetailPriceForUserDependencyAttributeDTO.prototype, "id", void 0);
_ts_decorate$5f([
    dto({
        string: true
    }),
    _ts_metadata$5f("design:type", String)
], ProductDetailPriceForUserDependencyAttributeDTO.prototype, "value", void 0);
class ProductDetailPriceForUserDependencyDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5f(this, "base", void 0), _define_property$5f(this, "result", void 0), _define_property$5f(this, "discountPercent", void 0), _define_property$5f(this, "attributes", void 0);
    }
}
_ts_decorate$5f([
    dto({
        number: true
    }),
    _ts_metadata$5f("design:type", Number)
], ProductDetailPriceForUserDependencyDTO.prototype, "base", void 0);
_ts_decorate$5f([
    dto({
        number: true
    }),
    _ts_metadata$5f("design:type", Number)
], ProductDetailPriceForUserDependencyDTO.prototype, "result", void 0);
_ts_decorate$5f([
    dto({
        number: true
    }),
    _ts_metadata$5f("design:type", Number)
], ProductDetailPriceForUserDependencyDTO.prototype, "discountPercent", void 0);
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductDetailPriceForUserDependencyAttributeDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailPriceForUserDependencyDTO.prototype, "attributes", void 0);
class ProductDetailPriceForUserDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5f(this, "base", void 0), _define_property$5f(this, "result", void 0), _define_property$5f(this, "discountPercent", void 0), _define_property$5f(this, "dependencies", void 0);
    }
}
_ts_decorate$5f([
    dto({
        number: true
    }),
    _ts_metadata$5f("design:type", Number)
], ProductDetailPriceForUserDTO.prototype, "base", void 0);
_ts_decorate$5f([
    dto({
        number: true
    }),
    _ts_metadata$5f("design:type", Number)
], ProductDetailPriceForUserDTO.prototype, "result", void 0);
_ts_decorate$5f([
    dto({
        number: true
    }),
    _ts_metadata$5f("design:type", Number)
], ProductDetailPriceForUserDTO.prototype, "discountPercent", void 0);
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductDetailPriceForUserDependencyDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailPriceForUserDTO.prototype, "dependencies", void 0);
class ProductDetailShopAvailabilityDependencyAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5f(this, "id", void 0), _define_property$5f(this, "value", void 0);
    }
}
_ts_decorate$5f([
    dto({
        string: true
    }),
    _ts_metadata$5f("design:type", String)
], ProductDetailShopAvailabilityDependencyAttributeDTO.prototype, "id", void 0);
_ts_decorate$5f([
    dto({
        string: true
    }),
    _ts_metadata$5f("design:type", String)
], ProductDetailShopAvailabilityDependencyAttributeDTO.prototype, "value", void 0);
class ProductDetailShopAvailabilityDependencyDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5f(this, "shopIds", void 0), _define_property$5f(this, "attributes", void 0);
    }
}
_ts_decorate$5f([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailShopAvailabilityDependencyDTO.prototype, "shopIds", void 0);
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductDetailShopAvailabilityDependencyAttributeDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailShopAvailabilityDependencyDTO.prototype, "attributes", void 0);
class ProductDetailShopAvailabilityDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5f(this, "dependencies", void 0);
    }
}
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductDetailShopAvailabilityDependencyDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailShopAvailabilityDTO.prototype, "dependencies", void 0);
class ProductDetailDTO extends ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$5f(this, "attributes", void 0), _define_property$5f(this, "parts", void 0), _define_property$5f(this, "price", void 0), _define_property$5f(this, "shopAvailabilities", void 0), _define_property$5f(this, "infoSections", void 0);
    }
}
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductAttributeDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailDTO.prototype, "attributes", void 0);
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductPartDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailDTO.prototype, "parts", void 0);
_ts_decorate$5f([
    dto({
        object: {
            type: ProductDetailPriceForUserDTO
        }
    }),
    _ts_metadata$5f("design:type", typeof ProductDetailPriceForUserDTO === "undefined" ? Object : ProductDetailPriceForUserDTO)
], ProductDetailDTO.prototype, "price", void 0);
_ts_decorate$5f([
    dto({
        object: {
            type: ProductDetailShopAvailabilityDTO
        }
    }),
    _ts_metadata$5f("design:type", typeof ProductDetailShopAvailabilityDTO === "undefined" ? Object : ProductDetailShopAvailabilityDTO)
], ProductDetailDTO.prototype, "shopAvailabilities", void 0);
_ts_decorate$5f([
    dto({
        array: true,
        object: {
            type: ProductInfoSectionDTO
        }
    }),
    _ts_metadata$5f("design:type", Array)
], ProductDetailDTO.prototype, "infoSections", void 0);

function _define_property$5e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5e(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5e(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Либо value (совпадение со значением атрибута), либо minNumber/maxNumber (диапазон по number).
// Ровно одно из двух: и пустой элемент, и обе половины сразу контроллер отклоняет.
class GetProductsListAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5e(this, "id", void 0), _define_property$5e(this, "value", void 0), _define_property$5e(this, "minNumber", void 0), _define_property$5e(this, "maxNumber", void 0);
    }
}
_ts_decorate$5e([
    dto({
        string: true
    }),
    _ts_metadata$5e("design:type", String)
], GetProductsListAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$5e([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListAttributeRequestDTO.prototype, "value", void 0);
_ts_decorate$5e([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListAttributeRequestDTO.prototype, "minNumber", void 0);
_ts_decorate$5e([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListAttributeRequestDTO.prototype, "maxNumber", void 0);
class GetProductsListPartRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5e(this, "id", void 0);
    }
}
_ts_decorate$5e([
    dto({
        string: true
    }),
    _ts_metadata$5e("design:type", String)
], GetProductsListPartRequestDTO.prototype, "id", void 0);
class GetProductsListPriceRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5e(this, "min", void 0), _define_property$5e(this, "max", void 0);
    }
}
_ts_decorate$5e([
    dto({
        optional: true,
        number: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListPriceRequestDTO.prototype, "min", void 0);
_ts_decorate$5e([
    dto({
        optional: true,
        number: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListPriceRequestDTO.prototype, "max", void 0);
var GetProductsListSortingFields = /*#__PURE__*/ function(GetProductsListSortingFields) {
    GetProductsListSortingFields["CREATED_AT"] = "createdAt";
    GetProductsListSortingFields["PRICE"] = "price";
    GetProductsListSortingFields["DISCOUNT"] = "discount";
    return GetProductsListSortingFields;
}({});
class GetProductsListRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5e(this, "categoryId", void 0), _define_property$5e(this, "search", void 0), _define_property$5e(this, "sort", void 0), _define_property$5e(this, "attributes", void 0), _define_property$5e(this, "parts", void 0), _define_property$5e(this, "price", void 0);
    }
}
_ts_decorate$5e([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListRequestDTO.prototype, "categoryId", void 0);
_ts_decorate$5e([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListRequestDTO.prototype, "search", void 0);
_ts_decorate$5e([
    dto({
        optional: true,
        sorting: GetProductsListSortingFields
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListRequestDTO.prototype, "sort", void 0);
_ts_decorate$5e([
    dto({
        optional: true,
        array: true,
        object: {
            type: GetProductsListAttributeRequestDTO
        }
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListRequestDTO.prototype, "attributes", void 0);
_ts_decorate$5e([
    dto({
        optional: true,
        array: true,
        object: {
            type: GetProductsListPartRequestDTO
        }
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListRequestDTO.prototype, "parts", void 0);
_ts_decorate$5e([
    dto({
        optional: true,
        object: {
            type: GetProductsListPriceRequestDTO
        }
    }),
    _ts_metadata$5e("design:type", Object)
], GetProductsListRequestDTO.prototype, "price", void 0);

function _define_property$5d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5d(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5d(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetMostSearchableProductsListRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5d(this, "categoryId", void 0);
    }
}
_ts_decorate$5d([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$5d("design:type", Object)
], GetMostSearchableProductsListRequestDTO.prototype, "categoryId", void 0);

function _define_property$5c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5c(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5c(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MostSearchableProductNameDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5c(this, "name", void 0);
    }
}
_ts_decorate$5c([
    dto({
        string: true
    }),
    _ts_metadata$5c("design:type", String)
], MostSearchableProductNameDTO.prototype, "name", void 0);

function _define_property$5b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5b(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CreateProductsListSearchHistoryRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5b(this, "text", void 0);
    }
}
_ts_decorate$5b([
    dto({
        string: true
    }),
    _ts_metadata$5b("design:type", String)
], CreateProductsListSearchHistoryRequestDTO.prototype, "text", void 0);

function _define_property$5a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5a(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5a(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetProductsListSearchHistoryResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5a(this, "items", void 0);
    }
}
_ts_decorate$5a([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$5a("design:type", Array)
], GetProductsListSearchHistoryResponseDTO.prototype, "items", void 0);

function _define_property$59(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$59(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$59(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetProductsListViewHistoryRequestDTO {
    constructor(){
        _define_property$59(this, "categoryId", void 0);
    }
}
_ts_decorate$59([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$59("design:type", Object)
], GetProductsListViewHistoryRequestDTO.prototype, "categoryId", void 0);

function _define_property$58(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$58(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$58(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetBannerProductsCatalogRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$58(this, "bannerId", void 0), _define_property$58(this, "search", void 0), _define_property$58(this, "sort", void 0), _define_property$58(this, "price", void 0);
    }
}
_ts_decorate$58([
    dto({
        string: true
    }),
    _ts_metadata$58("design:type", String)
], GetBannerProductsCatalogRequestDTO.prototype, "bannerId", void 0);
_ts_decorate$58([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$58("design:type", Object)
], GetBannerProductsCatalogRequestDTO.prototype, "search", void 0);
_ts_decorate$58([
    dto({
        optional: true,
        sorting: GetProductsListSortingFields
    }),
    _ts_metadata$58("design:type", Object)
], GetBannerProductsCatalogRequestDTO.prototype, "sort", void 0);
_ts_decorate$58([
    dto({
        optional: true,
        object: {
            type: GetProductsListPriceRequestDTO
        }
    }),
    _ts_metadata$58("design:type", Object)
], GetBannerProductsCatalogRequestDTO.prototype, "price", void 0);

function _define_property$57(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$57(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$57(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetBannerProductsCatalogFiltersRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$57(this, "bannerId", void 0);
    }
}
_ts_decorate$57([
    dto({
        string: true
    }),
    _ts_metadata$57("design:type", String)
], GetBannerProductsCatalogFiltersRequestDTO.prototype, "bannerId", void 0);

function _define_property$56(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$56(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$56(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetBannerProductsCatalogFiltersSortingResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$56(this, "code", void 0), _define_property$56(this, "name", void 0);
    }
}
_ts_decorate$56([
    dto({
        string: true
    }),
    _ts_metadata$56("design:type", String)
], GetBannerProductsCatalogFiltersSortingResponseDTO.prototype, "code", void 0);
_ts_decorate$56([
    dto({
        string: true
    }),
    _ts_metadata$56("design:type", String)
], GetBannerProductsCatalogFiltersSortingResponseDTO.prototype, "name", void 0);
class GetBannerProductsCatalogFiltersPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$56(this, "min", void 0), _define_property$56(this, "max", void 0);
    }
}
_ts_decorate$56([
    dto({
        number: true
    }),
    _ts_metadata$56("design:type", Number)
], GetBannerProductsCatalogFiltersPriceResponseDTO.prototype, "min", void 0);
_ts_decorate$56([
    dto({
        number: true
    }),
    _ts_metadata$56("design:type", Number)
], GetBannerProductsCatalogFiltersPriceResponseDTO.prototype, "max", void 0);
class GetBannerProductsCatalogFiltersResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$56(this, "sortingFields", void 0), _define_property$56(this, "price", void 0);
    }
}
_ts_decorate$56([
    dto({
        array: true,
        object: {
            type: GetBannerProductsCatalogFiltersSortingResponseDTO
        }
    }),
    _ts_metadata$56("design:type", Array)
], GetBannerProductsCatalogFiltersResponseDTO.prototype, "sortingFields", void 0);
_ts_decorate$56([
    dto({
        object: {
            type: GetBannerProductsCatalogFiltersPriceResponseDTO
        }
    }),
    _ts_metadata$56("design:type", typeof GetBannerProductsCatalogFiltersPriceResponseDTO === "undefined" ? Object : GetBannerProductsCatalogFiltersPriceResponseDTO)
], GetBannerProductsCatalogFiltersResponseDTO.prototype, "price", void 0);

var ContentBlockComponent = /*#__PURE__*/ function(ContentBlockComponent) {
    ContentBlockComponent["SEARCH"] = "search";
    ContentBlockComponent["ACTIVE_ORDERS"] = "active_orders";
    ContentBlockComponent["BONUS_CARD"] = "bonus_card";
    ContentBlockComponent["BANNERS"] = "banners";
    ContentBlockComponent["SHORTCUTS"] = "shortcuts";
    ContentBlockComponent["PRODUCTS"] = "products";
    return ContentBlockComponent;
}({});
var ContentBlockResponseComponent = /*#__PURE__*/ function(ContentBlockResponseComponent) {
    ContentBlockResponseComponent["SEARCH"] = "search_v1";
    ContentBlockResponseComponent["ACTIVE_ORDERS"] = "active_orders_v1";
    ContentBlockResponseComponent["BONUS_CARD"] = "bonus_card_v1";
    ContentBlockResponseComponent["BANNERS"] = "banners_v1";
    ContentBlockResponseComponent["SHORTCUTS"] = "shortcuts_v1";
    ContentBlockResponseComponent["PRODUCTS"] = "products_v1";
    return ContentBlockResponseComponent;
}({});
var ContentBlockBannerActionType = /*#__PURE__*/ function(ContentBlockBannerActionType) {
    ContentBlockBannerActionType["PRODUCT"] = "product";
    ContentBlockBannerActionType["PRODUCTS_CATALOG"] = "products_catalog";
    ContentBlockBannerActionType["HTML"] = "html";
    return ContentBlockBannerActionType;
}({});
var ContentBlockBannerMediaType = /*#__PURE__*/ function(ContentBlockBannerMediaType) {
    ContentBlockBannerMediaType["IMAGE"] = "image";
    ContentBlockBannerMediaType["VIDEO"] = "video";
    return ContentBlockBannerMediaType;
}({});
var ContentBlockBannerAspectRatio = /*#__PURE__*/ function(ContentBlockBannerAspectRatio) {
    ContentBlockBannerAspectRatio["SQUARE"] = "1.0";
    ContentBlockBannerAspectRatio["WIDE"] = "2.0";
    return ContentBlockBannerAspectRatio;
}({});
var ContentBlockShortcutsStyle = /*#__PURE__*/ function(ContentBlockShortcutsStyle) {
    ContentBlockShortcutsStyle["LINKS"] = "links";
    ContentBlockShortcutsStyle["MINI"] = "mini";
    return ContentBlockShortcutsStyle;
}({});
var ContentBlockShortcutActionType = /*#__PURE__*/ function(ContentBlockShortcutActionType) {
    ContentBlockShortcutActionType["HTML"] = "html";
    ContentBlockShortcutActionType["CATALOG"] = "catalog";
    ContentBlockShortcutActionType["SHOPS"] = "shops";
    ContentBlockShortcutActionType["CAMERA_SCAN"] = "camera_scan";
    return ContentBlockShortcutActionType;
}({});
var ContentBlockProductsView = /*#__PURE__*/ function(ContentBlockProductsView) {
    ContentBlockProductsView["CAROUSEL"] = "carousel";
    ContentBlockProductsView["LIST"] = "list";
    ContentBlockProductsView["GRID2"] = "grid2";
    ContentBlockProductsView["GRID3"] = "grid3";
    return ContentBlockProductsView;
}({});

function _define_property$55(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$55(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$55(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockPageRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Любая строка: на странице без блоков ответ — пустой список.
        _define_property$55(this, "page", void 0), // Компоненты, которые умеет рисовать приложение, и их версии: `<компонент>:<версия1>,<версия2>`, версия — целое
        // от 1. Блок компонента, которого здесь нет или с которым нет общей версии, на страницу не попадает.
        _define_property$55(this, "supports", void 0);
    }
}
_ts_decorate$55([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$55("design:type", String)
], ContentBlockPageRequestDTO.prototype, "page", void 0);
_ts_decorate$55([
    dto({
        array: true,
        string: {
            pattern: {
                regexp: /^[a-z0-9_]+:[1-9]\d*(,[1-9]\d*)*$/,
                format: "<компонент>:<версия1>,<версия2>, версия от 1"
            }
        }
    }),
    wrapToArray,
    _ts_metadata$55("design:type", Array)
], ContentBlockPageRequestDTO.prototype, "supports", void 0);

function _define_property$54(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$54(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$54(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$54(this, "result", void 0);
    }
}
_ts_decorate$54([
    dto({
        number: true
    }),
    _ts_metadata$54("design:type", Number)
], OrderProductPriceDTO.prototype, "result", void 0);
class OrderBaseProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$54(this, "id", void 0), _define_property$54(this, "name", void 0), _define_property$54(this, "count", void 0), _define_property$54(this, "articleNumber", void 0), _define_property$54(this, "price", void 0);
    }
}
_ts_decorate$54([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$54("design:type", Object)
], OrderBaseProductDTO.prototype, "id", void 0);
_ts_decorate$54([
    dto({
        string: true
    }),
    _ts_metadata$54("design:type", String)
], OrderBaseProductDTO.prototype, "name", void 0);
_ts_decorate$54([
    dto({
        number: true
    }),
    _ts_metadata$54("design:type", Number)
], OrderBaseProductDTO.prototype, "count", void 0);
_ts_decorate$54([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$54("design:type", Object)
], OrderBaseProductDTO.prototype, "articleNumber", void 0);
_ts_decorate$54([
    dto({
        object: {
            type: OrderProductPriceDTO
        }
    }),
    _ts_metadata$54("design:type", typeof OrderProductPriceDTO === "undefined" ? Object : OrderProductPriceDTO)
], OrderBaseProductDTO.prototype, "price", void 0);
class OrderListProductDTO extends OrderBaseProductDTO {
    constructor(...args){
        super(...args), _define_property$54(this, "media", void 0);
    }
}
_ts_decorate$54([
    dto({
        optional: true,
        array: true,
        object: {
            type: ProductMediaDTO
        }
    }),
    _ts_metadata$54("design:type", Object)
], OrderListProductDTO.prototype, "media", void 0);

function _define_property$53(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$53(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$53(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var OrderStatus = /*#__PURE__*/ function(OrderStatus) {
    OrderStatus["CREATED"] = "created";
    OrderStatus["PROCESSING"] = "processing";
    OrderStatus["WAITING_PAYMENT"] = "waiting_payment";
    OrderStatus["DELIVERING"] = "delivering";
    OrderStatus["READY"] = "ready";
    OrderStatus["COMPLETED"] = "completed";
    OrderStatus["CANCELLED"] = "cancelled";
    OrderStatus["WAITING_CANCELLED"] = "waiting_cancelled";
    return OrderStatus;
}({});
class OrderPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$53(this, "result", void 0);
    }
}
_ts_decorate$53([
    dto({
        number: true
    }),
    _ts_metadata$53("design:type", Number)
], OrderPriceResponseDTO.prototype, "result", void 0);
class OrderBaseResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$53(this, "id", void 0), _define_property$53(this, "number", void 0), _define_property$53(this, "comment", void 0), _define_property$53(this, "createdAt", void 0), _define_property$53(this, "price", void 0);
    }
}
_ts_decorate$53([
    dto({
        string: true
    }),
    _ts_metadata$53("design:type", String)
], OrderBaseResponseDTO.prototype, "id", void 0);
_ts_decorate$53([
    dto({
        string: true
    }),
    _ts_metadata$53("design:type", String)
], OrderBaseResponseDTO.prototype, "number", void 0);
_ts_decorate$53([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$53("design:type", Object)
], OrderBaseResponseDTO.prototype, "comment", void 0);
_ts_decorate$53([
    dto({
        date: true
    }),
    _ts_metadata$53("design:type", typeof Date === "undefined" ? Object : Date)
], OrderBaseResponseDTO.prototype, "createdAt", void 0);
_ts_decorate$53([
    dto({
        object: {
            type: OrderPriceResponseDTO
        }
    }),
    _ts_metadata$53("design:type", typeof OrderPriceResponseDTO === "undefined" ? Object : OrderPriceResponseDTO)
], OrderBaseResponseDTO.prototype, "price", void 0);
class OrderResponseDTO extends OrderBaseResponseDTO {
    constructor(...args){
        super(...args), _define_property$53(this, "status", void 0), _define_property$53(this, "statusName", void 0), _define_property$53(this, "products", void 0);
    }
}
_ts_decorate$53([
    dto({
        enum: OrderStatus
    }),
    _ts_metadata$53("design:type", String)
], OrderResponseDTO.prototype, "status", void 0);
_ts_decorate$53([
    dto({
        string: true
    }),
    _ts_metadata$53("design:type", String)
], OrderResponseDTO.prototype, "statusName", void 0);
_ts_decorate$53([
    dto({
        array: true,
        object: {
            type: OrderListProductDTO
        }
    }),
    _ts_metadata$53("design:type", Array)
], OrderResponseDTO.prototype, "products", void 0);

function _define_property$52(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$52(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$52(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrdersListRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$52(this, "status", void 0);
    }
}
_ts_decorate$52([
    dto({
        optional: {
            allowNull: false
        },
        enum: OrderStatus
    }),
    _ts_metadata$52("design:type", Object)
], OrdersListRequestDTO.prototype, "status", void 0);

function _define_property$51(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$51(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$51(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockResponseBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$51(this, "id", void 0), _define_property$51(this, "title", void 0);
    }
}
_ts_decorate$51([
    dto({
        string: true
    }),
    _ts_metadata$51("design:type", String)
], ContentBlockResponseBaseDTO.prototype, "id", void 0);
_ts_decorate$51([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$51("design:type", Object)
], ContentBlockResponseBaseDTO.prototype, "title", void 0);

function _define_property$50(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$50(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$50(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockActiveOrdersDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$50(this, "orders", void 0);
    }
}
_ts_decorate$50([
    dto({
        array: true,
        object: {
            type: OrderResponseDTO
        }
    }),
    _ts_metadata$50("design:type", Array)
], ContentBlockActiveOrdersDataDTO.prototype, "orders", void 0);
class ContentBlockActiveOrdersResponseDTO extends ContentBlockResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$50(this, "component", void 0), _define_property$50(this, "data", void 0);
    }
}
_ts_decorate$50([
    dto({
        equals: ContentBlockResponseComponent.ACTIVE_ORDERS
    }),
    _ts_metadata$50("design:type", typeof ContentBlockResponseComponent === "undefined" || typeof ContentBlockResponseComponent.ACTIVE_ORDERS === "undefined" ? Object : ContentBlockResponseComponent.ACTIVE_ORDERS)
], ContentBlockActiveOrdersResponseDTO.prototype, "component", void 0);
_ts_decorate$50([
    dto({
        object: {
            type: ContentBlockActiveOrdersDataDTO
        }
    }),
    _ts_metadata$50("design:type", typeof ContentBlockActiveOrdersDataDTO === "undefined" ? Object : ContentBlockActiveOrdersDataDTO)
], ContentBlockActiveOrdersResponseDTO.prototype, "data", void 0);

function _define_property$4$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4$(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4$(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockBannerMediaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4$(this, "type", void 0), _define_property$4$(this, "file", void 0);
    }
}
_ts_decorate$4$([
    dto({
        enum: ContentBlockBannerMediaType
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannerMediaType === "undefined" ? Object : ContentBlockBannerMediaType)
], ContentBlockBannerMediaDTO.prototype, "type", void 0);
_ts_decorate$4$([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$4$("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], ContentBlockBannerMediaDTO.prototype, "file", void 0);
class ContentBlockBannerActionProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4$(this, "type", void 0), _define_property$4$(this, "productId", void 0);
    }
}
_ts_decorate$4$([
    dto({
        equals: ContentBlockBannerActionType.PRODUCT
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannerActionType === "undefined" || typeof ContentBlockBannerActionType.PRODUCT === "undefined" ? Object : ContentBlockBannerActionType.PRODUCT)
], ContentBlockBannerActionProductDTO.prototype, "type", void 0);
_ts_decorate$4$([
    dto({
        string: true
    }),
    _ts_metadata$4$("design:type", String)
], ContentBlockBannerActionProductDTO.prototype, "productId", void 0);
class ContentBlockBannerActionProductsCatalogDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4$(this, "type", void 0), _define_property$4$(this, "description", void 0);
    }
}
_ts_decorate$4$([
    dto({
        equals: ContentBlockBannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannerActionType === "undefined" || typeof ContentBlockBannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : ContentBlockBannerActionType.PRODUCTS_CATALOG)
], ContentBlockBannerActionProductsCatalogDTO.prototype, "type", void 0);
_ts_decorate$4$([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4$("design:type", Object)
], ContentBlockBannerActionProductsCatalogDTO.prototype, "description", void 0);
class ContentBlockBannerActionHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4$(this, "type", void 0), _define_property$4$(this, "file", void 0);
    }
}
_ts_decorate$4$([
    dto({
        equals: ContentBlockBannerActionType.HTML
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannerActionType === "undefined" || typeof ContentBlockBannerActionType.HTML === "undefined" ? Object : ContentBlockBannerActionType.HTML)
], ContentBlockBannerActionHtmlDTO.prototype, "type", void 0);
_ts_decorate$4$([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$4$("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], ContentBlockBannerActionHtmlDTO.prototype, "file", void 0);
class ContentBlockBannerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4$(this, "id", void 0), _define_property$4$(this, "name", void 0), _define_property$4$(this, "aspectRatio", void 0), _define_property$4$(this, "media", void 0), _define_property$4$(this, "startDate", void 0), _define_property$4$(this, "endDate", void 0), _define_property$4$(this, "action", void 0);
    }
}
_ts_decorate$4$([
    dto({
        string: true
    }),
    _ts_metadata$4$("design:type", String)
], ContentBlockBannerDTO.prototype, "id", void 0);
_ts_decorate$4$([
    dto({
        string: true
    }),
    _ts_metadata$4$("design:type", String)
], ContentBlockBannerDTO.prototype, "name", void 0);
_ts_decorate$4$([
    dto({
        enum: ContentBlockBannerAspectRatio
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannerAspectRatio === "undefined" ? Object : ContentBlockBannerAspectRatio)
], ContentBlockBannerDTO.prototype, "aspectRatio", void 0);
_ts_decorate$4$([
    dto({
        object: {
            type: ContentBlockBannerMediaDTO
        }
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannerMediaDTO === "undefined" ? Object : ContentBlockBannerMediaDTO)
], ContentBlockBannerDTO.prototype, "media", void 0);
_ts_decorate$4$([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$4$("design:type", Object)
], ContentBlockBannerDTO.prototype, "startDate", void 0);
_ts_decorate$4$([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$4$("design:type", Object)
], ContentBlockBannerDTO.prototype, "endDate", void 0);
_ts_decorate$4$([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: ContentBlockBannerActionType.PRODUCT,
                        value: ContentBlockBannerActionProductDTO
                    },
                    {
                        name: ContentBlockBannerActionType.PRODUCTS_CATALOG,
                        value: ContentBlockBannerActionProductsCatalogDTO
                    },
                    {
                        name: ContentBlockBannerActionType.HTML,
                        value: ContentBlockBannerActionHtmlDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4$("design:type", Object)
], ContentBlockBannerDTO.prototype, "action", void 0);
class ContentBlockBannersDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4$(this, "banners", void 0);
    }
}
_ts_decorate$4$([
    dto({
        array: true,
        object: {
            type: ContentBlockBannerDTO
        }
    }),
    _ts_metadata$4$("design:type", Array)
], ContentBlockBannersDataDTO.prototype, "banners", void 0);
class ContentBlockBannersResponseDTO extends ContentBlockResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$4$(this, "component", void 0), _define_property$4$(this, "data", void 0);
    }
}
_ts_decorate$4$([
    dto({
        equals: ContentBlockResponseComponent.BANNERS
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockResponseComponent === "undefined" || typeof ContentBlockResponseComponent.BANNERS === "undefined" ? Object : ContentBlockResponseComponent.BANNERS)
], ContentBlockBannersResponseDTO.prototype, "component", void 0);
_ts_decorate$4$([
    dto({
        object: {
            type: ContentBlockBannersDataDTO
        }
    }),
    _ts_metadata$4$("design:type", typeof ContentBlockBannersDataDTO === "undefined" ? Object : ContentBlockBannersDataDTO)
], ContentBlockBannersResponseDTO.prototype, "data", void 0);

// Роли, которыми пользователя наделяет внешняя система (1С). К UserRole отношения не имеют:
// UserRole — роль сессии, UserExternalRole — признак пользователя, дающий доступ к отдельному интерфейсу.
var UserExternalRole = /*#__PURE__*/ function(UserExternalRole) {
    UserExternalRole["MERCHANT_DEFAULT_EMPLOYEE"] = "merchant_default_employee";
    return UserExternalRole;
}({});

function _define_property$4_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4_(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4_(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UserExternalInterfaceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4_(this, "name", void 0), // Абсолютный адрес чужого хоста: клиент открывает его как есть, не подставляя свой API-хост.
        _define_property$4_(this, "url", void 0);
    }
}
_ts_decorate$4_([
    dto({
        string: true
    }),
    _ts_metadata$4_("design:type", String)
], UserExternalInterfaceDTO.prototype, "name", void 0);
_ts_decorate$4_([
    dto({
        string: true
    }),
    _ts_metadata$4_("design:type", String)
], UserExternalInterfaceDTO.prototype, "url", void 0);

var UserRole = /*#__PURE__*/ function(UserRole) {
    UserRole["ANONYMOUS_CUSTOMER"] = "anonymous_customer";
    UserRole["CUSTOMER"] = "customer";
    UserRole["MERCHANT"] = "merchant";
    UserRole["EMPLOYEE"] = "employee";
    UserRole["ADMIN"] = "admin";
    UserRole["MERCHANT_EMPLOYEE"] = "merchant_employee";
    return UserRole;
}({});

function _define_property$4Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4Z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4Z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UserDetailAdminDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4Z(this, "id", void 0);
    }
}
_ts_decorate$4Z([
    dto({
        string: true
    }),
    _ts_metadata$4Z("design:type", String)
], UserDetailAdminDTO.prototype, "id", void 0);

function _define_property$4Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4Y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4Y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var Gender = /*#__PURE__*/ function(Gender) {
    Gender["MALE"] = "male";
    Gender["FEMALE"] = "female";
    return Gender;
}({});
var CustomerAllowedNotificationLevel = /*#__PURE__*/ function(CustomerAllowedNotificationLevel) {
    CustomerAllowedNotificationLevel["ORDERS_PUSH"] = "orders_push";
    CustomerAllowedNotificationLevel["MARKETING_PUSH"] = "marketing_push";
    return CustomerAllowedNotificationLevel;
}({});
class UserDetailCustomerShopDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4Y(this, "id", void 0), _define_property$4Y(this, "address", void 0), _define_property$4Y(this, "cityName", void 0);
    }
}
_ts_decorate$4Y([
    dto({
        string: true
    }),
    _ts_metadata$4Y("design:type", String)
], UserDetailCustomerShopDTO.prototype, "id", void 0);
_ts_decorate$4Y([
    dto({
        string: true
    }),
    _ts_metadata$4Y("design:type", String)
], UserDetailCustomerShopDTO.prototype, "address", void 0);
_ts_decorate$4Y([
    dto({
        string: true
    }),
    _ts_metadata$4Y("design:type", String)
], UserDetailCustomerShopDTO.prototype, "cityName", void 0);
class UserDetailCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4Y(this, "id", void 0), _define_property$4Y(this, "firstName", void 0), _define_property$4Y(this, "lastName", void 0), _define_property$4Y(this, "phone", void 0), _define_property$4Y(this, "birthdate", void 0), _define_property$4Y(this, "gender", void 0), _define_property$4Y(this, "avatar", void 0), _define_property$4Y(this, "role", void 0), _define_property$4Y(this, "customerAllowedToSendNotifications", void 0), _define_property$4Y(this, "selectedShop", void 0), _define_property$4Y(this, "externalInterfaces", void 0);
    }
}
_ts_decorate$4Y([
    dto({
        string: true
    }),
    _ts_metadata$4Y("design:type", String)
], UserDetailCustomerDTO.prototype, "id", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "firstName", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "lastName", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        phone: true
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "phone", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "birthdate", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        enum: Gender
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "gender", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "avatar", void 0);
_ts_decorate$4Y([
    dto({
        enum: UserRole
    }),
    _ts_metadata$4Y("design:type", typeof UserRole === "undefined" ? Object : UserRole)
], UserDetailCustomerDTO.prototype, "role", void 0);
_ts_decorate$4Y([
    dto({
        enum: CustomerAllowedNotificationLevel,
        array: true
    }),
    _ts_metadata$4Y("design:type", Array)
], UserDetailCustomerDTO.prototype, "customerAllowedToSendNotifications", void 0);
_ts_decorate$4Y([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: UserDetailCustomerShopDTO
        }
    }),
    _ts_metadata$4Y("design:type", Object)
], UserDetailCustomerDTO.prototype, "selectedShop", void 0);
_ts_decorate$4Y([
    dto({
        object: {
            type: UserExternalInterfaceDTO
        },
        array: true
    }),
    _ts_metadata$4Y("design:type", Array)
], UserDetailCustomerDTO.prototype, "externalInterfaces", void 0);

function _define_property$4X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4X(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4X(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetCurrentUserCustomerSettingsResponseNotificationDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4X(this, "type", void 0), _define_property$4X(this, "enabled", void 0), _define_property$4X(this, "name", void 0);
    }
}
_ts_decorate$4X([
    dto({
        enum: CustomerAllowedNotificationLevel
    }),
    _ts_metadata$4X("design:type", typeof CustomerAllowedNotificationLevel === "undefined" ? Object : CustomerAllowedNotificationLevel)
], GetCurrentUserCustomerSettingsResponseNotificationDTO.prototype, "type", void 0);
_ts_decorate$4X([
    dto({
        boolean: true
    }),
    _ts_metadata$4X("design:type", String)
], GetCurrentUserCustomerSettingsResponseNotificationDTO.prototype, "enabled", void 0);
_ts_decorate$4X([
    dto({
        string: true
    }),
    _ts_metadata$4X("design:type", String)
], GetCurrentUserCustomerSettingsResponseNotificationDTO.prototype, "name", void 0);
class GetCurrentUserCustomerSettingsResponseNotificationPresetDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4X(this, "name", void 0), _define_property$4X(this, "value", void 0), _define_property$4X(this, "filled", void 0);
    }
}
_ts_decorate$4X([
    dto({
        string: true
    }),
    _ts_metadata$4X("design:type", String)
], GetCurrentUserCustomerSettingsResponseNotificationPresetDTO.prototype, "name", void 0);
_ts_decorate$4X([
    dto({
        string: true
    }),
    _ts_metadata$4X("design:type", String)
], GetCurrentUserCustomerSettingsResponseNotificationPresetDTO.prototype, "value", void 0);
_ts_decorate$4X([
    dto({
        boolean: true
    }),
    _ts_metadata$4X("design:type", Boolean)
], GetCurrentUserCustomerSettingsResponseNotificationPresetDTO.prototype, "filled", void 0);
class GetCurrentUserCustomerSettingsResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4X(this, "availableNotifications", void 0), _define_property$4X(this, "availableNotificationPresets", void 0);
    }
}
_ts_decorate$4X([
    dto({
        object: {
            type: GetCurrentUserCustomerSettingsResponseNotificationDTO
        },
        array: true
    }),
    _ts_metadata$4X("design:type", Array)
], GetCurrentUserCustomerSettingsResponseDTO.prototype, "availableNotifications", void 0);
_ts_decorate$4X([
    dto({
        object: {
            type: GetCurrentUserCustomerSettingsResponseNotificationPresetDTO
        },
        array: true
    }),
    _ts_metadata$4X("design:type", Array)
], GetCurrentUserCustomerSettingsResponseDTO.prototype, "availableNotificationPresets", void 0);

function _define_property$4W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4W(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4W(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UserDetailCustomerShopV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4W(this, "id", void 0), _define_property$4W(this, "address", void 0), _define_property$4W(this, "cityName", void 0);
    }
}
_ts_decorate$4W([
    dto({
        string: true
    }),
    _ts_metadata$4W("design:type", String)
], UserDetailCustomerShopV1DTO.prototype, "id", void 0);
_ts_decorate$4W([
    dto({
        string: true
    }),
    _ts_metadata$4W("design:type", String)
], UserDetailCustomerShopV1DTO.prototype, "address", void 0);
_ts_decorate$4W([
    dto({
        string: true
    }),
    _ts_metadata$4W("design:type", String)
], UserDetailCustomerShopV1DTO.prototype, "cityName", void 0);
class UserDetailCustomerV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4W(this, "id", void 0), _define_property$4W(this, "firstName", void 0), _define_property$4W(this, "lastName", void 0), _define_property$4W(this, "phone", void 0), _define_property$4W(this, "birthdate", void 0), _define_property$4W(this, "gender", void 0), _define_property$4W(this, "avatar", void 0), _define_property$4W(this, "role", void 0), _define_property$4W(this, "customerAllowedToSendMarketingSms", void 0), _define_property$4W(this, "selectedShop", void 0);
    }
}
_ts_decorate$4W([
    dto({
        string: true
    }),
    _ts_metadata$4W("design:type", String)
], UserDetailCustomerV1DTO.prototype, "id", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "firstName", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "lastName", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        phone: true
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "phone", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        date: true
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "birthdate", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        enum: Gender
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "gender", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "avatar", void 0);
_ts_decorate$4W([
    dto({
        enum: UserRole
    }),
    _ts_metadata$4W("design:type", typeof UserRole === "undefined" ? Object : UserRole)
], UserDetailCustomerV1DTO.prototype, "role", void 0);
_ts_decorate$4W([
    dto({
        boolean: true
    }),
    _ts_metadata$4W("design:type", Boolean)
], UserDetailCustomerV1DTO.prototype, "customerAllowedToSendMarketingSms", void 0);
_ts_decorate$4W([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: UserDetailCustomerShopV1DTO
        }
    }),
    _ts_metadata$4W("design:type", Object)
], UserDetailCustomerV1DTO.prototype, "selectedShop", void 0);

function _define_property$4V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4V(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4V(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var GetUserReferralCodeStatus = /*#__PURE__*/ function(GetUserReferralCodeStatus) {
    GetUserReferralCodeStatus["ENABLED"] = "enabled";
    GetUserReferralCodeStatus["DISABLED"] = "disabled";
    return GetUserReferralCodeStatus;
}({});
class IntegrationGetUserReferralCodeMyEarnedDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4V(this, "value", void 0), _define_property$4V(this, "description", void 0);
    }
}
_ts_decorate$4V([
    dto({
        string: true
    }),
    _ts_metadata$4V("design:type", String)
], IntegrationGetUserReferralCodeMyEarnedDTO.prototype, "value", void 0);
_ts_decorate$4V([
    dto({
        string: true
    }),
    _ts_metadata$4V("design:type", String)
], IntegrationGetUserReferralCodeMyEarnedDTO.prototype, "description", void 0);
class IntegrationGetUserReferralCodeMyDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4V(this, "code", void 0), _define_property$4V(this, "activationsCount", void 0), _define_property$4V(this, "earned", void 0);
    }
}
_ts_decorate$4V([
    dto({
        string: true
    }),
    _ts_metadata$4V("design:type", String)
], IntegrationGetUserReferralCodeMyDTO.prototype, "code", void 0);
_ts_decorate$4V([
    dto({
        number: true
    }),
    _ts_metadata$4V("design:type", Number)
], IntegrationGetUserReferralCodeMyDTO.prototype, "activationsCount", void 0);
_ts_decorate$4V([
    dto({
        object: {
            type: IntegrationGetUserReferralCodeMyEarnedDTO
        }
    }),
    _ts_metadata$4V("design:type", typeof IntegrationGetUserReferralCodeMyEarnedDTO === "undefined" ? Object : IntegrationGetUserReferralCodeMyEarnedDTO)
], IntegrationGetUserReferralCodeMyDTO.prototype, "earned", void 0);
class IntegrationGetUserReferralCodeOtherDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4V(this, "applied", void 0), _define_property$4V(this, "info", void 0);
    }
}
_ts_decorate$4V([
    dto({
        boolean: true
    }),
    _ts_metadata$4V("design:type", Boolean)
], IntegrationGetUserReferralCodeOtherDTO.prototype, "applied", void 0);
_ts_decorate$4V([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4V("design:type", Object)
], IntegrationGetUserReferralCodeOtherDTO.prototype, "info", void 0);
class IntegrationGetUserReferralCodeEnabledResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4V(this, "status", void 0), _define_property$4V(this, "my", void 0), _define_property$4V(this, "other", void 0), _define_property$4V(this, "shareText", void 0), _define_property$4V(this, "description", void 0), _define_property$4V(this, "terms", void 0);
    }
}
_ts_decorate$4V([
    dto({
        equals: "enabled"
    }),
    _ts_metadata$4V("design:type", typeof GetUserReferralCodeStatus === "undefined" || "string" === "undefined" ? Object : "enabled")
], IntegrationGetUserReferralCodeEnabledResponseDTO.prototype, "status", void 0);
_ts_decorate$4V([
    dto({
        object: {
            type: IntegrationGetUserReferralCodeMyDTO
        }
    }),
    _ts_metadata$4V("design:type", typeof IntegrationGetUserReferralCodeMyDTO === "undefined" ? Object : IntegrationGetUserReferralCodeMyDTO)
], IntegrationGetUserReferralCodeEnabledResponseDTO.prototype, "my", void 0);
_ts_decorate$4V([
    dto({
        object: {
            type: IntegrationGetUserReferralCodeOtherDTO
        }
    }),
    _ts_metadata$4V("design:type", typeof IntegrationGetUserReferralCodeOtherDTO === "undefined" ? Object : IntegrationGetUserReferralCodeOtherDTO)
], IntegrationGetUserReferralCodeEnabledResponseDTO.prototype, "other", void 0);
_ts_decorate$4V([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4V("design:type", Object)
], IntegrationGetUserReferralCodeEnabledResponseDTO.prototype, "shareText", void 0);
_ts_decorate$4V([
    dto({
        string: true
    }),
    _ts_metadata$4V("design:type", String)
], IntegrationGetUserReferralCodeEnabledResponseDTO.prototype, "description", void 0);
_ts_decorate$4V([
    dto({
        string: true
    }),
    _ts_metadata$4V("design:type", String)
], IntegrationGetUserReferralCodeEnabledResponseDTO.prototype, "terms", void 0);
class IntegrationGetUserReferralCodeDisabledResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4V(this, "status", void 0);
    }
}
_ts_decorate$4V([
    dto({
        equals: "disabled"
    }),
    _ts_metadata$4V("design:type", typeof GetUserReferralCodeStatus === "undefined" || "string" === "undefined" ? Object : "disabled")
], IntegrationGetUserReferralCodeDisabledResponseDTO.prototype, "status", void 0);
class IntegrationGetUserReferralCodeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4V(this, "data", void 0);
    }
}
_ts_decorate$4V([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "enabled",
                        value: IntegrationGetUserReferralCodeEnabledResponseDTO
                    },
                    {
                        name: "disabled",
                        value: IntegrationGetUserReferralCodeDisabledResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4V("design:type", Object)
], IntegrationGetUserReferralCodeResponseDTO.prototype, "data", void 0);

function _define_property$4U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4U(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4U(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UserReferralCustomerMyEarnedDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4U(this, "value", void 0), _define_property$4U(this, "description", void 0);
    }
}
_ts_decorate$4U([
    dto({
        string: true
    }),
    _ts_metadata$4U("design:type", String)
], UserReferralCustomerMyEarnedDTO.prototype, "value", void 0);
_ts_decorate$4U([
    dto({
        string: true
    }),
    _ts_metadata$4U("design:type", String)
], UserReferralCustomerMyEarnedDTO.prototype, "description", void 0);
class UserReferralCustomerMyDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4U(this, "code", void 0), // Ссылку собирает бэкенд, приложение её только шарит. Не optional: ветка disabled блока my
        // не содержит вовсе, а в enabled мерчант и его slug есть всегда.
        _define_property$4U(this, "link", void 0), _define_property$4U(this, "activationsCount", void 0), _define_property$4U(this, "earned", void 0);
    }
}
_ts_decorate$4U([
    dto({
        string: true
    }),
    _ts_metadata$4U("design:type", String)
], UserReferralCustomerMyDTO.prototype, "code", void 0);
_ts_decorate$4U([
    dto({
        string: true
    }),
    _ts_metadata$4U("design:type", String)
], UserReferralCustomerMyDTO.prototype, "link", void 0);
_ts_decorate$4U([
    dto({
        number: true
    }),
    _ts_metadata$4U("design:type", Number)
], UserReferralCustomerMyDTO.prototype, "activationsCount", void 0);
_ts_decorate$4U([
    dto({
        object: {
            type: UserReferralCustomerMyEarnedDTO
        }
    }),
    _ts_metadata$4U("design:type", typeof UserReferralCustomerMyEarnedDTO === "undefined" ? Object : UserReferralCustomerMyEarnedDTO)
], UserReferralCustomerMyDTO.prototype, "earned", void 0);
class UserReferralCustomerOtherDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4U(this, "applied", void 0), _define_property$4U(this, "info", void 0);
    }
}
_ts_decorate$4U([
    dto({
        boolean: true
    }),
    _ts_metadata$4U("design:type", Boolean)
], UserReferralCustomerOtherDTO.prototype, "applied", void 0);
_ts_decorate$4U([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4U("design:type", Object)
], UserReferralCustomerOtherDTO.prototype, "info", void 0);
class UserReferralEnabledCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4U(this, "status", void 0), _define_property$4U(this, "my", void 0), _define_property$4U(this, "other", void 0), _define_property$4U(this, "shareText", void 0), _define_property$4U(this, "description", void 0), _define_property$4U(this, "terms", void 0);
    }
}
_ts_decorate$4U([
    dto({
        equals: GetUserReferralCodeStatus.ENABLED
    }),
    _ts_metadata$4U("design:type", typeof GetUserReferralCodeStatus === "undefined" || typeof GetUserReferralCodeStatus.ENABLED === "undefined" ? Object : GetUserReferralCodeStatus.ENABLED)
], UserReferralEnabledCustomerDTO.prototype, "status", void 0);
_ts_decorate$4U([
    dto({
        object: {
            type: UserReferralCustomerMyDTO
        }
    }),
    _ts_metadata$4U("design:type", typeof UserReferralCustomerMyDTO === "undefined" ? Object : UserReferralCustomerMyDTO)
], UserReferralEnabledCustomerDTO.prototype, "my", void 0);
_ts_decorate$4U([
    dto({
        object: {
            type: UserReferralCustomerOtherDTO
        }
    }),
    _ts_metadata$4U("design:type", typeof UserReferralCustomerOtherDTO === "undefined" ? Object : UserReferralCustomerOtherDTO)
], UserReferralEnabledCustomerDTO.prototype, "other", void 0);
_ts_decorate$4U([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4U("design:type", Object)
], UserReferralEnabledCustomerDTO.prototype, "shareText", void 0);
_ts_decorate$4U([
    dto({
        string: true
    }),
    _ts_metadata$4U("design:type", String)
], UserReferralEnabledCustomerDTO.prototype, "description", void 0);
_ts_decorate$4U([
    dto({
        string: true
    }),
    _ts_metadata$4U("design:type", String)
], UserReferralEnabledCustomerDTO.prototype, "terms", void 0);
class UserReferralDisabledCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4U(this, "status", void 0);
    }
}
_ts_decorate$4U([
    dto({
        equals: GetUserReferralCodeStatus.DISABLED
    }),
    _ts_metadata$4U("design:type", typeof GetUserReferralCodeStatus === "undefined" || typeof GetUserReferralCodeStatus.DISABLED === "undefined" ? Object : GetUserReferralCodeStatus.DISABLED)
], UserReferralDisabledCustomerDTO.prototype, "status", void 0);
class UserReferralCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4U(this, "data", void 0);
    }
}
_ts_decorate$4U([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: GetUserReferralCodeStatus.ENABLED,
                        value: UserReferralEnabledCustomerDTO
                    },
                    {
                        name: GetUserReferralCodeStatus.DISABLED,
                        value: UserReferralDisabledCustomerDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4U("design:type", Object)
], UserReferralCustomerDTO.prototype, "data", void 0);

function _define_property$4T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4T(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4T(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var GetUserPromoCodeStatus = /*#__PURE__*/ function(GetUserPromoCodeStatus) {
    GetUserPromoCodeStatus["ENABLED"] = "enabled";
    GetUserPromoCodeStatus["DISABLED"] = "disabled";
    return GetUserPromoCodeStatus;
}({});
class IntegrationGetUserPromoCodeItemResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4T(this, "name", void 0), _define_property$4T(this, "code", void 0), _define_property$4T(this, "category", void 0), _define_property$4T(this, "description", void 0), _define_property$4T(this, "isActive", void 0), _define_property$4T(this, "dateFrom", void 0), _define_property$4T(this, "dateTo", void 0);
    }
}
_ts_decorate$4T([
    dto({
        string: true
    }),
    _ts_metadata$4T("design:type", String)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "name", void 0);
_ts_decorate$4T([
    dto({
        string: true
    }),
    _ts_metadata$4T("design:type", String)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "code", void 0);
_ts_decorate$4T([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4T("design:type", Object)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "category", void 0);
_ts_decorate$4T([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4T("design:type", Object)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "description", void 0);
_ts_decorate$4T([
    dto({
        boolean: true
    }),
    _ts_metadata$4T("design:type", Boolean)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "isActive", void 0);
_ts_decorate$4T([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4T("design:type", Object)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "dateFrom", void 0);
_ts_decorate$4T([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4T("design:type", Object)
], IntegrationGetUserPromoCodeItemResponseDTO.prototype, "dateTo", void 0);
class IntegrationGetUserPromoCodesEnabledResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4T(this, "status", void 0), _define_property$4T(this, "promoCodes", void 0);
    }
}
_ts_decorate$4T([
    dto({
        equals: "enabled"
    }),
    _ts_metadata$4T("design:type", typeof GetUserPromoCodeStatus === "undefined" || "string" === "undefined" ? Object : "enabled")
], IntegrationGetUserPromoCodesEnabledResponseDTO.prototype, "status", void 0);
_ts_decorate$4T([
    dto({
        array: true,
        object: {
            type: IntegrationGetUserPromoCodeItemResponseDTO
        }
    }),
    _ts_metadata$4T("design:type", Array)
], IntegrationGetUserPromoCodesEnabledResponseDTO.prototype, "promoCodes", void 0);
class IntegrationGetUserPromoCodesDisabledResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4T(this, "status", void 0);
    }
}
_ts_decorate$4T([
    dto({
        equals: "disabled"
    }),
    _ts_metadata$4T("design:type", typeof GetUserPromoCodeStatus === "undefined" || "string" === "undefined" ? Object : "disabled")
], IntegrationGetUserPromoCodesDisabledResponseDTO.prototype, "status", void 0);
class IntegrationGetUserPromoCodesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4T(this, "data", void 0);
    }
}
_ts_decorate$4T([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "enabled",
                        value: IntegrationGetUserPromoCodesEnabledResponseDTO
                    },
                    {
                        name: "disabled",
                        value: IntegrationGetUserPromoCodesDisabledResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4T("design:type", Object)
], IntegrationGetUserPromoCodesResponseDTO.prototype, "data", void 0);

function _define_property$4S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4S(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4S(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UserPromoCodeCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4S(this, "name", void 0), _define_property$4S(this, "code", void 0), _define_property$4S(this, "category", void 0), _define_property$4S(this, "description", void 0), _define_property$4S(this, "isActive", void 0), _define_property$4S(this, "dateFrom", void 0), _define_property$4S(this, "dateTo", void 0);
    }
}
_ts_decorate$4S([
    dto({
        string: true
    }),
    _ts_metadata$4S("design:type", String)
], UserPromoCodeCustomerDTO.prototype, "name", void 0);
_ts_decorate$4S([
    dto({
        string: true
    }),
    _ts_metadata$4S("design:type", String)
], UserPromoCodeCustomerDTO.prototype, "code", void 0);
_ts_decorate$4S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4S("design:type", Object)
], UserPromoCodeCustomerDTO.prototype, "category", void 0);
_ts_decorate$4S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4S("design:type", Object)
], UserPromoCodeCustomerDTO.prototype, "description", void 0);
_ts_decorate$4S([
    dto({
        boolean: true
    }),
    _ts_metadata$4S("design:type", Boolean)
], UserPromoCodeCustomerDTO.prototype, "isActive", void 0);
_ts_decorate$4S([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$4S("design:type", Object)
], UserPromoCodeCustomerDTO.prototype, "dateFrom", void 0);
_ts_decorate$4S([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$4S("design:type", Object)
], UserPromoCodeCustomerDTO.prototype, "dateTo", void 0);
class UserPromoCodesEnabledCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4S(this, "status", void 0), _define_property$4S(this, "promoCodes", void 0);
    }
}
_ts_decorate$4S([
    dto({
        equals: GetUserPromoCodeStatus.ENABLED
    }),
    _ts_metadata$4S("design:type", typeof GetUserPromoCodeStatus === "undefined" || typeof GetUserPromoCodeStatus.ENABLED === "undefined" ? Object : GetUserPromoCodeStatus.ENABLED)
], UserPromoCodesEnabledCustomerDTO.prototype, "status", void 0);
_ts_decorate$4S([
    dto({
        array: true,
        object: {
            type: UserPromoCodeCustomerDTO
        }
    }),
    _ts_metadata$4S("design:type", Array)
], UserPromoCodesEnabledCustomerDTO.prototype, "promoCodes", void 0);
class UserPromoCodesDisabledCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4S(this, "status", void 0);
    }
}
_ts_decorate$4S([
    dto({
        equals: GetUserPromoCodeStatus.DISABLED
    }),
    _ts_metadata$4S("design:type", typeof GetUserPromoCodeStatus === "undefined" || typeof GetUserPromoCodeStatus.DISABLED === "undefined" ? Object : GetUserPromoCodeStatus.DISABLED)
], UserPromoCodesDisabledCustomerDTO.prototype, "status", void 0);
class UserPromoCodesCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4S(this, "data", void 0);
    }
}
_ts_decorate$4S([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: GetUserPromoCodeStatus.ENABLED,
                        value: UserPromoCodesEnabledCustomerDTO
                    },
                    {
                        name: GetUserPromoCodeStatus.DISABLED,
                        value: UserPromoCodesDisabledCustomerDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4S("design:type", Object)
], UserPromoCodesCustomerDTO.prototype, "data", void 0);

function _define_property$4R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4R(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4R(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ApplyReferralCodeCurrentUserCustomerRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4R(this, "referralCode", void 0);
    }
}
_ts_decorate$4R([
    dto({
        string: true
    }),
    _ts_metadata$4R("design:type", String)
], ApplyReferralCodeCurrentUserCustomerRequestDTO.prototype, "referralCode", void 0);

function _define_property$4Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4Q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4Q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ApplyReferralCodeStatus = /*#__PURE__*/ function(ApplyReferralCodeStatus) {
    ApplyReferralCodeStatus["SUCCESS"] = "success";
    ApplyReferralCodeStatus["ERROR"] = "error";
    return ApplyReferralCodeStatus;
}({});
class ApplyReferralCodeSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4Q(this, "status", void 0);
    }
}
_ts_decorate$4Q([
    dto({
        equals: "success"
    }),
    _ts_metadata$4Q("design:type", typeof ApplyReferralCodeStatus === "undefined" || "string" === "undefined" ? Object : "success")
], ApplyReferralCodeSuccessResponseDTO.prototype, "status", void 0);
class ApplyReferralCodeErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4Q(this, "status", void 0), _define_property$4Q(this, "message", void 0);
    }
}
_ts_decorate$4Q([
    dto({
        equals: "error"
    }),
    _ts_metadata$4Q("design:type", typeof ApplyReferralCodeStatus === "undefined" || "string" === "undefined" ? Object : "error")
], ApplyReferralCodeErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$4Q([
    dto({
        string: true
    }),
    _ts_metadata$4Q("design:type", String)
], ApplyReferralCodeErrorResponseDTO.prototype, "message", void 0);
class ApplyReferralCodeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4Q(this, "data", void 0);
    }
}
_ts_decorate$4Q([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: ApplyReferralCodeSuccessResponseDTO
                    },
                    {
                        name: "error",
                        value: ApplyReferralCodeErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4Q("design:type", Object)
], ApplyReferralCodeResponseDTO.prototype, "data", void 0);

function _define_property$4P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4P(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4P(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCurrentUserCustomerRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4P(this, "firstName", void 0), _define_property$4P(this, "lastName", void 0), _define_property$4P(this, "gender", void 0), _define_property$4P(this, "birthdate", void 0), _define_property$4P(this, "customerAllowedToSendNotifications", void 0);
    }
}
_ts_decorate$4P([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4P("design:type", Object)
], UpdateCurrentUserCustomerRequestDTO.prototype, "firstName", void 0);
_ts_decorate$4P([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4P("design:type", Object)
], UpdateCurrentUserCustomerRequestDTO.prototype, "lastName", void 0);
_ts_decorate$4P([
    dto({
        optional: {
            allowNull: false
        },
        enum: Gender
    }),
    _ts_metadata$4P("design:type", Object)
], UpdateCurrentUserCustomerRequestDTO.prototype, "gender", void 0);
_ts_decorate$4P([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$4P("design:type", Object)
], UpdateCurrentUserCustomerRequestDTO.prototype, "birthdate", void 0);
_ts_decorate$4P([
    dto({
        optional: {
            allowNull: false
        },
        enum: CustomerAllowedNotificationLevel,
        array: true
    }),
    _ts_metadata$4P("design:type", Object)
], UpdateCurrentUserCustomerRequestDTO.prototype, "customerAllowedToSendNotifications", void 0);

function _define_property$4O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4O(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4O(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCurrentUserRequestCustomerShopDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4O(this, "shopId", void 0);
    }
}
_ts_decorate$4O([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4O("design:type", Object)
], UpdateCurrentUserRequestCustomerShopDTO.prototype, "shopId", void 0);

function _define_property$4N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4N(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4N(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCurrentUserCustomerSettingsRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4N(this, "customerAllowedToSendNotifications", void 0), _define_property$4N(this, "customerAllowedToSendNotificationPreset", void 0);
    }
}
_ts_decorate$4N([
    dto({
        optional: {
            allowNull: false
        },
        enum: CustomerAllowedNotificationLevel,
        array: true
    }),
    _ts_metadata$4N("design:type", Object)
], UpdateCurrentUserCustomerSettingsRequestDTO.prototype, "customerAllowedToSendNotifications", void 0);
_ts_decorate$4N([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4N("design:type", Object)
], UpdateCurrentUserCustomerSettingsRequestDTO.prototype, "customerAllowedToSendNotificationPreset", void 0);

function _define_property$4M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4M(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4M(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CustomerProfileWalletV1EventType = /*#__PURE__*/ function(CustomerProfileWalletV1EventType) {
    CustomerProfileWalletV1EventType["ACCRUAL"] = "ACCRUAL";
    CustomerProfileWalletV1EventType["BURNING"] = "BURNING";
    CustomerProfileWalletV1EventType["SPENDING"] = "SPENDING";
    return CustomerProfileWalletV1EventType;
}({});
class CustomerProfileWalletV1EventResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4M(this, "type", void 0), _define_property$4M(this, "description", void 0), _define_property$4M(this, "amount", void 0), _define_property$4M(this, "date", void 0);
    }
}
_ts_decorate$4M([
    dto({
        enum: CustomerProfileWalletV1EventType
    }),
    _ts_metadata$4M("design:type", String)
], CustomerProfileWalletV1EventResponseDTO.prototype, "type", void 0);
_ts_decorate$4M([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4M("design:type", Object)
], CustomerProfileWalletV1EventResponseDTO.prototype, "description", void 0);
_ts_decorate$4M([
    dto({
        number: true
    }),
    _ts_metadata$4M("design:type", Number)
], CustomerProfileWalletV1EventResponseDTO.prototype, "amount", void 0);
_ts_decorate$4M([
    dto({
        date: true
    }),
    _ts_metadata$4M("design:type", typeof Date === "undefined" ? Object : Date)
], CustomerProfileWalletV1EventResponseDTO.prototype, "date", void 0);
class CustomerProfileWalletV1ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4M(this, "cashbackPercent", void 0), _define_property$4M(this, "amount", void 0), _define_property$4M(this, "history", void 0), _define_property$4M(this, "nearestEvents", void 0), _define_property$4M(this, "exceptionReason", void 0);
    }
}
_ts_decorate$4M([
    dto({
        number: true
    }),
    _ts_metadata$4M("design:type", Number)
], CustomerProfileWalletV1ResponseDTO.prototype, "cashbackPercent", void 0);
_ts_decorate$4M([
    dto({
        number: true
    }),
    _ts_metadata$4M("design:type", Number)
], CustomerProfileWalletV1ResponseDTO.prototype, "amount", void 0);
_ts_decorate$4M([
    dto({
        array: true,
        object: {
            type: CustomerProfileWalletV1EventResponseDTO
        }
    }),
    _ts_metadata$4M("design:type", Array)
], CustomerProfileWalletV1ResponseDTO.prototype, "history", void 0);
_ts_decorate$4M([
    dto({
        array: true,
        object: {
            type: CustomerProfileWalletV1EventResponseDTO
        }
    }),
    _ts_metadata$4M("design:type", Array)
], CustomerProfileWalletV1ResponseDTO.prototype, "nearestEvents", void 0);
_ts_decorate$4M([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4M("design:type", Object)
], CustomerProfileWalletV1ResponseDTO.prototype, "exceptionReason", void 0);

function _define_property$4L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4L(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4L(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CustomerProfileWalletV3EventType = /*#__PURE__*/ function(CustomerProfileWalletV3EventType) {
    CustomerProfileWalletV3EventType["ACCRUAL"] = "ACCRUAL";
    CustomerProfileWalletV3EventType["BURNING"] = "BURNING";
    CustomerProfileWalletV3EventType["SPENDING"] = "SPENDING";
    return CustomerProfileWalletV3EventType;
}({});
class CustomerProfileWalletV3EventResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4L(this, "type", void 0), _define_property$4L(this, "description", void 0), _define_property$4L(this, "amount", void 0), _define_property$4L(this, "date", void 0);
    }
}
_ts_decorate$4L([
    dto({
        enum: CustomerProfileWalletV3EventType
    }),
    _ts_metadata$4L("design:type", String)
], CustomerProfileWalletV3EventResponseDTO.prototype, "type", void 0);
_ts_decorate$4L([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4L("design:type", Object)
], CustomerProfileWalletV3EventResponseDTO.prototype, "description", void 0);
_ts_decorate$4L([
    dto({
        number: true
    }),
    _ts_metadata$4L("design:type", Number)
], CustomerProfileWalletV3EventResponseDTO.prototype, "amount", void 0);
_ts_decorate$4L([
    dto({
        date: true
    }),
    _ts_metadata$4L("design:type", typeof Date === "undefined" ? Object : Date)
], CustomerProfileWalletV3EventResponseDTO.prototype, "date", void 0);

var CustomerProfileWalletV3Type = /*#__PURE__*/ function(CustomerProfileWalletV3Type) {
    CustomerProfileWalletV3Type["CASHBACK"] = "cashback";
    CustomerProfileWalletV3Type["BONUS"] = "bonus";
    return CustomerProfileWalletV3Type;
}({});

function _define_property$4K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4K(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4K(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CustomerProfileWalletV3CashbackResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4K(this, "type", void 0), _define_property$4K(this, "cashbackPercent", void 0), _define_property$4K(this, "amount", void 0), _define_property$4K(this, "history", void 0), _define_property$4K(this, "nearestEvents", void 0), _define_property$4K(this, "exceptionReason", void 0);
    }
}
_ts_decorate$4K([
    dto({
        equals: CustomerProfileWalletV3Type.CASHBACK
    }),
    _ts_metadata$4K("design:type", typeof CustomerProfileWalletV3Type === "undefined" || typeof CustomerProfileWalletV3Type.CASHBACK === "undefined" ? Object : CustomerProfileWalletV3Type.CASHBACK)
], CustomerProfileWalletV3CashbackResponseDTO.prototype, "type", void 0);
_ts_decorate$4K([
    dto({
        number: true
    }),
    _ts_metadata$4K("design:type", Number)
], CustomerProfileWalletV3CashbackResponseDTO.prototype, "cashbackPercent", void 0);
_ts_decorate$4K([
    dto({
        number: true
    }),
    _ts_metadata$4K("design:type", Number)
], CustomerProfileWalletV3CashbackResponseDTO.prototype, "amount", void 0);
_ts_decorate$4K([
    dto({
        array: true,
        object: {
            type: CustomerProfileWalletV3EventResponseDTO
        }
    }),
    _ts_metadata$4K("design:type", Array)
], CustomerProfileWalletV3CashbackResponseDTO.prototype, "history", void 0);
_ts_decorate$4K([
    dto({
        array: true,
        object: {
            type: CustomerProfileWalletV3EventResponseDTO
        }
    }),
    _ts_metadata$4K("design:type", Array)
], CustomerProfileWalletV3CashbackResponseDTO.prototype, "nearestEvents", void 0);
_ts_decorate$4K([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4K("design:type", Object)
], CustomerProfileWalletV3CashbackResponseDTO.prototype, "exceptionReason", void 0);

function _define_property$4J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4J(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4J(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CustomerProfileWalletV3BonusResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4J(this, "type", void 0), _define_property$4J(this, "name", void 0), _define_property$4J(this, "description", void 0), _define_property$4J(this, "color", void 0), _define_property$4J(this, "bonus", void 0), _define_property$4J(this, "exceptionReason", void 0);
    }
}
_ts_decorate$4J([
    dto({
        equals: CustomerProfileWalletV3Type.BONUS
    }),
    _ts_metadata$4J("design:type", typeof CustomerProfileWalletV3Type === "undefined" || typeof CustomerProfileWalletV3Type.BONUS === "undefined" ? Object : CustomerProfileWalletV3Type.BONUS)
], CustomerProfileWalletV3BonusResponseDTO.prototype, "type", void 0);
_ts_decorate$4J([
    dto({
        string: true
    }),
    _ts_metadata$4J("design:type", String)
], CustomerProfileWalletV3BonusResponseDTO.prototype, "name", void 0);
_ts_decorate$4J([
    dto({
        string: true
    }),
    _ts_metadata$4J("design:type", String)
], CustomerProfileWalletV3BonusResponseDTO.prototype, "description", void 0);
_ts_decorate$4J([
    dto({
        string: true
    }),
    _ts_metadata$4J("design:type", String)
], CustomerProfileWalletV3BonusResponseDTO.prototype, "color", void 0);
_ts_decorate$4J([
    dto({
        string: true
    }),
    _ts_metadata$4J("design:type", String)
], CustomerProfileWalletV3BonusResponseDTO.prototype, "bonus", void 0);
_ts_decorate$4J([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4J("design:type", Object)
], CustomerProfileWalletV3BonusResponseDTO.prototype, "exceptionReason", void 0);

function _define_property$4I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4I(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4I(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CustomerProfileWalletV3ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4I(this, "card", void 0);
    }
}
_ts_decorate$4I([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: CustomerProfileWalletV3Type.CASHBACK,
                        value: CustomerProfileWalletV3CashbackResponseDTO
                    },
                    {
                        name: CustomerProfileWalletV3Type.BONUS,
                        value: CustomerProfileWalletV3BonusResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4I("design:type", Object)
], CustomerProfileWalletV3ResponseDTO.prototype, "card", void 0);

function _define_property$4H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4H(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4H(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockBonusCardDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Карты (поля) нет, пока у покупателя не заведён кошелёк.
        _define_property$4H(this, "card", void 0);
    }
}
_ts_decorate$4H([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: CustomerProfileWalletV3Type.CASHBACK,
                        value: CustomerProfileWalletV3CashbackResponseDTO
                    },
                    {
                        name: CustomerProfileWalletV3Type.BONUS,
                        value: CustomerProfileWalletV3BonusResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4H("design:type", Object)
], ContentBlockBonusCardDataDTO.prototype, "card", void 0);
class ContentBlockBonusCardResponseDTO extends ContentBlockResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$4H(this, "component", void 0), _define_property$4H(this, "data", void 0);
    }
}
_ts_decorate$4H([
    dto({
        equals: ContentBlockResponseComponent.BONUS_CARD
    }),
    _ts_metadata$4H("design:type", typeof ContentBlockResponseComponent === "undefined" || typeof ContentBlockResponseComponent.BONUS_CARD === "undefined" ? Object : ContentBlockResponseComponent.BONUS_CARD)
], ContentBlockBonusCardResponseDTO.prototype, "component", void 0);
_ts_decorate$4H([
    dto({
        object: {
            type: ContentBlockBonusCardDataDTO
        }
    }),
    _ts_metadata$4H("design:type", typeof ContentBlockBonusCardDataDTO === "undefined" ? Object : ContentBlockBonusCardDataDTO)
], ContentBlockBonusCardResponseDTO.prototype, "data", void 0);

function _define_property$4G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4G(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4G(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockProductsDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4G(this, "view", void 0), _define_property$4G(this, "products", void 0);
    }
}
_ts_decorate$4G([
    dto({
        enum: ContentBlockProductsView
    }),
    _ts_metadata$4G("design:type", typeof ContentBlockProductsView === "undefined" ? Object : ContentBlockProductsView)
], ContentBlockProductsDataDTO.prototype, "view", void 0);
_ts_decorate$4G([
    dto({
        array: true,
        object: {
            type: ProductDTO
        }
    }),
    _ts_metadata$4G("design:type", Array)
], ContentBlockProductsDataDTO.prototype, "products", void 0);
class ContentBlockProductsResponseDTO extends ContentBlockResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$4G(this, "component", void 0), _define_property$4G(this, "data", void 0);
    }
}
_ts_decorate$4G([
    dto({
        equals: ContentBlockResponseComponent.PRODUCTS
    }),
    _ts_metadata$4G("design:type", typeof ContentBlockResponseComponent === "undefined" || typeof ContentBlockResponseComponent.PRODUCTS === "undefined" ? Object : ContentBlockResponseComponent.PRODUCTS)
], ContentBlockProductsResponseDTO.prototype, "component", void 0);
_ts_decorate$4G([
    dto({
        object: {
            type: ContentBlockProductsDataDTO
        }
    }),
    _ts_metadata$4G("design:type", typeof ContentBlockProductsDataDTO === "undefined" ? Object : ContentBlockProductsDataDTO)
], ContentBlockProductsResponseDTO.prototype, "data", void 0);

function _define_property$4F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4F(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4F(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockSearchDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4F(this, "placeholders", void 0);
    }
}
_ts_decorate$4F([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$4F("design:type", Array)
], ContentBlockSearchDataDTO.prototype, "placeholders", void 0);
class ContentBlockSearchResponseDTO extends ContentBlockResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$4F(this, "component", void 0), _define_property$4F(this, "data", void 0);
    }
}
_ts_decorate$4F([
    dto({
        equals: ContentBlockResponseComponent.SEARCH
    }),
    _ts_metadata$4F("design:type", typeof ContentBlockResponseComponent === "undefined" || typeof ContentBlockResponseComponent.SEARCH === "undefined" ? Object : ContentBlockResponseComponent.SEARCH)
], ContentBlockSearchResponseDTO.prototype, "component", void 0);
_ts_decorate$4F([
    dto({
        object: {
            type: ContentBlockSearchDataDTO
        }
    }),
    _ts_metadata$4F("design:type", typeof ContentBlockSearchDataDTO === "undefined" ? Object : ContentBlockSearchDataDTO)
], ContentBlockSearchResponseDTO.prototype, "data", void 0);

function _define_property$4E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4E(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4E(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockShortcutActionHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4E(this, "type", void 0), _define_property$4E(this, "file", void 0);
    }
}
_ts_decorate$4E([
    dto({
        equals: ContentBlockShortcutActionType.HTML
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockShortcutActionType === "undefined" || typeof ContentBlockShortcutActionType.HTML === "undefined" ? Object : ContentBlockShortcutActionType.HTML)
], ContentBlockShortcutActionHtmlDTO.prototype, "type", void 0);
_ts_decorate$4E([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$4E("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], ContentBlockShortcutActionHtmlDTO.prototype, "file", void 0);
class ContentBlockShortcutActionCatalogDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4E(this, "type", void 0);
    }
}
_ts_decorate$4E([
    dto({
        equals: ContentBlockShortcutActionType.CATALOG
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockShortcutActionType === "undefined" || typeof ContentBlockShortcutActionType.CATALOG === "undefined" ? Object : ContentBlockShortcutActionType.CATALOG)
], ContentBlockShortcutActionCatalogDTO.prototype, "type", void 0);
class ContentBlockShortcutActionShopsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4E(this, "type", void 0);
    }
}
_ts_decorate$4E([
    dto({
        equals: ContentBlockShortcutActionType.SHOPS
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockShortcutActionType === "undefined" || typeof ContentBlockShortcutActionType.SHOPS === "undefined" ? Object : ContentBlockShortcutActionType.SHOPS)
], ContentBlockShortcutActionShopsDTO.prototype, "type", void 0);
class ContentBlockShortcutActionCameraScanDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4E(this, "type", void 0);
    }
}
_ts_decorate$4E([
    dto({
        equals: ContentBlockShortcutActionType.CAMERA_SCAN
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockShortcutActionType === "undefined" || typeof ContentBlockShortcutActionType.CAMERA_SCAN === "undefined" ? Object : ContentBlockShortcutActionType.CAMERA_SCAN)
], ContentBlockShortcutActionCameraScanDTO.prototype, "type", void 0);
class ContentBlockShortcutDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4E(this, "id", void 0), _define_property$4E(this, "name", void 0), _define_property$4E(this, "icon", void 0), _define_property$4E(this, "action", void 0);
    }
}
_ts_decorate$4E([
    dto({
        string: true
    }),
    _ts_metadata$4E("design:type", String)
], ContentBlockShortcutDTO.prototype, "id", void 0);
_ts_decorate$4E([
    dto({
        string: true
    }),
    _ts_metadata$4E("design:type", String)
], ContentBlockShortcutDTO.prototype, "name", void 0);
_ts_decorate$4E([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4E("design:type", Object)
], ContentBlockShortcutDTO.prototype, "icon", void 0);
_ts_decorate$4E([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: ContentBlockShortcutActionType.HTML,
                        value: ContentBlockShortcutActionHtmlDTO
                    },
                    {
                        name: ContentBlockShortcutActionType.CATALOG,
                        value: ContentBlockShortcutActionCatalogDTO
                    },
                    {
                        name: ContentBlockShortcutActionType.SHOPS,
                        value: ContentBlockShortcutActionShopsDTO
                    },
                    {
                        name: ContentBlockShortcutActionType.CAMERA_SCAN,
                        value: ContentBlockShortcutActionCameraScanDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4E("design:type", Object)
], ContentBlockShortcutDTO.prototype, "action", void 0);
class ContentBlockShortcutsDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4E(this, "style", void 0), _define_property$4E(this, "shortcuts", void 0);
    }
}
_ts_decorate$4E([
    dto({
        enum: ContentBlockShortcutsStyle
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockShortcutsStyle === "undefined" ? Object : ContentBlockShortcutsStyle)
], ContentBlockShortcutsDataDTO.prototype, "style", void 0);
_ts_decorate$4E([
    dto({
        array: true,
        object: {
            type: ContentBlockShortcutDTO
        }
    }),
    _ts_metadata$4E("design:type", Array)
], ContentBlockShortcutsDataDTO.prototype, "shortcuts", void 0);
class ContentBlockShortcutsResponseDTO extends ContentBlockResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$4E(this, "component", void 0), _define_property$4E(this, "data", void 0);
    }
}
_ts_decorate$4E([
    dto({
        equals: ContentBlockResponseComponent.SHORTCUTS
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockResponseComponent === "undefined" || typeof ContentBlockResponseComponent.SHORTCUTS === "undefined" ? Object : ContentBlockResponseComponent.SHORTCUTS)
], ContentBlockShortcutsResponseDTO.prototype, "component", void 0);
_ts_decorate$4E([
    dto({
        object: {
            type: ContentBlockShortcutsDataDTO
        }
    }),
    _ts_metadata$4E("design:type", typeof ContentBlockShortcutsDataDTO === "undefined" ? Object : ContentBlockShortcutsDataDTO)
], ContentBlockShortcutsResponseDTO.prototype, "data", void 0);

function _define_property$4D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4D(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4D(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ContentBlockPageResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4D(this, "blocks", void 0);
    }
}
_ts_decorate$4D([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "component",
                subTypes: [
                    {
                        name: ContentBlockResponseComponent.SEARCH,
                        value: ContentBlockSearchResponseDTO
                    },
                    {
                        name: ContentBlockResponseComponent.ACTIVE_ORDERS,
                        value: ContentBlockActiveOrdersResponseDTO
                    },
                    {
                        name: ContentBlockResponseComponent.BONUS_CARD,
                        value: ContentBlockBonusCardResponseDTO
                    },
                    {
                        name: ContentBlockResponseComponent.BANNERS,
                        value: ContentBlockBannersResponseDTO
                    },
                    {
                        name: ContentBlockResponseComponent.SHORTCUTS,
                        value: ContentBlockShortcutsResponseDTO
                    },
                    {
                        name: ContentBlockResponseComponent.PRODUCTS,
                        value: ContentBlockProductsResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4D("design:type", Array)
], ContentBlockPageResponseDTO.prototype, "blocks", void 0);

function _define_property$4C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4C(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4C(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetContentBlockBannerProductsCatalogRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4C(this, "bannerId", void 0), _define_property$4C(this, "search", void 0), _define_property$4C(this, "sort", void 0), _define_property$4C(this, "price", void 0);
    }
}
_ts_decorate$4C([
    dto({
        string: true
    }),
    _ts_metadata$4C("design:type", String)
], GetContentBlockBannerProductsCatalogRequestDTO.prototype, "bannerId", void 0);
_ts_decorate$4C([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4C("design:type", Object)
], GetContentBlockBannerProductsCatalogRequestDTO.prototype, "search", void 0);
_ts_decorate$4C([
    dto({
        optional: {
            allowNull: false
        },
        sorting: GetProductsListSortingFields
    }),
    _ts_metadata$4C("design:type", Object)
], GetContentBlockBannerProductsCatalogRequestDTO.prototype, "sort", void 0);
_ts_decorate$4C([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: GetProductsListPriceRequestDTO
        }
    }),
    _ts_metadata$4C("design:type", Object)
], GetContentBlockBannerProductsCatalogRequestDTO.prototype, "price", void 0);

function _define_property$4B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4B(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4B(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetContentBlockBannerProductsCatalogFiltersRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4B(this, "bannerId", void 0);
    }
}
_ts_decorate$4B([
    dto({
        string: true
    }),
    _ts_metadata$4B("design:type", String)
], GetContentBlockBannerProductsCatalogFiltersRequestDTO.prototype, "bannerId", void 0);

function _define_property$4A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4A(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4A(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetContentBlockBannerProductsCatalogFiltersSortingResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4A(this, "code", void 0), _define_property$4A(this, "name", void 0);
    }
}
_ts_decorate$4A([
    dto({
        string: true
    }),
    _ts_metadata$4A("design:type", String)
], GetContentBlockBannerProductsCatalogFiltersSortingResponseDTO.prototype, "code", void 0);
_ts_decorate$4A([
    dto({
        string: true
    }),
    _ts_metadata$4A("design:type", String)
], GetContentBlockBannerProductsCatalogFiltersSortingResponseDTO.prototype, "name", void 0);
class GetContentBlockBannerProductsCatalogFiltersPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4A(this, "min", void 0), _define_property$4A(this, "max", void 0);
    }
}
_ts_decorate$4A([
    dto({
        number: true
    }),
    _ts_metadata$4A("design:type", Number)
], GetContentBlockBannerProductsCatalogFiltersPriceResponseDTO.prototype, "min", void 0);
_ts_decorate$4A([
    dto({
        number: true
    }),
    _ts_metadata$4A("design:type", Number)
], GetContentBlockBannerProductsCatalogFiltersPriceResponseDTO.prototype, "max", void 0);
class GetContentBlockBannerProductsCatalogFiltersResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4A(this, "sortingFields", void 0), _define_property$4A(this, "price", void 0);
    }
}
_ts_decorate$4A([
    dto({
        array: true,
        object: {
            type: GetContentBlockBannerProductsCatalogFiltersSortingResponseDTO
        }
    }),
    _ts_metadata$4A("design:type", Array)
], GetContentBlockBannerProductsCatalogFiltersResponseDTO.prototype, "sortingFields", void 0);
_ts_decorate$4A([
    dto({
        object: {
            type: GetContentBlockBannerProductsCatalogFiltersPriceResponseDTO
        }
    }),
    _ts_metadata$4A("design:type", typeof GetContentBlockBannerProductsCatalogFiltersPriceResponseDTO === "undefined" ? Object : GetContentBlockBannerProductsCatalogFiltersPriceResponseDTO)
], GetContentBlockBannerProductsCatalogFiltersResponseDTO.prototype, "price", void 0);

function _define_property$4z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV1ProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4z(this, "base", void 0), _define_property$4z(this, "result", void 0), _define_property$4z(this, "discount", void 0);
    }
}
_ts_decorate$4z([
    dto({
        number: true
    }),
    _ts_metadata$4z("design:type", Number)
], CartV1ProductPriceDTO.prototype, "base", void 0);
_ts_decorate$4z([
    dto({
        number: true
    }),
    _ts_metadata$4z("design:type", Number)
], CartV1ProductPriceDTO.prototype, "result", void 0);
_ts_decorate$4z([
    dto({
        number: true
    }),
    _ts_metadata$4z("design:type", Number)
], CartV1ProductPriceDTO.prototype, "discount", void 0);
class CartV1ProductRequiredAttributeValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4z(this, "id", void 0), _define_property$4z(this, "name", void 0);
    }
}
_ts_decorate$4z([
    dto({
        string: true
    }),
    _ts_metadata$4z("design:type", String)
], CartV1ProductRequiredAttributeValueDTO.prototype, "id", void 0);
_ts_decorate$4z([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$4z("design:type", Object)
], CartV1ProductRequiredAttributeValueDTO.prototype, "name", void 0);
class CartV1ProductRequiredAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4z(this, "id", void 0), _define_property$4z(this, "name", void 0), _define_property$4z(this, "measure", void 0), _define_property$4z(this, "value", void 0);
    }
}
_ts_decorate$4z([
    dto({
        string: true
    }),
    _ts_metadata$4z("design:type", String)
], CartV1ProductRequiredAttributeDTO.prototype, "id", void 0);
_ts_decorate$4z([
    dto({
        string: true
    }),
    _ts_metadata$4z("design:type", String)
], CartV1ProductRequiredAttributeDTO.prototype, "name", void 0);
_ts_decorate$4z([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$4z("design:type", Object)
], CartV1ProductRequiredAttributeDTO.prototype, "measure", void 0);
_ts_decorate$4z([
    dto({
        object: {
            type: CartV1ProductRequiredAttributeValueDTO
        }
    }),
    _ts_metadata$4z("design:type", typeof CartV1ProductRequiredAttributeValueDTO === "undefined" ? Object : CartV1ProductRequiredAttributeValueDTO)
], CartV1ProductRequiredAttributeDTO.prototype, "value", void 0);
class CartV1ProductOriginalDTO extends ProductDTO {
    constructor(...args){
        super(...args), _define_property$4z(this, "attributes", void 0), _define_property$4z(this, "parts", void 0);
    }
}
_ts_decorate$4z([
    dto({
        array: true,
        object: {
            type: ProductAttributeDTO
        }
    }),
    _ts_metadata$4z("design:type", Array)
], CartV1ProductOriginalDTO.prototype, "attributes", void 0);
_ts_decorate$4z([
    dto({
        array: true,
        object: {
            type: ProductPartDTO
        }
    }),
    _ts_metadata$4z("design:type", Array)
], CartV1ProductOriginalDTO.prototype, "parts", void 0);
class CartV1ProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4z(this, "id", void 0), _define_property$4z(this, "name", void 0), _define_property$4z(this, "count", void 0), _define_property$4z(this, "applied", void 0), _define_property$4z(this, "originalProduct", void 0), _define_property$4z(this, "price", void 0), _define_property$4z(this, "maxCount", void 0), _define_property$4z(this, "errorMessage", void 0), _define_property$4z(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$4z([
    dto({
        string: true
    }),
    _ts_metadata$4z("design:type", String)
], CartV1ProductDTO.prototype, "id", void 0);
_ts_decorate$4z([
    dto({
        string: true
    }),
    _ts_metadata$4z("design:type", String)
], CartV1ProductDTO.prototype, "name", void 0);
_ts_decorate$4z([
    dto({
        number: true
    }),
    _ts_metadata$4z("design:type", Number)
], CartV1ProductDTO.prototype, "count", void 0);
_ts_decorate$4z([
    dto({
        boolean: true
    }),
    _ts_metadata$4z("design:type", Boolean)
], CartV1ProductDTO.prototype, "applied", void 0);
_ts_decorate$4z([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: CartV1ProductOriginalDTO
        }
    }),
    _ts_metadata$4z("design:type", Object)
], CartV1ProductDTO.prototype, "originalProduct", void 0);
_ts_decorate$4z([
    dto({
        object: {
            type: CartV1ProductPriceDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4z("design:type", Object)
], CartV1ProductDTO.prototype, "price", void 0);
_ts_decorate$4z([
    dto({
        number: {
            maxDecimalPlaces: 0
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4z("design:type", Object)
], CartV1ProductDTO.prototype, "maxCount", void 0);
_ts_decorate$4z([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4z("design:type", Object)
], CartV1ProductDTO.prototype, "errorMessage", void 0);
_ts_decorate$4z([
    dto({
        array: true,
        object: {
            type: CartV1ProductRequiredAttributeDTO
        }
    }),
    _ts_metadata$4z("design:type", Array)
], CartV1ProductDTO.prototype, "requiredAttributes", void 0);

function _define_property$4y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV1InfoPriceCalculationDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4y(this, "name", void 0), _define_property$4y(this, "description", void 0), _define_property$4y(this, "value", void 0), _define_property$4y(this, "measure", void 0);
    }
}
_ts_decorate$4y([
    dto({
        string: true
    }),
    _ts_metadata$4y("design:type", String)
], CartV1InfoPriceCalculationDTO.prototype, "name", void 0);
_ts_decorate$4y([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4y("design:type", Object)
], CartV1InfoPriceCalculationDTO.prototype, "description", void 0);
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoPriceCalculationDTO.prototype, "value", void 0);
_ts_decorate$4y([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4y("design:type", Object)
], CartV1InfoPriceCalculationDTO.prototype, "measure", void 0);
class CartV1InfoPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4y(this, "base", void 0), _define_property$4y(this, "result", void 0), _define_property$4y(this, "discount", void 0), _define_property$4y(this, "calculation", void 0);
    }
}
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoPriceDTO.prototype, "base", void 0);
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoPriceDTO.prototype, "result", void 0);
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoPriceDTO.prototype, "discount", void 0);
_ts_decorate$4y([
    dto({
        object: {
            type: CartV1InfoPriceCalculationDTO
        },
        array: true
    }),
    _ts_metadata$4y("design:type", Array)
], CartV1InfoPriceDTO.prototype, "calculation", void 0);
class CartV1InfoBonesesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4y(this, "willUse", void 0), _define_property$4y(this, "willAdd", void 0), _define_property$4y(this, "current", void 0);
    }
}
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoBonesesDTO.prototype, "willUse", void 0);
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoBonesesDTO.prototype, "willAdd", void 0);
_ts_decorate$4y([
    dto({
        number: true
    }),
    _ts_metadata$4y("design:type", Number)
], CartV1InfoBonesesDTO.prototype, "current", void 0);
class CartV1InfoDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4y(this, "price", void 0), _define_property$4y(this, "bonuses", void 0);
    }
}
_ts_decorate$4y([
    dto({
        object: {
            type: CartV1InfoPriceDTO
        }
    }),
    _ts_metadata$4y("design:type", typeof CartV1InfoPriceDTO === "undefined" ? Object : CartV1InfoPriceDTO)
], CartV1InfoDTO.prototype, "price", void 0);
_ts_decorate$4y([
    dto({
        object: {
            type: CartV1InfoBonesesDTO
        }
    }),
    _ts_metadata$4y("design:type", typeof CartV1InfoBonesesDTO === "undefined" ? Object : CartV1InfoBonesesDTO)
], CartV1InfoDTO.prototype, "bonuses", void 0);
class CartV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4y(this, "products", void 0), _define_property$4y(this, "lastValidatedHash", void 0), _define_property$4y(this, "info", void 0), _define_property$4y(this, "applyBonuses", void 0);
    }
}
_ts_decorate$4y([
    dto({
        array: true,
        object: {
            type: CartV1ProductDTO
        }
    }),
    _ts_metadata$4y("design:type", Array)
], CartV1DTO.prototype, "products", void 0);
_ts_decorate$4y([
    dto({
        string: true
    }),
    _ts_metadata$4y("design:type", String)
], CartV1DTO.prototype, "lastValidatedHash", void 0);
_ts_decorate$4y([
    dto({
        object: {
            type: CartV1InfoDTO
        }
    }),
    _ts_metadata$4y("design:type", typeof CartV1InfoDTO === "undefined" ? Object : CartV1InfoDTO)
], CartV1DTO.prototype, "info", void 0);
_ts_decorate$4y([
    dto({
        boolean: true
    }),
    _ts_metadata$4y("design:type", Boolean)
], CartV1DTO.prototype, "applyBonuses", void 0);

function _define_property$4x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4x(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4x(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV1SingleAddProductAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4x(this, "id", void 0), _define_property$4x(this, "value", void 0);
    }
}
_ts_decorate$4x([
    dto({
        string: true
    }),
    _ts_metadata$4x("design:type", String)
], CartV1SingleAddProductAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$4x([
    dto({
        string: true
    }),
    _ts_metadata$4x("design:type", String)
], CartV1SingleAddProductAttributeRequestDTO.prototype, "value", void 0);
class CartV1SingleAddProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4x(this, "originalProductId", void 0), _define_property$4x(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$4x([
    dto({
        string: true
    }),
    _ts_metadata$4x("design:type", String)
], CartV1SingleAddProductRequestDTO.prototype, "originalProductId", void 0);
_ts_decorate$4x([
    dto({
        array: true,
        object: {
            type: CartV1SingleAddProductAttributeRequestDTO
        }
    }),
    _ts_metadata$4x("design:type", Array)
], CartV1SingleAddProductRequestDTO.prototype, "requiredAttributes", void 0);

function _define_property$4w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4w(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4w(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV1SingleRemoveProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4w(this, "id", void 0);
    }
}
_ts_decorate$4w([
    dto({
        string: true
    }),
    _ts_metadata$4w("design:type", String)
], CartV1SingleRemoveProductRequestDTO.prototype, "id", void 0);

function _define_property$4v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4v(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4v(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV1SingleUpdateProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4v(this, "id", void 0), _define_property$4v(this, "applied", void 0), _define_property$4v(this, "count", void 0);
    }
}
_ts_decorate$4v([
    dto({
        string: true
    }),
    _ts_metadata$4v("design:type", String)
], CartV1SingleUpdateProductRequestDTO.prototype, "id", void 0);
_ts_decorate$4v([
    dto({
        boolean: true
    }),
    _ts_metadata$4v("design:type", Boolean)
], CartV1SingleUpdateProductRequestDTO.prototype, "applied", void 0);
_ts_decorate$4v([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$4v("design:type", Number)
], CartV1SingleUpdateProductRequestDTO.prototype, "count", void 0);

function _define_property$4u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4u(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4u(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV1SingleUpdateRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4u(this, "applyBonuses", void 0);
    }
}
_ts_decorate$4u([
    dto({
        boolean: true
    }),
    _ts_metadata$4u("design:type", Boolean)
], CartV1SingleUpdateRequestDTO.prototype, "applyBonuses", void 0);

function _define_property$4t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4t(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4t(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateV1CartProductRequiredAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4t(this, "id", void 0), _define_property$4t(this, "value", void 0);
    }
}
_ts_decorate$4t([
    dto({
        string: true
    }),
    _ts_metadata$4t("design:type", String)
], UpdateV1CartProductRequiredAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$4t([
    dto({
        string: true
    }),
    _ts_metadata$4t("design:type", String)
], UpdateV1CartProductRequiredAttributeRequestDTO.prototype, "value", void 0);
class UpdateV1CartProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4t(this, "id", void 0), _define_property$4t(this, "originalProductId", void 0), _define_property$4t(this, "count", void 0), _define_property$4t(this, "applied", void 0), _define_property$4t(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$4t([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4t("design:type", Object)
], UpdateV1CartProductRequestDTO.prototype, "id", void 0);
_ts_decorate$4t([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4t("design:type", Object)
], UpdateV1CartProductRequestDTO.prototype, "originalProductId", void 0);
_ts_decorate$4t([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$4t("design:type", Number)
], UpdateV1CartProductRequestDTO.prototype, "count", void 0);
_ts_decorate$4t([
    dto({
        boolean: true
    }),
    _ts_metadata$4t("design:type", Boolean)
], UpdateV1CartProductRequestDTO.prototype, "applied", void 0);
_ts_decorate$4t([
    dto({
        array: true,
        object: {
            type: UpdateV1CartProductRequiredAttributeRequestDTO
        }
    }),
    _ts_metadata$4t("design:type", Array)
], UpdateV1CartProductRequestDTO.prototype, "requiredAttributes", void 0);

function _define_property$4s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4s(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4s(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateV1CartRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4s(this, "products", void 0), _define_property$4s(this, "applyBonuses", void 0);
    }
}
_ts_decorate$4s([
    dto({
        array: true,
        object: {
            type: UpdateV1CartProductRequestDTO
        }
    }),
    _ts_metadata$4s("design:type", Array)
], UpdateV1CartRequestDTO.prototype, "products", void 0);
_ts_decorate$4s([
    dto({
        boolean: true
    }),
    _ts_metadata$4s("design:type", Boolean)
], UpdateV1CartRequestDTO.prototype, "applyBonuses", void 0);

function _define_property$4r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4r(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4r(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2ConfigurationDeliveryCourierCitiesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4r(this, "deliveryTypeId", void 0), _define_property$4r(this, "search", void 0);
    }
}
_ts_decorate$4r([
    dto({
        string: true
    }),
    _ts_metadata$4r("design:type", String)
], CartV2ConfigurationDeliveryCourierCitiesRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$4r([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4r("design:type", Object)
], CartV2ConfigurationDeliveryCourierCitiesRequestDTO.prototype, "search", void 0);

function _define_property$4q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartConfigurationDeliveryCourierCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4q(this, "id", void 0), _define_property$4q(this, "name", void 0);
    }
}
_ts_decorate$4q([
    dto({
        string: true
    }),
    _ts_metadata$4q("design:type", String)
], CartConfigurationDeliveryCourierCityResponseDTO.prototype, "id", void 0);
_ts_decorate$4q([
    dto({
        string: true
    }),
    _ts_metadata$4q("design:type", String)
], CartConfigurationDeliveryCourierCityResponseDTO.prototype, "name", void 0);
class CartV2ConfigurationDeliveryCourierCitiesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4q(this, "cities", void 0);
    }
}
_ts_decorate$4q([
    dto({
        array: true,
        object: {
            type: CartConfigurationDeliveryCourierCityResponseDTO
        }
    }),
    _ts_metadata$4q("design:type", Array)
], CartV2ConfigurationDeliveryCourierCitiesResponseDTO.prototype, "cities", void 0);

function _define_property$4p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4p(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4p(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartConfigurationDeliveryPickupShopCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4p(this, "latitude", void 0), _define_property$4p(this, "longitude", void 0);
    }
}
_ts_decorate$4p([
    dto({
        number: true
    }),
    _ts_metadata$4p("design:type", Number)
], CartConfigurationDeliveryPickupShopCoordinatesResponseDTO.prototype, "latitude", void 0);
_ts_decorate$4p([
    dto({
        number: true
    }),
    _ts_metadata$4p("design:type", Number)
], CartConfigurationDeliveryPickupShopCoordinatesResponseDTO.prototype, "longitude", void 0);
class CartV2ConfigurationDeliveryPickupShopResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4p(this, "id", void 0), _define_property$4p(this, "cityName", void 0), _define_property$4p(this, "address", void 0), _define_property$4p(this, "coordinates", void 0);
    }
}
_ts_decorate$4p([
    dto({
        string: true
    }),
    _ts_metadata$4p("design:type", String)
], CartV2ConfigurationDeliveryPickupShopResponseDTO.prototype, "id", void 0);
_ts_decorate$4p([
    dto({
        string: true
    }),
    _ts_metadata$4p("design:type", String)
], CartV2ConfigurationDeliveryPickupShopResponseDTO.prototype, "cityName", void 0);
_ts_decorate$4p([
    dto({
        string: true
    }),
    _ts_metadata$4p("design:type", String)
], CartV2ConfigurationDeliveryPickupShopResponseDTO.prototype, "address", void 0);
_ts_decorate$4p([
    dto({
        object: {
            type: CartConfigurationDeliveryPickupShopCoordinatesResponseDTO
        },
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$4p("design:type", Object)
], CartV2ConfigurationDeliveryPickupShopResponseDTO.prototype, "coordinates", void 0);

function _define_property$4o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4o(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4o(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CartV2ConfigurationDeliveryPickupPointAddressesQueryType = /*#__PURE__*/ function(CartV2ConfigurationDeliveryPickupPointAddressesQueryType) {
    CartV2ConfigurationDeliveryPickupPointAddressesQueryType["SEARCH"] = "search";
    CartV2ConfigurationDeliveryPickupPointAddressesQueryType["COORDINATES"] = "coordinates";
    return CartV2ConfigurationDeliveryPickupPointAddressesQueryType;
}({});
class CartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4o(this, "type", void 0), _define_property$4o(this, "search", void 0);
    }
}
_ts_decorate$4o([
    dto({
        equals: "search"
    }),
    _ts_metadata$4o("design:type", typeof CartV2ConfigurationDeliveryPickupPointAddressesQueryType === "undefined" || "string" === "undefined" ? Object : "search")
], CartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO.prototype, "type", void 0);
_ts_decorate$4o([
    dto({
        string: true
    }),
    _ts_metadata$4o("design:type", String)
], CartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO.prototype, "search", void 0);
class CartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4o(this, "type", void 0), _define_property$4o(this, "latitude", void 0), _define_property$4o(this, "longitude", void 0);
    }
}
_ts_decorate$4o([
    dto({
        equals: "coordinates"
    }),
    _ts_metadata$4o("design:type", typeof CartV2ConfigurationDeliveryPickupPointAddressesQueryType === "undefined" || "string" === "undefined" ? Object : "coordinates")
], CartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO.prototype, "type", void 0);
_ts_decorate$4o([
    dto({
        number: true
    }),
    _ts_metadata$4o("design:type", Number)
], CartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO.prototype, "latitude", void 0);
_ts_decorate$4o([
    dto({
        number: true
    }),
    _ts_metadata$4o("design:type", Number)
], CartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO.prototype, "longitude", void 0);
class CartV2ConfigurationDeliveryPickupPointAddressesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4o(this, "deliveryTypeId", void 0), _define_property$4o(this, "cityId", void 0), _define_property$4o(this, "query", void 0);
    }
}
_ts_decorate$4o([
    dto({
        string: true
    }),
    _ts_metadata$4o("design:type", String)
], CartV2ConfigurationDeliveryPickupPointAddressesRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$4o([
    dto({
        string: true
    }),
    _ts_metadata$4o("design:type", String)
], CartV2ConfigurationDeliveryPickupPointAddressesRequestDTO.prototype, "cityId", void 0);
_ts_decorate$4o([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "search",
                        value: CartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO
                    },
                    {
                        name: "coordinates",
                        value: CartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4o("design:type", Object)
], CartV2ConfigurationDeliveryPickupPointAddressesRequestDTO.prototype, "query", void 0);

function _define_property$4n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4n(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4n(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteMerchantBannerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4n(this, "id", void 0);
    }
}
_ts_decorate$4n([
    dto({
        string: true
    }),
    _ts_metadata$4n("design:type", String)
], IntegrationDeleteMerchantBannerDTO.prototype, "id", void 0);

function _define_property$4m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4m(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4m(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantBannerActionProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4m(this, "type", void 0), _define_property$4m(this, "productId", void 0);
    }
}
_ts_decorate$4m([
    dto({
        equals: BannerActionType.PRODUCT
    }),
    _ts_metadata$4m("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCT === "undefined" ? Object : BannerActionType.PRODUCT)
], IntegrationInitializeMerchantBannerActionProductDTO.prototype, "type", void 0);
_ts_decorate$4m([
    dto({
        string: true
    }),
    _ts_metadata$4m("design:type", String)
], IntegrationInitializeMerchantBannerActionProductDTO.prototype, "productId", void 0);
class IntegrationInitializeMerchantBannerActionProductsCatalogDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4m(this, "type", void 0), _define_property$4m(this, "description", void 0), _define_property$4m(this, "productIds", void 0);
    }
}
_ts_decorate$4m([
    dto({
        equals: BannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$4m("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : BannerActionType.PRODUCTS_CATALOG)
], IntegrationInitializeMerchantBannerActionProductsCatalogDTO.prototype, "type", void 0);
_ts_decorate$4m([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerActionProductsCatalogDTO.prototype, "description", void 0);
_ts_decorate$4m([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$4m("design:type", Array)
], IntegrationInitializeMerchantBannerActionProductsCatalogDTO.prototype, "productIds", void 0);
class IntegrationInitializeMerchantBannerActionHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4m(this, "type", void 0);
    }
}
_ts_decorate$4m([
    dto({
        equals: BannerActionType.HTML
    }),
    _ts_metadata$4m("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.HTML === "undefined" ? Object : BannerActionType.HTML)
], IntegrationInitializeMerchantBannerActionHtmlDTO.prototype, "type", void 0);
class IntegrationInitializeMerchantBannerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4m(this, "id", void 0), _define_property$4m(this, "place", void 0), _define_property$4m(this, "index", void 0), _define_property$4m(this, "name", void 0), _define_property$4m(this, "startDate", void 0), _define_property$4m(this, "endDate", void 0), _define_property$4m(this, "action", void 0);
    }
}
_ts_decorate$4m([
    dto({
        string: true
    }),
    _ts_metadata$4m("design:type", String)
], IntegrationInitializeMerchantBannerDTO.prototype, "id", void 0);
_ts_decorate$4m([
    dto({
        optional: true,
        enum: BannerGroupPlace
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerDTO.prototype, "place", void 0);
_ts_decorate$4m([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0,
            asString: true
        }
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerDTO.prototype, "index", void 0);
_ts_decorate$4m([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerDTO.prototype, "name", void 0);
_ts_decorate$4m([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerDTO.prototype, "startDate", void 0);
_ts_decorate$4m([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerDTO.prototype, "endDate", void 0);
_ts_decorate$4m([
    dto({
        name: "action__json",
        optional: true,
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: BannerActionType.PRODUCT,
                        value: IntegrationInitializeMerchantBannerActionProductDTO
                    },
                    {
                        name: BannerActionType.PRODUCTS_CATALOG,
                        value: IntegrationInitializeMerchantBannerActionProductsCatalogDTO
                    },
                    {
                        name: BannerActionType.HTML,
                        value: IntegrationInitializeMerchantBannerActionHtmlDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4m("design:type", Object)
], IntegrationInitializeMerchantBannerDTO.prototype, "action", void 0);

function _define_property$4l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4l(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4l(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantBannerImageDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4l(this, "id", void 0);
    }
}
_ts_decorate$4l([
    dto({
        string: true
    }),
    _ts_metadata$4l("design:type", String)
], IntegrationInitializeMerchantBannerImageDTO.prototype, "id", void 0);

function _define_property$4k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4k(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4k(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantBannerMediaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4k(this, "id", void 0), _define_property$4k(this, "type", void 0);
    }
}
_ts_decorate$4k([
    dto({
        string: true
    }),
    _ts_metadata$4k("design:type", String)
], IntegrationInitializeMerchantBannerMediaDTO.prototype, "id", void 0);
_ts_decorate$4k([
    dto({
        enum: BannerMediaType
    }),
    _ts_metadata$4k("design:type", typeof BannerMediaType === "undefined" ? Object : BannerMediaType)
], IntegrationInitializeMerchantBannerMediaDTO.prototype, "type", void 0);

function _define_property$4j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4j(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4j(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantBannerActionProductV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4j(this, "type", void 0), _define_property$4j(this, "productId", void 0);
    }
}
_ts_decorate$4j([
    dto({
        equals: BannerActionType.PRODUCT
    }),
    _ts_metadata$4j("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCT === "undefined" ? Object : BannerActionType.PRODUCT)
], IntegrationInitializeMerchantBannerActionProductV1DTO.prototype, "type", void 0);
_ts_decorate$4j([
    dto({
        string: true
    }),
    _ts_metadata$4j("design:type", String)
], IntegrationInitializeMerchantBannerActionProductV1DTO.prototype, "productId", void 0);
class IntegrationInitializeMerchantBannerActionProductsCatalogV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4j(this, "type", void 0), _define_property$4j(this, "description", void 0), _define_property$4j(this, "productIds", void 0);
    }
}
_ts_decorate$4j([
    dto({
        equals: BannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$4j("design:type", typeof BannerActionType === "undefined" || typeof BannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : BannerActionType.PRODUCTS_CATALOG)
], IntegrationInitializeMerchantBannerActionProductsCatalogV1DTO.prototype, "type", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerActionProductsCatalogV1DTO.prototype, "description", void 0);
_ts_decorate$4j([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$4j("design:type", Array)
], IntegrationInitializeMerchantBannerActionProductsCatalogV1DTO.prototype, "productIds", void 0);
class IntegrationInitializeMerchantBannerV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4j(this, "id", void 0), _define_property$4j(this, "place", void 0), _define_property$4j(this, "index", void 0), _define_property$4j(this, "name", void 0), _define_property$4j(this, "startDate", void 0), _define_property$4j(this, "endDate", void 0), _define_property$4j(this, "action", void 0);
    }
}
_ts_decorate$4j([
    dto({
        string: true
    }),
    _ts_metadata$4j("design:type", String)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "id", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        enum: BannerGroupPlace
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "place", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "index", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "name", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "startDate", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "endDate", void 0);
_ts_decorate$4j([
    dto({
        optional: true,
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: BannerActionType.PRODUCT,
                        value: IntegrationInitializeMerchantBannerActionProductV1DTO
                    },
                    {
                        name: BannerActionType.PRODUCTS_CATALOG,
                        value: IntegrationInitializeMerchantBannerActionProductsCatalogV1DTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4j("design:type", Object)
], IntegrationInitializeMerchantBannerV1DTO.prototype, "action", void 0);

function _define_property$4i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4i(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4i(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryCourierCitiesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4i(this, "deliveryTypeId", void 0), _define_property$4i(this, "search", void 0);
    }
}
_ts_decorate$4i([
    dto({
        string: true
    }),
    _ts_metadata$4i("design:type", String)
], IntegrationCartConfigurationDeliveryCourierCitiesRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$4i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4i("design:type", Object)
], IntegrationCartConfigurationDeliveryCourierCitiesRequestDTO.prototype, "search", void 0);

function _define_property$4h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4h(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4h(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryCourierCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4h(this, "id", void 0), _define_property$4h(this, "name", void 0);
    }
}
_ts_decorate$4h([
    dto({
        string: true
    }),
    _ts_metadata$4h("design:type", String)
], IntegrationCartConfigurationDeliveryCourierCityResponseDTO.prototype, "id", void 0);
_ts_decorate$4h([
    dto({
        string: true
    }),
    _ts_metadata$4h("design:type", String)
], IntegrationCartConfigurationDeliveryCourierCityResponseDTO.prototype, "name", void 0);
class IntegrationCartConfigurationDeliveryCourierCitiesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4h(this, "cities", void 0);
    }
}
_ts_decorate$4h([
    dto({
        array: true,
        object: {
            type: IntegrationCartConfigurationDeliveryCourierCityResponseDTO
        }
    }),
    _ts_metadata$4h("design:type", Array)
], IntegrationCartConfigurationDeliveryCourierCitiesResponseDTO.prototype, "cities", void 0);

function _define_property$4g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4g(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4g(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4g(this, "type", void 0), _define_property$4g(this, "search", void 0);
    }
}
_ts_decorate$4g([
    dto({
        equals: CartV2ConfigurationDeliveryPickupPointAddressesQueryType.SEARCH
    }),
    _ts_metadata$4g("design:type", typeof CartV2ConfigurationDeliveryPickupPointAddressesQueryType === "undefined" || typeof CartV2ConfigurationDeliveryPickupPointAddressesQueryType.SEARCH === "undefined" ? Object : CartV2ConfigurationDeliveryPickupPointAddressesQueryType.SEARCH)
], IntegrationCartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO.prototype, "type", void 0);
_ts_decorate$4g([
    dto({
        string: true
    }),
    _ts_metadata$4g("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO.prototype, "search", void 0);
class IntegrationCartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4g(this, "type", void 0), _define_property$4g(this, "latitude", void 0), _define_property$4g(this, "longitude", void 0);
    }
}
_ts_decorate$4g([
    dto({
        equals: CartV2ConfigurationDeliveryPickupPointAddressesQueryType.COORDINATES
    }),
    _ts_metadata$4g("design:type", typeof CartV2ConfigurationDeliveryPickupPointAddressesQueryType === "undefined" || typeof CartV2ConfigurationDeliveryPickupPointAddressesQueryType.COORDINATES === "undefined" ? Object : CartV2ConfigurationDeliveryPickupPointAddressesQueryType.COORDINATES)
], IntegrationCartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO.prototype, "type", void 0);
_ts_decorate$4g([
    dto({
        number: true
    }),
    _ts_metadata$4g("design:type", Number)
], IntegrationCartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO.prototype, "latitude", void 0);
_ts_decorate$4g([
    dto({
        number: true
    }),
    _ts_metadata$4g("design:type", Number)
], IntegrationCartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO.prototype, "longitude", void 0);
class IntegrationCartConfigurationDeliveryPickupPointAddressesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4g(this, "deliveryTypeId", void 0), _define_property$4g(this, "cityId", void 0), _define_property$4g(this, "query", void 0);
    }
}
_ts_decorate$4g([
    dto({
        string: true
    }),
    _ts_metadata$4g("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointAddressesRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$4g([
    dto({
        string: true
    }),
    _ts_metadata$4g("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointAddressesRequestDTO.prototype, "cityId", void 0);
_ts_decorate$4g([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: CartV2ConfigurationDeliveryPickupPointAddressesQueryType.SEARCH,
                        value: IntegrationCartConfigurationDeliveryPickupPointAddressesQuerySearchRequestDTO
                    },
                    {
                        name: CartV2ConfigurationDeliveryPickupPointAddressesQueryType.COORDINATES,
                        value: IntegrationCartConfigurationDeliveryPickupPointAddressesQueryCoordinatesRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4g("design:type", Object)
], IntegrationCartConfigurationDeliveryPickupPointAddressesRequestDTO.prototype, "query", void 0);

var IntegrationCartConfigurationDeliveryPickupPointAddressesStatus = /*#__PURE__*/ function(IntegrationCartConfigurationDeliveryPickupPointAddressesStatus) {
    IntegrationCartConfigurationDeliveryPickupPointAddressesStatus["SUCCESS"] = "success";
    IntegrationCartConfigurationDeliveryPickupPointAddressesStatus["ERROR"] = "error";
    return IntegrationCartConfigurationDeliveryPickupPointAddressesStatus;
}({});

function _define_property$4f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4f(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4f(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryPickupPointAddressesErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4f(this, "status", void 0), _define_property$4f(this, "message", void 0);
    }
}
_ts_decorate$4f([
    dto({
        equals: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR
    }),
    _ts_metadata$4f("design:type", typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus === "undefined" || typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR === "undefined" ? Object : IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR)
], IntegrationCartConfigurationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$4f([
    dto({
        string: true
    }),
    _ts_metadata$4f("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "message", void 0);

function _define_property$4e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4e(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4e(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4e(this, "latitude", void 0), _define_property$4e(this, "longitude", void 0);
    }
}
_ts_decorate$4e([
    dto({
        number: true
    }),
    _ts_metadata$4e("design:type", Number)
], IntegrationCartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO.prototype, "latitude", void 0);
_ts_decorate$4e([
    dto({
        number: true
    }),
    _ts_metadata$4e("design:type", Number)
], IntegrationCartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO.prototype, "longitude", void 0);
class IntegrationCartConfigurationDeliveryPickupPointAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4e(this, "id", void 0), _define_property$4e(this, "name", void 0), _define_property$4e(this, "coordinates", void 0);
    }
}
_ts_decorate$4e([
    dto({
        string: true
    }),
    _ts_metadata$4e("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointAddressResponseDTO.prototype, "id", void 0);
_ts_decorate$4e([
    dto({
        string: true
    }),
    _ts_metadata$4e("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointAddressResponseDTO.prototype, "name", void 0);
_ts_decorate$4e([
    dto({
        object: {
            type: IntegrationCartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO
        }
    }),
    _ts_metadata$4e("design:type", typeof IntegrationCartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO === "undefined" ? Object : IntegrationCartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO)
], IntegrationCartConfigurationDeliveryPickupPointAddressResponseDTO.prototype, "coordinates", void 0);
class IntegrationCartConfigurationDeliveryPickupPointAddressesSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4e(this, "status", void 0), _define_property$4e(this, "addresses", void 0);
    }
}
_ts_decorate$4e([
    dto({
        equals: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS
    }),
    _ts_metadata$4e("design:type", typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus === "undefined" || typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS === "undefined" ? Object : IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS)
], IntegrationCartConfigurationDeliveryPickupPointAddressesSuccessResponseDTO.prototype, "status", void 0);
_ts_decorate$4e([
    dto({
        array: true,
        object: {
            type: IntegrationCartConfigurationDeliveryPickupPointAddressResponseDTO
        }
    }),
    _ts_metadata$4e("design:type", Array)
], IntegrationCartConfigurationDeliveryPickupPointAddressesSuccessResponseDTO.prototype, "addresses", void 0);

function _define_property$4d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4d(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4d(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryPickupPointAddressesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4d(this, "info", void 0);
    }
}
_ts_decorate$4d([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS,
                        value: IntegrationCartConfigurationDeliveryPickupPointAddressesSuccessResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR,
                        value: IntegrationCartConfigurationDeliveryPickupPointAddressesErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$4d("design:type", Object)
], IntegrationCartConfigurationDeliveryPickupPointAddressesResponseDTO.prototype, "info", void 0);

function _define_property$4c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4c(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4c(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryPickupPointCitiesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4c(this, "deliveryTypeId", void 0), _define_property$4c(this, "search", void 0);
    }
}
_ts_decorate$4c([
    dto({
        string: true
    }),
    _ts_metadata$4c("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointCitiesRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$4c([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$4c("design:type", Object)
], IntegrationCartConfigurationDeliveryPickupPointCitiesRequestDTO.prototype, "search", void 0);

function _define_property$4b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4b(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartConfigurationDeliveryPickupPointCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4b(this, "id", void 0), _define_property$4b(this, "name", void 0);
    }
}
_ts_decorate$4b([
    dto({
        string: true
    }),
    _ts_metadata$4b("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointCityResponseDTO.prototype, "id", void 0);
_ts_decorate$4b([
    dto({
        string: true
    }),
    _ts_metadata$4b("design:type", String)
], IntegrationCartConfigurationDeliveryPickupPointCityResponseDTO.prototype, "name", void 0);
class IntegrationCartConfigurationDeliveryPickupPointCitiesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4b(this, "cities", void 0);
    }
}
_ts_decorate$4b([
    dto({
        array: true,
        object: {
            type: IntegrationCartConfigurationDeliveryPickupPointCityResponseDTO
        }
    }),
    _ts_metadata$4b("design:type", Array)
], IntegrationCartConfigurationDeliveryPickupPointCitiesResponseDTO.prototype, "cities", void 0);

function _define_property$4a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4a(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4a(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationCartConfigurationDeliveryType = /*#__PURE__*/ function(IntegrationCartConfigurationDeliveryType) {
    IntegrationCartConfigurationDeliveryType["PICKUP"] = "pickup";
    IntegrationCartConfigurationDeliveryType["COURIER"] = "courier";
    IntegrationCartConfigurationDeliveryType["PICKUP_POINT"] = "pickup_point";
    return IntegrationCartConfigurationDeliveryType;
}({});
class IntegrationCartConfigurationDeliveryTypeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4a(this, "type", void 0), _define_property$4a(this, "name", void 0), _define_property$4a(this, "id", void 0);
    }
}
_ts_decorate$4a([
    dto({
        enum: IntegrationCartConfigurationDeliveryType
    }),
    _ts_metadata$4a("design:type", String)
], IntegrationCartConfigurationDeliveryTypeResponseDTO.prototype, "type", void 0);
_ts_decorate$4a([
    dto({
        string: true
    }),
    _ts_metadata$4a("design:type", String)
], IntegrationCartConfigurationDeliveryTypeResponseDTO.prototype, "name", void 0);
_ts_decorate$4a([
    dto({
        string: true
    }),
    _ts_metadata$4a("design:type", String)
], IntegrationCartConfigurationDeliveryTypeResponseDTO.prototype, "id", void 0);
class IntegrationCartConfigurationDeliveryTypesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4a(this, "deliveryTypes", void 0);
    }
}
_ts_decorate$4a([
    dto({
        array: true,
        object: {
            type: IntegrationCartConfigurationDeliveryTypeResponseDTO
        }
    }),
    _ts_metadata$4a("design:type", Array)
], IntegrationCartConfigurationDeliveryTypesResponseDTO.prototype, "deliveryTypes", void 0);

function _define_property$49(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$49(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$49(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartGetConfigurationPaymentTypesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$49(this, "deliveryType", void 0), _define_property$49(this, "deliveryTypeId", void 0);
    }
}
_ts_decorate$49([
    dto({
        enum: IntegrationCartConfigurationDeliveryType
    }),
    _ts_metadata$49("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" ? Object : IntegrationCartConfigurationDeliveryType)
], IntegrationCartGetConfigurationPaymentTypesRequestDTO.prototype, "deliveryType", void 0);
_ts_decorate$49([
    dto({
        string: true
    }),
    _ts_metadata$49("design:type", String)
], IntegrationCartGetConfigurationPaymentTypesRequestDTO.prototype, "deliveryTypeId", void 0);

var IntegrationCartConfigurationPaymentType = /*#__PURE__*/ function(IntegrationCartConfigurationPaymentType) {
    IntegrationCartConfigurationPaymentType["ONLINE"] = "online";
    IntegrationCartConfigurationPaymentType["CASH"] = "cash";
    return IntegrationCartConfigurationPaymentType;
}({});

function _define_property$48(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$48(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$48(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCartGetConfigurationPaymentTypeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$48(this, "type", void 0), _define_property$48(this, "name", void 0), _define_property$48(this, "id", void 0);
    }
}
_ts_decorate$48([
    dto({
        enum: IntegrationCartConfigurationPaymentType
    }),
    _ts_metadata$48("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" ? Object : IntegrationCartConfigurationPaymentType)
], IntegrationCartGetConfigurationPaymentTypeResponseDTO.prototype, "type", void 0);
_ts_decorate$48([
    dto({
        string: true
    }),
    _ts_metadata$48("design:type", String)
], IntegrationCartGetConfigurationPaymentTypeResponseDTO.prototype, "name", void 0);
_ts_decorate$48([
    dto({
        string: true
    }),
    _ts_metadata$48("design:type", String)
], IntegrationCartGetConfigurationPaymentTypeResponseDTO.prototype, "id", void 0);
class IntegrationCartGetConfigurationPaymentTypesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$48(this, "paymentTypes", void 0);
    }
}
_ts_decorate$48([
    dto({
        array: true,
        object: {
            type: IntegrationCartGetConfigurationPaymentTypeResponseDTO
        }
    }),
    _ts_metadata$48("design:type", Array)
], IntegrationCartGetConfigurationPaymentTypesResponseDTO.prototype, "paymentTypes", void 0);

function _define_property$47(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$47(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$47(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let IntegrationValidateCartProductAttributeRequestDTO$1 = class IntegrationValidateCartProductAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$47(this, "id", void 0), _define_property$47(this, "value", void 0);
    }
};
_ts_decorate$47([
    dto({
        string: true
    }),
    _ts_metadata$47("design:type", String)
], IntegrationValidateCartProductAttributeRequestDTO$1.prototype, "id", void 0);
_ts_decorate$47([
    dto({
        string: true
    }),
    _ts_metadata$47("design:type", String)
], IntegrationValidateCartProductAttributeRequestDTO$1.prototype, "value", void 0);
class IntegrationValidateCartV2ProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$47(this, "id", void 0), _define_property$47(this, "backendId", void 0), _define_property$47(this, "count", void 0), _define_property$47(this, "attributes", void 0);
    }
}
_ts_decorate$47([
    dto({
        string: true
    }),
    _ts_metadata$47("design:type", String)
], IntegrationValidateCartV2ProductRequestDTO.prototype, "id", void 0);
_ts_decorate$47([
    dto({
        string: true
    }),
    _ts_metadata$47("design:type", String)
], IntegrationValidateCartV2ProductRequestDTO.prototype, "backendId", void 0);
_ts_decorate$47([
    dto({
        number: true
    }),
    _ts_metadata$47("design:type", Number)
], IntegrationValidateCartV2ProductRequestDTO.prototype, "count", void 0);
_ts_decorate$47([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartProductAttributeRequestDTO$1
        }
    }),
    _ts_metadata$47("design:type", Array)
], IntegrationValidateCartV2ProductRequestDTO.prototype, "attributes", void 0);

function _define_property$46(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$46(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$46(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let IntegrationValidateCartDeliveryCourierAddressRequestDTO$1 = class IntegrationValidateCartDeliveryCourierAddressRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$46(this, "street", void 0), _define_property$46(this, "houseNumber", void 0), _define_property$46(this, "appartmentNumber", void 0), _define_property$46(this, "comment", void 0);
    }
};
_ts_decorate$46([
    dto({
        string: true
    }),
    _ts_metadata$46("design:type", String)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO$1.prototype, "street", void 0);
_ts_decorate$46([
    dto({
        string: true
    }),
    _ts_metadata$46("design:type", String)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO$1.prototype, "houseNumber", void 0);
_ts_decorate$46([
    dto({
        string: true
    }),
    _ts_metadata$46("design:type", String)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO$1.prototype, "appartmentNumber", void 0);
_ts_decorate$46([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$46("design:type", Object)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO$1.prototype, "comment", void 0);
class IntegrationValidateCartV2DeliveryCourierRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$46(this, "type", void 0), _define_property$46(this, "deliveryTypeId", void 0), _define_property$46(this, "cityId", void 0), _define_property$46(this, "variantId", void 0), _define_property$46(this, "address", void 0);
    }
}
_ts_decorate$46([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$46("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], IntegrationValidateCartV2DeliveryCourierRequestDTO.prototype, "type", void 0);
_ts_decorate$46([
    dto({
        string: true
    }),
    _ts_metadata$46("design:type", String)
], IntegrationValidateCartV2DeliveryCourierRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$46([
    dto({
        string: true
    }),
    _ts_metadata$46("design:type", String)
], IntegrationValidateCartV2DeliveryCourierRequestDTO.prototype, "cityId", void 0);
_ts_decorate$46([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$46("design:type", Object)
], IntegrationValidateCartV2DeliveryCourierRequestDTO.prototype, "variantId", void 0);
_ts_decorate$46([
    dto({
        object: {
            type: IntegrationValidateCartDeliveryCourierAddressRequestDTO$1
        }
    }),
    _ts_metadata$46("design:type", typeof IntegrationValidateCartDeliveryCourierAddressRequestDTO$1 === "undefined" ? Object : IntegrationValidateCartDeliveryCourierAddressRequestDTO$1)
], IntegrationValidateCartV2DeliveryCourierRequestDTO.prototype, "address", void 0);

function _define_property$45(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$45(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$45(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2DeliveryPickupRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$45(this, "type", void 0), _define_property$45(this, "deliveryTypeId", void 0), _define_property$45(this, "shopId", void 0);
    }
}
_ts_decorate$45([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$45("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], IntegrationValidateCartV2DeliveryPickupRequestDTO.prototype, "type", void 0);
_ts_decorate$45([
    dto({
        string: true
    }),
    _ts_metadata$45("design:type", String)
], IntegrationValidateCartV2DeliveryPickupRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$45([
    dto({
        string: true
    }),
    _ts_metadata$45("design:type", String)
], IntegrationValidateCartV2DeliveryPickupRequestDTO.prototype, "shopId", void 0);

function _define_property$44(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$44(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$44(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2DeliveryPickupPointRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$44(this, "type", void 0), _define_property$44(this, "deliveryTypeId", void 0), _define_property$44(this, "cityId", void 0), _define_property$44(this, "variantId", void 0), _define_property$44(this, "addressId", void 0);
    }
}
_ts_decorate$44([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$44("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], IntegrationValidateCartV2DeliveryPickupPointRequestDTO.prototype, "type", void 0);
_ts_decorate$44([
    dto({
        string: true
    }),
    _ts_metadata$44("design:type", String)
], IntegrationValidateCartV2DeliveryPickupPointRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$44([
    dto({
        string: true
    }),
    _ts_metadata$44("design:type", String)
], IntegrationValidateCartV2DeliveryPickupPointRequestDTO.prototype, "cityId", void 0);
_ts_decorate$44([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$44("design:type", Object)
], IntegrationValidateCartV2DeliveryPickupPointRequestDTO.prototype, "variantId", void 0);
_ts_decorate$44([
    dto({
        string: true
    }),
    _ts_metadata$44("design:type", String)
], IntegrationValidateCartV2DeliveryPickupPointRequestDTO.prototype, "addressId", void 0);

function _define_property$43(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$43(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$43(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2PaymentRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$43(this, "type", void 0), _define_property$43(this, "paymentTypeId", void 0);
    }
}
_ts_decorate$43([
    dto({
        enum: IntegrationCartConfigurationPaymentType
    }),
    _ts_metadata$43("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" ? Object : IntegrationCartConfigurationPaymentType)
], IntegrationValidateCartV2PaymentRequestDTO.prototype, "type", void 0);
_ts_decorate$43([
    dto({
        string: true
    }),
    _ts_metadata$43("design:type", String)
], IntegrationValidateCartV2PaymentRequestDTO.prototype, "paymentTypeId", void 0);

function _define_property$42(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$42(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$42(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$42(this, "userId", void 0), _define_property$42(this, "applyBonuses", void 0), _define_property$42(this, "products", void 0), _define_property$42(this, "delivery", void 0), _define_property$42(this, "payment", void 0);
    }
}
_ts_decorate$42([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$42("design:type", Object)
], IntegrationValidateCartV2RequestDTO.prototype, "userId", void 0);
_ts_decorate$42([
    dto({
        boolean: true
    }),
    _ts_metadata$42("design:type", Boolean)
], IntegrationValidateCartV2RequestDTO.prototype, "applyBonuses", void 0);
_ts_decorate$42([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartV2ProductRequestDTO
        }
    }),
    _ts_metadata$42("design:type", Array)
], IntegrationValidateCartV2RequestDTO.prototype, "products", void 0);
_ts_decorate$42([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: IntegrationValidateCartV2DeliveryPickupRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: IntegrationValidateCartV2DeliveryCourierRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: IntegrationValidateCartV2DeliveryPickupPointRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$42("design:type", Object)
], IntegrationValidateCartV2RequestDTO.prototype, "delivery", void 0);
_ts_decorate$42([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: IntegrationValidateCartV2PaymentRequestDTO
        }
    }),
    _ts_metadata$42("design:type", Object)
], IntegrationValidateCartV2RequestDTO.prototype, "payment", void 0);

var IntegrationValidateCartV2InfoResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV2InfoResponseStatus) {
    IntegrationValidateCartV2InfoResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV2InfoResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV2InfoResponseStatus;
}({});

function _define_property$41(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$41(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$41(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2InfoBonusesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$41(this, "willUse", void 0), _define_property$41(this, "willAdd", void 0), _define_property$41(this, "current", void 0);
    }
}
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoBonusesResponseDTO.prototype, "willUse", void 0);
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoBonusesResponseDTO.prototype, "willAdd", void 0);
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoBonusesResponseDTO.prototype, "current", void 0);
class IntegrationValidateCartV2InfoCalculationResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$41(this, "name", void 0), _define_property$41(this, "description", void 0), _define_property$41(this, "value", void 0), _define_property$41(this, "measure", void 0);
    }
}
_ts_decorate$41([
    dto({
        string: true
    }),
    _ts_metadata$41("design:type", String)
], IntegrationValidateCartV2InfoCalculationResponseDTO.prototype, "name", void 0);
_ts_decorate$41([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$41("design:type", Object)
], IntegrationValidateCartV2InfoCalculationResponseDTO.prototype, "description", void 0);
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoCalculationResponseDTO.prototype, "value", void 0);
_ts_decorate$41([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$41("design:type", Object)
], IntegrationValidateCartV2InfoCalculationResponseDTO.prototype, "measure", void 0);
class IntegrationValidateCartV2InfoPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$41(this, "base", void 0), _define_property$41(this, "result", void 0), _define_property$41(this, "discount", void 0);
    }
}
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoPriceResponseDTO.prototype, "base", void 0);
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoPriceResponseDTO.prototype, "result", void 0);
_ts_decorate$41([
    dto({
        number: true
    }),
    _ts_metadata$41("design:type", Number)
], IntegrationValidateCartV2InfoPriceResponseDTO.prototype, "discount", void 0);
class IntegrationValidateCartV2InfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$41(this, "status", void 0), _define_property$41(this, "price", void 0), _define_property$41(this, "bonuses", void 0), _define_property$41(this, "calculation", void 0);
    }
}
_ts_decorate$41([
    dto({
        equals: IntegrationValidateCartV2InfoResponseStatus.SUCCESS
    }),
    _ts_metadata$41("design:type", typeof IntegrationValidateCartV2InfoResponseStatus === "undefined" || typeof IntegrationValidateCartV2InfoResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV2InfoResponseStatus.SUCCESS)
], IntegrationValidateCartV2InfoResponseDTO.prototype, "status", void 0);
_ts_decorate$41([
    dto({
        object: {
            type: IntegrationValidateCartV2InfoPriceResponseDTO
        }
    }),
    _ts_metadata$41("design:type", typeof IntegrationValidateCartV2InfoPriceResponseDTO === "undefined" ? Object : IntegrationValidateCartV2InfoPriceResponseDTO)
], IntegrationValidateCartV2InfoResponseDTO.prototype, "price", void 0);
_ts_decorate$41([
    dto({
        object: {
            type: IntegrationValidateCartV2InfoBonusesResponseDTO
        }
    }),
    _ts_metadata$41("design:type", typeof IntegrationValidateCartV2InfoBonusesResponseDTO === "undefined" ? Object : IntegrationValidateCartV2InfoBonusesResponseDTO)
], IntegrationValidateCartV2InfoResponseDTO.prototype, "bonuses", void 0);
_ts_decorate$41([
    dto({
        object: {
            type: IntegrationValidateCartV2InfoCalculationResponseDTO
        },
        array: true
    }),
    _ts_metadata$41("design:type", Array)
], IntegrationValidateCartV2InfoResponseDTO.prototype, "calculation", void 0);

function _define_property$40(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$40(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$40(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2InfoErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$40(this, "status", void 0), _define_property$40(this, "message", void 0);
    }
}
_ts_decorate$40([
    dto({
        equals: IntegrationValidateCartV2InfoResponseStatus.ERROR
    }),
    _ts_metadata$40("design:type", typeof IntegrationValidateCartV2InfoResponseStatus === "undefined" || typeof IntegrationValidateCartV2InfoResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV2InfoResponseStatus.ERROR)
], IntegrationValidateCartV2InfoErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$40([
    dto({
        string: true
    }),
    _ts_metadata$40("design:type", String)
], IntegrationValidateCartV2InfoErrorResponseDTO.prototype, "message", void 0);

var IntegrationValidateCartV2ProductResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV2ProductResponseStatus) {
    IntegrationValidateCartV2ProductResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV2ProductResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV2ProductResponseStatus;
}({});

function _define_property$3$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3$(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3$(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2ProductResponseBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3$(this, "id", void 0), _define_property$3$(this, "backendId", void 0), _define_property$3$(this, "maxCount", void 0);
    }
}
_ts_decorate$3$([
    dto({
        string: true
    }),
    _ts_metadata$3$("design:type", String)
], IntegrationValidateCartV2ProductResponseBaseDTO.prototype, "id", void 0);
_ts_decorate$3$([
    dto({
        string: true
    }),
    _ts_metadata$3$("design:type", String)
], IntegrationValidateCartV2ProductResponseBaseDTO.prototype, "backendId", void 0);
_ts_decorate$3$([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3$("design:type", Number)
], IntegrationValidateCartV2ProductResponseBaseDTO.prototype, "maxCount", void 0);

function _define_property$3_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3_(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3_(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2ProductResponseErrorDTO extends IntegrationValidateCartV2ProductResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$3_(this, "status", void 0), _define_property$3_(this, "message", void 0);
    }
}
_ts_decorate$3_([
    dto({
        equals: IntegrationValidateCartV2ProductResponseStatus.ERROR
    }),
    _ts_metadata$3_("design:type", typeof IntegrationValidateCartV2ProductResponseStatus === "undefined" || typeof IntegrationValidateCartV2ProductResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV2ProductResponseStatus.ERROR)
], IntegrationValidateCartV2ProductResponseErrorDTO.prototype, "status", void 0);
_ts_decorate$3_([
    dto({
        string: true
    }),
    _ts_metadata$3_("design:type", String)
], IntegrationValidateCartV2ProductResponseErrorDTO.prototype, "message", void 0);

function _define_property$3Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3Z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3Z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2ProductPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3Z(this, "base", void 0), _define_property$3Z(this, "result", void 0), _define_property$3Z(this, "discount", void 0);
    }
}
_ts_decorate$3Z([
    dto({
        number: true
    }),
    _ts_metadata$3Z("design:type", Number)
], IntegrationValidateCartV2ProductPriceResponseDTO.prototype, "base", void 0);
_ts_decorate$3Z([
    dto({
        number: true
    }),
    _ts_metadata$3Z("design:type", Number)
], IntegrationValidateCartV2ProductPriceResponseDTO.prototype, "result", void 0);
_ts_decorate$3Z([
    dto({
        number: true
    }),
    _ts_metadata$3Z("design:type", Number)
], IntegrationValidateCartV2ProductPriceResponseDTO.prototype, "discount", void 0);
// eslint-disable-next-line max-len
class IntegrationValidateCartV2ProductResponseSuccessDTO extends IntegrationValidateCartV2ProductResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$3Z(this, "status", void 0), _define_property$3Z(this, "price", void 0);
    }
}
_ts_decorate$3Z([
    dto({
        equals: IntegrationValidateCartV2ProductResponseStatus.SUCCESS
    }),
    _ts_metadata$3Z("design:type", typeof IntegrationValidateCartV2ProductResponseStatus === "undefined" || typeof IntegrationValidateCartV2ProductResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV2ProductResponseStatus.SUCCESS)
], IntegrationValidateCartV2ProductResponseSuccessDTO.prototype, "status", void 0);
_ts_decorate$3Z([
    dto({
        object: {
            type: IntegrationValidateCartV2ProductPriceResponseDTO
        }
    }),
    _ts_metadata$3Z("design:type", typeof IntegrationValidateCartV2ProductPriceResponseDTO === "undefined" ? Object : IntegrationValidateCartV2ProductPriceResponseDTO)
], IntegrationValidateCartV2ProductResponseSuccessDTO.prototype, "price", void 0);

var IntegrationValidateCartV2PaymentCalculationResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV2PaymentCalculationResponseStatus) {
    IntegrationValidateCartV2PaymentCalculationResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV2PaymentCalculationResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV2PaymentCalculationResponseStatus;
}({});

function _define_property$3Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3Y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3Y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2PaymentCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3Y(this, "status", void 0);
    }
}
_ts_decorate$3Y([
    dto({
        equals: IntegrationValidateCartV2PaymentCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$3Y("design:type", typeof IntegrationValidateCartV2PaymentCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV2PaymentCalculationResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV2PaymentCalculationResponseStatus.SUCCESS)
], IntegrationValidateCartV2PaymentCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$3X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3X(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3X(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2PaymentCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3X(this, "status", void 0), _define_property$3X(this, "message", void 0);
    }
}
_ts_decorate$3X([
    dto({
        equals: IntegrationValidateCartV2PaymentCalculationResponseStatus.ERROR
    }),
    _ts_metadata$3X("design:type", typeof IntegrationValidateCartV2PaymentCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV2PaymentCalculationResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV2PaymentCalculationResponseStatus.ERROR)
], IntegrationValidateCartV2PaymentCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$3X([
    dto({
        string: true
    }),
    _ts_metadata$3X("design:type", String)
], IntegrationValidateCartV2PaymentCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$3W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3W(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3W(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2PaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3W(this, "calculation", void 0);
    }
}
_ts_decorate$3W([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV2PaymentCalculationResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV2PaymentCalculationSuccessResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV2PaymentCalculationResponseStatus.ERROR,
                        value: IntegrationValidateCartV2PaymentCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3W("design:type", Object)
], IntegrationValidateCartV2PaymentResponseDTO.prototype, "calculation", void 0);

function _define_property$3V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3V(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3V(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2DeliveryInfoVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3V(this, "id", void 0), _define_property$3V(this, "name", void 0);
    }
}
_ts_decorate$3V([
    dto({
        string: true
    }),
    _ts_metadata$3V("design:type", String)
], IntegrationValidateCartV2DeliveryInfoVariantResponseDTO.prototype, "id", void 0);
_ts_decorate$3V([
    dto({
        string: true
    }),
    _ts_metadata$3V("design:type", String)
], IntegrationValidateCartV2DeliveryInfoVariantResponseDTO.prototype, "name", void 0);
class IntegrationValidateCartV2DeliveryInfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3V(this, "variants", void 0);
    }
}
_ts_decorate$3V([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartV2DeliveryInfoVariantResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3V("design:type", Object)
], IntegrationValidateCartV2DeliveryInfoResponseDTO.prototype, "variants", void 0);

var IntegrationValidateCartV2DeliveryCalculationResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV2DeliveryCalculationResponseStatus) {
    IntegrationValidateCartV2DeliveryCalculationResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV2DeliveryCalculationResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV2DeliveryCalculationResponseStatus;
}({});

function _define_property$3U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3U(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3U(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2DeliveryCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3U(this, "status", void 0), _define_property$3U(this, "message", void 0);
    }
}
_ts_decorate$3U([
    dto({
        equals: IntegrationValidateCartV2DeliveryCalculationResponseStatus.ERROR
    }),
    _ts_metadata$3U("design:type", typeof IntegrationValidateCartV2DeliveryCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV2DeliveryCalculationResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV2DeliveryCalculationResponseStatus.ERROR)
], IntegrationValidateCartV2DeliveryCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$3U([
    dto({
        string: true
    }),
    _ts_metadata$3U("design:type", String)
], IntegrationValidateCartV2DeliveryCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$3T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3T(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3T(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2DeliveryCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3T(this, "status", void 0);
    }
}
_ts_decorate$3T([
    dto({
        equals: IntegrationValidateCartV2DeliveryCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$3T("design:type", typeof IntegrationValidateCartV2DeliveryCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV2DeliveryCalculationResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV2DeliveryCalculationResponseStatus.SUCCESS)
], IntegrationValidateCartV2DeliveryCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$3S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3S(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3S(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2DeliveryResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3S(this, "calculation", void 0), _define_property$3S(this, "info", void 0);
    }
}
_ts_decorate$3S([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV2DeliveryCalculationResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV2DeliveryCalculationSuccessResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV2DeliveryCalculationResponseStatus.ERROR,
                        value: IntegrationValidateCartV2DeliveryCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3S("design:type", Object)
], IntegrationValidateCartV2DeliveryResponseDTO.prototype, "calculation", void 0);
_ts_decorate$3S([
    dto({
        object: {
            type: IntegrationValidateCartV2DeliveryInfoResponseDTO
        }
    }),
    _ts_metadata$3S("design:type", typeof IntegrationValidateCartV2DeliveryInfoResponseDTO === "undefined" ? Object : IntegrationValidateCartV2DeliveryInfoResponseDTO)
], IntegrationValidateCartV2DeliveryResponseDTO.prototype, "info", void 0);

function _define_property$3R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3R(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3R(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV2ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3R(this, "info", void 0), _define_property$3R(this, "delivery", void 0), _define_property$3R(this, "payment", void 0), _define_property$3R(this, "products", void 0);
    }
}
_ts_decorate$3R([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV2InfoResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV2InfoResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV2InfoResponseStatus.ERROR,
                        value: IntegrationValidateCartV2InfoErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3R("design:type", Object)
], IntegrationValidateCartV2ResponseDTO.prototype, "info", void 0);
_ts_decorate$3R([
    dto({
        object: {
            type: IntegrationValidateCartV2DeliveryResponseDTO
        }
    }),
    _ts_metadata$3R("design:type", typeof IntegrationValidateCartV2DeliveryResponseDTO === "undefined" ? Object : IntegrationValidateCartV2DeliveryResponseDTO)
], IntegrationValidateCartV2ResponseDTO.prototype, "delivery", void 0);
_ts_decorate$3R([
    dto({
        object: {
            type: IntegrationValidateCartV2PaymentResponseDTO
        }
    }),
    _ts_metadata$3R("design:type", typeof IntegrationValidateCartV2PaymentResponseDTO === "undefined" ? Object : IntegrationValidateCartV2PaymentResponseDTO)
], IntegrationValidateCartV2ResponseDTO.prototype, "payment", void 0);
_ts_decorate$3R([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV2ProductResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV2ProductResponseSuccessDTO
                    },
                    {
                        name: IntegrationValidateCartV2ProductResponseStatus.ERROR,
                        value: IntegrationValidateCartV2ProductResponseErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3R("design:type", Array)
], IntegrationValidateCartV2ResponseDTO.prototype, "products", void 0);

function _define_property$3Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3Q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3Q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartProductAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3Q(this, "id", void 0), _define_property$3Q(this, "value", void 0);
    }
}
_ts_decorate$3Q([
    dto({
        string: true
    }),
    _ts_metadata$3Q("design:type", String)
], IntegrationValidateCartProductAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$3Q([
    dto({
        string: true
    }),
    _ts_metadata$3Q("design:type", String)
], IntegrationValidateCartProductAttributeRequestDTO.prototype, "value", void 0);
class IntegrationValidateCartV3ProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3Q(this, "id", void 0), _define_property$3Q(this, "backendId", void 0), _define_property$3Q(this, "count", void 0), _define_property$3Q(this, "attributes", void 0);
    }
}
_ts_decorate$3Q([
    dto({
        string: true
    }),
    _ts_metadata$3Q("design:type", String)
], IntegrationValidateCartV3ProductRequestDTO.prototype, "id", void 0);
_ts_decorate$3Q([
    dto({
        string: true
    }),
    _ts_metadata$3Q("design:type", String)
], IntegrationValidateCartV3ProductRequestDTO.prototype, "backendId", void 0);
_ts_decorate$3Q([
    dto({
        number: true
    }),
    _ts_metadata$3Q("design:type", Number)
], IntegrationValidateCartV3ProductRequestDTO.prototype, "count", void 0);
_ts_decorate$3Q([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartProductAttributeRequestDTO
        }
    }),
    _ts_metadata$3Q("design:type", Array)
], IntegrationValidateCartV3ProductRequestDTO.prototype, "attributes", void 0);

function _define_property$3P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3P(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3P(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartDeliveryCourierAddressRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3P(this, "street", void 0), _define_property$3P(this, "houseNumber", void 0), _define_property$3P(this, "appartmentNumber", void 0), _define_property$3P(this, "comment", void 0);
    }
}
_ts_decorate$3P([
    dto({
        string: true
    }),
    _ts_metadata$3P("design:type", String)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO.prototype, "street", void 0);
_ts_decorate$3P([
    dto({
        string: true
    }),
    _ts_metadata$3P("design:type", String)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO.prototype, "houseNumber", void 0);
_ts_decorate$3P([
    dto({
        string: true
    }),
    _ts_metadata$3P("design:type", String)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO.prototype, "appartmentNumber", void 0);
_ts_decorate$3P([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3P("design:type", Object)
], IntegrationValidateCartDeliveryCourierAddressRequestDTO.prototype, "comment", void 0);
class IntegrationValidateCartV3DeliveryCourierRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3P(this, "type", void 0), _define_property$3P(this, "deliveryTypeId", void 0), _define_property$3P(this, "cityId", void 0), _define_property$3P(this, "variantId", void 0), _define_property$3P(this, "address", void 0);
    }
}
_ts_decorate$3P([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$3P("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], IntegrationValidateCartV3DeliveryCourierRequestDTO.prototype, "type", void 0);
_ts_decorate$3P([
    dto({
        string: true
    }),
    _ts_metadata$3P("design:type", String)
], IntegrationValidateCartV3DeliveryCourierRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$3P([
    dto({
        string: true
    }),
    _ts_metadata$3P("design:type", String)
], IntegrationValidateCartV3DeliveryCourierRequestDTO.prototype, "cityId", void 0);
_ts_decorate$3P([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3P("design:type", Object)
], IntegrationValidateCartV3DeliveryCourierRequestDTO.prototype, "variantId", void 0);
_ts_decorate$3P([
    dto({
        object: {
            type: IntegrationValidateCartDeliveryCourierAddressRequestDTO
        }
    }),
    _ts_metadata$3P("design:type", typeof IntegrationValidateCartDeliveryCourierAddressRequestDTO === "undefined" ? Object : IntegrationValidateCartDeliveryCourierAddressRequestDTO)
], IntegrationValidateCartV3DeliveryCourierRequestDTO.prototype, "address", void 0);

function _define_property$3O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3O(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3O(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3DeliveryPickupRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3O(this, "type", void 0), _define_property$3O(this, "deliveryTypeId", void 0), _define_property$3O(this, "shopId", void 0);
    }
}
_ts_decorate$3O([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$3O("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], IntegrationValidateCartV3DeliveryPickupRequestDTO.prototype, "type", void 0);
_ts_decorate$3O([
    dto({
        string: true
    }),
    _ts_metadata$3O("design:type", String)
], IntegrationValidateCartV3DeliveryPickupRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$3O([
    dto({
        string: true
    }),
    _ts_metadata$3O("design:type", String)
], IntegrationValidateCartV3DeliveryPickupRequestDTO.prototype, "shopId", void 0);

function _define_property$3N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3N(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3N(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3DeliveryPickupPointRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3N(this, "type", void 0), _define_property$3N(this, "deliveryTypeId", void 0), _define_property$3N(this, "cityId", void 0), _define_property$3N(this, "variantId", void 0), _define_property$3N(this, "addressId", void 0);
    }
}
_ts_decorate$3N([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$3N("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], IntegrationValidateCartV3DeliveryPickupPointRequestDTO.prototype, "type", void 0);
_ts_decorate$3N([
    dto({
        string: true
    }),
    _ts_metadata$3N("design:type", String)
], IntegrationValidateCartV3DeliveryPickupPointRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$3N([
    dto({
        string: true
    }),
    _ts_metadata$3N("design:type", String)
], IntegrationValidateCartV3DeliveryPickupPointRequestDTO.prototype, "cityId", void 0);
_ts_decorate$3N([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3N("design:type", Object)
], IntegrationValidateCartV3DeliveryPickupPointRequestDTO.prototype, "variantId", void 0);
_ts_decorate$3N([
    dto({
        string: true
    }),
    _ts_metadata$3N("design:type", String)
], IntegrationValidateCartV3DeliveryPickupPointRequestDTO.prototype, "addressId", void 0);

function _define_property$3M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3M(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3M(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3PaymentRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3M(this, "type", void 0), _define_property$3M(this, "paymentTypeId", void 0);
    }
}
_ts_decorate$3M([
    dto({
        enum: IntegrationCartConfigurationPaymentType
    }),
    _ts_metadata$3M("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" ? Object : IntegrationCartConfigurationPaymentType)
], IntegrationValidateCartV3PaymentRequestDTO.prototype, "type", void 0);
_ts_decorate$3M([
    dto({
        string: true
    }),
    _ts_metadata$3M("design:type", String)
], IntegrationValidateCartV3PaymentRequestDTO.prototype, "paymentTypeId", void 0);

function _define_property$3L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3L(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3L(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3L(this, "userId", void 0), _define_property$3L(this, "applyBonuses", void 0), _define_property$3L(this, "products", void 0), _define_property$3L(this, "delivery", void 0), _define_property$3L(this, "payment", void 0), _define_property$3L(this, "promoCodes", void 0);
    }
}
_ts_decorate$3L([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3L("design:type", Object)
], IntegrationValidateCartV3RequestDTO.prototype, "userId", void 0);
_ts_decorate$3L([
    dto({
        boolean: true
    }),
    _ts_metadata$3L("design:type", Boolean)
], IntegrationValidateCartV3RequestDTO.prototype, "applyBonuses", void 0);
_ts_decorate$3L([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartV3ProductRequestDTO
        }
    }),
    _ts_metadata$3L("design:type", Array)
], IntegrationValidateCartV3RequestDTO.prototype, "products", void 0);
_ts_decorate$3L([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: IntegrationValidateCartV3DeliveryPickupRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: IntegrationValidateCartV3DeliveryCourierRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: IntegrationValidateCartV3DeliveryPickupPointRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3L("design:type", Object)
], IntegrationValidateCartV3RequestDTO.prototype, "delivery", void 0);
_ts_decorate$3L([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: IntegrationValidateCartV3PaymentRequestDTO
        }
    }),
    _ts_metadata$3L("design:type", Object)
], IntegrationValidateCartV3RequestDTO.prototype, "payment", void 0);
_ts_decorate$3L([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$3L("design:type", Array)
], IntegrationValidateCartV3RequestDTO.prototype, "promoCodes", void 0);

var IntegrationValidateCartV3InfoBonusesWalletType = /*#__PURE__*/ function(IntegrationValidateCartV3InfoBonusesWalletType) {
    IntegrationValidateCartV3InfoBonusesWalletType["CASHBACK"] = "cashback";
    IntegrationValidateCartV3InfoBonusesWalletType["BONUS"] = "bonus";
    return IntegrationValidateCartV3InfoBonusesWalletType;
}({});
var IntegrationValidateCartV3InfoResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV3InfoResponseStatus) {
    IntegrationValidateCartV3InfoResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV3InfoResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV3InfoResponseStatus;
}({});

function _define_property$3K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3K(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3K(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3InfoErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3K(this, "status", void 0), _define_property$3K(this, "message", void 0);
    }
}
_ts_decorate$3K([
    dto({
        equals: IntegrationValidateCartV3InfoResponseStatus.ERROR
    }),
    _ts_metadata$3K("design:type", typeof IntegrationValidateCartV3InfoResponseStatus === "undefined" || typeof IntegrationValidateCartV3InfoResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV3InfoResponseStatus.ERROR)
], IntegrationValidateCartV3InfoErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$3K([
    dto({
        string: true
    }),
    _ts_metadata$3K("design:type", String)
], IntegrationValidateCartV3InfoErrorResponseDTO.prototype, "message", void 0);

function _define_property$3J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3J(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3J(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3J(this, "type", void 0), _define_property$3J(this, "willUse", void 0), _define_property$3J(this, "willAdd", void 0), _define_property$3J(this, "current", void 0);
    }
}
_ts_decorate$3J([
    dto({
        equals: IntegrationValidateCartV3InfoBonusesWalletType.CASHBACK
    }),
    _ts_metadata$3J("design:type", typeof IntegrationValidateCartV3InfoBonusesWalletType === "undefined" || typeof IntegrationValidateCartV3InfoBonusesWalletType.CASHBACK === "undefined" ? Object : IntegrationValidateCartV3InfoBonusesWalletType.CASHBACK)
], IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO.prototype, "type", void 0);
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO.prototype, "willUse", void 0);
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO.prototype, "willAdd", void 0);
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO.prototype, "current", void 0);
class IntegrationValidateCartV3InfoBonusesWalletBonusResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3J(this, "type", void 0);
    }
}
_ts_decorate$3J([
    dto({
        equals: IntegrationValidateCartV3InfoBonusesWalletType.BONUS
    }),
    _ts_metadata$3J("design:type", typeof IntegrationValidateCartV3InfoBonusesWalletType === "undefined" || typeof IntegrationValidateCartV3InfoBonusesWalletType.BONUS === "undefined" ? Object : IntegrationValidateCartV3InfoBonusesWalletType.BONUS)
], IntegrationValidateCartV3InfoBonusesWalletBonusResponseDTO.prototype, "type", void 0);
class IntegrationValidateCartV3InfoCalculationResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3J(this, "name", void 0), _define_property$3J(this, "description", void 0), _define_property$3J(this, "value", void 0), _define_property$3J(this, "measure", void 0);
    }
}
_ts_decorate$3J([
    dto({
        string: true
    }),
    _ts_metadata$3J("design:type", String)
], IntegrationValidateCartV3InfoCalculationResponseDTO.prototype, "name", void 0);
_ts_decorate$3J([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$3J("design:type", Object)
], IntegrationValidateCartV3InfoCalculationResponseDTO.prototype, "description", void 0);
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoCalculationResponseDTO.prototype, "value", void 0);
_ts_decorate$3J([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$3J("design:type", Object)
], IntegrationValidateCartV3InfoCalculationResponseDTO.prototype, "measure", void 0);
class IntegrationValidateCartV3InfoPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3J(this, "base", void 0), _define_property$3J(this, "result", void 0), _define_property$3J(this, "discount", void 0);
    }
}
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoPriceResponseDTO.prototype, "base", void 0);
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoPriceResponseDTO.prototype, "result", void 0);
_ts_decorate$3J([
    dto({
        number: true
    }),
    _ts_metadata$3J("design:type", Number)
], IntegrationValidateCartV3InfoPriceResponseDTO.prototype, "discount", void 0);
class IntegrationValidateCartV3InfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3J(this, "status", void 0), _define_property$3J(this, "price", void 0), _define_property$3J(this, "bonusesWallet", void 0), _define_property$3J(this, "calculation", void 0);
    }
}
_ts_decorate$3J([
    dto({
        equals: IntegrationValidateCartV3InfoResponseStatus.SUCCESS
    }),
    _ts_metadata$3J("design:type", typeof IntegrationValidateCartV3InfoResponseStatus === "undefined" || typeof IntegrationValidateCartV3InfoResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV3InfoResponseStatus.SUCCESS)
], IntegrationValidateCartV3InfoResponseDTO.prototype, "status", void 0);
_ts_decorate$3J([
    dto({
        object: {
            type: IntegrationValidateCartV3InfoPriceResponseDTO
        }
    }),
    _ts_metadata$3J("design:type", typeof IntegrationValidateCartV3InfoPriceResponseDTO === "undefined" ? Object : IntegrationValidateCartV3InfoPriceResponseDTO)
], IntegrationValidateCartV3InfoResponseDTO.prototype, "price", void 0);
_ts_decorate$3J([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationValidateCartV3InfoBonusesWalletType.CASHBACK,
                        value: IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV3InfoBonusesWalletType.BONUS,
                        value: IntegrationValidateCartV3InfoBonusesWalletBonusResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3J("design:type", Object)
], IntegrationValidateCartV3InfoResponseDTO.prototype, "bonusesWallet", void 0);
_ts_decorate$3J([
    dto({
        object: {
            type: IntegrationValidateCartV3InfoCalculationResponseDTO
        },
        array: true
    }),
    _ts_metadata$3J("design:type", Array)
], IntegrationValidateCartV3InfoResponseDTO.prototype, "calculation", void 0);

var IntegrationValidateCartV3ProductResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV3ProductResponseStatus) {
    IntegrationValidateCartV3ProductResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV3ProductResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV3ProductResponseStatus;
}({});

function _define_property$3I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3I(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3I(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3ProductResponseBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3I(this, "id", void 0), _define_property$3I(this, "backendId", void 0), _define_property$3I(this, "maxCount", void 0);
    }
}
_ts_decorate$3I([
    dto({
        string: true
    }),
    _ts_metadata$3I("design:type", String)
], IntegrationValidateCartV3ProductResponseBaseDTO.prototype, "id", void 0);
_ts_decorate$3I([
    dto({
        string: true
    }),
    _ts_metadata$3I("design:type", String)
], IntegrationValidateCartV3ProductResponseBaseDTO.prototype, "backendId", void 0);
_ts_decorate$3I([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3I("design:type", Number)
], IntegrationValidateCartV3ProductResponseBaseDTO.prototype, "maxCount", void 0);

function _define_property$3H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3H(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3H(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3ProductResponseErrorDTO extends IntegrationValidateCartV3ProductResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$3H(this, "status", void 0), _define_property$3H(this, "message", void 0);
    }
}
_ts_decorate$3H([
    dto({
        equals: IntegrationValidateCartV3ProductResponseStatus.ERROR
    }),
    _ts_metadata$3H("design:type", typeof IntegrationValidateCartV3ProductResponseStatus === "undefined" || typeof IntegrationValidateCartV3ProductResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV3ProductResponseStatus.ERROR)
], IntegrationValidateCartV3ProductResponseErrorDTO.prototype, "status", void 0);
_ts_decorate$3H([
    dto({
        string: true
    }),
    _ts_metadata$3H("design:type", String)
], IntegrationValidateCartV3ProductResponseErrorDTO.prototype, "message", void 0);

function _define_property$3G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3G(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3G(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3ProductPriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3G(this, "base", void 0), _define_property$3G(this, "result", void 0), _define_property$3G(this, "discount", void 0);
    }
}
_ts_decorate$3G([
    dto({
        number: true
    }),
    _ts_metadata$3G("design:type", Number)
], IntegrationValidateCartV3ProductPriceResponseDTO.prototype, "base", void 0);
_ts_decorate$3G([
    dto({
        number: true
    }),
    _ts_metadata$3G("design:type", Number)
], IntegrationValidateCartV3ProductPriceResponseDTO.prototype, "result", void 0);
_ts_decorate$3G([
    dto({
        number: true
    }),
    _ts_metadata$3G("design:type", Number)
], IntegrationValidateCartV3ProductPriceResponseDTO.prototype, "discount", void 0);
// eslint-disable-next-line max-len
class IntegrationValidateCartV3ProductResponseSuccessDTO extends IntegrationValidateCartV3ProductResponseBaseDTO {
    constructor(...args){
        super(...args), _define_property$3G(this, "status", void 0), _define_property$3G(this, "price", void 0);
    }
}
_ts_decorate$3G([
    dto({
        equals: IntegrationValidateCartV3ProductResponseStatus.SUCCESS
    }),
    _ts_metadata$3G("design:type", typeof IntegrationValidateCartV3ProductResponseStatus === "undefined" || typeof IntegrationValidateCartV3ProductResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV3ProductResponseStatus.SUCCESS)
], IntegrationValidateCartV3ProductResponseSuccessDTO.prototype, "status", void 0);
_ts_decorate$3G([
    dto({
        object: {
            type: IntegrationValidateCartV3ProductPriceResponseDTO
        }
    }),
    _ts_metadata$3G("design:type", typeof IntegrationValidateCartV3ProductPriceResponseDTO === "undefined" ? Object : IntegrationValidateCartV3ProductPriceResponseDTO)
], IntegrationValidateCartV3ProductResponseSuccessDTO.prototype, "price", void 0);

var IntegrationValidateCartV3PaymentCalculationResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV3PaymentCalculationResponseStatus) {
    IntegrationValidateCartV3PaymentCalculationResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV3PaymentCalculationResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV3PaymentCalculationResponseStatus;
}({});

function _define_property$3F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3F(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3F(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3PaymentCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3F(this, "status", void 0);
    }
}
_ts_decorate$3F([
    dto({
        equals: IntegrationValidateCartV3PaymentCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$3F("design:type", typeof IntegrationValidateCartV3PaymentCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV3PaymentCalculationResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV3PaymentCalculationResponseStatus.SUCCESS)
], IntegrationValidateCartV3PaymentCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$3E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3E(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3E(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3PaymentCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3E(this, "status", void 0), _define_property$3E(this, "message", void 0);
    }
}
_ts_decorate$3E([
    dto({
        equals: IntegrationValidateCartV3PaymentCalculationResponseStatus.ERROR
    }),
    _ts_metadata$3E("design:type", typeof IntegrationValidateCartV3PaymentCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV3PaymentCalculationResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV3PaymentCalculationResponseStatus.ERROR)
], IntegrationValidateCartV3PaymentCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$3E([
    dto({
        string: true
    }),
    _ts_metadata$3E("design:type", String)
], IntegrationValidateCartV3PaymentCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$3D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3D(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3D(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3PaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3D(this, "calculation", void 0);
    }
}
_ts_decorate$3D([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV3PaymentCalculationResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV3PaymentCalculationSuccessResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV3PaymentCalculationResponseStatus.ERROR,
                        value: IntegrationValidateCartV3PaymentCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3D("design:type", Object)
], IntegrationValidateCartV3PaymentResponseDTO.prototype, "calculation", void 0);

function _define_property$3C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3C(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3C(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3DeliveryInfoVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3C(this, "id", void 0), _define_property$3C(this, "name", void 0);
    }
}
_ts_decorate$3C([
    dto({
        string: true
    }),
    _ts_metadata$3C("design:type", String)
], IntegrationValidateCartV3DeliveryInfoVariantResponseDTO.prototype, "id", void 0);
_ts_decorate$3C([
    dto({
        string: true
    }),
    _ts_metadata$3C("design:type", String)
], IntegrationValidateCartV3DeliveryInfoVariantResponseDTO.prototype, "name", void 0);
class IntegrationValidateCartV3DeliveryInfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3C(this, "variants", void 0);
    }
}
_ts_decorate$3C([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartV3DeliveryInfoVariantResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3C("design:type", Object)
], IntegrationValidateCartV3DeliveryInfoResponseDTO.prototype, "variants", void 0);

var IntegrationValidateCartV3DeliveryCalculationResponseStatus = /*#__PURE__*/ function(IntegrationValidateCartV3DeliveryCalculationResponseStatus) {
    IntegrationValidateCartV3DeliveryCalculationResponseStatus["SUCCESS"] = "success";
    IntegrationValidateCartV3DeliveryCalculationResponseStatus["ERROR"] = "error";
    return IntegrationValidateCartV3DeliveryCalculationResponseStatus;
}({});

function _define_property$3B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3B(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3B(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3DeliveryCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3B(this, "status", void 0), _define_property$3B(this, "message", void 0);
    }
}
_ts_decorate$3B([
    dto({
        equals: IntegrationValidateCartV3DeliveryCalculationResponseStatus.ERROR
    }),
    _ts_metadata$3B("design:type", typeof IntegrationValidateCartV3DeliveryCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV3DeliveryCalculationResponseStatus.ERROR === "undefined" ? Object : IntegrationValidateCartV3DeliveryCalculationResponseStatus.ERROR)
], IntegrationValidateCartV3DeliveryCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$3B([
    dto({
        string: true
    }),
    _ts_metadata$3B("design:type", String)
], IntegrationValidateCartV3DeliveryCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$3A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3A(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3A(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3DeliveryCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3A(this, "status", void 0);
    }
}
_ts_decorate$3A([
    dto({
        equals: IntegrationValidateCartV3DeliveryCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$3A("design:type", typeof IntegrationValidateCartV3DeliveryCalculationResponseStatus === "undefined" || typeof IntegrationValidateCartV3DeliveryCalculationResponseStatus.SUCCESS === "undefined" ? Object : IntegrationValidateCartV3DeliveryCalculationResponseStatus.SUCCESS)
], IntegrationValidateCartV3DeliveryCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$3z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3DeliveryResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3z(this, "calculation", void 0), _define_property$3z(this, "info", void 0);
    }
}
_ts_decorate$3z([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV3DeliveryCalculationResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV3DeliveryCalculationSuccessResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV3DeliveryCalculationResponseStatus.ERROR,
                        value: IntegrationValidateCartV3DeliveryCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3z("design:type", Object)
], IntegrationValidateCartV3DeliveryResponseDTO.prototype, "calculation", void 0);
_ts_decorate$3z([
    dto({
        object: {
            type: IntegrationValidateCartV3DeliveryInfoResponseDTO
        }
    }),
    _ts_metadata$3z("design:type", typeof IntegrationValidateCartV3DeliveryInfoResponseDTO === "undefined" ? Object : IntegrationValidateCartV3DeliveryInfoResponseDTO)
], IntegrationValidateCartV3DeliveryResponseDTO.prototype, "info", void 0);

function _define_property$3y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationValidateCartV3PromoCodeStatus = /*#__PURE__*/ function(IntegrationValidateCartV3PromoCodeStatus) {
    IntegrationValidateCartV3PromoCodeStatus["SUCCESS"] = "success";
    IntegrationValidateCartV3PromoCodeStatus["ERROR"] = "error";
    return IntegrationValidateCartV3PromoCodeStatus;
}({});
class IntegrationValidateCartV3PromoCodeSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3y(this, "status", void 0), _define_property$3y(this, "name", void 0), _define_property$3y(this, "description", void 0), _define_property$3y(this, "category", void 0);
    }
}
_ts_decorate$3y([
    dto({
        equals: "success"
    }),
    _ts_metadata$3y("design:type", typeof IntegrationValidateCartV3PromoCodeStatus === "undefined" || "string" === "undefined" ? Object : "success")
], IntegrationValidateCartV3PromoCodeSuccessDTO.prototype, "status", void 0);
_ts_decorate$3y([
    dto({
        string: true
    }),
    _ts_metadata$3y("design:type", String)
], IntegrationValidateCartV3PromoCodeSuccessDTO.prototype, "name", void 0);
_ts_decorate$3y([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$3y("design:type", Object)
], IntegrationValidateCartV3PromoCodeSuccessDTO.prototype, "description", void 0);
_ts_decorate$3y([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$3y("design:type", Object)
], IntegrationValidateCartV3PromoCodeSuccessDTO.prototype, "category", void 0);
class IntegrationValidateCartV3PromoCodeErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3y(this, "status", void 0), _define_property$3y(this, "message", void 0);
    }
}
_ts_decorate$3y([
    dto({
        equals: "error"
    }),
    _ts_metadata$3y("design:type", typeof IntegrationValidateCartV3PromoCodeStatus === "undefined" || "string" === "undefined" ? Object : "error")
], IntegrationValidateCartV3PromoCodeErrorDTO.prototype, "status", void 0);
_ts_decorate$3y([
    dto({
        string: true
    }),
    _ts_metadata$3y("design:type", String)
], IntegrationValidateCartV3PromoCodeErrorDTO.prototype, "message", void 0);
class IntegrationValidateCartV3PromoCodeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3y(this, "code", void 0), _define_property$3y(this, "data", void 0);
    }
}
_ts_decorate$3y([
    dto({
        string: true
    }),
    _ts_metadata$3y("design:type", String)
], IntegrationValidateCartV3PromoCodeResponseDTO.prototype, "code", void 0);
_ts_decorate$3y([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: IntegrationValidateCartV3PromoCodeSuccessDTO
                    },
                    {
                        name: "error",
                        value: IntegrationValidateCartV3PromoCodeErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3y("design:type", Object)
], IntegrationValidateCartV3PromoCodeResponseDTO.prototype, "data", void 0);
class IntegrationValidateCartV3PromoCodesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3y(this, "list", void 0), _define_property$3y(this, "limit", void 0);
    }
}
_ts_decorate$3y([
    dto({
        array: true,
        object: {
            type: IntegrationValidateCartV3PromoCodeResponseDTO
        }
    }),
    _ts_metadata$3y("design:type", Array)
], IntegrationValidateCartV3PromoCodesResponseDTO.prototype, "list", void 0);
_ts_decorate$3y([
    dto({
        number: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3y("design:type", Object)
], IntegrationValidateCartV3PromoCodesResponseDTO.prototype, "limit", void 0);

function _define_property$3x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3x(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3x(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV3ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3x(this, "info", void 0), _define_property$3x(this, "delivery", void 0), _define_property$3x(this, "payment", void 0), _define_property$3x(this, "promoCodes", void 0), _define_property$3x(this, "products", void 0);
    }
}
_ts_decorate$3x([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV3InfoResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV3InfoResponseDTO
                    },
                    {
                        name: IntegrationValidateCartV3InfoResponseStatus.ERROR,
                        value: IntegrationValidateCartV3InfoErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3x("design:type", Object)
], IntegrationValidateCartV3ResponseDTO.prototype, "info", void 0);
_ts_decorate$3x([
    dto({
        object: {
            type: IntegrationValidateCartV3DeliveryResponseDTO
        }
    }),
    _ts_metadata$3x("design:type", typeof IntegrationValidateCartV3DeliveryResponseDTO === "undefined" ? Object : IntegrationValidateCartV3DeliveryResponseDTO)
], IntegrationValidateCartV3ResponseDTO.prototype, "delivery", void 0);
_ts_decorate$3x([
    dto({
        object: {
            type: IntegrationValidateCartV3PaymentResponseDTO
        }
    }),
    _ts_metadata$3x("design:type", typeof IntegrationValidateCartV3PaymentResponseDTO === "undefined" ? Object : IntegrationValidateCartV3PaymentResponseDTO)
], IntegrationValidateCartV3ResponseDTO.prototype, "payment", void 0);
_ts_decorate$3x([
    dto({
        object: {
            type: IntegrationValidateCartV3PromoCodesResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3x("design:type", Object)
], IntegrationValidateCartV3ResponseDTO.prototype, "promoCodes", void 0);
_ts_decorate$3x([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationValidateCartV3ProductResponseStatus.SUCCESS,
                        value: IntegrationValidateCartV3ProductResponseSuccessDTO
                    },
                    {
                        name: IntegrationValidateCartV3ProductResponseStatus.ERROR,
                        value: IntegrationValidateCartV3ProductResponseErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3x("design:type", Array)
], IntegrationValidateCartV3ResponseDTO.prototype, "products", void 0);

function _define_property$3w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3w(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3w(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV4RequestDTO extends IntegrationValidateCartV3RequestDTO {
    constructor(...args){
        super(...args), // фиктивное поле: классу нужна хотя бы одна собственная @dto-метадата,
        // иначе автогенерация swagger-схем пропускает пустой класс-наследник и $ref повисает
        _define_property$3w(this, "__swaggerVersion", void 0);
    }
}
_ts_decorate$3w([
    dto({
        optional: true
    }),
    _ts_metadata$3w("design:type", void 0)
], IntegrationValidateCartV4RequestDTO.prototype, "__swaggerVersion", void 0);

function _define_property$3v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3v(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3v(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateCartV4ResponseDTO extends IntegrationValidateCartV3ResponseDTO {
    constructor(...args){
        super(...args), _define_property$3v(this, "deliveryTypes", void 0), _define_property$3v(this, "paymentTypes", void 0);
    }
}
_ts_decorate$3v([
    dto({
        array: true,
        object: {
            type: IntegrationCartConfigurationDeliveryTypeResponseDTO
        }
    }),
    _ts_metadata$3v("design:type", Array)
], IntegrationValidateCartV4ResponseDTO.prototype, "deliveryTypes", void 0);
_ts_decorate$3v([
    dto({
        array: true,
        object: {
            type: IntegrationCartGetConfigurationPaymentTypeResponseDTO
        }
    }),
    _ts_metadata$3v("design:type", Array)
], IntegrationValidateCartV4ResponseDTO.prototype, "paymentTypes", void 0);

function _define_property$3u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3u(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3u(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteMerchantCategoryDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3u(this, "id", void 0);
    }
}
_ts_decorate$3u([
    dto({
        string: true
    }),
    _ts_metadata$3u("design:type", String)
], IntegrationDeleteMerchantCategoryDTO.prototype, "id", void 0);

function _define_property$3t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3t(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3t(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCategoriesGetIdsResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3t(this, "id", void 0);
    }
}
_ts_decorate$3t([
    dto({
        string: true
    }),
    _ts_metadata$3t("design:type", String)
], IntegrationCategoriesGetIdsResponseDTO.prototype, "id", void 0);

function _define_property$3s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3s(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3s(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantAttributeStaticValueVariantViewOptionsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "numberIndex", void 0);
    }
}
_ts_decorate$3s([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeStaticValueVariantViewOptionsDTO.prototype, "numberIndex", void 0);
class IntegrationInitializeMerchantAttributeStaticValueVariantDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "id", void 0), _define_property$3s(this, "name", void 0), _define_property$3s(this, "viewOptions", void 0);
    }
}
_ts_decorate$3s([
    dto({
        string: true
    }),
    _ts_metadata$3s("design:type", String)
], IntegrationInitializeMerchantAttributeStaticValueVariantDTO.prototype, "id", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeStaticValueVariantDTO.prototype, "name", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        object: {
            type: IntegrationInitializeMerchantAttributeStaticValueVariantViewOptionsDTO
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeStaticValueVariantDTO.prototype, "viewOptions", void 0);
class IntegrationInitializeMerchantAttributeRequiredToOrderDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "mode", void 0);
    }
}
_ts_decorate$3s([
    dto({
        enum: ProductAttributeRequiredToOrderMode
    }),
    _ts_metadata$3s("design:type", typeof ProductAttributeRequiredToOrderMode === "undefined" ? Object : ProductAttributeRequiredToOrderMode)
], IntegrationInitializeMerchantAttributeRequiredToOrderDTO.prototype, "mode", void 0);
class IntegrationInitializeMerchantAttributeStaticValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "type", void 0), _define_property$3s(this, "requiredToOrder", void 0), _define_property$3s(this, "variants", void 0), _define_property$3s(this, "valueType", void 0);
    }
}
_ts_decorate$3s([
    dto({
        equals: ProductAttributeType.STATIC
    }),
    _ts_metadata$3s("design:type", typeof ProductAttributeType === "undefined" || typeof ProductAttributeType.STATIC === "undefined" ? Object : ProductAttributeType.STATIC)
], IntegrationInitializeMerchantAttributeStaticValueDTO.prototype, "type", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        object: {
            type: IntegrationInitializeMerchantAttributeRequiredToOrderDTO
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeStaticValueDTO.prototype, "requiredToOrder", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        array: true,
        object: {
            type: IntegrationInitializeMerchantAttributeStaticValueVariantDTO
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeStaticValueDTO.prototype, "variants", void 0);
_ts_decorate$3s([
    dto({
        enum: ProductAttributeValueType
    }),
    _ts_metadata$3s("design:type", typeof ProductAttributeValueType === "undefined" ? Object : ProductAttributeValueType)
], IntegrationInitializeMerchantAttributeStaticValueDTO.prototype, "valueType", void 0);
class IntegrationInitializeMerchantAttributeDynamicValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "type", void 0), _define_property$3s(this, "requiredToOrder", void 0), _define_property$3s(this, "valueType", void 0);
    }
}
_ts_decorate$3s([
    dto({
        equals: ProductAttributeType.DYNAMIC
    }),
    _ts_metadata$3s("design:type", typeof ProductAttributeType === "undefined" || typeof ProductAttributeType.DYNAMIC === "undefined" ? Object : ProductAttributeType.DYNAMIC)
], IntegrationInitializeMerchantAttributeDynamicValueDTO.prototype, "type", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        object: {
            type: IntegrationInitializeMerchantAttributeRequiredToOrderDTO
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeDynamicValueDTO.prototype, "requiredToOrder", void 0);
_ts_decorate$3s([
    dto({
        enum: ProductAttributeValueType
    }),
    _ts_metadata$3s("design:type", typeof ProductAttributeValueType === "undefined" ? Object : ProductAttributeValueType)
], IntegrationInitializeMerchantAttributeDynamicValueDTO.prototype, "valueType", void 0);
class IntegrationInitializeMerchantAttributeViewOptionsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "showInFilters", void 0), _define_property$3s(this, "showInDetail", void 0);
    }
}
_ts_decorate$3s([
    dto({
        boolean: true
    }),
    _ts_metadata$3s("design:type", Boolean)
], IntegrationInitializeMerchantAttributeViewOptionsDTO.prototype, "showInFilters", void 0);
_ts_decorate$3s([
    dto({
        boolean: true
    }),
    _ts_metadata$3s("design:type", Boolean)
], IntegrationInitializeMerchantAttributeViewOptionsDTO.prototype, "showInDetail", void 0);
class IntegrationInitializeMerchantAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "id", void 0), _define_property$3s(this, "name", void 0), _define_property$3s(this, "measure", void 0), _define_property$3s(this, "viewOptions", void 0), _define_property$3s(this, "value", void 0);
    }
}
_ts_decorate$3s([
    dto({
        string: true
    }),
    _ts_metadata$3s("design:type", String)
], IntegrationInitializeMerchantAttributeDTO.prototype, "id", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeDTO.prototype, "name", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeDTO.prototype, "measure", void 0);
_ts_decorate$3s([
    dto({
        object: {
            type: IntegrationInitializeMerchantAttributeViewOptionsDTO
        }
    }),
    _ts_metadata$3s("design:type", typeof IntegrationInitializeMerchantAttributeViewOptionsDTO === "undefined" ? Object : IntegrationInitializeMerchantAttributeViewOptionsDTO)
], IntegrationInitializeMerchantAttributeDTO.prototype, "viewOptions", void 0);
_ts_decorate$3s([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "static",
                        value: IntegrationInitializeMerchantAttributeStaticValueDTO
                    },
                    {
                        name: "dynamic",
                        value: IntegrationInitializeMerchantAttributeDynamicValueDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantAttributeDTO.prototype, "value", void 0);
class IntegrationInitializeMerchantPartDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "id", void 0), _define_property$3s(this, "name", void 0), _define_property$3s(this, "description", void 0), _define_property$3s(this, "attributes", void 0);
    }
}
_ts_decorate$3s([
    dto({
        string: true
    }),
    _ts_metadata$3s("design:type", String)
], IntegrationInitializeMerchantPartDTO.prototype, "id", void 0);
_ts_decorate$3s([
    dto({
        string: true
    }),
    _ts_metadata$3s("design:type", String)
], IntegrationInitializeMerchantPartDTO.prototype, "name", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantPartDTO.prototype, "description", void 0);
_ts_decorate$3s([
    dto({
        array: true,
        object: {
            type: IntegrationInitializeMerchantAttributeDTO
        }
    }),
    _ts_metadata$3s("design:type", Array)
], IntegrationInitializeMerchantPartDTO.prototype, "attributes", void 0);
class IntegrationInitializeMerchantCategoryDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3s(this, "id", void 0), _define_property$3s(this, "name", void 0), _define_property$3s(this, "imageUrl", void 0), _define_property$3s(this, "index", void 0), _define_property$3s(this, "attributes", void 0), _define_property$3s(this, "parts", void 0);
    }
}
_ts_decorate$3s([
    dto({
        string: true
    }),
    _ts_metadata$3s("design:type", String)
], IntegrationInitializeMerchantCategoryDTO.prototype, "id", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantCategoryDTO.prototype, "name", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantCategoryDTO.prototype, "imageUrl", void 0);
_ts_decorate$3s([
    dto({
        optional: {
            allowNull: false
        },
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantCategoryDTO.prototype, "index", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        array: true,
        object: {
            type: IntegrationInitializeMerchantAttributeDTO
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantCategoryDTO.prototype, "attributes", void 0);
_ts_decorate$3s([
    dto({
        optional: true,
        array: true,
        object: {
            type: IntegrationInitializeMerchantPartDTO
        }
    }),
    _ts_metadata$3s("design:type", Object)
], IntegrationInitializeMerchantCategoryDTO.prototype, "parts", void 0);

function _define_property$3r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3r(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3r(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantCategoryImageDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3r(this, "id", void 0);
    }
}
_ts_decorate$3r([
    dto({
        string: true
    }),
    _ts_metadata$3r("design:type", String)
], IntegrationInitializeMerchantCategoryImageDTO.prototype, "id", void 0);

function _define_property$3q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CategoryDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3q(this, "id", void 0), _define_property$3q(this, "name", void 0), _define_property$3q(this, "image", void 0);
    }
}
_ts_decorate$3q([
    dto({
        string: true
    }),
    _ts_metadata$3q("design:type", String)
], CategoryDTO.prototype, "id", void 0);
_ts_decorate$3q([
    dto({
        string: true
    }),
    _ts_metadata$3q("design:type", String)
], CategoryDTO.prototype, "name", void 0);
_ts_decorate$3q([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$3q("design:type", Object)
], CategoryDTO.prototype, "image", void 0);

function _define_property$3p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3p(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3p(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CategoryGroupDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3p(this, "id", void 0), _define_property$3p(this, "name", void 0), _define_property$3p(this, "image", void 0), _define_property$3p(this, "children", void 0);
    }
}
_ts_decorate$3p([
    dto({
        string: true
    }),
    _ts_metadata$3p("design:type", String)
], CategoryGroupDTO.prototype, "id", void 0);
_ts_decorate$3p([
    dto({
        string: true
    }),
    _ts_metadata$3p("design:type", String)
], CategoryGroupDTO.prototype, "name", void 0);
_ts_decorate$3p([
    dto({
        optional: {
            allowUndefined: false
        },
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$3p("design:type", Object)
], CategoryGroupDTO.prototype, "image", void 0);
_ts_decorate$3p([
    dto({
        array: true,
        object: {
            type: CategoryDTO
        }
    }),
    _ts_metadata$3p("design:type", Array)
], CategoryGroupDTO.prototype, "children", void 0);

function _define_property$3o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3o(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3o(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CategoryHierarchyItemType = /*#__PURE__*/ function(CategoryHierarchyItemType) {
    CategoryHierarchyItemType["CATEGORY"] = "category";
    CategoryHierarchyItemType["GROUP"] = "group";
    return CategoryHierarchyItemType;
}({});
class CategoryHierarchyItemCategoryDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3o(this, "type", void 0), _define_property$3o(this, "category", void 0);
    }
}
_ts_decorate$3o([
    dto({
        equals: "category"
    }),
    _ts_metadata$3o("design:type", typeof CategoryHierarchyItemType === "undefined" || "string" === "undefined" ? Object : "category")
], CategoryHierarchyItemCategoryDataDTO.prototype, "type", void 0);
_ts_decorate$3o([
    dto({
        object: {
            type: CategoryDTO
        }
    }),
    _ts_metadata$3o("design:type", typeof CategoryDTO === "undefined" ? Object : CategoryDTO)
], CategoryHierarchyItemCategoryDataDTO.prototype, "category", void 0);
class CategoryHierarchyItemGroupDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3o(this, "type", void 0), _define_property$3o(this, "group", void 0);
    }
}
_ts_decorate$3o([
    dto({
        equals: "group"
    }),
    _ts_metadata$3o("design:type", typeof CategoryHierarchyItemType === "undefined" || "string" === "undefined" ? Object : "group")
], CategoryHierarchyItemGroupDataDTO.prototype, "type", void 0);
_ts_decorate$3o([
    dto({
        object: {
            type: CategoryGroupDTO
        }
    }),
    _ts_metadata$3o("design:type", typeof CategoryGroupDTO === "undefined" ? Object : CategoryGroupDTO)
], CategoryHierarchyItemGroupDataDTO.prototype, "group", void 0);
class CategoryHierarchyItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3o(this, "data", void 0);
    }
}
_ts_decorate$3o([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "category",
                        value: CategoryHierarchyItemCategoryDataDTO
                    },
                    {
                        name: "group",
                        value: CategoryHierarchyItemGroupDataDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3o("design:type", Object)
], CategoryHierarchyItemDTO.prototype, "data", void 0);

function _define_property$3n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3n(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3n(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCategoryHierarchyCategoryDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3n(this, "id", void 0);
    }
}
_ts_decorate$3n([
    dto({
        string: true
    }),
    _ts_metadata$3n("design:type", String)
], IntegrationCategoryHierarchyCategoryDTO.prototype, "id", void 0);
class IntegrationCategoryHierarchyGroupDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3n(this, "id", void 0), _define_property$3n(this, "children", void 0);
    }
}
_ts_decorate$3n([
    dto({
        string: true
    }),
    _ts_metadata$3n("design:type", String)
], IntegrationCategoryHierarchyGroupDTO.prototype, "id", void 0);
_ts_decorate$3n([
    dto({
        array: true,
        object: {
            type: IntegrationCategoryHierarchyCategoryDTO
        }
    }),
    _ts_metadata$3n("design:type", Array)
], IntegrationCategoryHierarchyGroupDTO.prototype, "children", void 0);
class IntegrationCategoryHierarchyItemCategoryDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3n(this, "type", void 0), _define_property$3n(this, "category", void 0);
    }
}
_ts_decorate$3n([
    dto({
        equals: CategoryHierarchyItemType.CATEGORY
    }),
    _ts_metadata$3n("design:type", typeof CategoryHierarchyItemType === "undefined" || typeof CategoryHierarchyItemType.CATEGORY === "undefined" ? Object : CategoryHierarchyItemType.CATEGORY)
], IntegrationCategoryHierarchyItemCategoryDataDTO.prototype, "type", void 0);
_ts_decorate$3n([
    dto({
        object: {
            type: IntegrationCategoryHierarchyCategoryDTO
        }
    }),
    _ts_metadata$3n("design:type", typeof IntegrationCategoryHierarchyCategoryDTO === "undefined" ? Object : IntegrationCategoryHierarchyCategoryDTO)
], IntegrationCategoryHierarchyItemCategoryDataDTO.prototype, "category", void 0);
class IntegrationCategoryHierarchyItemGroupDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3n(this, "type", void 0), _define_property$3n(this, "group", void 0);
    }
}
_ts_decorate$3n([
    dto({
        equals: CategoryHierarchyItemType.GROUP
    }),
    _ts_metadata$3n("design:type", typeof CategoryHierarchyItemType === "undefined" || typeof CategoryHierarchyItemType.GROUP === "undefined" ? Object : CategoryHierarchyItemType.GROUP)
], IntegrationCategoryHierarchyItemGroupDataDTO.prototype, "type", void 0);
_ts_decorate$3n([
    dto({
        object: {
            type: IntegrationCategoryHierarchyGroupDTO
        }
    }),
    _ts_metadata$3n("design:type", typeof IntegrationCategoryHierarchyGroupDTO === "undefined" ? Object : IntegrationCategoryHierarchyGroupDTO)
], IntegrationCategoryHierarchyItemGroupDataDTO.prototype, "group", void 0);
class IntegrationGetCategoryHierarchyResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3n(this, "data", void 0);
    }
}
_ts_decorate$3n([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: CategoryHierarchyItemType.CATEGORY,
                        value: IntegrationCategoryHierarchyItemCategoryDataDTO
                    },
                    {
                        name: CategoryHierarchyItemType.GROUP,
                        value: IntegrationCategoryHierarchyItemGroupDataDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3n("design:type", Object)
], IntegrationGetCategoryHierarchyResponseDTO.prototype, "data", void 0);

function _define_property$3m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3m(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3m(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCategoryHierarchyChildDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3m(this, "id", void 0), _define_property$3m(this, "index", void 0);
    }
}
_ts_decorate$3m([
    dto({
        string: true
    }),
    _ts_metadata$3m("design:type", String)
], IntegrationCategoryHierarchyChildDTO.prototype, "id", void 0);
_ts_decorate$3m([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3m("design:type", Number)
], IntegrationCategoryHierarchyChildDTO.prototype, "index", void 0);
class IntegrationCategoryHierarchyGroupItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3m(this, "type", void 0), _define_property$3m(this, "id", void 0), _define_property$3m(this, "name", void 0), _define_property$3m(this, "index", void 0), _define_property$3m(this, "children", void 0);
    }
}
_ts_decorate$3m([
    dto({
        equals: CategoryHierarchyItemType.GROUP
    }),
    _ts_metadata$3m("design:type", typeof CategoryHierarchyItemType === "undefined" || typeof CategoryHierarchyItemType.GROUP === "undefined" ? Object : CategoryHierarchyItemType.GROUP)
], IntegrationCategoryHierarchyGroupItemDTO.prototype, "type", void 0);
_ts_decorate$3m([
    dto({
        string: true
    }),
    _ts_metadata$3m("design:type", String)
], IntegrationCategoryHierarchyGroupItemDTO.prototype, "id", void 0);
_ts_decorate$3m([
    dto({
        string: true
    }),
    _ts_metadata$3m("design:type", String)
], IntegrationCategoryHierarchyGroupItemDTO.prototype, "name", void 0);
_ts_decorate$3m([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3m("design:type", Number)
], IntegrationCategoryHierarchyGroupItemDTO.prototype, "index", void 0);
_ts_decorate$3m([
    dto({
        array: true,
        object: {
            type: IntegrationCategoryHierarchyChildDTO
        }
    }),
    _ts_metadata$3m("design:type", Array)
], IntegrationCategoryHierarchyGroupItemDTO.prototype, "children", void 0);
class IntegrationCategoryHierarchyCategoryItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3m(this, "type", void 0), _define_property$3m(this, "id", void 0), _define_property$3m(this, "index", void 0);
    }
}
_ts_decorate$3m([
    dto({
        equals: CategoryHierarchyItemType.CATEGORY
    }),
    _ts_metadata$3m("design:type", typeof CategoryHierarchyItemType === "undefined" || typeof CategoryHierarchyItemType.CATEGORY === "undefined" ? Object : CategoryHierarchyItemType.CATEGORY)
], IntegrationCategoryHierarchyCategoryItemDTO.prototype, "type", void 0);
_ts_decorate$3m([
    dto({
        string: true
    }),
    _ts_metadata$3m("design:type", String)
], IntegrationCategoryHierarchyCategoryItemDTO.prototype, "id", void 0);
_ts_decorate$3m([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3m("design:type", Number)
], IntegrationCategoryHierarchyCategoryItemDTO.prototype, "index", void 0);
class IntegrationSetCategoryHierarchyDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3m(this, "items", void 0);
    }
}
_ts_decorate$3m([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: CategoryHierarchyItemType.GROUP,
                        value: IntegrationCategoryHierarchyGroupItemDTO
                    },
                    {
                        name: CategoryHierarchyItemType.CATEGORY,
                        value: IntegrationCategoryHierarchyCategoryItemDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3m("design:type", Array)
], IntegrationSetCategoryHierarchyDTO.prototype, "items", void 0);

function _define_property$3l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3l(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3l(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSetCategoryGroupImageDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3l(this, "id", void 0);
    }
}
_ts_decorate$3l([
    dto({
        string: true
    }),
    _ts_metadata$3l("design:type", String)
], IntegrationSetCategoryGroupImageDTO.prototype, "id", void 0);

function _define_property$3k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3k(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3k(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Общая часть входящего блока; им же целиком принимаются active-orders и bonus-card.
class IntegrationContentBlockDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3k(this, "id", void 0), // Страница — любая строка: набор страниц не регламентирован, приложение запрашивает свою по имени.
        _define_property$3k(this, "page", void 0), _define_property$3k(this, "index", void 0), _define_property$3k(this, "title", void 0);
    }
}
_ts_decorate$3k([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3k("design:type", String)
], IntegrationContentBlockDTO.prototype, "id", void 0);
_ts_decorate$3k([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3k("design:type", String)
], IntegrationContentBlockDTO.prototype, "page", void 0);
_ts_decorate$3k([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$3k("design:type", Number)
], IntegrationContentBlockDTO.prototype, "index", void 0);
_ts_decorate$3k([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$3k("design:type", Object)
], IntegrationContentBlockDTO.prototype, "title", void 0);

function _define_property$3j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3j(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3j(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationContentBlockSearchDTO extends IntegrationContentBlockDTO {
    constructor(...args){
        super(...args), _define_property$3j(this, "placeholders", void 0);
    }
}
_ts_decorate$3j([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$3j("design:type", Array)
], IntegrationContentBlockSearchDTO.prototype, "placeholders", void 0);

function _define_property$3i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3i(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3i(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationContentBlockBannerActionProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3i(this, "type", void 0), _define_property$3i(this, "productId", void 0);
    }
}
_ts_decorate$3i([
    dto({
        equals: ContentBlockBannerActionType.PRODUCT
    }),
    _ts_metadata$3i("design:type", typeof ContentBlockBannerActionType === "undefined" || typeof ContentBlockBannerActionType.PRODUCT === "undefined" ? Object : ContentBlockBannerActionType.PRODUCT)
], IntegrationContentBlockBannerActionProductDTO.prototype, "type", void 0);
_ts_decorate$3i([
    dto({
        string: true
    }),
    _ts_metadata$3i("design:type", String)
], IntegrationContentBlockBannerActionProductDTO.prototype, "productId", void 0);
class IntegrationContentBlockBannerActionProductsCatalogDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3i(this, "type", void 0), _define_property$3i(this, "description", void 0), _define_property$3i(this, "productIds", void 0);
    }
}
_ts_decorate$3i([
    dto({
        equals: ContentBlockBannerActionType.PRODUCTS_CATALOG
    }),
    _ts_metadata$3i("design:type", typeof ContentBlockBannerActionType === "undefined" || typeof ContentBlockBannerActionType.PRODUCTS_CATALOG === "undefined" ? Object : ContentBlockBannerActionType.PRODUCTS_CATALOG)
], IntegrationContentBlockBannerActionProductsCatalogDTO.prototype, "type", void 0);
_ts_decorate$3i([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$3i("design:type", Object)
], IntegrationContentBlockBannerActionProductsCatalogDTO.prototype, "description", void 0);
_ts_decorate$3i([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$3i("design:type", Array)
], IntegrationContentBlockBannerActionProductsCatalogDTO.prototype, "productIds", void 0);
class IntegrationContentBlockBannerActionHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3i(this, "type", void 0);
    }
}
_ts_decorate$3i([
    dto({
        equals: ContentBlockBannerActionType.HTML
    }),
    _ts_metadata$3i("design:type", typeof ContentBlockBannerActionType === "undefined" || typeof ContentBlockBannerActionType.HTML === "undefined" ? Object : ContentBlockBannerActionType.HTML)
], IntegrationContentBlockBannerActionHtmlDTO.prototype, "type", void 0);
class IntegrationContentBlockBannerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3i(this, "id", void 0), _define_property$3i(this, "name", void 0), _define_property$3i(this, "aspectRatio", void 0), _define_property$3i(this, "startDate", void 0), _define_property$3i(this, "endDate", void 0), _define_property$3i(this, "action", void 0);
    }
}
_ts_decorate$3i([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3i("design:type", String)
], IntegrationContentBlockBannerDTO.prototype, "id", void 0);
_ts_decorate$3i([
    dto({
        string: true
    }),
    _ts_metadata$3i("design:type", String)
], IntegrationContentBlockBannerDTO.prototype, "name", void 0);
_ts_decorate$3i([
    dto({
        enum: ContentBlockBannerAspectRatio
    }),
    _ts_metadata$3i("design:type", typeof ContentBlockBannerAspectRatio === "undefined" ? Object : ContentBlockBannerAspectRatio)
], IntegrationContentBlockBannerDTO.prototype, "aspectRatio", void 0);
_ts_decorate$3i([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$3i("design:type", Object)
], IntegrationContentBlockBannerDTO.prototype, "startDate", void 0);
_ts_decorate$3i([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$3i("design:type", Object)
], IntegrationContentBlockBannerDTO.prototype, "endDate", void 0);
_ts_decorate$3i([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: ContentBlockBannerActionType.PRODUCT,
                        value: IntegrationContentBlockBannerActionProductDTO
                    },
                    {
                        name: ContentBlockBannerActionType.PRODUCTS_CATALOG,
                        value: IntegrationContentBlockBannerActionProductsCatalogDTO
                    },
                    {
                        name: ContentBlockBannerActionType.HTML,
                        value: IntegrationContentBlockBannerActionHtmlDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3i("design:type", Object)
], IntegrationContentBlockBannerDTO.prototype, "action", void 0);
class IntegrationContentBlockBannersDTO extends IntegrationContentBlockDTO {
    constructor(...args){
        super(...args), _define_property$3i(this, "banners", void 0);
    }
}
_ts_decorate$3i([
    dto({
        array: true,
        object: {
            type: IntegrationContentBlockBannerDTO
        }
    }),
    _ts_metadata$3i("design:type", Array)
], IntegrationContentBlockBannersDTO.prototype, "banners", void 0);

function _define_property$3h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3h(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3h(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Файл html для действия html приходит отдельным запросом shortcuts/html.
class IntegrationContentBlockShortcutActionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3h(this, "type", void 0);
    }
}
_ts_decorate$3h([
    dto({
        enum: ContentBlockShortcutActionType
    }),
    _ts_metadata$3h("design:type", typeof ContentBlockShortcutActionType === "undefined" ? Object : ContentBlockShortcutActionType)
], IntegrationContentBlockShortcutActionDTO.prototype, "type", void 0);
class IntegrationContentBlockShortcutDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3h(this, "id", void 0), _define_property$3h(this, "name", void 0), _define_property$3h(this, "icon", void 0), _define_property$3h(this, "action", void 0);
    }
}
_ts_decorate$3h([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3h("design:type", String)
], IntegrationContentBlockShortcutDTO.prototype, "id", void 0);
_ts_decorate$3h([
    dto({
        string: true
    }),
    _ts_metadata$3h("design:type", String)
], IntegrationContentBlockShortcutDTO.prototype, "name", void 0);
_ts_decorate$3h([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$3h("design:type", Object)
], IntegrationContentBlockShortcutDTO.prototype, "icon", void 0);
_ts_decorate$3h([
    dto({
        object: {
            type: IntegrationContentBlockShortcutActionDTO
        }
    }),
    _ts_metadata$3h("design:type", typeof IntegrationContentBlockShortcutActionDTO === "undefined" ? Object : IntegrationContentBlockShortcutActionDTO)
], IntegrationContentBlockShortcutDTO.prototype, "action", void 0);
class IntegrationContentBlockShortcutsDTO extends IntegrationContentBlockDTO {
    constructor(...args){
        super(...args), _define_property$3h(this, "style", void 0), _define_property$3h(this, "shortcuts", void 0);
    }
}
_ts_decorate$3h([
    dto({
        enum: ContentBlockShortcutsStyle
    }),
    _ts_metadata$3h("design:type", typeof ContentBlockShortcutsStyle === "undefined" ? Object : ContentBlockShortcutsStyle)
], IntegrationContentBlockShortcutsDTO.prototype, "style", void 0);
_ts_decorate$3h([
    dto({
        array: true,
        object: {
            type: IntegrationContentBlockShortcutDTO
        }
    }),
    _ts_metadata$3h("design:type", Array)
], IntegrationContentBlockShortcutsDTO.prototype, "shortcuts", void 0);

function _define_property$3g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3g(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3g(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationContentBlockProductsDTO extends IntegrationContentBlockDTO {
    constructor(...args){
        super(...args), _define_property$3g(this, "view", void 0), _define_property$3g(this, "productIds", void 0);
    }
}
_ts_decorate$3g([
    dto({
        enum: ContentBlockProductsView
    }),
    _ts_metadata$3g("design:type", typeof ContentBlockProductsView === "undefined" ? Object : ContentBlockProductsView)
], IntegrationContentBlockProductsDTO.prototype, "view", void 0);
_ts_decorate$3g([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$3g("design:type", Array)
], IntegrationContentBlockProductsDTO.prototype, "productIds", void 0);

function _define_property$3f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3f(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3f(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Поля multipart-запросов: сам файл идёт отдельной частью.
class IntegrationContentBlockBannerMediaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3f(this, "blockId", void 0), _define_property$3f(this, "bannerId", void 0), _define_property$3f(this, "type", void 0);
    }
}
_ts_decorate$3f([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3f("design:type", String)
], IntegrationContentBlockBannerMediaDTO.prototype, "blockId", void 0);
_ts_decorate$3f([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3f("design:type", String)
], IntegrationContentBlockBannerMediaDTO.prototype, "bannerId", void 0);
_ts_decorate$3f([
    dto({
        enum: ContentBlockBannerMediaType
    }),
    _ts_metadata$3f("design:type", typeof ContentBlockBannerMediaType === "undefined" ? Object : ContentBlockBannerMediaType)
], IntegrationContentBlockBannerMediaDTO.prototype, "type", void 0);
class IntegrationContentBlockBannerHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3f(this, "blockId", void 0), _define_property$3f(this, "bannerId", void 0);
    }
}
_ts_decorate$3f([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3f("design:type", String)
], IntegrationContentBlockBannerHtmlDTO.prototype, "blockId", void 0);
_ts_decorate$3f([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3f("design:type", String)
], IntegrationContentBlockBannerHtmlDTO.prototype, "bannerId", void 0);
class IntegrationContentBlockShortcutHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3f(this, "blockId", void 0), _define_property$3f(this, "shortcutId", void 0);
    }
}
_ts_decorate$3f([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3f("design:type", String)
], IntegrationContentBlockShortcutHtmlDTO.prototype, "blockId", void 0);
_ts_decorate$3f([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3f("design:type", String)
], IntegrationContentBlockShortcutHtmlDTO.prototype, "shortcutId", void 0);

function _define_property$3e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3e(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3e(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteContentBlockDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3e(this, "id", void 0);
    }
}
_ts_decorate$3e([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3e("design:type", String)
], IntegrationDeleteContentBlockDTO.prototype, "id", void 0);

function _define_property$3d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3d(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3d(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class DefaultConnectionUtilRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3d(this, "userId", void 0), _define_property$3d(this, "data", void 0);
    }
}
_ts_decorate$3d([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$3d("design:type", String)
], DefaultConnectionUtilRequestDTO.prototype, "userId", void 0);
_ts_decorate$3d([
    dto({
        rawObject: true
    }),
    _ts_metadata$3d("design:type", Object)
], DefaultConnectionUtilRequestDTO.prototype, "data", void 0);

function _define_property$3c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3c(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3c(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class DefaultConnectionUtilResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3c(this, "data", void 0);
    }
}
_ts_decorate$3c([
    dto({
        rawObject: true
    }),
    _ts_metadata$3c("design:type", Object)
], DefaultConnectionUtilResponseDTO.prototype, "data", void 0);

class IntegrationDefaultConnectionUtilRequestDTO extends DefaultConnectionUtilRequestDTO {
}
class IntegrationDefaultConnectionUtilResponseDTO extends DefaultConnectionUtilResponseDTO {
}

function _define_property$3b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3b(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CameraScanSourceType = /*#__PURE__*/ function(CameraScanSourceType) {
    CameraScanSourceType["HOME_INFO_LINK"] = "home_info_link";
    CameraScanSourceType["CONTENT_BLOCK_SHORTCUT"] = "content_block_shortcut";
    // Скан продавца без камеры (POST merchant-employee/shift/state/scan): ставит только сервер.
    CameraScanSourceType["MERCHANT_EMPLOYEE"] = "merchant_employee";
    return CameraScanSourceType;
}({});
// Источники, которые присылает клиент: источник продавца из приложения не подделать.
const CameraScanClientSourceType = {
    HOME_INFO_LINK: "home_info_link",
    CONTENT_BLOCK_SHORTCUT: "content_block_shortcut"
};
class CameraScanSourceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3b(this, "type", void 0), _define_property$3b(this, "id", void 0);
    }
}
_ts_decorate$3b([
    dto({
        enum: CameraScanClientSourceType
    }),
    _ts_metadata$3b("design:type", Object)
], CameraScanSourceDTO.prototype, "type", void 0);
_ts_decorate$3b([
    dto({
        string: true
    }),
    _ts_metadata$3b("design:type", String)
], CameraScanSourceDTO.prototype, "id", void 0);
class CameraScanRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3b(this, "source", void 0), _define_property$3b(this, "data", void 0);
    }
}
_ts_decorate$3b([
    dto({
        object: {
            type: CameraScanSourceDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$3b("design:type", Object)
], CameraScanRequestDTO.prototype, "source", void 0);
_ts_decorate$3b([
    dto({
        rawObject: true
    }),
    _ts_metadata$3b("design:type", Object)
], CameraScanRequestDTO.prototype, "data", void 0);

function _define_property$3a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3a(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3a(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CameraScanResultType = /*#__PURE__*/ function(CameraScanResultType) {
    CameraScanResultType["HTML"] = "html";
    return CameraScanResultType;
}({});
class CameraScanHtmlResultDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3a(this, "type", void 0), _define_property$3a(this, "data", void 0);
    }
}
_ts_decorate$3a([
    dto({
        equals: "html"
    }),
    _ts_metadata$3a("design:type", typeof CameraScanResultType === "undefined" || "string" === "undefined" ? Object : "html")
], CameraScanHtmlResultDTO.prototype, "type", void 0);
_ts_decorate$3a([
    dto({
        string: true
    }),
    _ts_metadata$3a("design:type", String)
], CameraScanHtmlResultDTO.prototype, "data", void 0);
class CameraScanResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$3a(this, "data", void 0);
    }
}
_ts_decorate$3a([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "html",
                        value: CameraScanHtmlResultDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$3a("design:type", typeof CameraScanHtmlResultDTO === "undefined" ? Object : CameraScanHtmlResultDTO)
], CameraScanResponseDTO.prototype, "data", void 0);

function _define_property$39(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$39(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$39(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCameraScanSourceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$39(this, "type", void 0), // Код ссылки на главной; у merchant_employee нет.
        _define_property$39(this, "id", void 0);
    }
}
_ts_decorate$39([
    dto({
        enum: CameraScanSourceType
    }),
    _ts_metadata$39("design:type", typeof CameraScanSourceType === "undefined" ? Object : CameraScanSourceType)
], IntegrationCameraScanSourceDTO.prototype, "type", void 0);
_ts_decorate$39([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$39("design:type", Object)
], IntegrationCameraScanSourceDTO.prototype, "id", void 0);
class IntegrationHomeInfoLinkCameraScanRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$39(this, "source", void 0), _define_property$39(this, "userId", void 0), // Ид и время скана — сервера: по ним 1С ведёт журнал действий сотрудника.
        _define_property$39(this, "scanId", void 0), _define_property$39(this, "scannedAt", void 0), _define_property$39(this, "data", void 0);
    }
}
_ts_decorate$39([
    dto({
        object: {
            type: IntegrationCameraScanSourceDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$39("design:type", Object)
], IntegrationHomeInfoLinkCameraScanRequestDTO.prototype, "source", void 0);
_ts_decorate$39([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$39("design:type", Object)
], IntegrationHomeInfoLinkCameraScanRequestDTO.prototype, "userId", void 0);
_ts_decorate$39([
    dto({
        string: true
    }),
    _ts_metadata$39("design:type", String)
], IntegrationHomeInfoLinkCameraScanRequestDTO.prototype, "scanId", void 0);
_ts_decorate$39([
    dto({
        date: true
    }),
    _ts_metadata$39("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationHomeInfoLinkCameraScanRequestDTO.prototype, "scannedAt", void 0);
_ts_decorate$39([
    dto({
        rawObject: true
    }),
    _ts_metadata$39("design:type", Object)
], IntegrationHomeInfoLinkCameraScanRequestDTO.prototype, "data", void 0);

function _define_property$38(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$38(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$38(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationHomeInfoLinkCameraScanHtmlResultDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$38(this, "type", void 0), _define_property$38(this, "data", void 0);
    }
}
_ts_decorate$38([
    dto({
        equals: CameraScanResultType.HTML
    }),
    _ts_metadata$38("design:type", typeof CameraScanResultType === "undefined" || typeof CameraScanResultType.HTML === "undefined" ? Object : CameraScanResultType.HTML)
], IntegrationHomeInfoLinkCameraScanHtmlResultDTO.prototype, "type", void 0);
_ts_decorate$38([
    dto({
        string: true
    }),
    _ts_metadata$38("design:type", String)
], IntegrationHomeInfoLinkCameraScanHtmlResultDTO.prototype, "data", void 0);
class IntegrationHomeInfoLinkCameraScanResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$38(this, "data", void 0);
    }
}
_ts_decorate$38([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: CameraScanResultType.HTML,
                        value: IntegrationHomeInfoLinkCameraScanHtmlResultDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$38("design:type", typeof IntegrationHomeInfoLinkCameraScanHtmlResultDTO === "undefined" ? Object : IntegrationHomeInfoLinkCameraScanHtmlResultDTO)
], IntegrationHomeInfoLinkCameraScanResponseDTO.prototype, "data", void 0);

function _define_property$37(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$37(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$37(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteHomeInfoLinkDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$37(this, "id", void 0);
    }
}
_ts_decorate$37([
    dto({
        string: true
    }),
    _ts_metadata$37("design:type", String)
], IntegrationDeleteHomeInfoLinkDTO.prototype, "id", void 0);

function _define_property$36(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$36(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$36(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeHomeInfoLinkDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$36(this, "id", void 0), _define_property$36(this, "index", void 0);
    }
}
_ts_decorate$36([
    dto({
        string: true
    }),
    _ts_metadata$36("design:type", String)
], IntegrationInitializeHomeInfoLinkDTO.prototype, "id", void 0);
_ts_decorate$36([
    dto({
        optional: {
            allowNull: false
        },
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$36("design:type", Object)
], IntegrationInitializeHomeInfoLinkDTO.prototype, "index", void 0);

var HomeInfoLinkType = /*#__PURE__*/ function(HomeInfoLinkType) {
    HomeInfoLinkType["HTML"] = "html";
    HomeInfoLinkType["CATALOG"] = "catalog";
    HomeInfoLinkType["SHOPS"] = "shops";
    HomeInfoLinkType["CAMERA_SCAN"] = "camera_scan";
    return HomeInfoLinkType;
}({});

function _define_property$35(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$35(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$35(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class HomeInfoLinkHTMLResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$35(this, "name", void 0), _define_property$35(this, "file", void 0);
    }
}
_ts_decorate$35([
    dto({
        string: true
    }),
    _ts_metadata$35("design:type", String)
], HomeInfoLinkHTMLResponseDTO.prototype, "name", void 0);
_ts_decorate$35([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$35("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], HomeInfoLinkHTMLResponseDTO.prototype, "file", void 0);

function _define_property$34(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$34(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$34(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class HomeInfoLinkResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$34(this, "id", void 0), _define_property$34(this, "name", void 0), _define_property$34(this, "type", void 0), _define_property$34(this, "icon", void 0);
    }
}
_ts_decorate$34([
    dto({
        string: true
    }),
    _ts_metadata$34("design:type", String)
], HomeInfoLinkResponseDTO.prototype, "id", void 0);
_ts_decorate$34([
    dto({
        string: true
    }),
    _ts_metadata$34("design:type", String)
], HomeInfoLinkResponseDTO.prototype, "name", void 0);
_ts_decorate$34([
    dto({
        enum: HomeInfoLinkType
    }),
    _ts_metadata$34("design:type", typeof HomeInfoLinkType === "undefined" ? Object : HomeInfoLinkType)
], HomeInfoLinkResponseDTO.prototype, "type", void 0);
_ts_decorate$34([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$34("design:type", Object)
], HomeInfoLinkResponseDTO.prototype, "icon", void 0);

function _define_property$33(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$33(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$33(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeHomeInfoLinkCatalogDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$33(this, "type", void 0), _define_property$33(this, "iconRaw", void 0);
    }
}
_ts_decorate$33([
    dto({
        equals: HomeInfoLinkType.CATALOG
    }),
    _ts_metadata$33("design:type", typeof HomeInfoLinkType === "undefined" || typeof HomeInfoLinkType.CATALOG === "undefined" ? Object : HomeInfoLinkType.CATALOG)
], IntegrationInitializeHomeInfoLinkCatalogDTO.prototype, "type", void 0);
_ts_decorate$33([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$33("design:type", Object)
], IntegrationInitializeHomeInfoLinkCatalogDTO.prototype, "iconRaw", void 0);
class IntegrationInitializeHomeInfoLinkHtmlDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$33(this, "type", void 0), _define_property$33(this, "iconRaw", void 0), _define_property$33(this, "name", void 0);
    }
}
_ts_decorate$33([
    dto({
        equals: HomeInfoLinkType.HTML
    }),
    _ts_metadata$33("design:type", typeof HomeInfoLinkType === "undefined" || typeof HomeInfoLinkType.HTML === "undefined" ? Object : HomeInfoLinkType.HTML)
], IntegrationInitializeHomeInfoLinkHtmlDTO.prototype, "type", void 0);
_ts_decorate$33([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$33("design:type", Object)
], IntegrationInitializeHomeInfoLinkHtmlDTO.prototype, "iconRaw", void 0);
_ts_decorate$33([
    dto({
        string: true
    }),
    _ts_metadata$33("design:type", String)
], IntegrationInitializeHomeInfoLinkHtmlDTO.prototype, "name", void 0);
class IntegrationInitializeHomeInfoLinkShopsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$33(this, "type", void 0), _define_property$33(this, "iconRaw", void 0);
    }
}
_ts_decorate$33([
    dto({
        equals: HomeInfoLinkType.SHOPS
    }),
    _ts_metadata$33("design:type", typeof HomeInfoLinkType === "undefined" || typeof HomeInfoLinkType.SHOPS === "undefined" ? Object : HomeInfoLinkType.SHOPS)
], IntegrationInitializeHomeInfoLinkShopsDTO.prototype, "type", void 0);
_ts_decorate$33([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$33("design:type", Object)
], IntegrationInitializeHomeInfoLinkShopsDTO.prototype, "iconRaw", void 0);
class IntegrationInitializeHomeInfoLinkCameraScanDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$33(this, "type", void 0), _define_property$33(this, "iconRaw", void 0), _define_property$33(this, "name", void 0);
    }
}
_ts_decorate$33([
    dto({
        equals: HomeInfoLinkType.CAMERA_SCAN
    }),
    _ts_metadata$33("design:type", typeof HomeInfoLinkType === "undefined" || typeof HomeInfoLinkType.CAMERA_SCAN === "undefined" ? Object : HomeInfoLinkType.CAMERA_SCAN)
], IntegrationInitializeHomeInfoLinkCameraScanDTO.prototype, "type", void 0);
_ts_decorate$33([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$33("design:type", Object)
], IntegrationInitializeHomeInfoLinkCameraScanDTO.prototype, "iconRaw", void 0);
_ts_decorate$33([
    dto({
        string: true
    }),
    _ts_metadata$33("design:type", String)
], IntegrationInitializeHomeInfoLinkCameraScanDTO.prototype, "name", void 0);
class IntegrationInitializeHomeInfoLinkDataDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$33(this, "homeInfoLinkId", void 0), _define_property$33(this, "info", void 0);
    }
}
_ts_decorate$33([
    dto({
        string: true
    }),
    _ts_metadata$33("design:type", String)
], IntegrationInitializeHomeInfoLinkDataDTO.prototype, "homeInfoLinkId", void 0);
_ts_decorate$33([
    dto({
        name: "info__json",
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: HomeInfoLinkType.CATALOG,
                        value: IntegrationInitializeHomeInfoLinkCatalogDTO
                    },
                    {
                        name: HomeInfoLinkType.HTML,
                        value: IntegrationInitializeHomeInfoLinkHtmlDTO
                    },
                    {
                        name: HomeInfoLinkType.SHOPS,
                        value: IntegrationInitializeHomeInfoLinkShopsDTO
                    },
                    {
                        name: HomeInfoLinkType.CAMERA_SCAN,
                        value: IntegrationInitializeHomeInfoLinkCameraScanDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$33("design:type", Object)
], IntegrationInitializeHomeInfoLinkDataDTO.prototype, "info", void 0);

function _define_property$32(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$32(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$32(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteMerchantIntegrationDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$32(this, "type", void 0);
    }
}
_ts_decorate$32([
    dto({
        enum: MERCHANT_INTEGRATION_SELF_SETTABLE_TYPES
    }),
    _ts_metadata$32("design:type", typeof MerchantIntegrationSelfSettableType === "undefined" ? Object : MerchantIntegrationSelfSettableType)
], IntegrationDeleteMerchantIntegrationDTO.prototype, "type", void 0);

function _define_property$31(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$31(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$31(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetMerchantApiVersionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$31(this, "currentMerchantVersion", void 0), _define_property$31(this, "apiActialVersion", void 0), _define_property$31(this, "apiOtherSupportedVersions", void 0);
    }
}
_ts_decorate$31([
    dto({
        string: true
    }),
    _ts_metadata$31("design:type", String)
], IntegrationGetMerchantApiVersionDTO.prototype, "currentMerchantVersion", void 0);
_ts_decorate$31([
    dto({
        string: true
    }),
    _ts_metadata$31("design:type", String)
], IntegrationGetMerchantApiVersionDTO.prototype, "apiActialVersion", void 0);
_ts_decorate$31([
    dto({
        string: true,
        array: true
    }),
    _ts_metadata$31("design:type", Array)
], IntegrationGetMerchantApiVersionDTO.prototype, "apiOtherSupportedVersions", void 0);

function _define_property$30(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$30(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$30(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetMerchantIntegrationsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$30(this, "types", void 0);
    }
}
_ts_decorate$30([
    dto({
        array: true,
        enum: MerchantIntegrationTypes
    }),
    _ts_metadata$30("design:type", Array)
], IntegrationGetMerchantIntegrationsDTO.prototype, "types", void 0);

function _define_property$2$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2$(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2$(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantApiVersionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2$(this, "version", void 0);
    }
}
_ts_decorate$2$([
    dto({
        string: true
    }),
    _ts_metadata$2$("design:type", String)
], IntegrationInitializeMerchantApiVersionDTO.prototype, "version", void 0);

function _define_property$2_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2_(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2_(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantCustomerAuthRequiredLegalFilesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2_(this, "requiredToAuthLegalFileTypes", void 0);
    }
}
_ts_decorate$2_([
    dto({
        enum: MerchantMetaLegalFileType,
        array: true
    }),
    _ts_metadata$2_("design:type", Array)
], IntegrationInitializeMerchantCustomerAuthRequiredLegalFilesDTO.prototype, "requiredToAuthLegalFileTypes", void 0);

function _define_property$2Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2Z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2Z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantMetaLinksDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2Z(this, "type", void 0), _define_property$2Z(this, "text", void 0), _define_property$2Z(this, "url", void 0);
    }
}
_ts_decorate$2Z([
    dto({
        enum: MerchantMetaLinkType
    }),
    _ts_metadata$2Z("design:type", typeof MerchantMetaLinkType === "undefined" ? Object : MerchantMetaLinkType)
], IntegrationInitializeMerchantMetaLinksDTO.prototype, "type", void 0);
_ts_decorate$2Z([
    dto({
        string: true
    }),
    _ts_metadata$2Z("design:type", String)
], IntegrationInitializeMerchantMetaLinksDTO.prototype, "text", void 0);
_ts_decorate$2Z([
    dto({
        url: true
    }),
    _ts_metadata$2Z("design:type", String)
], IntegrationInitializeMerchantMetaLinksDTO.prototype, "url", void 0);
class IntegrationInitializeMerchantMetaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2Z(this, "phones", void 0), _define_property$2Z(this, "links", void 0);
    }
}
_ts_decorate$2Z([
    dto({
        optional: true,
        array: true,
        phone: true
    }),
    _ts_metadata$2Z("design:type", Object)
], IntegrationInitializeMerchantMetaDTO.prototype, "phones", void 0);
_ts_decorate$2Z([
    dto({
        optional: true,
        array: true,
        object: {
            type: IntegrationInitializeMerchantMetaLinksDTO
        }
    }),
    _ts_metadata$2Z("design:type", Object)
], IntegrationInitializeMerchantMetaDTO.prototype, "links", void 0);

function _define_property$2Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2Y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2Y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantMetaLegalFileDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2Y(this, "type", void 0), _define_property$2Y(this, "date", void 0);
    }
}
_ts_decorate$2Y([
    dto({
        enum: MerchantMetaLegalFileType
    }),
    _ts_metadata$2Y("design:type", typeof MerchantMetaLegalFileType === "undefined" ? Object : MerchantMetaLegalFileType)
], IntegrationInitializeMerchantMetaLegalFileDTO.prototype, "type", void 0);
_ts_decorate$2Y([
    dto({
        date: true,
        optional: true
    }),
    _ts_metadata$2Y("design:type", Object)
], IntegrationInitializeMerchantMetaLegalFileDTO.prototype, "date", void 0);

function _define_property$2X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2X(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2X(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSetMerchantIntegrationDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2X(this, "integration", void 0);
    }
}
_ts_decorate$2X([
    dto({
        object: {
            discriminator: merchantIntegrationSelfSettableValueDiscriminator
        }
    }),
    _ts_metadata$2X("design:type", typeof MerchantIntegrationSelfSettableValue === "undefined" ? Object : MerchantIntegrationSelfSettableValue)
], IntegrationSetMerchantIntegrationDTO.prototype, "integration", void 0);

function _define_property$2W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2W(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2W(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Ответ — MerchantEmployeeActivityScansDTO: формат один у 1С и у приложения. Какие сканы попали в
// консультацию, 1С решает по своему журналу действий сотрудника.
class IntegrationGetMerchantEmployeeActivityScansRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Ид сотрудника в самой 1С (user_external_ids.default).
        _define_property$2W(this, "userId", void 0), _define_property$2W(this, "activityId", void 0), // Начало консультации.
        _define_property$2W(this, "from", void 0);
    }
}
_ts_decorate$2W([
    dto({
        string: true
    }),
    _ts_metadata$2W("design:type", String)
], IntegrationGetMerchantEmployeeActivityScansRequestDTO.prototype, "userId", void 0);
_ts_decorate$2W([
    dto({
        string: true
    }),
    _ts_metadata$2W("design:type", String)
], IntegrationGetMerchantEmployeeActivityScansRequestDTO.prototype, "activityId", void 0);
_ts_decorate$2W([
    dto({
        date: true
    }),
    _ts_metadata$2W("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationGetMerchantEmployeeActivityScansRequestDTO.prototype, "from", void 0);

function _define_property$2V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2V(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2V(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Ответ — MerchantEmployeeDocumentsResponseDTO: формат один у 1С и у приложения. Магазин сотрудника,
// тип документа (по исходу) и глубину просмотра 1С определяет сама.
class IntegrationGetMerchantEmployeeDocumentsRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Ид сотрудника в самой 1С (user_external_ids.default).
        _define_property$2V(this, "userId", void 0), _define_property$2V(this, "outcomeId", void 0);
    }
}
_ts_decorate$2V([
    dto({
        string: true
    }),
    _ts_metadata$2V("design:type", String)
], IntegrationGetMerchantEmployeeDocumentsRequestDTO.prototype, "userId", void 0);
_ts_decorate$2V([
    dto({
        string: true
    }),
    _ts_metadata$2V("design:type", String)
], IntegrationGetMerchantEmployeeDocumentsRequestDTO.prototype, "outcomeId", void 0);

function _define_property$2U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2U(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2U(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantEmployeeShiftDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2U(this, "id", void 0), _define_property$2U(this, "openedAt", void 0);
    }
}
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeShiftDTO.prototype, "id", void 0);
_ts_decorate$2U([
    dto({
        date: true
    }),
    _ts_metadata$2U("design:type", typeof Date === "undefined" ? Object : Date)
], MerchantEmployeeShiftDTO.prototype, "openedAt", void 0);
// Текущее пребывание сотрудника в состоянии. Имя состояния — снимок на момент входа в него.
class MerchantEmployeeCurrentActivityDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2U(this, "activityId", void 0), _define_property$2U(this, "stateId", void 0), _define_property$2U(this, "stateName", void 0), _define_property$2U(this, "isBase", void 0), _define_property$2U(this, "startedAt", void 0);
    }
}
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeCurrentActivityDTO.prototype, "activityId", void 0);
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeCurrentActivityDTO.prototype, "stateId", void 0);
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeCurrentActivityDTO.prototype, "stateName", void 0);
_ts_decorate$2U([
    dto({
        boolean: true
    }),
    _ts_metadata$2U("design:type", Boolean)
], MerchantEmployeeCurrentActivityDTO.prototype, "isBase", void 0);
_ts_decorate$2U([
    dto({
        date: true
    }),
    _ts_metadata$2U("design:type", typeof Date === "undefined" ? Object : Date)
], MerchantEmployeeCurrentActivityDTO.prototype, "startedAt", void 0);
// Ответ GET shift и всех действий над сменой: действие сразу отдаёт новое состояние.
class MerchantEmployeeShiftStatusDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Нет — смена закрыта.
        _define_property$2U(this, "shift", void 0), // Нет — смена закрыта.
        _define_property$2U(this, "current", void 0);
    }
}
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: MerchantEmployeeShiftDTO
        }
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeShiftStatusDTO.prototype, "shift", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: MerchantEmployeeCurrentActivityDTO
        }
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeShiftStatusDTO.prototype, "current", void 0);
class MerchantEmployeeEnterStateRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2U(this, "stateId", void 0);
    }
}
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeEnterStateRequestDTO.prototype, "stateId", void 0);
// Ответ на поле анкеты: у select — optionId, у select с multiple — optionIds, у number — value число ≥ 0,
// у text — value строка. Тип value DTO не проверяет: он зависит от поля анкеты, и не тот тип — это
// questionnaireInvalid с именем поля.
class MerchantEmployeeQuestionnaireAnswerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2U(this, "fieldId", void 0), _define_property$2U(this, "optionId", void 0), _define_property$2U(this, "optionIds", void 0), _define_property$2U(this, "value", void 0);
    }
}
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeQuestionnaireAnswerDTO.prototype, "fieldId", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeQuestionnaireAnswerDTO.prototype, "optionId", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        array: {
            notEmpty: true
        },
        string: true
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeQuestionnaireAnswerDTO.prototype, "optionIds", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeQuestionnaireAnswerDTO.prototype, "value", void 0);
class MerchantEmployeeFilledQuestionnaireDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2U(this, "questionnaireId", void 0), _define_property$2U(this, "answers", void 0);
    }
}
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeFilledQuestionnaireDTO.prototype, "questionnaireId", void 0);
_ts_decorate$2U([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeQuestionnaireAnswerDTO
        }
    }),
    _ts_metadata$2U("design:type", Array)
], MerchantEmployeeFilledQuestionnaireDTO.prototype, "answers", void 0);
// Один результат консультации: продажа, скупка, отказ… Формат один в finish и в выгрузке смены в 1С.
class MerchantEmployeeResultDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2U(this, "outcomeId", void 0), _define_property$2U(this, "reasonId", void 0), // Id документа из кандидатов 1С (shift/state/documents): сервер сверяет его с тем же списком.
        _define_property$2U(this, "documentId", void 0), _define_property$2U(this, "comment", void 0), _define_property$2U(this, "questionnaires", void 0);
    }
}
_ts_decorate$2U([
    dto({
        string: true
    }),
    _ts_metadata$2U("design:type", String)
], MerchantEmployeeResultDTO.prototype, "outcomeId", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeResultDTO.prototype, "reasonId", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeResultDTO.prototype, "documentId", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeResultDTO.prototype, "comment", void 0);
_ts_decorate$2U([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: MerchantEmployeeFilledQuestionnaireDTO
        }
    }),
    _ts_metadata$2U("design:type", Object)
], MerchantEmployeeResultDTO.prototype, "questionnaires", void 0);
class MerchantEmployeeFinishStateRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Пустой — у состояния нет исходов.
        _define_property$2U(this, "results", void 0);
    }
}
_ts_decorate$2U([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeResultDTO
        }
    }),
    _ts_metadata$2U("design:type", Array)
], MerchantEmployeeFinishStateRequestDTO.prototype, "results", void 0);

function _define_property$2T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2T(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2T(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Подбор изделий по анкете «Что искал»: продавец заполняет ту же анкету, что при отказе, 1С запускает схему
// подбора шаблона с ответами как параметрами и отдаёт готовый HTML (как карточку сканера) и цифры. Сервер
// проверяет анкету и проксирует: формат ответа один у 1С и у приложения.
var MerchantEmployeePickScope = /*#__PURE__*/ function(MerchantEmployeePickScope) {
    // Склады магазина продавца.
    MerchantEmployeePickScope["SHOP"] = "shop";
    // Все магазины сети.
    MerchantEmployeePickScope["NETWORK"] = "network";
    return MerchantEmployeePickScope;
}({});
class MerchantEmployeePickRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Анкета справочника с pick: true.
        _define_property$2T(this, "questionnaireId", void 0), _define_property$2T(this, "scope", void 0), // Как у анкеты результата: select — optionId, select с multiple — optionIds, number/text — value.
        _define_property$2T(this, "answers", void 0);
    }
}
_ts_decorate$2T([
    dto({
        string: true
    }),
    _ts_metadata$2T("design:type", String)
], MerchantEmployeePickRequestDTO.prototype, "questionnaireId", void 0);
_ts_decorate$2T([
    dto({
        enum: MerchantEmployeePickScope
    }),
    _ts_metadata$2T("design:type", String)
], MerchantEmployeePickRequestDTO.prototype, "scope", void 0);
_ts_decorate$2T([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeQuestionnaireAnswerDTO
        }
    }),
    _ts_metadata$2T("design:type", Array)
], MerchantEmployeePickRequestDTO.prototype, "answers", void 0);
class MerchantEmployeePickSummaryDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Сколько нашлось всего в выбранном контуре.
        _define_property$2T(this, "total", void 0), // Сколько строк в HTML: не больше лимита шаблона.
        _define_property$2T(this, "shown", void 0), // Сколько из найденного есть на складах магазина продавца.
        _define_property$2T(this, "inShop", void 0), // Только когда искали по магазину и не нашли: сколько есть по сети — чтобы предложить переключиться.
        _define_property$2T(this, "inNetwork", void 0), _define_property$2T(this, "truncated", void 0);
    }
}
_ts_decorate$2T([
    dto({
        number: true
    }),
    _ts_metadata$2T("design:type", Number)
], MerchantEmployeePickSummaryDTO.prototype, "total", void 0);
_ts_decorate$2T([
    dto({
        number: true
    }),
    _ts_metadata$2T("design:type", Number)
], MerchantEmployeePickSummaryDTO.prototype, "shown", void 0);
_ts_decorate$2T([
    dto({
        number: true
    }),
    _ts_metadata$2T("design:type", Number)
], MerchantEmployeePickSummaryDTO.prototype, "inShop", void 0);
_ts_decorate$2T([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$2T("design:type", Object)
], MerchantEmployeePickSummaryDTO.prototype, "inNetwork", void 0);
_ts_decorate$2T([
    dto({
        boolean: true
    }),
    _ts_metadata$2T("design:type", Boolean)
], MerchantEmployeePickSummaryDTO.prototype, "truncated", void 0);
class MerchantEmployeePickResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Готовая страница списка, как data у карточки сканера. Пусто по салону — тоже страница, а не пустая строка.
        _define_property$2T(this, "html", void 0), _define_property$2T(this, "summary", void 0);
    }
}
_ts_decorate$2T([
    dto({
        string: true
    }),
    MinLength(1, {
        message: dtoMessageIsNotEmptyString
    }),
    _ts_metadata$2T("design:type", String)
], MerchantEmployeePickResponseDTO.prototype, "html", void 0);
_ts_decorate$2T([
    dto({
        object: {
            type: MerchantEmployeePickSummaryDTO
        }
    }),
    _ts_metadata$2T("design:type", typeof MerchantEmployeePickSummaryDTO === "undefined" ? Object : MerchantEmployeePickSummaryDTO)
], MerchantEmployeePickResponseDTO.prototype, "summary", void 0);

function _define_property$2S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2S(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2S(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Подбор изделий по анкете (POST /v1/merchant-employee/pick). Ответ — IntegrationMerchantEmployeePickResponseDTO:
// список в формате приложения или отказ схемы подбора. Вызов синхронный: приложению нужен список.
class IntegrationMerchantEmployeePickRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Ид сотрудника в самой 1С (user_external_ids.default).
        _define_property$2S(this, "userId", void 0), // Только при открытой консультации: 1С привяжет подбор к ней в журнале действий.
        _define_property$2S(this, "activityId", void 0), _define_property$2S(this, "questionnaireId", void 0), _define_property$2S(this, "scope", void 0), // Проверенные сервером ответы в порядке полей анкеты.
        _define_property$2S(this, "answers", void 0);
    }
}
_ts_decorate$2S([
    dto({
        string: true
    }),
    _ts_metadata$2S("design:type", String)
], IntegrationMerchantEmployeePickRequestDTO.prototype, "userId", void 0);
_ts_decorate$2S([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2S("design:type", Object)
], IntegrationMerchantEmployeePickRequestDTO.prototype, "activityId", void 0);
_ts_decorate$2S([
    dto({
        string: true
    }),
    _ts_metadata$2S("design:type", String)
], IntegrationMerchantEmployeePickRequestDTO.prototype, "questionnaireId", void 0);
_ts_decorate$2S([
    dto({
        enum: MerchantEmployeePickScope
    }),
    _ts_metadata$2S("design:type", typeof MerchantEmployeePickScope === "undefined" ? Object : MerchantEmployeePickScope)
], IntegrationMerchantEmployeePickRequestDTO.prototype, "scope", void 0);
_ts_decorate$2S([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeQuestionnaireAnswerDTO
        }
    }),
    _ts_metadata$2S("design:type", Array)
], IntegrationMerchantEmployeePickRequestDTO.prototype, "answers", void 0);

function _define_property$2R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2R(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2R(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Исход подбора 1С отдаёт всегда 200: отказ схемы подбора — обычный ответ, а не ошибка запроса.
var IntegrationMerchantEmployeePickStatus = /*#__PURE__*/ function(IntegrationMerchantEmployeePickStatus) {
    IntegrationMerchantEmployeePickStatus["SUCCESS"] = "success";
    // Ответ на поле не подошёл схеме подбора.
    IntegrationMerchantEmployeePickStatus["QUESTIONNAIRE_INVALID"] = "questionnaireInvalid";
    // У шаблона анкеты нет схемы подбора — справочник у сервера устарел.
    IntegrationMerchantEmployeePickStatus["QUESTIONNAIRE_NOT_FOR_PICK"] = "questionnaireNotForPick";
    // Схема подбора не уложилась в свой лимит времени.
    IntegrationMerchantEmployeePickStatus["PICK_TIMEOUT"] = "pickTimeout";
    return IntegrationMerchantEmployeePickStatus;
}({});
// Список — в том же формате, что у приложения: сервер отдаёт его как есть.
class IntegrationMerchantEmployeePickSuccessDTO extends MerchantEmployeePickResponseDTO {
    constructor(...args){
        super(...args), _define_property$2R(this, "status", void 0);
    }
}
_ts_decorate$2R([
    dto({
        equals: "success"
    }),
    _ts_metadata$2R("design:type", typeof IntegrationMerchantEmployeePickStatus === "undefined" || "string" === "undefined" ? Object : "success")
], IntegrationMerchantEmployeePickSuccessDTO.prototype, "status", void 0);
class IntegrationMerchantEmployeePickQuestionnaireInvalidDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2R(this, "status", void 0), _define_property$2R(this, "fieldId", void 0);
    }
}
_ts_decorate$2R([
    dto({
        equals: "questionnaireInvalid"
    }),
    _ts_metadata$2R("design:type", typeof IntegrationMerchantEmployeePickStatus === "undefined" || "string" === "undefined" ? Object : "questionnaireInvalid")
], IntegrationMerchantEmployeePickQuestionnaireInvalidDTO.prototype, "status", void 0);
_ts_decorate$2R([
    dto({
        string: true
    }),
    _ts_metadata$2R("design:type", String)
], IntegrationMerchantEmployeePickQuestionnaireInvalidDTO.prototype, "fieldId", void 0);
class IntegrationMerchantEmployeePickNotForPickDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2R(this, "status", void 0);
    }
}
_ts_decorate$2R([
    dto({
        equals: "questionnaireNotForPick"
    }),
    _ts_metadata$2R("design:type", typeof IntegrationMerchantEmployeePickStatus === "undefined" || "string" === "undefined" ? Object : "questionnaireNotForPick")
], IntegrationMerchantEmployeePickNotForPickDTO.prototype, "status", void 0);
class IntegrationMerchantEmployeePickTimeoutDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2R(this, "status", void 0);
    }
}
_ts_decorate$2R([
    dto({
        equals: "pickTimeout"
    }),
    _ts_metadata$2R("design:type", typeof IntegrationMerchantEmployeePickStatus === "undefined" || "string" === "undefined" ? Object : "pickTimeout")
], IntegrationMerchantEmployeePickTimeoutDTO.prototype, "status", void 0);
class IntegrationMerchantEmployeePickResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2R(this, "data", void 0);
    }
}
_ts_decorate$2R([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: IntegrationMerchantEmployeePickSuccessDTO
                    },
                    {
                        name: "questionnaireInvalid",
                        value: IntegrationMerchantEmployeePickQuestionnaireInvalidDTO
                    },
                    {
                        name: "questionnaireNotForPick",
                        value: IntegrationMerchantEmployeePickNotForPickDTO
                    },
                    {
                        name: "pickTimeout",
                        value: IntegrationMerchantEmployeePickTimeoutDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2R("design:type", Object)
], IntegrationMerchantEmployeePickResponseDTO.prototype, "data", void 0);

function _define_property$2Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2Q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2Q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Переходы сотрудника для журнала действий в 1С. Базовое состояние отдельных событий не получает:
// оно — промежуток между stateFinished и следующим stateEntered.
var IntegrationMerchantEmployeeActivityEvent = /*#__PURE__*/ function(IntegrationMerchantEmployeeActivityEvent) {
    IntegrationMerchantEmployeeActivityEvent["SHIFT_OPENED"] = "shiftOpened";
    IntegrationMerchantEmployeeActivityEvent["STATE_ENTERED"] = "stateEntered";
    IntegrationMerchantEmployeeActivityEvent["STATE_FINISHED"] = "stateFinished";
    IntegrationMerchantEmployeeActivityEvent["SHIFT_CLOSED"] = "shiftClosed";
    return IntegrationMerchantEmployeeActivityEvent;
}({});
class IntegrationSendMerchantEmployeeActivityActivityDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Тот же, что у активности в выгрузке смены: по нему 1С сверяет журнал с итоговой сменой.
        _define_property$2Q(this, "activityId", void 0), _define_property$2Q(this, "stateId", void 0), _define_property$2Q(this, "stateName", void 0), // Только у stateFinished: у stateEntered начало — это at события.
        _define_property$2Q(this, "startedAt", void 0), // Только у stateFinished, и нет — у состояния нет исходов. Формат выгрузки смены.
        _define_property$2Q(this, "results", void 0);
    }
}
_ts_decorate$2Q([
    dto({
        string: true
    }),
    _ts_metadata$2Q("design:type", String)
], IntegrationSendMerchantEmployeeActivityActivityDTO.prototype, "activityId", void 0);
_ts_decorate$2Q([
    dto({
        string: true
    }),
    _ts_metadata$2Q("design:type", String)
], IntegrationSendMerchantEmployeeActivityActivityDTO.prototype, "stateId", void 0);
_ts_decorate$2Q([
    dto({
        string: true
    }),
    _ts_metadata$2Q("design:type", String)
], IntegrationSendMerchantEmployeeActivityActivityDTO.prototype, "stateName", void 0);
_ts_decorate$2Q([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$2Q("design:type", Object)
], IntegrationSendMerchantEmployeeActivityActivityDTO.prototype, "startedAt", void 0);
_ts_decorate$2Q([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: MerchantEmployeeResultDTO
        }
    }),
    _ts_metadata$2Q("design:type", Object)
], IntegrationSendMerchantEmployeeActivityActivityDTO.prototype, "results", void 0);
// Событие перехода (POST /v1/merchant-employee/activity): уходит после коммита, без повторов —
// недошедшее 1С дозаполнит по выгрузке смены.
class IntegrationSendMerchantEmployeeActivityRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Ид сотрудника в самой 1С (user_external_ids.default).
        _define_property$2Q(this, "userId", void 0), _define_property$2Q(this, "shiftId", void 0), _define_property$2Q(this, "event", void 0), _define_property$2Q(this, "at", void 0), // Есть у stateEntered и stateFinished.
        _define_property$2Q(this, "activity", void 0);
    }
}
_ts_decorate$2Q([
    dto({
        string: true
    }),
    _ts_metadata$2Q("design:type", String)
], IntegrationSendMerchantEmployeeActivityRequestDTO.prototype, "userId", void 0);
_ts_decorate$2Q([
    dto({
        string: true
    }),
    _ts_metadata$2Q("design:type", String)
], IntegrationSendMerchantEmployeeActivityRequestDTO.prototype, "shiftId", void 0);
_ts_decorate$2Q([
    dto({
        enum: IntegrationMerchantEmployeeActivityEvent
    }),
    _ts_metadata$2Q("design:type", String)
], IntegrationSendMerchantEmployeeActivityRequestDTO.prototype, "event", void 0);
_ts_decorate$2Q([
    dto({
        date: true
    }),
    _ts_metadata$2Q("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationSendMerchantEmployeeActivityRequestDTO.prototype, "at", void 0);
_ts_decorate$2Q([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: IntegrationSendMerchantEmployeeActivityActivityDTO
        }
    }),
    _ts_metadata$2Q("design:type", Object)
], IntegrationSendMerchantEmployeeActivityRequestDTO.prototype, "activity", void 0);

function _define_property$2P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2P(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2P(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncMerchantEmployeeShiftActivityRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Тот же, что в событиях переходов (POST /v1/merchant-employee/activity): по нему 1С сверяет журнал
        // с итоговой сменой и дозаполняет недошедшее.
        _define_property$2P(this, "activityId", void 0), _define_property$2P(this, "stateId", void 0), _define_property$2P(this, "startedAt", void 0), _define_property$2P(this, "finishedAt", void 0), // Нет — у состояния нет исходов (базовое тоже). Id исходов, причин, полей и вариантов анкет — те, что
        // прислала 1С; имена-снимки ей не нужны.
        _define_property$2P(this, "results", void 0);
    }
}
_ts_decorate$2P([
    dto({
        string: true
    }),
    _ts_metadata$2P("design:type", String)
], IntegrationSyncMerchantEmployeeShiftActivityRequestDTO.prototype, "activityId", void 0);
_ts_decorate$2P([
    dto({
        string: true
    }),
    _ts_metadata$2P("design:type", String)
], IntegrationSyncMerchantEmployeeShiftActivityRequestDTO.prototype, "stateId", void 0);
_ts_decorate$2P([
    dto({
        date: true
    }),
    _ts_metadata$2P("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationSyncMerchantEmployeeShiftActivityRequestDTO.prototype, "startedAt", void 0);
_ts_decorate$2P([
    dto({
        date: true
    }),
    _ts_metadata$2P("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationSyncMerchantEmployeeShiftActivityRequestDTO.prototype, "finishedAt", void 0);
_ts_decorate$2P([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: MerchantEmployeeResultDTO
        }
    }),
    _ts_metadata$2P("design:type", Object)
], IntegrationSyncMerchantEmployeeShiftActivityRequestDTO.prototype, "results", void 0);
// Закрытая смена с состояниями сотрудника (POST /v2/merchant-employee/shift).
class IntegrationSyncMerchantEmployeeShiftV2RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Ид сотрудника в самой 1С (user_external_ids.default). Из наших идентификаторов здесь только ид
        // активностей: по ним 1С сверяет выгрузку с событиями переходов.
        _define_property$2P(this, "userId", void 0), _define_property$2P(this, "openedAt", void 0), _define_property$2P(this, "closedAt", void 0), _define_property$2P(this, "activities", void 0);
    }
}
_ts_decorate$2P([
    dto({
        string: true
    }),
    _ts_metadata$2P("design:type", String)
], IntegrationSyncMerchantEmployeeShiftV2RequestDTO.prototype, "userId", void 0);
_ts_decorate$2P([
    dto({
        date: true
    }),
    _ts_metadata$2P("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationSyncMerchantEmployeeShiftV2RequestDTO.prototype, "openedAt", void 0);
_ts_decorate$2P([
    dto({
        date: true
    }),
    _ts_metadata$2P("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationSyncMerchantEmployeeShiftV2RequestDTO.prototype, "closedAt", void 0);
_ts_decorate$2P([
    dto({
        array: true,
        object: {
            type: IntegrationSyncMerchantEmployeeShiftActivityRequestDTO
        }
    }),
    _ts_metadata$2P("design:type", Array)
], IntegrationSyncMerchantEmployeeShiftV2RequestDTO.prototype, "activities", void 0);

function _define_property$2O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2O(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2O(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationNotificationPushLevel = /*#__PURE__*/ function(IntegrationNotificationPushLevel) {
    IntegrationNotificationPushLevel["MARKETING"] = "marketing";
    IntegrationNotificationPushLevel["ORDER"] = "order";
    return IntegrationNotificationPushLevel;
}({});
class IntegrationNotificationPushRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2O(this, "userIds", void 0), _define_property$2O(this, "sendToAllAnonymousCustomers", void 0), _define_property$2O(this, "title", void 0), _define_property$2O(this, "body", void 0), _define_property$2O(this, "level", void 0);
    }
}
_ts_decorate$2O([
    dto({
        string: true,
        array: true
    }),
    _ts_metadata$2O("design:type", Array)
], IntegrationNotificationPushRequestDTO.prototype, "userIds", void 0);
_ts_decorate$2O([
    dto({
        boolean: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2O("design:type", Object)
], IntegrationNotificationPushRequestDTO.prototype, "sendToAllAnonymousCustomers", void 0);
_ts_decorate$2O([
    dto({
        string: true
    }),
    _ts_metadata$2O("design:type", String)
], IntegrationNotificationPushRequestDTO.prototype, "title", void 0);
_ts_decorate$2O([
    dto({
        string: true
    }),
    _ts_metadata$2O("design:type", String)
], IntegrationNotificationPushRequestDTO.prototype, "body", void 0);
_ts_decorate$2O([
    dto({
        enum: IntegrationNotificationPushLevel
    }),
    _ts_metadata$2O("design:type", String)
], IntegrationNotificationPushRequestDTO.prototype, "level", void 0);

function _define_property$2N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2N(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2N(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Причина нужна отправителю рассылки: без неё ответ говорит только «не доставлено», и почему
// письмо на 91 покупателя дошло до троих, приходится выяснять по логам и SQL на нашей стороне.
var IntegrationNotificationPushNotDeliveredReason = /*#__PURE__*/ function(IntegrationNotificationPushNotDeliveredReason) {
    IntegrationNotificationPushNotDeliveredReason["USER_NOT_FOUND"] = "userNotFound";
    IntegrationNotificationPushNotDeliveredReason["NO_DEVICE"] = "noDevice";
    IntegrationNotificationPushNotDeliveredReason["NOT_ALLOWED"] = "notAllowed";
    IntegrationNotificationPushNotDeliveredReason["TOKEN_REJECTED"] = "tokenRejected";
    IntegrationNotificationPushNotDeliveredReason["SEND_ERROR"] = "sendError";
    return IntegrationNotificationPushNotDeliveredReason;
}({});
class IntegrationNotificationPushUserResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2N(this, "userId", void 0), _define_property$2N(this, "delivered", void 0), _define_property$2N(this, "reason", void 0);
    }
}
_ts_decorate$2N([
    dto({
        string: true
    }),
    _ts_metadata$2N("design:type", String)
], IntegrationNotificationPushUserResponseDTO.prototype, "userId", void 0);
_ts_decorate$2N([
    dto({
        boolean: true
    }),
    _ts_metadata$2N("design:type", Boolean)
], IntegrationNotificationPushUserResponseDTO.prototype, "delivered", void 0);
_ts_decorate$2N([
    dto({
        enum: IntegrationNotificationPushNotDeliveredReason,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2N("design:type", Object)
], IntegrationNotificationPushUserResponseDTO.prototype, "reason", void 0);
class IntegrationNotificationPushResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2N(this, "users", void 0);
    }
}
_ts_decorate$2N([
    dto({
        object: {
            type: IntegrationNotificationPushUserResponseDTO
        },
        array: true
    }),
    _ts_metadata$2N("design:type", Array)
], IntegrationNotificationPushResponseDTO.prototype, "users", void 0);

function _define_property$2M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2M(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2M(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationNotificationPushAudienceRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Список необязателен: 1С формирует аудиторию по своей клиентской базе и главный её вопрос —
        // «сколько человек я вообще могу охватить». Без списка охват считается по всем покупателям
        // мерчанта, со списком — строго по нему.
        _define_property$2M(this, "userIds", void 0), _define_property$2M(this, "sendToAllAnonymousCustomers", void 0), _define_property$2M(this, "level", void 0);
    }
}
_ts_decorate$2M([
    dto({
        string: true,
        array: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2M("design:type", Object)
], IntegrationNotificationPushAudienceRequestDTO.prototype, "userIds", void 0);
_ts_decorate$2M([
    dto({
        boolean: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2M("design:type", Object)
], IntegrationNotificationPushAudienceRequestDTO.prototype, "sendToAllAnonymousCustomers", void 0);
_ts_decorate$2M([
    dto({
        enum: IntegrationNotificationPushLevel
    }),
    _ts_metadata$2M("design:type", typeof IntegrationNotificationPushLevel === "undefined" ? Object : IntegrationNotificationPushLevel)
], IntegrationNotificationPushAudienceRequestDTO.prototype, "level", void 0);

function _define_property$2L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2L(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2L(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Причины те же, что в ответе на саму отправку, — 1С сверяет обе ручки одним списком. Но
// tokenRejected и sendError сюда попасть не могут: подсчёт охвата ничего не отправляет, и
// отказать Firebase нечему.
const INTEGRATION_NOTIFICATION_PUSH_AUDIENCE_REASONS = [
    IntegrationNotificationPushNotDeliveredReason.USER_NOT_FOUND,
    IntegrationNotificationPushNotDeliveredReason.NO_DEVICE,
    IntegrationNotificationPushNotDeliveredReason.NOT_ALLOWED
];
class IntegrationNotificationPushAudienceReasonResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2L(this, "reason", void 0), _define_property$2L(this, "count", void 0);
    }
}
_ts_decorate$2L([
    dto({
        enum: IntegrationNotificationPushNotDeliveredReason
    }),
    _ts_metadata$2L("design:type", typeof IntegrationNotificationPushNotDeliveredReason === "undefined" ? Object : IntegrationNotificationPushNotDeliveredReason)
], IntegrationNotificationPushAudienceReasonResponseDTO.prototype, "reason", void 0);
_ts_decorate$2L([
    dto({
        number: true
    }),
    _ts_metadata$2L("design:type", Number)
], IntegrationNotificationPushAudienceReasonResponseDTO.prototype, "count", void 0);
class IntegrationNotificationPushAudienceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), // total = reachable + сумма count по notReachable: иначе у 1С не сойдётся арифметика рассылки.
        _define_property$2L(this, "total", void 0), _define_property$2L(this, "reachable", void 0), _define_property$2L(this, "notReachable", void 0);
    }
}
_ts_decorate$2L([
    dto({
        number: true
    }),
    _ts_metadata$2L("design:type", Number)
], IntegrationNotificationPushAudienceResponseDTO.prototype, "total", void 0);
_ts_decorate$2L([
    dto({
        number: true
    }),
    _ts_metadata$2L("design:type", Number)
], IntegrationNotificationPushAudienceResponseDTO.prototype, "reachable", void 0);
_ts_decorate$2L([
    dto({
        object: {
            type: IntegrationNotificationPushAudienceReasonResponseDTO
        },
        array: true
    }),
    _ts_metadata$2L("design:type", Array)
], IntegrationNotificationPushAudienceResponseDTO.prototype, "notReachable", void 0);

function _define_property$2K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2K(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2K(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCancelOrderRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2K(this, "orderId", void 0), _define_property$2K(this, "reason", void 0);
    }
}
_ts_decorate$2K([
    dto({
        string: true
    }),
    _ts_metadata$2K("design:type", String)
], IntegrationCancelOrderRequestDTO.prototype, "orderId", void 0);
_ts_decorate$2K([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2K("design:type", Object)
], IntegrationCancelOrderRequestDTO.prototype, "reason", void 0);

var IntegrationCancelOrderResponseStatus = /*#__PURE__*/ function(IntegrationCancelOrderResponseStatus) {
    IntegrationCancelOrderResponseStatus["SUCCESS"] = "success";
    IntegrationCancelOrderResponseStatus["ERROR"] = "error";
    return IntegrationCancelOrderResponseStatus;
}({});

function _define_property$2J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2J(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2J(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCancelOrderErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2J(this, "status", void 0), _define_property$2J(this, "message", void 0);
    }
}
_ts_decorate$2J([
    dto({
        equals: IntegrationCancelOrderResponseStatus.ERROR
    }),
    _ts_metadata$2J("design:type", typeof IntegrationCancelOrderResponseStatus === "undefined" || typeof IntegrationCancelOrderResponseStatus.ERROR === "undefined" ? Object : IntegrationCancelOrderResponseStatus.ERROR)
], IntegrationCancelOrderErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$2J([
    dto({
        string: true
    }),
    _ts_metadata$2J("design:type", String)
], IntegrationCancelOrderErrorResponseDTO.prototype, "message", void 0);

function _define_property$2I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2I(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2I(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CancelOrderRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2I(this, "orderId", void 0), _define_property$2I(this, "reason", void 0);
    }
}
_ts_decorate$2I([
    dto({
        string: true
    }),
    _ts_metadata$2I("design:type", String)
], CancelOrderRequestDTO.prototype, "orderId", void 0);
_ts_decorate$2I([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2I("design:type", Object)
], CancelOrderRequestDTO.prototype, "reason", void 0);

function _define_property$2H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2H(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2H(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CancelOrderStatus = /*#__PURE__*/ function(CancelOrderStatus) {
    CancelOrderStatus["SUCCESS"] = "success";
    CancelOrderStatus["ERROR"] = "error";
    return CancelOrderStatus;
}({});
class CancelOrderSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2H(this, "status", void 0);
    }
}
_ts_decorate$2H([
    dto({
        equals: "success"
    }),
    _ts_metadata$2H("design:type", typeof CancelOrderStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CancelOrderSuccessResponseDTO.prototype, "status", void 0);
class CancelOrderErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2H(this, "status", void 0), _define_property$2H(this, "message", void 0);
    }
}
_ts_decorate$2H([
    dto({
        equals: "error"
    }),
    _ts_metadata$2H("design:type", typeof CancelOrderStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CancelOrderErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$2H([
    dto({
        string: true
    }),
    _ts_metadata$2H("design:type", String)
], CancelOrderErrorResponseDTO.prototype, "message", void 0);
class CancelOrderResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2H(this, "data", void 0);
    }
}
_ts_decorate$2H([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: CancelOrderSuccessResponseDTO
                    },
                    {
                        name: "error",
                        value: CancelOrderErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2H("design:type", Object)
], CancelOrderResponseDTO.prototype, "data", void 0);

function _define_property$2G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2G(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2G(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CreateOrderRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2G(this, "lastValidatedHash", void 0), _define_property$2G(this, "onlinePaymentRedirectUrl", void 0), _define_property$2G(this, "comment", void 0);
    }
}
_ts_decorate$2G([
    dto({
        string: true
    }),
    _ts_metadata$2G("design:type", String)
], CreateOrderRequestDTO.prototype, "lastValidatedHash", void 0);
_ts_decorate$2G([
    dto({
        url: {
            allowAnyProtocol: true
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2G("design:type", Object)
], CreateOrderRequestDTO.prototype, "onlinePaymentRedirectUrl", void 0);
_ts_decorate$2G([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2G("design:type", Object)
], CreateOrderRequestDTO.prototype, "comment", void 0);

function _define_property$2F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2F(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2F(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CreateOrderStatus = /*#__PURE__*/ function(CreateOrderStatus) {
    CreateOrderStatus["SUCCESS"] = "success";
    CreateOrderStatus["ERROR"] = "error";
    return CreateOrderStatus;
}({});
class CreateOrderSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2F(this, "status", void 0), _define_property$2F(this, "id", void 0);
    }
}
_ts_decorate$2F([
    dto({
        equals: "success"
    }),
    _ts_metadata$2F("design:type", typeof CreateOrderStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CreateOrderSuccessResponseDTO.prototype, "status", void 0);
_ts_decorate$2F([
    dto({
        string: true
    }),
    _ts_metadata$2F("design:type", String)
], CreateOrderSuccessResponseDTO.prototype, "id", void 0);
class CreateOrderErrorInfoProductResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2F(this, "id", void 0), _define_property$2F(this, "message", void 0);
    }
}
_ts_decorate$2F([
    dto({
        string: true
    }),
    _ts_metadata$2F("design:type", String)
], CreateOrderErrorInfoProductResponseDTO.prototype, "id", void 0);
_ts_decorate$2F([
    dto({
        string: true
    }),
    _ts_metadata$2F("design:type", String)
], CreateOrderErrorInfoProductResponseDTO.prototype, "message", void 0);
class CreateOrderErrorInfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2F(this, "message", void 0), _define_property$2F(this, "code", void 0), _define_property$2F(this, "errorProducts", void 0);
    }
}
_ts_decorate$2F([
    dto({
        string: true
    }),
    _ts_metadata$2F("design:type", String)
], CreateOrderErrorInfoResponseDTO.prototype, "message", void 0);
_ts_decorate$2F([
    dto({
        enum: IntegrationCreateOrderErrorCode,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2F("design:type", Object)
], CreateOrderErrorInfoResponseDTO.prototype, "code", void 0);
_ts_decorate$2F([
    dto({
        array: true,
        object: {
            type: CreateOrderErrorInfoProductResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2F("design:type", Object)
], CreateOrderErrorInfoResponseDTO.prototype, "errorProducts", void 0);
class CreateOrderErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2F(this, "status", void 0), _define_property$2F(this, "info", void 0);
    }
}
_ts_decorate$2F([
    dto({
        equals: "error"
    }),
    _ts_metadata$2F("design:type", typeof CreateOrderStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CreateOrderErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$2F([
    dto({
        object: {
            type: CreateOrderErrorInfoResponseDTO
        }
    }),
    _ts_metadata$2F("design:type", typeof CreateOrderErrorInfoResponseDTO === "undefined" ? Object : CreateOrderErrorInfoResponseDTO)
], CreateOrderErrorResponseDTO.prototype, "info", void 0);
class CreateOrderResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2F(this, "data", void 0);
    }
}
_ts_decorate$2F([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: CreateOrderSuccessResponseDTO
                    },
                    {
                        name: "error",
                        value: CreateOrderErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2F("design:type", Object)
], CreateOrderResponseDTO.prototype, "data", void 0);

function _define_property$2E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2E(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2E(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailDeliveryCourierDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2E(this, "type", void 0), _define_property$2E(this, "typeName", void 0), _define_property$2E(this, "city", void 0), _define_property$2E(this, "address", void 0), _define_property$2E(this, "comment", void 0);
    }
}
_ts_decorate$2E([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$2E("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], OrderDetailDeliveryCourierDTO.prototype, "type", void 0);
_ts_decorate$2E([
    dto({
        string: true
    }),
    _ts_metadata$2E("design:type", String)
], OrderDetailDeliveryCourierDTO.prototype, "typeName", void 0);
_ts_decorate$2E([
    dto({
        string: true
    }),
    _ts_metadata$2E("design:type", String)
], OrderDetailDeliveryCourierDTO.prototype, "city", void 0);
_ts_decorate$2E([
    dto({
        string: true
    }),
    _ts_metadata$2E("design:type", String)
], OrderDetailDeliveryCourierDTO.prototype, "address", void 0);
_ts_decorate$2E([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2E("design:type", Object)
], OrderDetailDeliveryCourierDTO.prototype, "comment", void 0);

function _define_property$2D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2D(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2D(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailDeliveryPickupDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2D(this, "type", void 0), _define_property$2D(this, "typeName", void 0), _define_property$2D(this, "address", void 0), _define_property$2D(this, "city", void 0);
    }
}
_ts_decorate$2D([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$2D("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], OrderDetailDeliveryPickupDTO.prototype, "type", void 0);
_ts_decorate$2D([
    dto({
        string: true
    }),
    _ts_metadata$2D("design:type", String)
], OrderDetailDeliveryPickupDTO.prototype, "typeName", void 0);
_ts_decorate$2D([
    dto({
        string: true
    }),
    _ts_metadata$2D("design:type", String)
], OrderDetailDeliveryPickupDTO.prototype, "address", void 0);
_ts_decorate$2D([
    dto({
        string: true
    }),
    _ts_metadata$2D("design:type", String)
], OrderDetailDeliveryPickupDTO.prototype, "city", void 0);

function _define_property$2C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2C(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2C(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailDeliveryPickupPointDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2C(this, "type", void 0), _define_property$2C(this, "typeName", void 0), _define_property$2C(this, "city", void 0), _define_property$2C(this, "address", void 0);
    }
}
_ts_decorate$2C([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$2C("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], OrderDetailDeliveryPickupPointDTO.prototype, "type", void 0);
_ts_decorate$2C([
    dto({
        string: true
    }),
    _ts_metadata$2C("design:type", String)
], OrderDetailDeliveryPickupPointDTO.prototype, "typeName", void 0);
_ts_decorate$2C([
    dto({
        string: true
    }),
    _ts_metadata$2C("design:type", String)
], OrderDetailDeliveryPickupPointDTO.prototype, "city", void 0);
_ts_decorate$2C([
    dto({
        string: true
    }),
    _ts_metadata$2C("design:type", String)
], OrderDetailDeliveryPickupPointDTO.prototype, "address", void 0);

function _define_property$2B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2B(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2B(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailPaymentOnlineDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2B(this, "type", void 0), _define_property$2B(this, "typeName", void 0);
    }
}
_ts_decorate$2B([
    dto({
        equals: IntegrationCartConfigurationPaymentType.ONLINE
    }),
    _ts_metadata$2B("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.ONLINE === "undefined" ? Object : IntegrationCartConfigurationPaymentType.ONLINE)
], OrderDetailPaymentOnlineDTO.prototype, "type", void 0);
_ts_decorate$2B([
    dto({
        string: true
    }),
    _ts_metadata$2B("design:type", String)
], OrderDetailPaymentOnlineDTO.prototype, "typeName", void 0);

function _define_property$2A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2A(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2A(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailPaymentCashDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2A(this, "type", void 0), _define_property$2A(this, "typeName", void 0);
    }
}
_ts_decorate$2A([
    dto({
        equals: IntegrationCartConfigurationPaymentType.CASH
    }),
    _ts_metadata$2A("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.CASH === "undefined" ? Object : IntegrationCartConfigurationPaymentType.CASH)
], OrderDetailPaymentCashDTO.prototype, "type", void 0);
_ts_decorate$2A([
    dto({
        string: true
    }),
    _ts_metadata$2A("design:type", String)
], OrderDetailPaymentCashDTO.prototype, "typeName", void 0);

function _define_property$2z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusInfoCommonDeliveryResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2z(this, "number", void 0), _define_property$2z(this, "infoUrl", void 0);
    }
}
_ts_decorate$2z([
    dto({
        string: true
    }),
    _ts_metadata$2z("design:type", String)
], OrderDetailStatusInfoCommonDeliveryResponseDTO.prototype, "number", void 0);
_ts_decorate$2z([
    dto({
        url: true
    }),
    _ts_metadata$2z("design:type", String)
], OrderDetailStatusInfoCommonDeliveryResponseDTO.prototype, "infoUrl", void 0);

function _define_property$2y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusInfoCommonPaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2y(this, "receiptUrl", void 0);
    }
}
_ts_decorate$2y([
    dto({
        string: true
    }),
    _ts_metadata$2y("design:type", String)
], OrderDetailStatusInfoCommonPaymentResponseDTO.prototype, "receiptUrl", void 0);

function _define_property$2x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2x(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2x(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusCanceledResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2x(this, "status", void 0), _define_property$2x(this, "message", void 0), _define_property$2x(this, "payment", void 0), _define_property$2x(this, "delivery", void 0);
    }
}
_ts_decorate$2x([
    dto({
        equals: OrderStatus.CANCELLED
    }),
    _ts_metadata$2x("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.CANCELLED === "undefined" ? Object : OrderStatus.CANCELLED)
], OrderDetailStatusCanceledResponseDTO.prototype, "status", void 0);
_ts_decorate$2x([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2x("design:type", Object)
], OrderDetailStatusCanceledResponseDTO.prototype, "message", void 0);
_ts_decorate$2x([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonPaymentResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2x("design:type", Object)
], OrderDetailStatusCanceledResponseDTO.prototype, "payment", void 0);
_ts_decorate$2x([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonDeliveryResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2x("design:type", Object)
], OrderDetailStatusCanceledResponseDTO.prototype, "delivery", void 0);

function _define_property$2w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2w(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2w(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusCompletedResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2w(this, "status", void 0), _define_property$2w(this, "payment", void 0), _define_property$2w(this, "delivery", void 0);
    }
}
_ts_decorate$2w([
    dto({
        equals: OrderStatus.COMPLETED
    }),
    _ts_metadata$2w("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.COMPLETED === "undefined" ? Object : OrderStatus.COMPLETED)
], OrderDetailStatusCompletedResponseDTO.prototype, "status", void 0);
_ts_decorate$2w([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonPaymentResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2w("design:type", Object)
], OrderDetailStatusCompletedResponseDTO.prototype, "payment", void 0);
_ts_decorate$2w([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonDeliveryResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2w("design:type", Object)
], OrderDetailStatusCompletedResponseDTO.prototype, "delivery", void 0);

function _define_property$2v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2v(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2v(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusDeliveringResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2v(this, "status", void 0), _define_property$2v(this, "payment", void 0), _define_property$2v(this, "delivery", void 0);
    }
}
_ts_decorate$2v([
    dto({
        equals: OrderStatus.DELIVERING
    }),
    _ts_metadata$2v("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.DELIVERING === "undefined" ? Object : OrderStatus.DELIVERING)
], OrderDetailStatusDeliveringResponseDTO.prototype, "status", void 0);
_ts_decorate$2v([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonPaymentResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2v("design:type", Object)
], OrderDetailStatusDeliveringResponseDTO.prototype, "payment", void 0);
_ts_decorate$2v([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonDeliveryResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2v("design:type", Object)
], OrderDetailStatusDeliveringResponseDTO.prototype, "delivery", void 0);

function _define_property$2u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2u(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2u(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusReadyResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2u(this, "status", void 0), _define_property$2u(this, "payment", void 0), _define_property$2u(this, "delivery", void 0);
    }
}
_ts_decorate$2u([
    dto({
        equals: OrderStatus.READY
    }),
    _ts_metadata$2u("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.READY === "undefined" ? Object : OrderStatus.READY)
], OrderDetailStatusReadyResponseDTO.prototype, "status", void 0);
_ts_decorate$2u([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonPaymentResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2u("design:type", Object)
], OrderDetailStatusReadyResponseDTO.prototype, "payment", void 0);
_ts_decorate$2u([
    dto({
        object: {
            type: OrderDetailStatusInfoCommonDeliveryResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2u("design:type", Object)
], OrderDetailStatusReadyResponseDTO.prototype, "delivery", void 0);

function _define_property$2t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2t(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2t(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderStatusInfoForWaitingPaymentLinkResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2t(this, "link", void 0), _define_property$2t(this, "name", void 0);
    }
}
_ts_decorate$2t([
    dto({
        url: {
            allowAnyProtocol: true
        }
    }),
    _ts_metadata$2t("design:type", String)
], OrderStatusInfoForWaitingPaymentLinkResponseDTO.prototype, "link", void 0);
_ts_decorate$2t([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2t("design:type", Object)
], OrderStatusInfoForWaitingPaymentLinkResponseDTO.prototype, "name", void 0);
class OrderDetailStatusWaitingPaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2t(this, "status", void 0), _define_property$2t(this, "paymentLinks", void 0), _define_property$2t(this, "message", void 0);
    }
}
_ts_decorate$2t([
    dto({
        equals: OrderStatus.WAITING_PAYMENT
    }),
    _ts_metadata$2t("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.WAITING_PAYMENT === "undefined" ? Object : OrderStatus.WAITING_PAYMENT)
], OrderDetailStatusWaitingPaymentResponseDTO.prototype, "status", void 0);
_ts_decorate$2t([
    dto({
        array: true,
        object: {
            type: OrderStatusInfoForWaitingPaymentLinkResponseDTO
        }
    }),
    _ts_metadata$2t("design:type", Array)
], OrderDetailStatusWaitingPaymentResponseDTO.prototype, "paymentLinks", void 0);
_ts_decorate$2t([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2t("design:type", Object)
], OrderDetailStatusWaitingPaymentResponseDTO.prototype, "message", void 0);

function _define_property$2s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2s(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2s(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailStatusResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2s(this, "status", void 0), _define_property$2s(this, "statusName", void 0), _define_property$2s(this, "info", void 0);
    }
}
_ts_decorate$2s([
    dto({
        enum: OrderStatus
    }),
    _ts_metadata$2s("design:type", typeof OrderStatus === "undefined" ? Object : OrderStatus)
], OrderDetailStatusResponseDTO.prototype, "status", void 0);
_ts_decorate$2s([
    dto({
        string: true
    }),
    _ts_metadata$2s("design:type", String)
], OrderDetailStatusResponseDTO.prototype, "statusName", void 0);
_ts_decorate$2s([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: OrderStatus.CANCELLED,
                        value: OrderDetailStatusCanceledResponseDTO
                    },
                    {
                        name: OrderStatus.COMPLETED,
                        value: OrderDetailStatusCompletedResponseDTO
                    },
                    {
                        name: OrderStatus.DELIVERING,
                        value: OrderDetailStatusDeliveringResponseDTO
                    },
                    {
                        name: OrderStatus.READY,
                        value: OrderDetailStatusReadyResponseDTO
                    },
                    {
                        name: OrderStatus.WAITING_PAYMENT,
                        value: OrderDetailStatusWaitingPaymentResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2s("design:type", Object)
], OrderDetailStatusResponseDTO.prototype, "info", void 0);

function _define_property$2r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2r(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2r(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var OrderProductStatus = /*#__PURE__*/ function(OrderProductStatus) {
    OrderProductStatus["INITIAL"] = "initial";
    OrderProductStatus["ASSEMBLING"] = "assembling";
    OrderProductStatus["ON_THE_WAY"] = "on_the_way";
    OrderProductStatus["READY_FOR_PICKUP"] = "ready_for_pickup";
    OrderProductStatus["DELIVERED"] = "delivered";
    OrderProductStatus["CANCELED"] = "canceled";
    OrderProductStatus["CANCELED_OUT_OF_STOCK"] = "canceled_out_of_stock";
    OrderProductStatus["CANCELED_BY_CUSTOMER"] = "canceled_by_customer";
    OrderProductStatus["CANCELED_BY_CUSTOMERS_WAITING_PERIOD_EXPIRED"] = "canceled_by_customers_waiting_period_expired";
    OrderProductStatus["CANCELLED_RETURN"] = "canceled_return";
    return OrderProductStatus;
}({});
class OrderProductDetailDTO extends OrderBaseProductDTO {
    constructor(...args){
        super(...args), _define_property$2r(this, "originalProduct", void 0), _define_property$2r(this, "managerComment", void 0), _define_property$2r(this, "status", void 0), _define_property$2r(this, "statusName", void 0);
    }
}
_ts_decorate$2r([
    dto({
        optional: true,
        object: {
            type: ProductBaseDTO
        }
    }),
    _ts_metadata$2r("design:type", Object)
], OrderProductDetailDTO.prototype, "originalProduct", void 0);
_ts_decorate$2r([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2r("design:type", Object)
], OrderProductDetailDTO.prototype, "managerComment", void 0);
_ts_decorate$2r([
    dto({
        enum: OrderProductStatus
    }),
    _ts_metadata$2r("design:type", String)
], OrderProductDetailDTO.prototype, "status", void 0);
_ts_decorate$2r([
    dto({
        string: true
    }),
    _ts_metadata$2r("design:type", String)
], OrderProductDetailDTO.prototype, "statusName", void 0);

function _define_property$2q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderDetailResponseDTO extends OrderBaseResponseDTO {
    constructor(...args){
        super(...args), _define_property$2q(this, "products", void 0), _define_property$2q(this, "status", void 0), _define_property$2q(this, "delivery", void 0), _define_property$2q(this, "payment", void 0), _define_property$2q(this, "managerComment", void 0), _define_property$2q(this, "canCancel", void 0);
    }
}
_ts_decorate$2q([
    dto({
        array: true,
        object: {
            type: OrderProductDetailDTO
        }
    }),
    _ts_metadata$2q("design:type", Array)
], OrderDetailResponseDTO.prototype, "products", void 0);
_ts_decorate$2q([
    dto({
        object: {
            type: OrderDetailStatusResponseDTO
        }
    }),
    _ts_metadata$2q("design:type", typeof OrderDetailStatusResponseDTO === "undefined" ? Object : OrderDetailStatusResponseDTO)
], OrderDetailResponseDTO.prototype, "status", void 0);
_ts_decorate$2q([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: OrderDetailDeliveryCourierDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: OrderDetailDeliveryPickupDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: OrderDetailDeliveryPickupPointDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2q("design:type", Object)
], OrderDetailResponseDTO.prototype, "delivery", void 0);
_ts_decorate$2q([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationPaymentType.ONLINE,
                        value: OrderDetailPaymentOnlineDTO
                    },
                    {
                        name: IntegrationCartConfigurationPaymentType.CASH,
                        value: OrderDetailPaymentCashDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2q("design:type", Object)
], OrderDetailResponseDTO.prototype, "payment", void 0);
_ts_decorate$2q([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2q("design:type", Object)
], OrderDetailResponseDTO.prototype, "managerComment", void 0);
_ts_decorate$2q([
    dto({
        boolean: true
    }),
    _ts_metadata$2q("design:type", Boolean)
], OrderDetailResponseDTO.prototype, "canCancel", void 0);

function _define_property$2p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2p(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2p(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCancelOrderSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2p(this, "status", void 0), _define_property$2p(this, "newOrderStatus", void 0);
    }
}
_ts_decorate$2p([
    dto({
        equals: IntegrationCancelOrderResponseStatus.SUCCESS
    }),
    _ts_metadata$2p("design:type", typeof IntegrationCancelOrderResponseStatus === "undefined" || typeof IntegrationCancelOrderResponseStatus.SUCCESS === "undefined" ? Object : IntegrationCancelOrderResponseStatus.SUCCESS)
], IntegrationCancelOrderSuccessResponseDTO.prototype, "status", void 0);
_ts_decorate$2p([
    dto({
        optional: {
            allowNull: false
        },
        enum: OrderStatus
    }),
    _ts_metadata$2p("design:type", Object)
], IntegrationCancelOrderSuccessResponseDTO.prototype, "newOrderStatus", void 0);

function _define_property$2o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2o(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2o(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCancelOrderResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2o(this, "data", void 0);
    }
}
_ts_decorate$2o([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationCancelOrderResponseStatus.SUCCESS,
                        value: IntegrationCancelOrderSuccessResponseDTO
                    },
                    {
                        name: IntegrationCancelOrderResponseStatus.ERROR,
                        value: IntegrationCancelOrderErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2o("design:type", Object)
], IntegrationCancelOrderResponseDTO.prototype, "data", void 0);

function _define_property$2n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2n(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2n(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderProductAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2n(this, "id", void 0), _define_property$2n(this, "value", void 0);
    }
}
_ts_decorate$2n([
    dto({
        string: true
    }),
    _ts_metadata$2n("design:type", String)
], IntegrationCreateOrderProductAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$2n([
    dto({
        string: true
    }),
    _ts_metadata$2n("design:type", String)
], IntegrationCreateOrderProductAttributeRequestDTO.prototype, "value", void 0);
class IntegrationCreateOrderProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2n(this, "id", void 0), _define_property$2n(this, "backendId", void 0), _define_property$2n(this, "count", void 0), _define_property$2n(this, "price", void 0), _define_property$2n(this, "attributes", void 0);
    }
}
_ts_decorate$2n([
    dto({
        string: true
    }),
    _ts_metadata$2n("design:type", String)
], IntegrationCreateOrderProductRequestDTO.prototype, "id", void 0);
_ts_decorate$2n([
    dto({
        string: true
    }),
    _ts_metadata$2n("design:type", String)
], IntegrationCreateOrderProductRequestDTO.prototype, "backendId", void 0);
_ts_decorate$2n([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$2n("design:type", Number)
], IntegrationCreateOrderProductRequestDTO.prototype, "count", void 0);
_ts_decorate$2n([
    dto({
        number: true
    }),
    _ts_metadata$2n("design:type", Number)
], IntegrationCreateOrderProductRequestDTO.prototype, "price", void 0);
_ts_decorate$2n([
    dto({
        array: true,
        object: {
            type: IntegrationCreateOrderProductAttributeRequestDTO
        }
    }),
    _ts_metadata$2n("design:type", Array)
], IntegrationCreateOrderProductRequestDTO.prototype, "attributes", void 0);

function _define_property$2m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2m(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2m(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderDeliveryCourierAddressRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2m(this, "street", void 0), _define_property$2m(this, "houseNumber", void 0), _define_property$2m(this, "appartmentNumber", void 0), _define_property$2m(this, "comment", void 0);
    }
}
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierAddressRequestDTO.prototype, "street", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierAddressRequestDTO.prototype, "houseNumber", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierAddressRequestDTO.prototype, "appartmentNumber", void 0);
_ts_decorate$2m([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2m("design:type", Object)
], IntegrationCreateOrderDeliveryCourierAddressRequestDTO.prototype, "comment", void 0);
class IntegrationCreateOrderDeliveryCourierRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2m(this, "type", void 0), _define_property$2m(this, "deliveryTypeId", void 0), _define_property$2m(this, "deliveryTypeName", void 0), _define_property$2m(this, "cityId", void 0), _define_property$2m(this, "cityName", void 0), _define_property$2m(this, "variantId", void 0), _define_property$2m(this, "variantName", void 0), _define_property$2m(this, "address", void 0);
    }
}
_ts_decorate$2m([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$2m("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "type", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "deliveryTypeName", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "cityId", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "cityName", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "variantId", void 0);
_ts_decorate$2m([
    dto({
        string: true
    }),
    _ts_metadata$2m("design:type", String)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "variantName", void 0);
_ts_decorate$2m([
    dto({
        object: {
            type: IntegrationCreateOrderDeliveryCourierAddressRequestDTO
        }
    }),
    _ts_metadata$2m("design:type", typeof IntegrationCreateOrderDeliveryCourierAddressRequestDTO === "undefined" ? Object : IntegrationCreateOrderDeliveryCourierAddressRequestDTO)
], IntegrationCreateOrderDeliveryCourierRequestDTO.prototype, "address", void 0);

function _define_property$2l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2l(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2l(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderDeliveryPickupRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2l(this, "type", void 0), _define_property$2l(this, "deliveryTypeId", void 0), _define_property$2l(this, "deliveryTypeName", void 0), _define_property$2l(this, "shopId", void 0);
    }
}
_ts_decorate$2l([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$2l("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], IntegrationCreateOrderDeliveryPickupRequestDTO.prototype, "type", void 0);
_ts_decorate$2l([
    dto({
        string: true
    }),
    _ts_metadata$2l("design:type", String)
], IntegrationCreateOrderDeliveryPickupRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$2l([
    dto({
        string: true
    }),
    _ts_metadata$2l("design:type", String)
], IntegrationCreateOrderDeliveryPickupRequestDTO.prototype, "deliveryTypeName", void 0);
_ts_decorate$2l([
    dto({
        string: true
    }),
    _ts_metadata$2l("design:type", String)
], IntegrationCreateOrderDeliveryPickupRequestDTO.prototype, "shopId", void 0);

function _define_property$2k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2k(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2k(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderDeliveryPickupPointCoordinatesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2k(this, "latitude", void 0), _define_property$2k(this, "longitude", void 0);
    }
}
_ts_decorate$2k([
    dto({
        number: true
    }),
    _ts_metadata$2k("design:type", Number)
], IntegrationCreateOrderDeliveryPickupPointCoordinatesRequestDTO.prototype, "latitude", void 0);
_ts_decorate$2k([
    dto({
        number: true
    }),
    _ts_metadata$2k("design:type", Number)
], IntegrationCreateOrderDeliveryPickupPointCoordinatesRequestDTO.prototype, "longitude", void 0);
class IntegrationCreateOrderDeliveryPickupPointRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2k(this, "type", void 0), _define_property$2k(this, "deliveryTypeId", void 0), _define_property$2k(this, "deliveryTypeName", void 0), _define_property$2k(this, "cityId", void 0), _define_property$2k(this, "cityName", void 0), _define_property$2k(this, "addressId", void 0), _define_property$2k(this, "addressName", void 0), _define_property$2k(this, "addressCoordinates", void 0), _define_property$2k(this, "variantId", void 0), _define_property$2k(this, "variantName", void 0);
    }
}
_ts_decorate$2k([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$2k("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "type", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "deliveryTypeName", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "cityId", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "cityName", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "addressId", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "addressName", void 0);
_ts_decorate$2k([
    dto({
        object: {
            type: IntegrationCreateOrderDeliveryPickupPointCoordinatesRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2k("design:type", Object)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "addressCoordinates", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "variantId", void 0);
_ts_decorate$2k([
    dto({
        string: true
    }),
    _ts_metadata$2k("design:type", String)
], IntegrationCreateOrderDeliveryPickupPointRequestDTO.prototype, "variantName", void 0);

function _define_property$2j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2j(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2j(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderPaymentOnlineRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2j(this, "type", void 0), _define_property$2j(this, "redirectUrl", void 0), _define_property$2j(this, "paymentTypeId", void 0), _define_property$2j(this, "paymentTypeName", void 0);
    }
}
_ts_decorate$2j([
    dto({
        equals: IntegrationCartConfigurationPaymentType.ONLINE
    }),
    _ts_metadata$2j("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.ONLINE === "undefined" ? Object : IntegrationCartConfigurationPaymentType.ONLINE)
], IntegrationCreateOrderPaymentOnlineRequestDTO.prototype, "type", void 0);
_ts_decorate$2j([
    dto({
        url: {
            allowAnyProtocol: true
        }
    }),
    _ts_metadata$2j("design:type", String)
], IntegrationCreateOrderPaymentOnlineRequestDTO.prototype, "redirectUrl", void 0);
_ts_decorate$2j([
    dto({
        string: true
    }),
    _ts_metadata$2j("design:type", String)
], IntegrationCreateOrderPaymentOnlineRequestDTO.prototype, "paymentTypeId", void 0);
_ts_decorate$2j([
    dto({
        string: true
    }),
    _ts_metadata$2j("design:type", String)
], IntegrationCreateOrderPaymentOnlineRequestDTO.prototype, "paymentTypeName", void 0);
class IntegrationCreateOrderPaymentCashRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2j(this, "type", void 0), _define_property$2j(this, "paymentTypeId", void 0), _define_property$2j(this, "paymentTypeName", void 0);
    }
}
_ts_decorate$2j([
    dto({
        equals: IntegrationCartConfigurationPaymentType.CASH
    }),
    _ts_metadata$2j("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.CASH === "undefined" ? Object : IntegrationCartConfigurationPaymentType.CASH)
], IntegrationCreateOrderPaymentCashRequestDTO.prototype, "type", void 0);
_ts_decorate$2j([
    dto({
        string: true
    }),
    _ts_metadata$2j("design:type", String)
], IntegrationCreateOrderPaymentCashRequestDTO.prototype, "paymentTypeId", void 0);
_ts_decorate$2j([
    dto({
        string: true
    }),
    _ts_metadata$2j("design:type", String)
], IntegrationCreateOrderPaymentCashRequestDTO.prototype, "paymentTypeName", void 0);

function _define_property$2i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2i(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2i(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2i(this, "delivery", void 0), _define_property$2i(this, "payment", void 0), _define_property$2i(this, "products", void 0), _define_property$2i(this, "promoCodes", void 0), _define_property$2i(this, "userId", void 0), _define_property$2i(this, "userIp", void 0), _define_property$2i(this, "applyBonuses", void 0), _define_property$2i(this, "currentUserPrice", void 0), _define_property$2i(this, "comment", void 0);
    }
}
_ts_decorate$2i([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: IntegrationCreateOrderDeliveryPickupRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: IntegrationCreateOrderDeliveryPickupPointRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: IntegrationCreateOrderDeliveryCourierRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2i("design:type", Object)
], IntegrationCreateOrderRequestDTO.prototype, "delivery", void 0);
_ts_decorate$2i([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationPaymentType.ONLINE,
                        value: IntegrationCreateOrderPaymentOnlineRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationPaymentType.CASH,
                        value: IntegrationCreateOrderPaymentCashRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$2i("design:type", Object)
], IntegrationCreateOrderRequestDTO.prototype, "payment", void 0);
_ts_decorate$2i([
    dto({
        array: true,
        object: {
            type: IntegrationCreateOrderProductRequestDTO
        }
    }),
    _ts_metadata$2i("design:type", Array)
], IntegrationCreateOrderRequestDTO.prototype, "products", void 0);
_ts_decorate$2i([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$2i("design:type", Array)
], IntegrationCreateOrderRequestDTO.prototype, "promoCodes", void 0);
_ts_decorate$2i([
    dto({
        string: true
    }),
    _ts_metadata$2i("design:type", String)
], IntegrationCreateOrderRequestDTO.prototype, "userId", void 0);
_ts_decorate$2i([
    dto({
        string: true
    }),
    _ts_metadata$2i("design:type", String)
], IntegrationCreateOrderRequestDTO.prototype, "userIp", void 0);
_ts_decorate$2i([
    dto({
        boolean: true
    }),
    _ts_metadata$2i("design:type", Boolean)
], IntegrationCreateOrderRequestDTO.prototype, "applyBonuses", void 0);
_ts_decorate$2i([
    dto({
        number: true
    }),
    _ts_metadata$2i("design:type", Number)
], IntegrationCreateOrderRequestDTO.prototype, "currentUserPrice", void 0);
_ts_decorate$2i([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$2i("design:type", Object)
], IntegrationCreateOrderRequestDTO.prototype, "comment", void 0);

function _define_property$2h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2h(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2h(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderErrorProductResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2h(this, "id", void 0), _define_property$2h(this, "message", void 0);
    }
}
_ts_decorate$2h([
    dto({
        string: true
    }),
    _ts_metadata$2h("design:type", String)
], IntegrationCreateOrderErrorProductResponseDTO.prototype, "id", void 0);
_ts_decorate$2h([
    dto({
        string: true
    }),
    _ts_metadata$2h("design:type", String)
], IntegrationCreateOrderErrorProductResponseDTO.prototype, "message", void 0);

var IntegrationCreateOrderResponseStatus = /*#__PURE__*/ function(IntegrationCreateOrderResponseStatus) {
    IntegrationCreateOrderResponseStatus["SUCCESS"] = "success";
    IntegrationCreateOrderResponseStatus["ERROR"] = "error";
    return IntegrationCreateOrderResponseStatus;
}({});

function _define_property$2g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2g(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2g(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationCreateOrderErrorCode = /*#__PURE__*/ function(IntegrationCreateOrderErrorCode) {
    IntegrationCreateOrderErrorCode["PRICE_CHANGED"] = "priceChanged";
    IntegrationCreateOrderErrorCode["ORDER_CREATION_LIMIT"] = "orderCreationLimit";
    return IntegrationCreateOrderErrorCode;
}({});
class IntegrationCreateOrderErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2g(this, "status", void 0), _define_property$2g(this, "errorProducts", void 0), _define_property$2g(this, "code", void 0), _define_property$2g(this, "message", void 0);
    }
}
_ts_decorate$2g([
    dto({
        equals: IntegrationCreateOrderResponseStatus.ERROR
    }),
    _ts_metadata$2g("design:type", typeof IntegrationCreateOrderResponseStatus === "undefined" || typeof IntegrationCreateOrderResponseStatus.ERROR === "undefined" ? Object : IntegrationCreateOrderResponseStatus.ERROR)
], IntegrationCreateOrderErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$2g([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: IntegrationCreateOrderErrorProductResponseDTO
        }
    }),
    _ts_metadata$2g("design:type", Object)
], IntegrationCreateOrderErrorResponseDTO.prototype, "errorProducts", void 0);
_ts_decorate$2g([
    dto({
        enum: IntegrationCreateOrderErrorCode,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2g("design:type", Object)
], IntegrationCreateOrderErrorResponseDTO.prototype, "code", void 0);
_ts_decorate$2g([
    dto({
        string: true
    }),
    _ts_metadata$2g("design:type", String)
], IntegrationCreateOrderErrorResponseDTO.prototype, "message", void 0);

function _define_property$2f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2f(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2f(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2f(this, "id", void 0), _define_property$2f(this, "price", void 0), _define_property$2f(this, "count", void 0), _define_property$2f(this, "status", void 0), _define_property$2f(this, "managerComment", void 0);
    }
}
_ts_decorate$2f([
    dto({
        string: true
    }),
    _ts_metadata$2f("design:type", String)
], IntegrationSyncOrderProductRequestDTO.prototype, "id", void 0);
_ts_decorate$2f([
    dto({
        number: true
    }),
    _ts_metadata$2f("design:type", Number)
], IntegrationSyncOrderProductRequestDTO.prototype, "price", void 0);
_ts_decorate$2f([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$2f("design:type", Number)
], IntegrationSyncOrderProductRequestDTO.prototype, "count", void 0);
_ts_decorate$2f([
    dto({
        enum: OrderProductStatus
    }),
    _ts_metadata$2f("design:type", typeof OrderProductStatus === "undefined" ? Object : OrderProductStatus)
], IntegrationSyncOrderProductRequestDTO.prototype, "status", void 0);
_ts_decorate$2f([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2f("design:type", Object)
], IntegrationSyncOrderProductRequestDTO.prototype, "managerComment", void 0);

function _define_property$2e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2e(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2e(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoCommonPaymentRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2e(this, "receiptUrl", void 0);
    }
}
_ts_decorate$2e([
    dto({
        string: true
    }),
    _ts_metadata$2e("design:type", String)
], IntegrationSyncOrderStatusInfoCommonPaymentRequestDTO.prototype, "receiptUrl", void 0);

function _define_property$2d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2d(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2d(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2d(this, "number", void 0), _define_property$2d(this, "infoUrl", void 0);
    }
}
_ts_decorate$2d([
    dto({
        string: true
    }),
    _ts_metadata$2d("design:type", String)
], IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO.prototype, "number", void 0);
_ts_decorate$2d([
    dto({
        url: true
    }),
    _ts_metadata$2d("design:type", String)
], IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO.prototype, "infoUrl", void 0);

function _define_property$2c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2c(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2c(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoCancelledRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2c(this, "status", void 0), _define_property$2c(this, "message", void 0), _define_property$2c(this, "payment", void 0), _define_property$2c(this, "delivery", void 0);
    }
}
_ts_decorate$2c([
    dto({
        equals: OrderStatus.CANCELLED
    }),
    _ts_metadata$2c("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.CANCELLED === "undefined" ? Object : OrderStatus.CANCELLED)
], IntegrationSyncOrderStatusInfoCancelledRequestDTO.prototype, "status", void 0);
_ts_decorate$2c([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2c("design:type", Object)
], IntegrationSyncOrderStatusInfoCancelledRequestDTO.prototype, "message", void 0);
_ts_decorate$2c([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonPaymentRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2c("design:type", Object)
], IntegrationSyncOrderStatusInfoCancelledRequestDTO.prototype, "payment", void 0);
_ts_decorate$2c([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2c("design:type", Object)
], IntegrationSyncOrderStatusInfoCancelledRequestDTO.prototype, "delivery", void 0);

function _define_property$2b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2b(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoCompletedResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2b(this, "status", void 0), _define_property$2b(this, "payment", void 0), _define_property$2b(this, "delivery", void 0);
    }
}
_ts_decorate$2b([
    dto({
        equals: OrderStatus.COMPLETED
    }),
    _ts_metadata$2b("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.COMPLETED === "undefined" ? Object : OrderStatus.COMPLETED)
], IntegrationSyncOrderStatusInfoCompletedResponseDTO.prototype, "status", void 0);
_ts_decorate$2b([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonPaymentRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2b("design:type", Object)
], IntegrationSyncOrderStatusInfoCompletedResponseDTO.prototype, "payment", void 0);
_ts_decorate$2b([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2b("design:type", Object)
], IntegrationSyncOrderStatusInfoCompletedResponseDTO.prototype, "delivery", void 0);

function _define_property$2a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2a(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2a(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoDeliveringResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2a(this, "status", void 0), _define_property$2a(this, "payment", void 0), _define_property$2a(this, "delivery", void 0);
    }
}
_ts_decorate$2a([
    dto({
        equals: OrderStatus.DELIVERING
    }),
    _ts_metadata$2a("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.DELIVERING === "undefined" ? Object : OrderStatus.DELIVERING)
], IntegrationSyncOrderStatusInfoDeliveringResponseDTO.prototype, "status", void 0);
_ts_decorate$2a([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonPaymentRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2a("design:type", Object)
], IntegrationSyncOrderStatusInfoDeliveringResponseDTO.prototype, "payment", void 0);
_ts_decorate$2a([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$2a("design:type", Object)
], IntegrationSyncOrderStatusInfoDeliveringResponseDTO.prototype, "delivery", void 0);

function _define_property$29(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$29(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$29(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoReadyResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$29(this, "status", void 0), _define_property$29(this, "payment", void 0), _define_property$29(this, "delivery", void 0);
    }
}
_ts_decorate$29([
    dto({
        equals: OrderStatus.READY
    }),
    _ts_metadata$29("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.READY === "undefined" ? Object : OrderStatus.READY)
], IntegrationSyncOrderStatusInfoReadyResponseDTO.prototype, "status", void 0);
_ts_decorate$29([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonPaymentRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$29("design:type", Object)
], IntegrationSyncOrderStatusInfoReadyResponseDTO.prototype, "payment", void 0);
_ts_decorate$29([
    dto({
        object: {
            type: IntegrationSyncOrderStatusInfoCommonDeliveryRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$29("design:type", Object)
], IntegrationSyncOrderStatusInfoReadyResponseDTO.prototype, "delivery", void 0);

function _define_property$28(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$28(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$28(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusInfoWaitingPaymentLinkResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$28(this, "link", void 0), _define_property$28(this, "name", void 0);
    }
}
_ts_decorate$28([
    dto({
        url: {
            allowAnyProtocol: true
        }
    }),
    _ts_metadata$28("design:type", String)
], IntegrationSyncOrderStatusInfoWaitingPaymentLinkResponseDTO.prototype, "link", void 0);
_ts_decorate$28([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$28("design:type", Object)
], IntegrationSyncOrderStatusInfoWaitingPaymentLinkResponseDTO.prototype, "name", void 0);
class IntegrationSyncOrderStatusInfoWaitingPaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$28(this, "status", void 0), _define_property$28(this, "message", void 0), _define_property$28(this, "paymentLinks", void 0);
    }
}
_ts_decorate$28([
    dto({
        equals: OrderStatus.WAITING_PAYMENT
    }),
    _ts_metadata$28("design:type", typeof OrderStatus === "undefined" || typeof OrderStatus.WAITING_PAYMENT === "undefined" ? Object : OrderStatus.WAITING_PAYMENT)
], IntegrationSyncOrderStatusInfoWaitingPaymentResponseDTO.prototype, "status", void 0);
_ts_decorate$28([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$28("design:type", Object)
], IntegrationSyncOrderStatusInfoWaitingPaymentResponseDTO.prototype, "message", void 0);
_ts_decorate$28([
    dto({
        array: true,
        object: {
            type: IntegrationSyncOrderStatusInfoWaitingPaymentLinkResponseDTO
        }
    }),
    _ts_metadata$28("design:type", Array)
], IntegrationSyncOrderStatusInfoWaitingPaymentResponseDTO.prototype, "paymentLinks", void 0);

function _define_property$27(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$27(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$27(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderStatusRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$27(this, "status", void 0), _define_property$27(this, "info", void 0);
    }
}
_ts_decorate$27([
    dto({
        enum: OrderStatus
    }),
    _ts_metadata$27("design:type", typeof OrderStatus === "undefined" ? Object : OrderStatus)
], IntegrationSyncOrderStatusRequestDTO.prototype, "status", void 0);
_ts_decorate$27([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: OrderStatus.CANCELLED,
                        value: IntegrationSyncOrderStatusInfoCancelledRequestDTO
                    },
                    {
                        name: OrderStatus.COMPLETED,
                        value: IntegrationSyncOrderStatusInfoCompletedResponseDTO
                    },
                    {
                        name: OrderStatus.DELIVERING,
                        value: IntegrationSyncOrderStatusInfoDeliveringResponseDTO
                    },
                    {
                        name: OrderStatus.READY,
                        value: IntegrationSyncOrderStatusInfoReadyResponseDTO
                    },
                    {
                        name: OrderStatus.WAITING_PAYMENT,
                        value: IntegrationSyncOrderStatusInfoWaitingPaymentResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$27("design:type", Object)
], IntegrationSyncOrderStatusRequestDTO.prototype, "info", void 0);

function _define_property$26(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$26(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$26(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderBaseRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$26(this, "managerComment", void 0), _define_property$26(this, "price", void 0), _define_property$26(this, "products", void 0), _define_property$26(this, "status", void 0);
    }
}
_ts_decorate$26([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$26("design:type", Object)
], IntegrationSyncOrderBaseRequestDTO.prototype, "managerComment", void 0);
_ts_decorate$26([
    dto({
        number: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$26("design:type", Object)
], IntegrationSyncOrderBaseRequestDTO.prototype, "price", void 0);
_ts_decorate$26([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: IntegrationSyncOrderProductRequestDTO
        },
        array: true
    }),
    _ts_metadata$26("design:type", Object)
], IntegrationSyncOrderBaseRequestDTO.prototype, "products", void 0);
_ts_decorate$26([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: IntegrationSyncOrderStatusRequestDTO
        }
    }),
    _ts_metadata$26("design:type", Object)
], IntegrationSyncOrderBaseRequestDTO.prototype, "status", void 0);
class IntegrationSyncOrderRequestDTO extends IntegrationSyncOrderBaseRequestDTO {
    constructor(...args){
        super(...args), _define_property$26(this, "orderId", void 0);
    }
}
_ts_decorate$26([
    dto({
        string: true
    }),
    _ts_metadata$26("design:type", String)
], IntegrationSyncOrderRequestDTO.prototype, "orderId", void 0);

function _define_property$25(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$25(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$25(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$25(this, "status", void 0), _define_property$25(this, "id", void 0), _define_property$25(this, "number", void 0), _define_property$25(this, "sync", void 0);
    }
}
_ts_decorate$25([
    dto({
        equals: IntegrationCreateOrderResponseStatus.SUCCESS
    }),
    _ts_metadata$25("design:type", typeof IntegrationCreateOrderResponseStatus === "undefined" || typeof IntegrationCreateOrderResponseStatus.SUCCESS === "undefined" ? Object : IntegrationCreateOrderResponseStatus.SUCCESS)
], IntegrationCreateOrderSuccessResponseDTO.prototype, "status", void 0);
_ts_decorate$25([
    dto({
        string: true
    }),
    _ts_metadata$25("design:type", String)
], IntegrationCreateOrderSuccessResponseDTO.prototype, "id", void 0);
_ts_decorate$25([
    dto({
        string: true
    }),
    _ts_metadata$25("design:type", String)
], IntegrationCreateOrderSuccessResponseDTO.prototype, "number", void 0);
_ts_decorate$25([
    dto({
        object: {
            type: IntegrationSyncOrderBaseRequestDTO
        },
        optional: true
    }),
    _ts_metadata$25("design:type", Object)
], IntegrationCreateOrderSuccessResponseDTO.prototype, "sync", void 0);

function _define_property$24(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$24(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$24(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$24(this, "data", void 0);
    }
}
_ts_decorate$24([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationCreateOrderResponseStatus.SUCCESS,
                        value: IntegrationCreateOrderSuccessResponseDTO
                    },
                    {
                        name: IntegrationCreateOrderResponseStatus.ERROR,
                        value: IntegrationCreateOrderErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$24("design:type", Object)
], IntegrationCreateOrderResponseDTO.prototype, "data", void 0);

function _define_property$23(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$23(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$23(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderProductAttributeRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$23(this, "id", void 0), _define_property$23(this, "value", void 0);
    }
}
_ts_decorate$23([
    dto({
        string: true
    }),
    _ts_metadata$23("design:type", String)
], IntegrationCreateOrderProductAttributeRequestV1DTO.prototype, "id", void 0);
_ts_decorate$23([
    dto({
        string: true
    }),
    _ts_metadata$23("design:type", String)
], IntegrationCreateOrderProductAttributeRequestV1DTO.prototype, "value", void 0);
class IntegrationCreateOrderProductRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$23(this, "id", void 0), _define_property$23(this, "count", void 0), _define_property$23(this, "price", void 0), _define_property$23(this, "attributes", void 0);
    }
}
_ts_decorate$23([
    dto({
        string: true
    }),
    _ts_metadata$23("design:type", String)
], IntegrationCreateOrderProductRequestV1DTO.prototype, "id", void 0);
_ts_decorate$23([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$23("design:type", Number)
], IntegrationCreateOrderProductRequestV1DTO.prototype, "count", void 0);
_ts_decorate$23([
    dto({
        number: true
    }),
    _ts_metadata$23("design:type", Number)
], IntegrationCreateOrderProductRequestV1DTO.prototype, "price", void 0);
_ts_decorate$23([
    dto({
        array: true,
        object: {
            type: IntegrationCreateOrderProductAttributeRequestV1DTO
        }
    }),
    _ts_metadata$23("design:type", Array)
], IntegrationCreateOrderProductRequestV1DTO.prototype, "attributes", void 0);
class IntegrationCreateOrderRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$23(this, "userId", void 0), _define_property$23(this, "products", void 0), _define_property$23(this, "applyBonuses", void 0), _define_property$23(this, "shopId", void 0), _define_property$23(this, "currentUserPrice", void 0), _define_property$23(this, "comment", void 0);
    }
}
_ts_decorate$23([
    dto({
        string: true
    }),
    _ts_metadata$23("design:type", String)
], IntegrationCreateOrderRequestV1DTO.prototype, "userId", void 0);
_ts_decorate$23([
    dto({
        array: true,
        object: {
            type: IntegrationCreateOrderProductRequestV1DTO
        }
    }),
    _ts_metadata$23("design:type", Array)
], IntegrationCreateOrderRequestV1DTO.prototype, "products", void 0);
_ts_decorate$23([
    dto({
        boolean: true
    }),
    _ts_metadata$23("design:type", Boolean)
], IntegrationCreateOrderRequestV1DTO.prototype, "applyBonuses", void 0);
_ts_decorate$23([
    dto({
        string: true
    }),
    _ts_metadata$23("design:type", String)
], IntegrationCreateOrderRequestV1DTO.prototype, "shopId", void 0);
_ts_decorate$23([
    dto({
        number: true
    }),
    _ts_metadata$23("design:type", Number)
], IntegrationCreateOrderRequestV1DTO.prototype, "currentUserPrice", void 0);
_ts_decorate$23([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$23("design:type", Object)
], IntegrationCreateOrderRequestV1DTO.prototype, "comment", void 0);

function _define_property$22(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$22(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$22(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrderResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$22(this, "id", void 0), _define_property$22(this, "status", void 0), _define_property$22(this, "number", void 0);
    }
}
_ts_decorate$22([
    dto({
        string: true
    }),
    _ts_metadata$22("design:type", String)
], IntegrationCreateOrderResponseV1DTO.prototype, "id", void 0);
_ts_decorate$22([
    dto({
        enum: OrderStatus
    }),
    _ts_metadata$22("design:type", typeof OrderStatus === "undefined" ? Object : OrderStatus)
], IntegrationCreateOrderResponseV1DTO.prototype, "status", void 0);
_ts_decorate$22([
    dto({
        string: true
    }),
    _ts_metadata$22("design:type", String)
], IntegrationCreateOrderResponseV1DTO.prototype, "number", void 0);

function _define_property$21(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$21(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$21(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncOrderRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$21(this, "orderId", void 0), _define_property$21(this, "status", void 0), _define_property$21(this, "managerComment", void 0), _define_property$21(this, "price", void 0);
    }
}
_ts_decorate$21([
    dto({
        string: true
    }),
    _ts_metadata$21("design:type", String)
], IntegrationSyncOrderRequestV1DTO.prototype, "orderId", void 0);
_ts_decorate$21([
    dto({
        optional: true,
        enum: OrderStatus
    }),
    _ts_metadata$21("design:type", Object)
], IntegrationSyncOrderRequestV1DTO.prototype, "status", void 0);
_ts_decorate$21([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$21("design:type", Object)
], IntegrationSyncOrderRequestV1DTO.prototype, "managerComment", void 0);
_ts_decorate$21([
    dto({
        number: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$21("design:type", Object)
], IntegrationSyncOrderRequestV1DTO.prototype, "price", void 0);

function _define_property$20(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$20(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$20(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateOrderProductAttributeRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$20(this, "id", void 0), _define_property$20(this, "value", void 0);
    }
}
_ts_decorate$20([
    dto({
        string: true
    }),
    _ts_metadata$20("design:type", String)
], IntegrationValidateOrderProductAttributeRequestV1DTO.prototype, "id", void 0);
_ts_decorate$20([
    dto({
        string: true
    }),
    _ts_metadata$20("design:type", String)
], IntegrationValidateOrderProductAttributeRequestV1DTO.prototype, "value", void 0);
class IntegrationValidateOrderProductRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$20(this, "id", void 0), _define_property$20(this, "backendId", void 0), _define_property$20(this, "count", void 0), _define_property$20(this, "attributes", void 0);
    }
}
_ts_decorate$20([
    dto({
        string: true
    }),
    _ts_metadata$20("design:type", String)
], IntegrationValidateOrderProductRequestV1DTO.prototype, "id", void 0);
_ts_decorate$20([
    dto({
        string: true
    }),
    _ts_metadata$20("design:type", String)
], IntegrationValidateOrderProductRequestV1DTO.prototype, "backendId", void 0);
_ts_decorate$20([
    dto({
        number: true
    }),
    _ts_metadata$20("design:type", Number)
], IntegrationValidateOrderProductRequestV1DTO.prototype, "count", void 0);
_ts_decorate$20([
    dto({
        array: true,
        object: {
            type: IntegrationValidateOrderProductAttributeRequestV1DTO
        }
    }),
    _ts_metadata$20("design:type", Array)
], IntegrationValidateOrderProductRequestV1DTO.prototype, "attributes", void 0);
class IntegrationValidateOrderRequestV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$20(this, "userId", void 0), _define_property$20(this, "products", void 0), _define_property$20(this, "applyBonuses", void 0), _define_property$20(this, "shopId", void 0);
    }
}
_ts_decorate$20([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$20("design:type", Object)
], IntegrationValidateOrderRequestV1DTO.prototype, "userId", void 0);
_ts_decorate$20([
    dto({
        array: true,
        object: {
            type: IntegrationValidateOrderProductRequestV1DTO
        }
    }),
    _ts_metadata$20("design:type", Array)
], IntegrationValidateOrderRequestV1DTO.prototype, "products", void 0);
_ts_decorate$20([
    dto({
        boolean: true
    }),
    _ts_metadata$20("design:type", Boolean)
], IntegrationValidateOrderRequestV1DTO.prototype, "applyBonuses", void 0);
_ts_decorate$20([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$20("design:type", Object)
], IntegrationValidateOrderRequestV1DTO.prototype, "shopId", void 0);

function _define_property$1$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1$(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1$(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationValidateOrderProductInfoResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1$(this, "basePrice", void 0), _define_property$1$(this, "resultPrice", void 0), _define_property$1$(this, "discount", void 0);
    }
}
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderProductInfoResponseV1DTO.prototype, "basePrice", void 0);
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderProductInfoResponseV1DTO.prototype, "resultPrice", void 0);
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderProductInfoResponseV1DTO.prototype, "discount", void 0);
class IntegrationValidateOrderProductResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1$(this, "id", void 0), _define_property$1$(this, "backendId", void 0), _define_property$1$(this, "maxCount", void 0), _define_property$1$(this, "info", void 0);
    }
}
_ts_decorate$1$([
    dto({
        string: true
    }),
    _ts_metadata$1$("design:type", String)
], IntegrationValidateOrderProductResponseV1DTO.prototype, "id", void 0);
_ts_decorate$1$([
    dto({
        string: true
    }),
    _ts_metadata$1$("design:type", String)
], IntegrationValidateOrderProductResponseV1DTO.prototype, "backendId", void 0);
_ts_decorate$1$([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderProductResponseV1DTO.prototype, "maxCount", void 0);
_ts_decorate$1$([
    dto({
        object: {
            type: IntegrationValidateOrderProductInfoResponseV1DTO
        }
    }),
    _ts_metadata$1$("design:type", typeof IntegrationValidateOrderProductInfoResponseV1DTO === "undefined" ? Object : IntegrationValidateOrderProductInfoResponseV1DTO)
], IntegrationValidateOrderProductResponseV1DTO.prototype, "info", void 0);
class IntegrationValidateOrderInfoBonusesResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1$(this, "willUse", void 0), _define_property$1$(this, "willAdd", void 0), _define_property$1$(this, "current", void 0);
    }
}
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderInfoBonusesResponseV1DTO.prototype, "willUse", void 0);
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderInfoBonusesResponseV1DTO.prototype, "willAdd", void 0);
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderInfoBonusesResponseV1DTO.prototype, "current", void 0);
class IntegrationValidateOrderInfoCalculationResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1$(this, "name", void 0), _define_property$1$(this, "description", void 0), _define_property$1$(this, "value", void 0), _define_property$1$(this, "measure", void 0);
    }
}
_ts_decorate$1$([
    dto({
        string: true
    }),
    _ts_metadata$1$("design:type", String)
], IntegrationValidateOrderInfoCalculationResponseV1DTO.prototype, "name", void 0);
_ts_decorate$1$([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$1$("design:type", Object)
], IntegrationValidateOrderInfoCalculationResponseV1DTO.prototype, "description", void 0);
_ts_decorate$1$([
    dto({
        number: true
    }),
    _ts_metadata$1$("design:type", Number)
], IntegrationValidateOrderInfoCalculationResponseV1DTO.prototype, "value", void 0);
_ts_decorate$1$([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$1$("design:type", Object)
], IntegrationValidateOrderInfoCalculationResponseV1DTO.prototype, "measure", void 0);
class IntegrationValidateOrderInfoResponseV1DTO extends IntegrationValidateOrderProductInfoResponseV1DTO {
    constructor(...args){
        super(...args), _define_property$1$(this, "bonuses", void 0), _define_property$1$(this, "calculation", void 0);
    }
}
_ts_decorate$1$([
    dto({
        object: {
            type: IntegrationValidateOrderInfoBonusesResponseV1DTO
        }
    }),
    _ts_metadata$1$("design:type", typeof IntegrationValidateOrderInfoBonusesResponseV1DTO === "undefined" ? Object : IntegrationValidateOrderInfoBonusesResponseV1DTO)
], IntegrationValidateOrderInfoResponseV1DTO.prototype, "bonuses", void 0);
_ts_decorate$1$([
    dto({
        object: {
            type: IntegrationValidateOrderInfoCalculationResponseV1DTO
        },
        array: true
    }),
    _ts_metadata$1$("design:type", Array)
], IntegrationValidateOrderInfoResponseV1DTO.prototype, "calculation", void 0);
class IntegrationValidateOrderResponseV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1$(this, "info", void 0), _define_property$1$(this, "products", void 0);
    }
}
_ts_decorate$1$([
    dto({
        object: {
            type: IntegrationValidateOrderInfoResponseV1DTO
        }
    }),
    _ts_metadata$1$("design:type", typeof IntegrationValidateOrderInfoResponseV1DTO === "undefined" ? Object : IntegrationValidateOrderInfoResponseV1DTO)
], IntegrationValidateOrderResponseV1DTO.prototype, "info", void 0);
_ts_decorate$1$([
    dto({
        array: true,
        object: {
            type: IntegrationValidateOrderProductResponseV1DTO
        }
    }),
    _ts_metadata$1$("design:type", Array)
], IntegrationValidateOrderResponseV1DTO.prototype, "products", void 0);

function _define_property$1_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1_(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1_(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeletePromoSplashDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1_(this, "id", void 0);
    }
}
_ts_decorate$1_([
    dto({
        string: true
    }),
    _ts_metadata$1_("design:type", String)
], IntegrationDeletePromoSplashDTO.prototype, "id", void 0);

function _define_property$1Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1Z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1Z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializePromoSplashDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1Z(this, "id", void 0), _define_property$1Z(this, "startDate", void 0), _define_property$1Z(this, "endDate", void 0), _define_property$1Z(this, "durationSeconds", void 0), _define_property$1Z(this, "index", void 0);
    }
}
_ts_decorate$1Z([
    dto({
        string: true
    }),
    _ts_metadata$1Z("design:type", String)
], IntegrationInitializePromoSplashDTO.prototype, "id", void 0);
_ts_decorate$1Z([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$1Z("design:type", Object)
], IntegrationInitializePromoSplashDTO.prototype, "startDate", void 0);
_ts_decorate$1Z([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$1Z("design:type", Object)
], IntegrationInitializePromoSplashDTO.prototype, "endDate", void 0);
_ts_decorate$1Z([
    dto({
        optional: {
            allowNull: false
        },
        number: {
            maxDecimalPlaces: 1,
            min: 0
        }
    }),
    _ts_metadata$1Z("design:type", Object)
], IntegrationInitializePromoSplashDTO.prototype, "durationSeconds", void 0);
_ts_decorate$1Z([
    dto({
        optional: {
            allowNull: false
        },
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$1Z("design:type", Object)
], IntegrationInitializePromoSplashDTO.prototype, "index", void 0);

var PromoSplashInfoType = /*#__PURE__*/ function(PromoSplashInfoType) {
    PromoSplashInfoType["IMAGE"] = "image";
    return PromoSplashInfoType;
}({});

function _define_property$1Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1Y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1Y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class PromoSplashImageInfoDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1Y(this, "type", void 0), _define_property$1Y(this, "file", void 0);
    }
}
_ts_decorate$1Y([
    dto({
        equals: PromoSplashInfoType.IMAGE
    }),
    _ts_metadata$1Y("design:type", typeof PromoSplashInfoType === "undefined" || typeof PromoSplashInfoType.IMAGE === "undefined" ? Object : PromoSplashInfoType.IMAGE)
], PromoSplashImageInfoDTO.prototype, "type", void 0);
_ts_decorate$1Y([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$1Y("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], PromoSplashImageInfoDTO.prototype, "file", void 0);
class PromoSplashResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1Y(this, "id", void 0), _define_property$1Y(this, "startDate", void 0), _define_property$1Y(this, "endDate", void 0), _define_property$1Y(this, "durationSeconds", void 0), _define_property$1Y(this, "info", void 0);
    }
}
_ts_decorate$1Y([
    dto({
        string: true
    }),
    _ts_metadata$1Y("design:type", String)
], PromoSplashResponseDTO.prototype, "id", void 0);
_ts_decorate$1Y([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$1Y("design:type", Object)
], PromoSplashResponseDTO.prototype, "startDate", void 0);
_ts_decorate$1Y([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$1Y("design:type", Object)
], PromoSplashResponseDTO.prototype, "endDate", void 0);
_ts_decorate$1Y([
    dto({
        number: {
            maxDecimalPlaces: 1,
            min: 0
        }
    }),
    _ts_metadata$1Y("design:type", Number)
], PromoSplashResponseDTO.prototype, "durationSeconds", void 0);
_ts_decorate$1Y([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: PromoSplashInfoType.IMAGE,
                        value: PromoSplashImageInfoDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1Y("design:type", typeof PromoSplashImageInfoDTO === "undefined" ? Object : PromoSplashImageInfoDTO)
], PromoSplashResponseDTO.prototype, "info", void 0);

function _define_property$1X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1X(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1X(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializePromoSplashInfoImageDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1X(this, "type", void 0);
    }
}
_ts_decorate$1X([
    dto({
        equals: PromoSplashInfoType.IMAGE
    }),
    _ts_metadata$1X("design:type", typeof PromoSplashInfoType === "undefined" || typeof PromoSplashInfoType.IMAGE === "undefined" ? Object : PromoSplashInfoType.IMAGE)
], IntegrationInitializePromoSplashInfoImageDTO.prototype, "type", void 0);
class IntegrationInitializePromoSplashInfoDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1X(this, "promoSplashId", void 0), _define_property$1X(this, "info", void 0);
    }
}
_ts_decorate$1X([
    dto({
        string: true
    }),
    _ts_metadata$1X("design:type", String)
], IntegrationInitializePromoSplashInfoDTO.prototype, "promoSplashId", void 0);
_ts_decorate$1X([
    dto({
        name: "info__json",
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: PromoSplashInfoType.IMAGE,
                        value: IntegrationInitializePromoSplashInfoImageDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1X("design:type", typeof IntegrationInitializePromoSplashInfoImageDTO === "undefined" ? Object : IntegrationInitializePromoSplashInfoImageDTO)
], IntegrationInitializePromoSplashInfoDTO.prototype, "info", void 0);

function _define_property$1W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1W(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1W(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CreateProductReviewComplaintRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1W(this, "reviewId", void 0), _define_property$1W(this, "message", void 0);
    }
}
_ts_decorate$1W([
    dto({
        string: true
    }),
    _ts_metadata$1W("design:type", String)
], CreateProductReviewComplaintRequestDTO.prototype, "reviewId", void 0);
_ts_decorate$1W([
    dto({
        string: true
    }),
    _ts_metadata$1W("design:type", String)
], CreateProductReviewComplaintRequestDTO.prototype, "message", void 0);

function _define_property$1V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1V(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1V(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CreateProductReviewComplaintResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1V(this, "daysForReview", void 0);
    }
}
_ts_decorate$1V([
    dto({
        number: {
            min: 1
        }
    }),
    _ts_metadata$1V("design:type", Number)
], CreateProductReviewComplaintResponseDTO.prototype, "daysForReview", void 0);

function _define_property$1U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1U(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1U(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CreateProductReviewMessageRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1U(this, "reviewId", void 0), _define_property$1U(this, "replyToMessageId", void 0), _define_property$1U(this, "text", void 0);
    }
}
_ts_decorate$1U([
    dto({
        string: true
    }),
    _ts_metadata$1U("design:type", String)
], CreateProductReviewMessageRequestDTO.prototype, "reviewId", void 0);
_ts_decorate$1U([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1U("design:type", Object)
], CreateProductReviewMessageRequestDTO.prototype, "replyToMessageId", void 0);
_ts_decorate$1U([
    dto({
        string: true
    }),
    _ts_metadata$1U("design:type", String)
], CreateProductReviewMessageRequestDTO.prototype, "text", void 0);
class CreateProductReviewRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1U(this, "productId", void 0), _define_property$1U(this, "rating", void 0), _define_property$1U(this, "text", void 0);
    }
}
_ts_decorate$1U([
    dto({
        string: true
    }),
    _ts_metadata$1U("design:type", String)
], CreateProductReviewRequestDTO.prototype, "productId", void 0);
_ts_decorate$1U([
    dto({
        number: {
            maxDecimalPlaces: 0,
            min: 1,
            max: 5,
            asString: true
        },
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$1U("design:type", Object)
], CreateProductReviewRequestDTO.prototype, "rating", void 0);
_ts_decorate$1U([
    dto({
        string: true
    }),
    _ts_metadata$1U("design:type", String)
], CreateProductReviewRequestDTO.prototype, "text", void 0);

function _define_property$1T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1T(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1T(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetProductReviewRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1T(this, "productId", void 0);
    }
}
_ts_decorate$1T([
    dto({
        string: true
    }),
    _ts_metadata$1T("design:type", String)
], GetProductReviewRequestDTO.prototype, "productId", void 0);

function _define_property$1S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1S(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1S(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class GetProductReviewAuthorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1S(this, "name", void 0);
    }
}
_ts_decorate$1S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1S("design:type", Object)
], GetProductReviewAuthorResponseDTO.prototype, "name", void 0);
var ProductReviewMessageMediaType = /*#__PURE__*/ function(ProductReviewMessageMediaType) {
    ProductReviewMessageMediaType["IMAGE"] = "image";
    ProductReviewMessageMediaType["VIDEO"] = "video";
    return ProductReviewMessageMediaType;
}({});
class GetProductReviewMessageMediaResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1S(this, "type", void 0), _define_property$1S(this, "file", void 0);
    }
}
_ts_decorate$1S([
    dto({
        enum: ProductReviewMessageMediaType
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewMessageMediaResponseDTO.prototype, "type", void 0);
_ts_decorate$1S([
    dto({
        object: {
            type: StorageFileDTO
        }
    }),
    _ts_metadata$1S("design:type", typeof StorageFileDTO === "undefined" ? Object : StorageFileDTO)
], GetProductReviewMessageMediaResponseDTO.prototype, "file", void 0);
class GetProductReviewMessageResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1S(this, "id", void 0), _define_property$1S(this, "author", void 0), _define_property$1S(this, "text", void 0), _define_property$1S(this, "createdAt", void 0), _define_property$1S(this, "replyToMessageId", void 0), _define_property$1S(this, "media", void 0);
    }
}
_ts_decorate$1S([
    dto({
        string: true
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewMessageResponseDTO.prototype, "id", void 0);
_ts_decorate$1S([
    dto({
        object: {
            type: GetProductReviewAuthorResponseDTO
        }
    }),
    _ts_metadata$1S("design:type", typeof GetProductReviewAuthorResponseDTO === "undefined" ? Object : GetProductReviewAuthorResponseDTO)
], GetProductReviewMessageResponseDTO.prototype, "author", void 0);
_ts_decorate$1S([
    dto({
        string: true
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewMessageResponseDTO.prototype, "text", void 0);
_ts_decorate$1S([
    dto({
        date: true
    }),
    _ts_metadata$1S("design:type", typeof Date === "undefined" ? Object : Date)
], GetProductReviewMessageResponseDTO.prototype, "createdAt", void 0);
_ts_decorate$1S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1S("design:type", Object)
], GetProductReviewMessageResponseDTO.prototype, "replyToMessageId", void 0);
_ts_decorate$1S([
    dto({
        array: true,
        object: {
            type: GetProductReviewMessageMediaResponseDTO
        }
    }),
    _ts_metadata$1S("design:type", Array)
], GetProductReviewMessageResponseDTO.prototype, "media", void 0);
var ProductReviewStatus = /*#__PURE__*/ function(ProductReviewStatus) {
    ProductReviewStatus["PENDING"] = "pending";
    ProductReviewStatus["APPROVED"] = "approved";
    ProductReviewStatus["DECLINED"] = "declined";
    return ProductReviewStatus;
}({});
class GetProductReviewResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1S(this, "id", void 0), _define_property$1S(this, "rating", void 0), _define_property$1S(this, "status", void 0), _define_property$1S(this, "public", void 0), _define_property$1S(this, "messages", void 0);
    }
}
_ts_decorate$1S([
    dto({
        string: true
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewResponseDTO.prototype, "id", void 0);
_ts_decorate$1S([
    dto({
        optional: {
            allowUndefined: false
        },
        number: {
            maxDecimalPlaces: 0,
            min: 1,
            max: 5
        }
    }),
    _ts_metadata$1S("design:type", Object)
], GetProductReviewResponseDTO.prototype, "rating", void 0);
_ts_decorate$1S([
    dto({
        enum: ProductReviewStatus
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewResponseDTO.prototype, "status", void 0);
_ts_decorate$1S([
    dto({
        boolean: true
    }),
    _ts_metadata$1S("design:type", Boolean)
], GetProductReviewResponseDTO.prototype, "public", void 0);
_ts_decorate$1S([
    dto({
        array: true,
        object: {
            type: GetProductReviewMessageResponseDTO
        }
    }),
    _ts_metadata$1S("design:type", Array)
], GetProductReviewResponseDTO.prototype, "messages", void 0);
class GetProductReviewListItemResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1S(this, "id", void 0), _define_property$1S(this, "rating", void 0), _define_property$1S(this, "status", void 0), _define_property$1S(this, "public", void 0), _define_property$1S(this, "messagesCount", void 0);
    }
}
_ts_decorate$1S([
    dto({
        string: true
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewListItemResponseDTO.prototype, "id", void 0);
_ts_decorate$1S([
    dto({
        optional: {
            allowUndefined: false
        },
        number: {
            maxDecimalPlaces: 0,
            min: 1,
            max: 5
        }
    }),
    _ts_metadata$1S("design:type", Object)
], GetProductReviewListItemResponseDTO.prototype, "rating", void 0);
_ts_decorate$1S([
    dto({
        enum: ProductReviewStatus
    }),
    _ts_metadata$1S("design:type", String)
], GetProductReviewListItemResponseDTO.prototype, "status", void 0);
_ts_decorate$1S([
    dto({
        boolean: true
    }),
    _ts_metadata$1S("design:type", Boolean)
], GetProductReviewListItemResponseDTO.prototype, "public", void 0);
_ts_decorate$1S([
    dto({
        number: {
            min: 0
        }
    }),
    _ts_metadata$1S("design:type", Number)
], GetProductReviewListItemResponseDTO.prototype, "messagesCount", void 0);

function _define_property$1R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1R(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1R(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateProductReviewMessageFileRequestDTO {
    constructor(){
        _define_property$1R(this, "type", void 0);
        _define_property$1R(this, "url", void 0);
    }
}
_ts_decorate$1R([
    dto({
        enum: ProductReviewMessageMediaType
    }),
    _ts_metadata$1R("design:type", typeof ProductReviewMessageMediaType === "undefined" ? Object : ProductReviewMessageMediaType)
], IntegrationCreateProductReviewMessageFileRequestDTO.prototype, "type", void 0);
_ts_decorate$1R([
    dto({
        url: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageFileRequestDTO.prototype, "url", void 0);
class IntegrationCreateProductReviewMessageForCustomerRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1R(this, "productId", void 0), _define_property$1R(this, "reviewId", void 0), _define_property$1R(this, "replyToMessageId", void 0), _define_property$1R(this, "authorId", void 0), _define_property$1R(this, "text", void 0), _define_property$1R(this, "files", void 0);
    }
}
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForCustomerRequestDTO.prototype, "productId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForCustomerRequestDTO.prototype, "reviewId", void 0);
_ts_decorate$1R([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1R("design:type", Object)
], IntegrationCreateProductReviewMessageForCustomerRequestDTO.prototype, "replyToMessageId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForCustomerRequestDTO.prototype, "authorId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForCustomerRequestDTO.prototype, "text", void 0);
_ts_decorate$1R([
    dto({
        array: true,
        object: {
            type: IntegrationCreateProductReviewMessageFileRequestDTO
        }
    }),
    _ts_metadata$1R("design:type", Array)
], IntegrationCreateProductReviewMessageForCustomerRequestDTO.prototype, "files", void 0);
class IntegrationCreateProductReviewMessageForManagerRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1R(this, "messageId", void 0), _define_property$1R(this, "reviewId", void 0), _define_property$1R(this, "replyToMessageId", void 0), _define_property$1R(this, "text", void 0);
    }
}
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForManagerRequestDTO.prototype, "messageId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForManagerRequestDTO.prototype, "reviewId", void 0);
_ts_decorate$1R([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1R("design:type", Object)
], IntegrationCreateProductReviewMessageForManagerRequestDTO.prototype, "replyToMessageId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewMessageForManagerRequestDTO.prototype, "text", void 0);
class IntegrationCreateProductReviewForCustomerRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1R(this, "productId", void 0), _define_property$1R(this, "authorId", void 0), _define_property$1R(this, "text", void 0), _define_property$1R(this, "rating", void 0), _define_property$1R(this, "files", void 0);
    }
}
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewForCustomerRequestDTO.prototype, "productId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewForCustomerRequestDTO.prototype, "authorId", void 0);
_ts_decorate$1R([
    dto({
        string: true
    }),
    _ts_metadata$1R("design:type", String)
], IntegrationCreateProductReviewForCustomerRequestDTO.prototype, "text", void 0);
_ts_decorate$1R([
    dto({
        number: {
            maxDecimalPlaces: 0,
            min: 1,
            max: 5
        },
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$1R("design:type", Object)
], IntegrationCreateProductReviewForCustomerRequestDTO.prototype, "rating", void 0);
_ts_decorate$1R([
    dto({
        array: true,
        object: {
            type: IntegrationCreateProductReviewMessageFileRequestDTO
        }
    }),
    _ts_metadata$1R("design:type", Array)
], IntegrationCreateProductReviewForCustomerRequestDTO.prototype, "files", void 0);

function _define_property$1Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1Q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1Q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateProductReviewMessageForCustomerResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1Q(this, "id", void 0);
    }
}
_ts_decorate$1Q([
    dto({
        string: true
    }),
    _ts_metadata$1Q("design:type", String)
], IntegrationCreateProductReviewMessageForCustomerResponseDTO.prototype, "id", void 0);
class IntegrationCreateProductReviewForCustomerResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1Q(this, "id", void 0), _define_property$1Q(this, "status", void 0), _define_property$1Q(this, "public", void 0);
    }
}
_ts_decorate$1Q([
    dto({
        string: true
    }),
    _ts_metadata$1Q("design:type", String)
], IntegrationCreateProductReviewForCustomerResponseDTO.prototype, "id", void 0);
_ts_decorate$1Q([
    dto({
        enum: ProductReviewStatus
    }),
    _ts_metadata$1Q("design:type", typeof ProductReviewStatus === "undefined" ? Object : ProductReviewStatus)
], IntegrationCreateProductReviewForCustomerResponseDTO.prototype, "status", void 0);
_ts_decorate$1Q([
    dto({
        boolean: true
    }),
    _ts_metadata$1Q("design:type", Boolean)
], IntegrationCreateProductReviewForCustomerResponseDTO.prototype, "public", void 0);

function _define_property$1P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1P(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1P(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationSyncProductReviewRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1P(this, "reviewId", void 0), _define_property$1P(this, "status", void 0), _define_property$1P(this, "public", void 0);
    }
}
_ts_decorate$1P([
    dto({
        string: true
    }),
    _ts_metadata$1P("design:type", String)
], IntegrationSyncProductReviewRequestDTO.prototype, "reviewId", void 0);
_ts_decorate$1P([
    dto({
        enum: ProductReviewStatus,
        optional: true
    }),
    _ts_metadata$1P("design:type", Object)
], IntegrationSyncProductReviewRequestDTO.prototype, "status", void 0);
_ts_decorate$1P([
    dto({
        boolean: true,
        optional: true
    }),
    _ts_metadata$1P("design:type", Object)
], IntegrationSyncProductReviewRequestDTO.prototype, "public", void 0);

function _define_property$1O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1O(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1O(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteProductInfoSectionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1O(this, "productId", void 0), _define_property$1O(this, "id", void 0);
    }
}
_ts_decorate$1O([
    dto({
        string: true
    }),
    _ts_metadata$1O("design:type", String)
], IntegrationDeleteProductInfoSectionDTO.prototype, "productId", void 0);
_ts_decorate$1O([
    dto({
        string: true
    }),
    _ts_metadata$1O("design:type", String)
], IntegrationDeleteProductInfoSectionDTO.prototype, "id", void 0);

function _define_property$1N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1N(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1N(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationProductDisplayStatusRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1N(this, "productId", void 0);
    }
}
_ts_decorate$1N([
    dto({
        string: true
    }),
    _ts_metadata$1N("design:type", String)
], IntegrationProductDisplayStatusRequestDTO.prototype, "productId", void 0);

function _define_property$1M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1M(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1M(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationProductDisplayStatusResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1M(this, "exists", void 0), _define_property$1M(this, "active", void 0), _define_property$1M(this, "hasMedia", void 0), _define_property$1M(this, "updatedAt", void 0), _define_property$1M(this, "catalogIndexedAt", void 0), _define_property$1M(this, "bannersIndexedAt", void 0);
    }
}
_ts_decorate$1M([
    dto({
        boolean: true
    }),
    _ts_metadata$1M("design:type", Boolean)
], IntegrationProductDisplayStatusResponseDTO.prototype, "exists", void 0);
_ts_decorate$1M([
    dto({
        boolean: true
    }),
    _ts_metadata$1M("design:type", Boolean)
], IntegrationProductDisplayStatusResponseDTO.prototype, "active", void 0);
_ts_decorate$1M([
    dto({
        boolean: true
    }),
    _ts_metadata$1M("design:type", Boolean)
], IntegrationProductDisplayStatusResponseDTO.prototype, "hasMedia", void 0);
_ts_decorate$1M([
    dto({
        date: true,
        optional: true
    }),
    _ts_metadata$1M("design:type", Object)
], IntegrationProductDisplayStatusResponseDTO.prototype, "updatedAt", void 0);
_ts_decorate$1M([
    dto({
        date: true,
        optional: true
    }),
    _ts_metadata$1M("design:type", Object)
], IntegrationProductDisplayStatusResponseDTO.prototype, "catalogIndexedAt", void 0);
_ts_decorate$1M([
    dto({
        date: true,
        optional: true
    }),
    _ts_metadata$1M("design:type", Object)
], IntegrationProductDisplayStatusResponseDTO.prototype, "bannersIndexedAt", void 0);

function _define_property$1L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1L(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1L(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationProductsGetIdsResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1L(this, "id", void 0);
    }
}
_ts_decorate$1L([
    dto({
        string: true
    }),
    _ts_metadata$1L("design:type", String)
], IntegrationProductsGetIdsResponseDTO.prototype, "id", void 0);

function _define_property$1K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1K(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1K(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetProductMediaHashRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1K(this, "productId", void 0);
    }
}
_ts_decorate$1K([
    dto({
        string: true
    }),
    _ts_metadata$1K("design:type", String)
], IntegrationGetProductMediaHashRequestDTO.prototype, "productId", void 0);

function _define_property$1J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1J(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1J(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationProductMediaHashResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1J(this, "hash", void 0);
    }
}
_ts_decorate$1J([
    dto({
        string: true
    }),
    _ts_metadata$1J("design:type", String)
], IntegrationProductMediaHashResponseDTO.prototype, "hash", void 0);

function _define_property$1I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1I(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1I(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeProductInfoSectionTextDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1I(this, "type", void 0);
    }
}
_ts_decorate$1I([
    dto({
        equals: ProductInfoSectionType.TEXT
    }),
    _ts_metadata$1I("design:type", typeof ProductInfoSectionType === "undefined" || typeof ProductInfoSectionType.TEXT === "undefined" ? Object : ProductInfoSectionType.TEXT)
], IntegrationInitializeProductInfoSectionTextDTO.prototype, "type", void 0);
class IntegrationInitializeProductInfoSectionLinkDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1I(this, "type", void 0), _define_property$1I(this, "link", void 0);
    }
}
_ts_decorate$1I([
    dto({
        equals: ProductInfoSectionType.LINK
    }),
    _ts_metadata$1I("design:type", typeof ProductInfoSectionType === "undefined" || typeof ProductInfoSectionType.LINK === "undefined" ? Object : ProductInfoSectionType.LINK)
], IntegrationInitializeProductInfoSectionLinkDTO.prototype, "type", void 0);
_ts_decorate$1I([
    dto({
        url: {
            allowAnyProtocol: true
        }
    }),
    _ts_metadata$1I("design:type", String)
], IntegrationInitializeProductInfoSectionLinkDTO.prototype, "link", void 0);
class IntegrationInitializeProductInfoSectionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1I(this, "productId", void 0), _define_property$1I(this, "id", void 0), _define_property$1I(this, "index", void 0), _define_property$1I(this, "text", void 0), _define_property$1I(this, "data", void 0);
    }
}
_ts_decorate$1I([
    dto({
        string: true
    }),
    _ts_metadata$1I("design:type", String)
], IntegrationInitializeProductInfoSectionDTO.prototype, "productId", void 0);
_ts_decorate$1I([
    dto({
        string: true
    }),
    _ts_metadata$1I("design:type", String)
], IntegrationInitializeProductInfoSectionDTO.prototype, "id", void 0);
_ts_decorate$1I([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$1I("design:type", Number)
], IntegrationInitializeProductInfoSectionDTO.prototype, "index", void 0);
_ts_decorate$1I([
    dto({
        string: true
    }),
    _ts_metadata$1I("design:type", String)
], IntegrationInitializeProductInfoSectionDTO.prototype, "text", void 0);
_ts_decorate$1I([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: ProductInfoSectionType.TEXT,
                        value: IntegrationInitializeProductInfoSectionTextDTO
                    },
                    {
                        name: ProductInfoSectionType.LINK,
                        value: IntegrationInitializeProductInfoSectionLinkDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1I("design:type", Object)
], IntegrationInitializeProductInfoSectionDTO.prototype, "data", void 0);

function _define_property$1H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1H(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1H(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeProductInfoSectionImageDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1H(this, "productId", void 0), _define_property$1H(this, "id", void 0);
    }
}
_ts_decorate$1H([
    dto({
        string: true
    }),
    _ts_metadata$1H("design:type", String)
], IntegrationInitializeProductInfoSectionImageDTO.prototype, "productId", void 0);
_ts_decorate$1H([
    dto({
        string: true
    }),
    _ts_metadata$1H("design:type", String)
], IntegrationInitializeProductInfoSectionImageDTO.prototype, "id", void 0);

function _define_property$1G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1G(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1G(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeProductMediaDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1G(this, "productId", void 0);
    }
}
_ts_decorate$1G([
    dto({
        string: true
    }),
    _ts_metadata$1G("design:type", String)
], IntegrationInitializeProductMediaDTO.prototype, "productId", void 0);

function _define_property$1F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1F(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1F(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OneCInitializeProductAttributeValueViewOptionsDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "numberIndex", void 0);
    }
}
_ts_decorate$1F([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductAttributeValueViewOptionsDTO.prototype, "numberIndex", void 0);
class OneCInitializeProductAttributeValueStringDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "value", void 0), _define_property$1F(this, "viewOptions", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductAttributeValueStringDTO.prototype, "value", void 0);
_ts_decorate$1F([
    dto({
        optional: true,
        object: {
            type: OneCInitializeProductAttributeValueViewOptionsDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductAttributeValueStringDTO.prototype, "viewOptions", void 0);
// Значение атрибута с valueType `number`. Порядкового номера у него нет намеренно: в справочнике
// фильтров числа сортируются числом, а не заданным из 1С индексом.
class OneCInitializeProductAttributeValueNumberDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "value", void 0);
    }
}
_ts_decorate$1F([
    dto({
        number: true
    }),
    _ts_metadata$1F("design:type", Number)
], OneCInitializeProductAttributeValueNumberDTO.prototype, "value", void 0);
class OneCInitializeProductAttributeValueStringArrayValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "value", void 0), _define_property$1F(this, "viewOptions", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductAttributeValueStringArrayValueDTO.prototype, "value", void 0);
_ts_decorate$1F([
    dto({
        optional: true,
        object: {
            type: OneCInitializeProductAttributeValueViewOptionsDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductAttributeValueStringArrayValueDTO.prototype, "viewOptions", void 0);
class OneCInitializeProductAttributeValueStringArrayDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "values", void 0);
    }
}
_ts_decorate$1F([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductAttributeValueStringArrayValueDTO
        }
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductAttributeValueStringArrayDTO.prototype, "values", void 0);
class OneCInitializeProductAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "id", void 0), _define_property$1F(this, "value", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductAttributeDTO.prototype, "id", void 0);
_ts_decorate$1F([
    dto({
        object: {
            transformer: transformProductAttributeValue$1,
            discriminator: {
                property: "__virtualType",
                subTypes: [
                    {
                        name: "string",
                        value: OneCInitializeProductAttributeValueStringDTO
                    },
                    {
                        name: "number",
                        value: OneCInitializeProductAttributeValueNumberDTO
                    },
                    {
                        name: "string_array",
                        value: OneCInitializeProductAttributeValueStringArrayDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductAttributeDTO.prototype, "value", void 0);
class OneCInitializeProductPartDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "categoryPartId", void 0), _define_property$1F(this, "id", void 0), _define_property$1F(this, "name", void 0), _define_property$1F(this, "attributes", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductPartDTO.prototype, "categoryPartId", void 0);
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductPartDTO.prototype, "id", void 0);
_ts_decorate$1F([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductPartDTO.prototype, "name", void 0);
_ts_decorate$1F([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductAttributeDTO
        }
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductPartDTO.prototype, "attributes", void 0);
class OneCInitializeProductPriceCustomPriceValuesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "base", void 0), _define_property$1F(this, "result", void 0), _define_property$1F(this, "discountPercent", void 0);
    }
}
_ts_decorate$1F([
    dto({
        number: true
    }),
    _ts_metadata$1F("design:type", Number)
], OneCInitializeProductPriceCustomPriceValuesDTO.prototype, "base", void 0);
_ts_decorate$1F([
    dto({
        number: true
    }),
    _ts_metadata$1F("design:type", Number)
], OneCInitializeProductPriceCustomPriceValuesDTO.prototype, "result", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductPriceCustomPriceValuesDTO.prototype, "discountPercent", void 0);
class OneCInitializeProductPriceCustomPriceAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "categoryId", void 0), _define_property$1F(this, "id", void 0), _define_property$1F(this, "value", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductPriceCustomPriceAttributeDTO.prototype, "categoryId", void 0);
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductPriceCustomPriceAttributeDTO.prototype, "id", void 0);
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductPriceCustomPriceAttributeDTO.prototype, "value", void 0);
class OneCInitializeProductPriceCustomPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "attributes", void 0), _define_property$1F(this, "price", void 0);
    }
}
_ts_decorate$1F([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductPriceCustomPriceAttributeDTO
        }
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductPriceCustomPriceDTO.prototype, "attributes", void 0);
_ts_decorate$1F([
    dto({
        object: {
            type: OneCInitializeProductPriceCustomPriceValuesDTO
        }
    }),
    _ts_metadata$1F("design:type", typeof OneCInitializeProductPriceCustomPriceValuesDTO === "undefined" ? Object : OneCInitializeProductPriceCustomPriceValuesDTO)
], OneCInitializeProductPriceCustomPriceDTO.prototype, "price", void 0);
class OneCInitializeProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "base", void 0), _define_property$1F(this, "result", void 0), _define_property$1F(this, "discountPercent", void 0), _define_property$1F(this, "customPrices", void 0);
    }
}
_ts_decorate$1F([
    dto({
        number: true
    }),
    _ts_metadata$1F("design:type", Number)
], OneCInitializeProductPriceDTO.prototype, "base", void 0);
_ts_decorate$1F([
    dto({
        number: true
    }),
    _ts_metadata$1F("design:type", Number)
], OneCInitializeProductPriceDTO.prototype, "result", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductPriceDTO.prototype, "discountPercent", void 0);
_ts_decorate$1F([
    dto({
        optional: true,
        array: true,
        object: {
            type: OneCInitializeProductPriceCustomPriceDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductPriceDTO.prototype, "customPrices", void 0);
class OneCInitializeProductCategoryDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "id", void 0), _define_property$1F(this, "attributes", void 0), _define_property$1F(this, "parts", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductCategoryDTO.prototype, "id", void 0);
_ts_decorate$1F([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductAttributeDTO
        }
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductCategoryDTO.prototype, "attributes", void 0);
_ts_decorate$1F([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductPartDTO
        }
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductCategoryDTO.prototype, "parts", void 0);
class OneCInitializeProductShopAvailabilitiesCustomShopAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "categoryId", void 0), _define_property$1F(this, "id", void 0), _define_property$1F(this, "value", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductShopAvailabilitiesCustomShopAttributeDTO.prototype, "categoryId", void 0);
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductShopAvailabilitiesCustomShopAttributeDTO.prototype, "id", void 0);
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], OneCInitializeProductShopAvailabilitiesCustomShopAttributeDTO.prototype, "value", void 0);
class OneCInitializeProductShopAvailabilitiesCustomShopDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "attributes", void 0), _define_property$1F(this, "shopIds", void 0);
    }
}
_ts_decorate$1F([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductShopAvailabilitiesCustomShopAttributeDTO
        }
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductShopAvailabilitiesCustomShopDTO.prototype, "attributes", void 0);
_ts_decorate$1F([
    dto({
        array: true,
        string: true
    }),
    _ts_metadata$1F("design:type", Array)
], OneCInitializeProductShopAvailabilitiesCustomShopDTO.prototype, "shopIds", void 0);
class OneCInitializeProductShopAvailabilitiesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "customShops", void 0);
    }
}
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: OneCInitializeProductShopAvailabilitiesCustomShopDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], OneCInitializeProductShopAvailabilitiesDTO.prototype, "customShops", void 0);
class IntegrationInitializeProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "id", void 0), _define_property$1F(this, "name", void 0), _define_property$1F(this, "active", void 0), _define_property$1F(this, "showInRecommendations", void 0), _define_property$1F(this, "description", void 0), _define_property$1F(this, "createdAt", void 0), _define_property$1F(this, "articleNumber", void 0), _define_property$1F(this, "price", void 0), _define_property$1F(this, "categories", void 0), _define_property$1F(this, "shopAvailabilities", void 0), _define_property$1F(this, "badges", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], IntegrationInitializeProductDTO.prototype, "id", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "name", void 0);
_ts_decorate$1F([
    dto({
        optional: true,
        boolean: true
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "active", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "showInRecommendations", void 0);
_ts_decorate$1F([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "description", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "createdAt", void 0);
_ts_decorate$1F([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "articleNumber", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: OneCInitializeProductPriceDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "price", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: OneCInitializeProductCategoryDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "categories", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: OneCInitializeProductShopAvailabilitiesDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "shopAvailabilities", void 0);
_ts_decorate$1F([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: ProductBadgeDTO
        }
    }),
    _ts_metadata$1F("design:type", Object)
], IntegrationInitializeProductDTO.prototype, "badges", void 0);
class IntegrationDeleteProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1F(this, "id", void 0);
    }
}
_ts_decorate$1F([
    dto({
        string: true
    }),
    _ts_metadata$1F("design:type", String)
], IntegrationDeleteProductDTO.prototype, "id", void 0);
function transformProductAttributeValue$1(options) {
    if (!options) return undefined;
    const attribute = options.object;
    if (!attribute) return undefined;
    if (!isPureObject(attribute)) return undefined;
    if (!isPureObject(attribute.value)) return undefined;
    // 1С выгружает числовое значение атрибута числом, а не строкой — у valueType `number` это его
    // естественная форма.
    if (isNumber(attribute.value.value)) {
        attribute.value.__virtualType = "number";
        return undefined;
    }
    if (isString(attribute.value.value)) {
        attribute.value.__virtualType = "string";
        return undefined;
    }
    if (isArray(attribute.value.values)) {
        const notStringArrayIndex = attribute.value.values.findIndex((value)=>!isPureObject(value) || !isString(value.value));
        if (notStringArrayIndex !== -1) return undefined;
        attribute.value.__virtualType = "string_array";
        return undefined;
    }
    return undefined;
}

function _define_property$1E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1E(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1E(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OneCInitializeProductAttributeValueViewOptionsV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "numberIndex", void 0);
    }
}
_ts_decorate$1E([
    dto({
        optional: true,
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$1E("design:type", Object)
], OneCInitializeProductAttributeValueViewOptionsV1DTO.prototype, "numberIndex", void 0);
class OneCInitializeProductAttributeValueStringV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "value", void 0), _define_property$1E(this, "viewOptions", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductAttributeValueStringV1DTO.prototype, "value", void 0);
_ts_decorate$1E([
    dto({
        optional: true,
        object: {
            type: OneCInitializeProductAttributeValueViewOptionsV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Object)
], OneCInitializeProductAttributeValueStringV1DTO.prototype, "viewOptions", void 0);
class OneCInitializeProductAttributeValueStringArrayValueV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "value", void 0), _define_property$1E(this, "viewOptions", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductAttributeValueStringArrayValueV1DTO.prototype, "value", void 0);
_ts_decorate$1E([
    dto({
        optional: true,
        object: {
            type: OneCInitializeProductAttributeValueViewOptionsV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Object)
], OneCInitializeProductAttributeValueStringArrayValueV1DTO.prototype, "viewOptions", void 0);
class OneCInitializeProductAttributeValueStringArrayV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "values", void 0);
    }
}
_ts_decorate$1E([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductAttributeValueStringArrayValueV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Array)
], OneCInitializeProductAttributeValueStringArrayV1DTO.prototype, "values", void 0);
class OneCInitializeProductAttributeV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "id", void 0), _define_property$1E(this, "value", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductAttributeV1DTO.prototype, "id", void 0);
_ts_decorate$1E([
    dto({
        object: {
            transformer: transformProductAttributeValue,
            discriminator: {
                property: "__virtualType",
                subTypes: [
                    {
                        name: "string",
                        value: OneCInitializeProductAttributeValueStringV1DTO
                    },
                    {
                        name: "string_array",
                        value: OneCInitializeProductAttributeValueStringArrayV1DTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1E("design:type", Object)
], OneCInitializeProductAttributeV1DTO.prototype, "value", void 0);
class OneCInitializeProductPartV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "categoryPartId", void 0), _define_property$1E(this, "id", void 0), _define_property$1E(this, "name", void 0), _define_property$1E(this, "attributes", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductPartV1DTO.prototype, "categoryPartId", void 0);
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductPartV1DTO.prototype, "id", void 0);
_ts_decorate$1E([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$1E("design:type", Object)
], OneCInitializeProductPartV1DTO.prototype, "name", void 0);
_ts_decorate$1E([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductAttributeV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Array)
], OneCInitializeProductPartV1DTO.prototype, "attributes", void 0);
class OneCInitializeProductPriceCustomPriceValuesV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "base", void 0), _define_property$1E(this, "baseWithDiscount", void 0);
    }
}
_ts_decorate$1E([
    dto({
        number: true
    }),
    _ts_metadata$1E("design:type", Number)
], OneCInitializeProductPriceCustomPriceValuesV1DTO.prototype, "base", void 0);
_ts_decorate$1E([
    dto({
        number: true
    }),
    _ts_metadata$1E("design:type", Number)
], OneCInitializeProductPriceCustomPriceValuesV1DTO.prototype, "baseWithDiscount", void 0);
class OneCInitializeProductPriceCustomPriceAttributeV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "categoryId", void 0), _define_property$1E(this, "id", void 0), _define_property$1E(this, "value", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductPriceCustomPriceAttributeV1DTO.prototype, "categoryId", void 0);
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductPriceCustomPriceAttributeV1DTO.prototype, "id", void 0);
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductPriceCustomPriceAttributeV1DTO.prototype, "value", void 0);
class OneCInitializeProductPriceCustomPriceV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "attributes", void 0), _define_property$1E(this, "price", void 0);
    }
}
_ts_decorate$1E([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductPriceCustomPriceAttributeV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Array)
], OneCInitializeProductPriceCustomPriceV1DTO.prototype, "attributes", void 0);
_ts_decorate$1E([
    dto({
        object: {
            type: OneCInitializeProductPriceCustomPriceValuesV1DTO
        }
    }),
    _ts_metadata$1E("design:type", typeof OneCInitializeProductPriceCustomPriceValuesV1DTO === "undefined" ? Object : OneCInitializeProductPriceCustomPriceValuesV1DTO)
], OneCInitializeProductPriceCustomPriceV1DTO.prototype, "price", void 0);
class OneCInitializeProductPriceV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "base", void 0), _define_property$1E(this, "baseWithDiscount", void 0), _define_property$1E(this, "customPrices", void 0);
    }
}
_ts_decorate$1E([
    dto({
        number: true
    }),
    _ts_metadata$1E("design:type", Number)
], OneCInitializeProductPriceV1DTO.prototype, "base", void 0);
_ts_decorate$1E([
    dto({
        number: true
    }),
    _ts_metadata$1E("design:type", Number)
], OneCInitializeProductPriceV1DTO.prototype, "baseWithDiscount", void 0);
_ts_decorate$1E([
    dto({
        optional: true,
        array: true,
        object: {
            type: OneCInitializeProductPriceCustomPriceV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Object)
], OneCInitializeProductPriceV1DTO.prototype, "customPrices", void 0);
class OneCInitializeProductCategoryV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "id", void 0), _define_property$1E(this, "attributes", void 0), _define_property$1E(this, "parts", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], OneCInitializeProductCategoryV1DTO.prototype, "id", void 0);
_ts_decorate$1E([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductAttributeV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Array)
], OneCInitializeProductCategoryV1DTO.prototype, "attributes", void 0);
_ts_decorate$1E([
    dto({
        array: true,
        object: {
            type: OneCInitializeProductPartV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Array)
], OneCInitializeProductCategoryV1DTO.prototype, "parts", void 0);
class IntegrationInitializeProductV1DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1E(this, "id", void 0), _define_property$1E(this, "name", void 0), _define_property$1E(this, "active", void 0), _define_property$1E(this, "showInRecommendations", void 0), _define_property$1E(this, "description", void 0), _define_property$1E(this, "createdAt", void 0), _define_property$1E(this, "articleNumber", void 0), _define_property$1E(this, "price", void 0), _define_property$1E(this, "categories", void 0);
    }
}
_ts_decorate$1E([
    dto({
        string: true
    }),
    _ts_metadata$1E("design:type", String)
], IntegrationInitializeProductV1DTO.prototype, "id", void 0);
_ts_decorate$1E([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "name", void 0);
_ts_decorate$1E([
    dto({
        optional: true,
        boolean: true
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "active", void 0);
_ts_decorate$1E([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "showInRecommendations", void 0);
_ts_decorate$1E([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "description", void 0);
_ts_decorate$1E([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "createdAt", void 0);
_ts_decorate$1E([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "articleNumber", void 0);
_ts_decorate$1E([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: OneCInitializeProductPriceV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "price", void 0);
_ts_decorate$1E([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: OneCInitializeProductCategoryV1DTO
        }
    }),
    _ts_metadata$1E("design:type", Object)
], IntegrationInitializeProductV1DTO.prototype, "categories", void 0);
function transformProductAttributeValue(options) {
    if (!options) return undefined;
    const attribute = options.object;
    if (!attribute) return undefined;
    if (!isPureObject(attribute)) return undefined;
    if (!isPureObject(attribute.value)) return undefined;
    if (isString(attribute.value.value)) {
        attribute.value.__virtualType = "string";
        return undefined;
    }
    if (isArray(attribute.value.values)) {
        const notStringArrayIndex = attribute.value.values.findIndex((value)=>!isPureObject(value) || !isString(value.value));
        if (notStringArrayIndex !== -1) return undefined;
        attribute.value.__virtualType = "string_array";
        return undefined;
    }
    return undefined;
}

function _define_property$1D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1D(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1D(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteMerchantShopDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1D(this, "id", void 0);
    }
}
_ts_decorate$1D([
    dto({
        string: true
    }),
    _ts_metadata$1D("design:type", String)
], IntegrationDeleteMerchantShopDTO.prototype, "id", void 0);

function _define_property$1C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1C(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1C(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationInitializeMerchantShopCoordinatesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1C(this, "latitude", void 0), _define_property$1C(this, "longitude", void 0);
    }
}
_ts_decorate$1C([
    dto({
        number: true
    }),
    _ts_metadata$1C("design:type", Number)
], IntegrationInitializeMerchantShopCoordinatesDTO.prototype, "latitude", void 0);
_ts_decorate$1C([
    dto({
        number: true
    }),
    _ts_metadata$1C("design:type", Number)
], IntegrationInitializeMerchantShopCoordinatesDTO.prototype, "longitude", void 0);
class IntegrationInitializeMerchantShopDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1C(this, "id", void 0), _define_property$1C(this, "cityName", void 0), _define_property$1C(this, "address", void 0), _define_property$1C(this, "index", void 0), _define_property$1C(this, "coordinates", void 0);
    }
}
_ts_decorate$1C([
    dto({
        string: true
    }),
    _ts_metadata$1C("design:type", String)
], IntegrationInitializeMerchantShopDTO.prototype, "id", void 0);
_ts_decorate$1C([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1C("design:type", Object)
], IntegrationInitializeMerchantShopDTO.prototype, "cityName", void 0);
_ts_decorate$1C([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1C("design:type", Object)
], IntegrationInitializeMerchantShopDTO.prototype, "address", void 0);
_ts_decorate$1C([
    dto({
        optional: true,
        number: true
    }),
    _ts_metadata$1C("design:type", Object)
], IntegrationInitializeMerchantShopDTO.prototype, "index", void 0);
_ts_decorate$1C([
    dto({
        optional: true,
        object: {
            type: IntegrationInitializeMerchantShopCoordinatesDTO
        }
    }),
    _ts_metadata$1C("design:type", Object)
], IntegrationInitializeMerchantShopDTO.prototype, "coordinates", void 0);

function _define_property$1B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1B(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1B(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationTempUserTrySendTelegramBotMessageActionRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1B(this, "text", void 0), _define_property$1B(this, "url", void 0);
    }
}
_ts_decorate$1B([
    dto({
        string: true
    }),
    _ts_metadata$1B("design:type", String)
], IntegrationTempUserTrySendTelegramBotMessageActionRequestDTO.prototype, "text", void 0);
_ts_decorate$1B([
    dto({
        url: true
    }),
    _ts_metadata$1B("design:type", String)
], IntegrationTempUserTrySendTelegramBotMessageActionRequestDTO.prototype, "url", void 0);
class IntegrationTempUserTrySendTelegramBotMessageRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1B(this, "useId", void 0), _define_property$1B(this, "message", void 0), _define_property$1B(this, "actions", void 0);
    }
}
_ts_decorate$1B([
    dto({
        string: true
    }),
    _ts_metadata$1B("design:type", String)
], IntegrationTempUserTrySendTelegramBotMessageRequestDTO.prototype, "useId", void 0);
_ts_decorate$1B([
    dto({
        string: true
    }),
    _ts_metadata$1B("design:type", String)
], IntegrationTempUserTrySendTelegramBotMessageRequestDTO.prototype, "message", void 0);
_ts_decorate$1B([
    dto({
        name: "actions__json",
        optional: true,
        array: true,
        object: {
            type: IntegrationTempUserTrySendTelegramBotMessageActionRequestDTO
        }
    }),
    _ts_metadata$1B("design:type", Object)
], IntegrationTempUserTrySendTelegramBotMessageRequestDTO.prototype, "actions", void 0);

function _define_property$1A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1A(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1A(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationTempUserTrySendTelegramBotMessageStatus = /*#__PURE__*/ function(IntegrationTempUserTrySendTelegramBotMessageStatus) {
    IntegrationTempUserTrySendTelegramBotMessageStatus["SUCCESS"] = "success";
    IntegrationTempUserTrySendTelegramBotMessageStatus["USER_NOT_LAUNCH_BOT"] = "user_not_launch_bot";
    return IntegrationTempUserTrySendTelegramBotMessageStatus;
}({});
class IntegrationTempUserTrySendTelegramBotMessageResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1A(this, "status", void 0);
    }
}
_ts_decorate$1A([
    dto({
        enum: IntegrationTempUserTrySendTelegramBotMessageStatus
    }),
    _ts_metadata$1A("design:type", String)
], IntegrationTempUserTrySendTelegramBotMessageResponseDTO.prototype, "status", void 0);

function _define_property$1z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrUpdateUserRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1z(this, "id", void 0), _define_property$1z(this, "phoneNumber", void 0), _define_property$1z(this, "firstName", void 0), _define_property$1z(this, "lastName", void 0), _define_property$1z(this, "gender", void 0), _define_property$1z(this, "birthdate", void 0), _define_property$1z(this, "customerAllowedToSendNotifications", void 0), _define_property$1z(this, "role", void 0), _define_property$1z(this, "lastOnline", void 0);
    }
}
_ts_decorate$1z([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "id", void 0);
_ts_decorate$1z([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "phoneNumber", void 0);
_ts_decorate$1z([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "firstName", void 0);
_ts_decorate$1z([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "lastName", void 0);
_ts_decorate$1z([
    dto({
        optional: true,
        enum: Gender
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "gender", void 0);
_ts_decorate$1z([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "birthdate", void 0);
_ts_decorate$1z([
    dto({
        enum: CustomerAllowedNotificationLevel,
        array: true
    }),
    _ts_metadata$1z("design:type", Array)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "customerAllowedToSendNotifications", void 0);
_ts_decorate$1z([
    dto({
        optional: {
            allowNull: false
        },
        enum: UserRole
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "role", void 0);
_ts_decorate$1z([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$1z("design:type", Object)
], IntegrationCreateOrUpdateUserRequestDTO.prototype, "lastOnline", void 0);

function _define_property$1y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationCreateOrUpdateUserResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1y(this, "id", void 0);
    }
}
_ts_decorate$1y([
    dto({
        string: true
    }),
    _ts_metadata$1y("design:type", String)
], IntegrationCreateOrUpdateUserResponseDTO.prototype, "id", void 0);

function _define_property$1x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1x(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1x(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationDeleteUserRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1x(this, "id", void 0);
    }
}
_ts_decorate$1x([
    dto({
        string: true
    }),
    _ts_metadata$1x("design:type", String)
], IntegrationDeleteUserRequestDTO.prototype, "id", void 0);

function _define_property$1w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1w(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1w(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationDeleteUserStatus = /*#__PURE__*/ function(IntegrationDeleteUserStatus) {
    IntegrationDeleteUserStatus["SUCCESS"] = "success";
    IntegrationDeleteUserStatus["HAS_ACTIVE_ORDER"] = "has_active_order";
    return IntegrationDeleteUserStatus;
}({});
class IntegrationDeleteUserResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1w(this, "deleteStatus", void 0);
    }
}
_ts_decorate$1w([
    dto({
        enum: IntegrationDeleteUserStatus
    }),
    _ts_metadata$1w("design:type", String)
], IntegrationDeleteUserResponseDTO.prototype, "deleteStatus", void 0);

function _define_property$1v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1v(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1v(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUnknownUserByPhoneRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1v(this, "phoneNumber", void 0);
    }
}
_ts_decorate$1v([
    dto({
        phone: true
    }),
    _ts_metadata$1v("design:type", String)
], IntegrationGetUnknownUserByPhoneRequestDTO.prototype, "phoneNumber", void 0);

function _define_property$1u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1u(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1u(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationGetUnknownUserByPhoneCodes = /*#__PURE__*/ function(IntegrationGetUnknownUserByPhoneCodes) {
    IntegrationGetUnknownUserByPhoneCodes["EXISTS"] = "exists";
    IntegrationGetUnknownUserByPhoneCodes["NOT_EXISTS"] = "not_exists";
    return IntegrationGetUnknownUserByPhoneCodes;
}({});
class IntegrationGetUnknownUserByPhoneExistsResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1u(this, "code", void 0), _define_property$1u(this, "id", void 0), _define_property$1u(this, "firstName", void 0), _define_property$1u(this, "lastName", void 0), _define_property$1u(this, "gender", void 0), _define_property$1u(this, "birthdate", void 0), _define_property$1u(this, "customerAllowedToSendNotifications", void 0), _define_property$1u(this, "externalRoles", void 0), // Код входа от 1С. Действует только вместе с ролью сотрудника: такому пользователю код не шлётся по SMS.
        _define_property$1u(this, "authCode", void 0);
    }
}
_ts_decorate$1u([
    dto({
        equals: "exists"
    }),
    _ts_metadata$1u("design:type", typeof IntegrationGetUnknownUserByPhoneCodes === "undefined" || "string" === "undefined" ? Object : "exists")
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "code", void 0);
_ts_decorate$1u([
    dto({
        string: true
    }),
    _ts_metadata$1u("design:type", String)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "id", void 0);
_ts_decorate$1u([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "firstName", void 0);
_ts_decorate$1u([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "lastName", void 0);
_ts_decorate$1u([
    dto({
        optional: true,
        enum: Gender
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "gender", void 0);
_ts_decorate$1u([
    dto({
        optional: true,
        date: true
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "birthdate", void 0);
_ts_decorate$1u([
    dto({
        optional: {
            allowNull: false
        },
        enum: CustomerAllowedNotificationLevel,
        array: true
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "customerAllowedToSendNotifications", void 0);
_ts_decorate$1u([
    dto({
        optional: {
            allowNull: false
        },
        enum: UserExternalRole,
        array: true
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "externalRoles", void 0);
_ts_decorate$1u([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneExistsResponseDTO.prototype, "authCode", void 0);
class IntegrationGetUnknownUserByPhoneNotExistsResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1u(this, "code", void 0);
    }
}
_ts_decorate$1u([
    dto({
        equals: "not_exists"
    }),
    _ts_metadata$1u("design:type", typeof IntegrationGetUnknownUserByPhoneCodes === "undefined" || "string" === "undefined" ? Object : "not_exists")
], IntegrationGetUnknownUserByPhoneNotExistsResponseDTO.prototype, "code", void 0);
class IntegrationGetUnknownUserByPhoneResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1u(this, "user", void 0);
    }
}
_ts_decorate$1u([
    dto({
        object: {
            discriminator: {
                property: "code",
                subTypes: [
                    {
                        name: "exists",
                        value: IntegrationGetUnknownUserByPhoneExistsResponseDTO
                    },
                    {
                        name: "not_exists",
                        value: IntegrationGetUnknownUserByPhoneNotExistsResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1u("design:type", Object)
], IntegrationGetUnknownUserByPhoneResponseDTO.prototype, "user", void 0);

function _define_property$1t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1t(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1t(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserPromoCodesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1t(this, "userId", void 0);
    }
}
_ts_decorate$1t([
    dto({
        string: true
    }),
    _ts_metadata$1t("design:type", String)
], IntegrationGetUserPromoCodesRequestDTO.prototype, "userId", void 0);

function _define_property$1s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1s(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1s(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserReferralCodeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1s(this, "userId", void 0);
    }
}
_ts_decorate$1s([
    dto({
        string: true
    }),
    _ts_metadata$1s("design:type", String)
], IntegrationGetUserReferralCodeRequestDTO.prototype, "userId", void 0);

function _define_property$1r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1r(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1r(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationApplyUserReferralCodeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1r(this, "userId", void 0), _define_property$1r(this, "code", void 0);
    }
}
_ts_decorate$1r([
    dto({
        string: true
    }),
    _ts_metadata$1r("design:type", String)
], IntegrationApplyUserReferralCodeRequestDTO.prototype, "userId", void 0);
_ts_decorate$1r([
    dto({
        string: true
    }),
    _ts_metadata$1r("design:type", String)
], IntegrationApplyUserReferralCodeRequestDTO.prototype, "code", void 0);

function _define_property$1q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var IntegrationApplyUserReferralCodeStatus = /*#__PURE__*/ function(IntegrationApplyUserReferralCodeStatus) {
    IntegrationApplyUserReferralCodeStatus["SUCCESS"] = "success";
    IntegrationApplyUserReferralCodeStatus["ERROR"] = "error";
    return IntegrationApplyUserReferralCodeStatus;
}({});
class IntegrationApplyUserReferralCodeSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1q(this, "status", void 0);
    }
}
_ts_decorate$1q([
    dto({
        equals: "success"
    }),
    _ts_metadata$1q("design:type", typeof IntegrationApplyUserReferralCodeStatus === "undefined" || "string" === "undefined" ? Object : "success")
], IntegrationApplyUserReferralCodeSuccessResponseDTO.prototype, "status", void 0);
class IntegrationApplyUserReferralCodeErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1q(this, "status", void 0), _define_property$1q(this, "message", void 0);
    }
}
_ts_decorate$1q([
    dto({
        equals: "error"
    }),
    _ts_metadata$1q("design:type", typeof IntegrationApplyUserReferralCodeStatus === "undefined" || "string" === "undefined" ? Object : "error")
], IntegrationApplyUserReferralCodeErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$1q([
    dto({
        string: true
    }),
    _ts_metadata$1q("design:type", String)
], IntegrationApplyUserReferralCodeErrorResponseDTO.prototype, "message", void 0);
class IntegrationApplyUserReferralCodeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1q(this, "data", void 0);
    }
}
_ts_decorate$1q([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: IntegrationApplyUserReferralCodeSuccessResponseDTO
                    },
                    {
                        name: "error",
                        value: IntegrationApplyUserReferralCodeErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1q("design:type", Object)
], IntegrationApplyUserReferralCodeResponseDTO.prototype, "data", void 0);

function _define_property$1p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1p(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1p(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV1RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1p(this, "userId", void 0);
    }
}
_ts_decorate$1p([
    dto({
        string: true
    }),
    _ts_metadata$1p("design:type", String)
], IntegrationGetUserWalletV1RequestDTO.prototype, "userId", void 0);

function _define_property$1o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1o(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1o(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV1EventResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1o(this, "type", void 0), _define_property$1o(this, "description", void 0), _define_property$1o(this, "amount", void 0), _define_property$1o(this, "date", void 0);
    }
}
_ts_decorate$1o([
    dto({
        enum: CustomerProfileWalletV1EventType
    }),
    _ts_metadata$1o("design:type", typeof CustomerProfileWalletV1EventType === "undefined" ? Object : CustomerProfileWalletV1EventType)
], IntegrationGetUserWalletV1EventResponseDTO.prototype, "type", void 0);
_ts_decorate$1o([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1o("design:type", Object)
], IntegrationGetUserWalletV1EventResponseDTO.prototype, "description", void 0);
_ts_decorate$1o([
    dto({
        number: true
    }),
    _ts_metadata$1o("design:type", Number)
], IntegrationGetUserWalletV1EventResponseDTO.prototype, "amount", void 0);
_ts_decorate$1o([
    dto({
        date: true
    }),
    _ts_metadata$1o("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationGetUserWalletV1EventResponseDTO.prototype, "date", void 0);
class IntegrationGetUserWalletV1ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1o(this, "cashbackPercent", void 0), _define_property$1o(this, "amount", void 0), _define_property$1o(this, "history", void 0), _define_property$1o(this, "nearestEvents", void 0), _define_property$1o(this, "exceptionReason", void 0);
    }
}
_ts_decorate$1o([
    dto({
        number: true
    }),
    _ts_metadata$1o("design:type", Number)
], IntegrationGetUserWalletV1ResponseDTO.prototype, "cashbackPercent", void 0);
_ts_decorate$1o([
    dto({
        number: true
    }),
    _ts_metadata$1o("design:type", Number)
], IntegrationGetUserWalletV1ResponseDTO.prototype, "amount", void 0);
_ts_decorate$1o([
    dto({
        array: true,
        object: {
            type: IntegrationGetUserWalletV1EventResponseDTO
        }
    }),
    _ts_metadata$1o("design:type", Array)
], IntegrationGetUserWalletV1ResponseDTO.prototype, "history", void 0);
_ts_decorate$1o([
    dto({
        array: true,
        object: {
            type: IntegrationGetUserWalletV1EventResponseDTO
        }
    }),
    _ts_metadata$1o("design:type", Array)
], IntegrationGetUserWalletV1ResponseDTO.prototype, "nearestEvents", void 0);
_ts_decorate$1o([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1o("design:type", Object)
], IntegrationGetUserWalletV1ResponseDTO.prototype, "exceptionReason", void 0);

function _define_property$1n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1n(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1n(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV3RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1n(this, "userId", void 0);
    }
}
_ts_decorate$1n([
    dto({
        string: true
    }),
    _ts_metadata$1n("design:type", String)
], IntegrationGetUserWalletV3RequestDTO.prototype, "userId", void 0);

function _define_property$1m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1m(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1m(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV3CashbackEventResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1m(this, "type", void 0), _define_property$1m(this, "description", void 0), _define_property$1m(this, "amount", void 0), _define_property$1m(this, "date", void 0);
    }
}
_ts_decorate$1m([
    dto({
        enum: CustomerProfileWalletV3EventType
    }),
    _ts_metadata$1m("design:type", typeof CustomerProfileWalletV3EventType === "undefined" ? Object : CustomerProfileWalletV3EventType)
], IntegrationGetUserWalletV3CashbackEventResponseDTO.prototype, "type", void 0);
_ts_decorate$1m([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1m("design:type", Object)
], IntegrationGetUserWalletV3CashbackEventResponseDTO.prototype, "description", void 0);
_ts_decorate$1m([
    dto({
        number: true
    }),
    _ts_metadata$1m("design:type", Number)
], IntegrationGetUserWalletV3CashbackEventResponseDTO.prototype, "amount", void 0);
_ts_decorate$1m([
    dto({
        date: true
    }),
    _ts_metadata$1m("design:type", typeof Date === "undefined" ? Object : Date)
], IntegrationGetUserWalletV3CashbackEventResponseDTO.prototype, "date", void 0);

var IntegrationGetUserWalletV3Type = /*#__PURE__*/ function(IntegrationGetUserWalletV3Type) {
    IntegrationGetUserWalletV3Type["CASHBACK"] = "cashback";
    IntegrationGetUserWalletV3Type["BONUS"] = "bonus";
    return IntegrationGetUserWalletV3Type;
}({});

function _define_property$1l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1l(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1l(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV3CashbackResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1l(this, "type", void 0), _define_property$1l(this, "cashbackPercent", void 0), _define_property$1l(this, "amount", void 0), _define_property$1l(this, "history", void 0), _define_property$1l(this, "nearestEvents", void 0), _define_property$1l(this, "exceptionReason", void 0);
    }
}
_ts_decorate$1l([
    dto({
        equals: IntegrationGetUserWalletV3Type.CASHBACK
    }),
    _ts_metadata$1l("design:type", typeof IntegrationGetUserWalletV3Type === "undefined" || typeof IntegrationGetUserWalletV3Type.CASHBACK === "undefined" ? Object : IntegrationGetUserWalletV3Type.CASHBACK)
], IntegrationGetUserWalletV3CashbackResponseDTO.prototype, "type", void 0);
_ts_decorate$1l([
    dto({
        number: true
    }),
    _ts_metadata$1l("design:type", Number)
], IntegrationGetUserWalletV3CashbackResponseDTO.prototype, "cashbackPercent", void 0);
_ts_decorate$1l([
    dto({
        number: true
    }),
    _ts_metadata$1l("design:type", Number)
], IntegrationGetUserWalletV3CashbackResponseDTO.prototype, "amount", void 0);
_ts_decorate$1l([
    dto({
        array: true,
        object: {
            type: IntegrationGetUserWalletV3CashbackEventResponseDTO
        }
    }),
    _ts_metadata$1l("design:type", Array)
], IntegrationGetUserWalletV3CashbackResponseDTO.prototype, "history", void 0);
_ts_decorate$1l([
    dto({
        array: true,
        object: {
            type: IntegrationGetUserWalletV3CashbackEventResponseDTO
        }
    }),
    _ts_metadata$1l("design:type", Array)
], IntegrationGetUserWalletV3CashbackResponseDTO.prototype, "nearestEvents", void 0);
_ts_decorate$1l([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1l("design:type", Object)
], IntegrationGetUserWalletV3CashbackResponseDTO.prototype, "exceptionReason", void 0);

function _define_property$1k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1k(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1k(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV3BonusResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1k(this, "type", void 0), _define_property$1k(this, "name", void 0), _define_property$1k(this, "description", void 0), _define_property$1k(this, "color", void 0), _define_property$1k(this, "bonus", void 0), _define_property$1k(this, "exceptionReason", void 0);
    }
}
_ts_decorate$1k([
    dto({
        equals: IntegrationGetUserWalletV3Type.BONUS
    }),
    _ts_metadata$1k("design:type", typeof IntegrationGetUserWalletV3Type === "undefined" || typeof IntegrationGetUserWalletV3Type.BONUS === "undefined" ? Object : IntegrationGetUserWalletV3Type.BONUS)
], IntegrationGetUserWalletV3BonusResponseDTO.prototype, "type", void 0);
_ts_decorate$1k([
    dto({
        string: true
    }),
    _ts_metadata$1k("design:type", String)
], IntegrationGetUserWalletV3BonusResponseDTO.prototype, "name", void 0);
_ts_decorate$1k([
    dto({
        string: true
    }),
    _ts_metadata$1k("design:type", String)
], IntegrationGetUserWalletV3BonusResponseDTO.prototype, "description", void 0);
_ts_decorate$1k([
    dto({
        string: true
    }),
    _ts_metadata$1k("design:type", String)
], IntegrationGetUserWalletV3BonusResponseDTO.prototype, "color", void 0);
_ts_decorate$1k([
    dto({
        string: true
    }),
    _ts_metadata$1k("design:type", String)
], IntegrationGetUserWalletV3BonusResponseDTO.prototype, "bonus", void 0);
_ts_decorate$1k([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$1k("design:type", Object)
], IntegrationGetUserWalletV3BonusResponseDTO.prototype, "exceptionReason", void 0);

function _define_property$1j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1j(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1j(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class IntegrationGetUserWalletV3ResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1j(this, "card", void 0);
    }
}
_ts_decorate$1j([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationGetUserWalletV3Type.CASHBACK,
                        value: IntegrationGetUserWalletV3CashbackResponseDTO
                    },
                    {
                        name: IntegrationGetUserWalletV3Type.BONUS,
                        value: IntegrationGetUserWalletV3BonusResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1j("design:type", Object)
], IntegrationGetUserWalletV3ResponseDTO.prototype, "card", void 0);

function _define_property$1i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1i(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1i(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2ConfigurationDeliveryPickupPointAddressesErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1i(this, "status", void 0), _define_property$1i(this, "message", void 0);
    }
}
_ts_decorate$1i([
    dto({
        equals: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR
    }),
    _ts_metadata$1i("design:type", typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus === "undefined" || typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR === "undefined" ? Object : IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR)
], CartV2ConfigurationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$1i([
    dto({
        string: true
    }),
    _ts_metadata$1i("design:type", String)
], CartV2ConfigurationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "message", void 0);

function _define_property$1h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1h(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1h(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1h(this, "latitude", void 0), _define_property$1h(this, "longitude", void 0);
    }
}
_ts_decorate$1h([
    dto({
        number: true
    }),
    _ts_metadata$1h("design:type", Number)
], CartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO.prototype, "latitude", void 0);
_ts_decorate$1h([
    dto({
        number: true
    }),
    _ts_metadata$1h("design:type", Number)
], CartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO.prototype, "longitude", void 0);
class CartConfigurationDeliveryPickupPointAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1h(this, "id", void 0), _define_property$1h(this, "name", void 0), _define_property$1h(this, "coordinates", void 0);
    }
}
_ts_decorate$1h([
    dto({
        string: true
    }),
    _ts_metadata$1h("design:type", String)
], CartConfigurationDeliveryPickupPointAddressResponseDTO.prototype, "id", void 0);
_ts_decorate$1h([
    dto({
        string: true
    }),
    _ts_metadata$1h("design:type", String)
], CartConfigurationDeliveryPickupPointAddressResponseDTO.prototype, "name", void 0);
_ts_decorate$1h([
    dto({
        object: {
            type: CartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO
        }
    }),
    _ts_metadata$1h("design:type", typeof CartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO === "undefined" ? Object : CartConfigurationDeliveryPickupPointAddressCoordinatesResponseDTO)
], CartConfigurationDeliveryPickupPointAddressResponseDTO.prototype, "coordinates", void 0);
class CartV2ConfigurationDeliveryPickupPointAddressesSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1h(this, "status", void 0), _define_property$1h(this, "addresses", void 0);
    }
}
_ts_decorate$1h([
    dto({
        equals: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS
    }),
    _ts_metadata$1h("design:type", typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus === "undefined" || typeof IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS === "undefined" ? Object : IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS)
], CartV2ConfigurationDeliveryPickupPointAddressesSuccessResponseDTO.prototype, "status", void 0);
_ts_decorate$1h([
    dto({
        array: true,
        object: {
            type: CartConfigurationDeliveryPickupPointAddressResponseDTO
        }
    }),
    _ts_metadata$1h("design:type", Array)
], CartV2ConfigurationDeliveryPickupPointAddressesSuccessResponseDTO.prototype, "addresses", void 0);

function _define_property$1g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1g(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1g(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2ConfigurationDeliveryPickupPointAddressesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1g(this, "info", void 0);
    }
}
_ts_decorate$1g([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.SUCCESS,
                        value: CartV2ConfigurationDeliveryPickupPointAddressesSuccessResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryPickupPointAddressesStatus.ERROR,
                        value: CartV2ConfigurationDeliveryPickupPointAddressesErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$1g("design:type", Object)
], CartV2ConfigurationDeliveryPickupPointAddressesResponseDTO.prototype, "info", void 0);

function _define_property$1f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1f(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1f(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2ConfigurationDeliveryPickupPointCitiesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1f(this, "deliveryTypeId", void 0), _define_property$1f(this, "search", void 0);
    }
}
_ts_decorate$1f([
    dto({
        string: true
    }),
    _ts_metadata$1f("design:type", String)
], CartV2ConfigurationDeliveryPickupPointCitiesRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$1f([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1f("design:type", Object)
], CartV2ConfigurationDeliveryPickupPointCitiesRequestDTO.prototype, "search", void 0);

function _define_property$1e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1e(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1e(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartConfigurationDeliveryPickupPointCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1e(this, "id", void 0), _define_property$1e(this, "name", void 0);
    }
}
_ts_decorate$1e([
    dto({
        string: true
    }),
    _ts_metadata$1e("design:type", String)
], CartConfigurationDeliveryPickupPointCityResponseDTO.prototype, "id", void 0);
_ts_decorate$1e([
    dto({
        string: true
    }),
    _ts_metadata$1e("design:type", String)
], CartConfigurationDeliveryPickupPointCityResponseDTO.prototype, "name", void 0);
class CartV2ConfigurationDeliveryPickupPointCitiesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1e(this, "cities", void 0);
    }
}
_ts_decorate$1e([
    dto({
        array: true,
        object: {
            type: CartConfigurationDeliveryPickupPointCityResponseDTO
        }
    }),
    _ts_metadata$1e("design:type", Array)
], CartV2ConfigurationDeliveryPickupPointCitiesResponseDTO.prototype, "cities", void 0);

function _define_property$1d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1d(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1d(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartConfigurationDeliveryTypeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1d(this, "type", void 0), _define_property$1d(this, "name", void 0), _define_property$1d(this, "id", void 0);
    }
}
_ts_decorate$1d([
    dto({
        enum: IntegrationCartConfigurationDeliveryType
    }),
    _ts_metadata$1d("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" ? Object : IntegrationCartConfigurationDeliveryType)
], CartConfigurationDeliveryTypeResponseDTO.prototype, "type", void 0);
_ts_decorate$1d([
    dto({
        string: true
    }),
    _ts_metadata$1d("design:type", String)
], CartConfigurationDeliveryTypeResponseDTO.prototype, "name", void 0);
_ts_decorate$1d([
    dto({
        string: true
    }),
    _ts_metadata$1d("design:type", String)
], CartConfigurationDeliveryTypeResponseDTO.prototype, "id", void 0);
class CartV2ConfigurationDeliveryTypesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1d(this, "deliveryTypes", void 0);
    }
}
_ts_decorate$1d([
    dto({
        array: true,
        object: {
            type: CartConfigurationDeliveryTypeResponseDTO
        }
    }),
    _ts_metadata$1d("design:type", Array)
], CartV2ConfigurationDeliveryTypesResponseDTO.prototype, "deliveryTypes", void 0);

function _define_property$1c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1c(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1c(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2GetConfigurationPaymentTypesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1c(this, "deliveryTypeId", void 0);
    }
}
_ts_decorate$1c([
    dto({
        string: true
    }),
    _ts_metadata$1c("design:type", String)
], CartV2GetConfigurationPaymentTypesRequestDTO.prototype, "deliveryTypeId", void 0);

function _define_property$1b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1b(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartGetConfigurationPaymentTypeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1b(this, "type", void 0), _define_property$1b(this, "name", void 0), _define_property$1b(this, "id", void 0);
    }
}
_ts_decorate$1b([
    dto({
        enum: IntegrationCartConfigurationPaymentType
    }),
    _ts_metadata$1b("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" ? Object : IntegrationCartConfigurationPaymentType)
], CartGetConfigurationPaymentTypeResponseDTO.prototype, "type", void 0);
_ts_decorate$1b([
    dto({
        string: true
    }),
    _ts_metadata$1b("design:type", String)
], CartGetConfigurationPaymentTypeResponseDTO.prototype, "name", void 0);
_ts_decorate$1b([
    dto({
        string: true
    }),
    _ts_metadata$1b("design:type", String)
], CartGetConfigurationPaymentTypeResponseDTO.prototype, "id", void 0);
class CartV2GetConfigurationPaymentTypesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1b(this, "paymentTypes", void 0);
    }
}
_ts_decorate$1b([
    dto({
        array: true,
        object: {
            type: CartGetConfigurationPaymentTypeResponseDTO
        }
    }),
    _ts_metadata$1b("design:type", Array)
], CartV2GetConfigurationPaymentTypesResponseDTO.prototype, "paymentTypes", void 0);

function _define_property$1a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1a(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1a(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let CartProductPriceDTO$1 = class CartProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1a(this, "base", void 0), _define_property$1a(this, "result", void 0), _define_property$1a(this, "discount", void 0);
    }
};
_ts_decorate$1a([
    dto({
        number: true
    }),
    _ts_metadata$1a("design:type", Number)
], CartProductPriceDTO$1.prototype, "base", void 0);
_ts_decorate$1a([
    dto({
        number: true
    }),
    _ts_metadata$1a("design:type", Number)
], CartProductPriceDTO$1.prototype, "result", void 0);
_ts_decorate$1a([
    dto({
        number: true
    }),
    _ts_metadata$1a("design:type", Number)
], CartProductPriceDTO$1.prototype, "discount", void 0);
let CartProductRequiredAttributeValueDTO$1 = class CartProductRequiredAttributeValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1a(this, "id", void 0), _define_property$1a(this, "name", void 0);
    }
};
_ts_decorate$1a([
    dto({
        string: true
    }),
    _ts_metadata$1a("design:type", String)
], CartProductRequiredAttributeValueDTO$1.prototype, "id", void 0);
_ts_decorate$1a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$1a("design:type", Object)
], CartProductRequiredAttributeValueDTO$1.prototype, "name", void 0);
let CartProductRequiredAttributeDTO$1 = class CartProductRequiredAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1a(this, "id", void 0), _define_property$1a(this, "name", void 0), _define_property$1a(this, "measure", void 0), _define_property$1a(this, "value", void 0);
    }
};
_ts_decorate$1a([
    dto({
        string: true
    }),
    _ts_metadata$1a("design:type", String)
], CartProductRequiredAttributeDTO$1.prototype, "id", void 0);
_ts_decorate$1a([
    dto({
        string: true
    }),
    _ts_metadata$1a("design:type", String)
], CartProductRequiredAttributeDTO$1.prototype, "name", void 0);
_ts_decorate$1a([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$1a("design:type", Object)
], CartProductRequiredAttributeDTO$1.prototype, "measure", void 0);
_ts_decorate$1a([
    dto({
        object: {
            type: CartProductRequiredAttributeValueDTO$1
        }
    }),
    _ts_metadata$1a("design:type", typeof CartProductRequiredAttributeValueDTO$1 === "undefined" ? Object : CartProductRequiredAttributeValueDTO$1)
], CartProductRequiredAttributeDTO$1.prototype, "value", void 0);
let CartProductOriginalDTO$1 = class CartProductOriginalDTO extends ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$1a(this, "attributes", void 0), _define_property$1a(this, "parts", void 0);
    }
};
_ts_decorate$1a([
    dto({
        array: true,
        object: {
            type: ProductAttributeDTO
        }
    }),
    _ts_metadata$1a("design:type", Array)
], CartProductOriginalDTO$1.prototype, "attributes", void 0);
_ts_decorate$1a([
    dto({
        array: true,
        object: {
            type: ProductPartDTO
        }
    }),
    _ts_metadata$1a("design:type", Array)
], CartProductOriginalDTO$1.prototype, "parts", void 0);
var CartV2ProductStatus = /*#__PURE__*/ function(CartV2ProductStatus) {
    CartV2ProductStatus["SUCCESS"] = "success";
    CartV2ProductStatus["ERROR"] = "error";
    return CartV2ProductStatus;
}({});
class CartV2ProductBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1a(this, "id", void 0), _define_property$1a(this, "name", void 0), _define_property$1a(this, "count", void 0), _define_property$1a(this, "applied", void 0), _define_property$1a(this, "originalProduct", void 0), _define_property$1a(this, "maxCount", void 0), _define_property$1a(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$1a([
    dto({
        string: true
    }),
    _ts_metadata$1a("design:type", String)
], CartV2ProductBaseDTO.prototype, "id", void 0);
_ts_decorate$1a([
    dto({
        string: true
    }),
    _ts_metadata$1a("design:type", String)
], CartV2ProductBaseDTO.prototype, "name", void 0);
_ts_decorate$1a([
    dto({
        number: true
    }),
    _ts_metadata$1a("design:type", Number)
], CartV2ProductBaseDTO.prototype, "count", void 0);
_ts_decorate$1a([
    dto({
        boolean: true
    }),
    _ts_metadata$1a("design:type", Boolean)
], CartV2ProductBaseDTO.prototype, "applied", void 0);
_ts_decorate$1a([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: CartProductOriginalDTO$1
        }
    }),
    _ts_metadata$1a("design:type", Object)
], CartV2ProductBaseDTO.prototype, "originalProduct", void 0);
_ts_decorate$1a([
    dto({
        number: {
            maxDecimalPlaces: 0
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$1a("design:type", Object)
], CartV2ProductBaseDTO.prototype, "maxCount", void 0);
_ts_decorate$1a([
    dto({
        array: true,
        object: {
            type: CartProductRequiredAttributeDTO$1
        }
    }),
    _ts_metadata$1a("design:type", Array)
], CartV2ProductBaseDTO.prototype, "requiredAttributes", void 0);
class CartV2ProductSuccessDTO extends CartV2ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$1a(this, "status", void 0), _define_property$1a(this, "price", void 0);
    }
}
_ts_decorate$1a([
    dto({
        equals: "success"
    }),
    _ts_metadata$1a("design:type", typeof CartV2ProductStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CartV2ProductSuccessDTO.prototype, "status", void 0);
_ts_decorate$1a([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: CartProductPriceDTO$1
        }
    }),
    _ts_metadata$1a("design:type", Object)
], CartV2ProductSuccessDTO.prototype, "price", void 0);
class CartV2ProductErrorDTO extends CartV2ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$1a(this, "status", void 0), _define_property$1a(this, "message", void 0);
    }
}
_ts_decorate$1a([
    dto({
        equals: "error"
    }),
    _ts_metadata$1a("design:type", typeof CartV2ProductStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CartV2ProductErrorDTO.prototype, "status", void 0);
_ts_decorate$1a([
    dto({
        string: true
    }),
    _ts_metadata$1a("design:type", String)
], CartV2ProductErrorDTO.prototype, "message", void 0);

function _define_property$19(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$19(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$19(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartInfoPriceCalculationDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$19(this, "name", void 0), _define_property$19(this, "description", void 0), _define_property$19(this, "value", void 0), _define_property$19(this, "measure", void 0);
    }
}
_ts_decorate$19([
    dto({
        string: true
    }),
    _ts_metadata$19("design:type", String)
], CartInfoPriceCalculationDTO.prototype, "name", void 0);
_ts_decorate$19([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$19("design:type", Object)
], CartInfoPriceCalculationDTO.prototype, "description", void 0);
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoPriceCalculationDTO.prototype, "value", void 0);
_ts_decorate$19([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$19("design:type", Object)
], CartInfoPriceCalculationDTO.prototype, "measure", void 0);
class CartInfoPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$19(this, "base", void 0), _define_property$19(this, "result", void 0), _define_property$19(this, "discount", void 0), _define_property$19(this, "calculation", void 0);
    }
}
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoPriceDTO.prototype, "base", void 0);
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoPriceDTO.prototype, "result", void 0);
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoPriceDTO.prototype, "discount", void 0);
_ts_decorate$19([
    dto({
        object: {
            type: CartInfoPriceCalculationDTO
        },
        array: true
    }),
    _ts_metadata$19("design:type", Array)
], CartInfoPriceDTO.prototype, "calculation", void 0);
class CartInfoBonesesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$19(this, "willUse", void 0), _define_property$19(this, "willAdd", void 0), _define_property$19(this, "current", void 0);
    }
}
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoBonesesDTO.prototype, "willUse", void 0);
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoBonesesDTO.prototype, "willAdd", void 0);
_ts_decorate$19([
    dto({
        number: true
    }),
    _ts_metadata$19("design:type", Number)
], CartInfoBonesesDTO.prototype, "current", void 0);
var CartV2InfoStatus = /*#__PURE__*/ function(CartV2InfoStatus) {
    CartV2InfoStatus["SUCCESS"] = "success";
    CartV2InfoStatus["ERROR"] = "error";
    return CartV2InfoStatus;
}({});
class CartV2InfoSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$19(this, "status", void 0), _define_property$19(this, "price", void 0), _define_property$19(this, "bonuses", void 0);
    }
}
_ts_decorate$19([
    dto({
        equals: "success"
    }),
    _ts_metadata$19("design:type", typeof CartV2InfoStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CartV2InfoSuccessDTO.prototype, "status", void 0);
_ts_decorate$19([
    dto({
        object: {
            type: CartInfoPriceDTO
        }
    }),
    _ts_metadata$19("design:type", typeof CartInfoPriceDTO === "undefined" ? Object : CartInfoPriceDTO)
], CartV2InfoSuccessDTO.prototype, "price", void 0);
_ts_decorate$19([
    dto({
        object: {
            type: CartInfoBonesesDTO
        }
    }),
    _ts_metadata$19("design:type", typeof CartInfoBonesesDTO === "undefined" ? Object : CartInfoBonesesDTO)
], CartV2InfoSuccessDTO.prototype, "bonuses", void 0);
class CartV2InfoErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$19(this, "status", void 0), _define_property$19(this, "message", void 0);
    }
}
_ts_decorate$19([
    dto({
        equals: "error"
    }),
    _ts_metadata$19("design:type", typeof CartV2InfoStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CartV2InfoErrorDTO.prototype, "status", void 0);
_ts_decorate$19([
    dto({
        string: true
    }),
    _ts_metadata$19("design:type", String)
], CartV2InfoErrorDTO.prototype, "message", void 0);

var CartV2DeliveryCalculationResponseStatus = /*#__PURE__*/ function(CartV2DeliveryCalculationResponseStatus) {
    CartV2DeliveryCalculationResponseStatus["SUCCESS"] = "success";
    CartV2DeliveryCalculationResponseStatus["ERROR"] = "error";
    return CartV2DeliveryCalculationResponseStatus;
}({});

function _define_property$18(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$18(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$18(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2DeliveryCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$18(this, "status", void 0), _define_property$18(this, "message", void 0);
    }
}
_ts_decorate$18([
    dto({
        equals: CartV2DeliveryCalculationResponseStatus.ERROR
    }),
    _ts_metadata$18("design:type", typeof CartV2DeliveryCalculationResponseStatus === "undefined" || typeof CartV2DeliveryCalculationResponseStatus.ERROR === "undefined" ? Object : CartV2DeliveryCalculationResponseStatus.ERROR)
], CartV2DeliveryCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$18([
    dto({
        string: true
    }),
    _ts_metadata$18("design:type", String)
], CartV2DeliveryCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$17(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$17(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$17(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2DeliveryCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$17(this, "status", void 0);
    }
}
_ts_decorate$17([
    dto({
        equals: CartV2DeliveryCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$17("design:type", typeof CartV2DeliveryCalculationResponseStatus === "undefined" || typeof CartV2DeliveryCalculationResponseStatus.SUCCESS === "undefined" ? Object : CartV2DeliveryCalculationResponseStatus.SUCCESS)
], CartV2DeliveryCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$16(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$16(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$16(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let CartDeliveryCartInfoCourierAddressResponseDTO$1 = class CartDeliveryCartInfoCourierAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$16(this, "street", void 0), _define_property$16(this, "houseNumber", void 0), _define_property$16(this, "appartmentNumber", void 0);
    }
};
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierAddressResponseDTO$1.prototype, "street", void 0);
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierAddressResponseDTO$1.prototype, "houseNumber", void 0);
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierAddressResponseDTO$1.prototype, "appartmentNumber", void 0);
let CartDeliveryCartInfoCourierCityResponseDTO$1 = class CartDeliveryCartInfoCourierCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$16(this, "id", void 0), _define_property$16(this, "name", void 0);
    }
};
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierCityResponseDTO$1.prototype, "id", void 0);
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierCityResponseDTO$1.prototype, "name", void 0);
let CartDeliveryCartInfoCourierVariantResponseDTO$1 = class CartDeliveryCartInfoCourierVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$16(this, "id", void 0), _define_property$16(this, "name", void 0);
    }
};
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierVariantResponseDTO$1.prototype, "id", void 0);
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartDeliveryCartInfoCourierVariantResponseDTO$1.prototype, "name", void 0);
class CartV2DeliveryCartInfoCourierResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$16(this, "type", void 0), _define_property$16(this, "typeId", void 0), _define_property$16(this, "typeName", void 0), _define_property$16(this, "city", void 0), _define_property$16(this, "address", void 0), _define_property$16(this, "variant", void 0), _define_property$16(this, "comment", void 0);
    }
}
_ts_decorate$16([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$16("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "type", void 0);
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "typeId", void 0);
_ts_decorate$16([
    dto({
        string: true
    }),
    _ts_metadata$16("design:type", String)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "typeName", void 0);
_ts_decorate$16([
    dto({
        object: {
            type: CartDeliveryCartInfoCourierCityResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$16("design:type", Object)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "city", void 0);
_ts_decorate$16([
    dto({
        object: {
            type: CartDeliveryCartInfoCourierAddressResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$16("design:type", Object)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "address", void 0);
_ts_decorate$16([
    dto({
        object: {
            type: CartDeliveryCartInfoCourierVariantResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$16("design:type", Object)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "variant", void 0);
_ts_decorate$16([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$16("design:type", Object)
], CartV2DeliveryCartInfoCourierResponseDTO.prototype, "comment", void 0);

function _define_property$15(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$15(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$15(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let CartDeliveryCartInfoPickupShopCoordinatesResponseDTO$1 = class CartDeliveryCartInfoPickupShopCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$15(this, "latitude", void 0), _define_property$15(this, "longitude", void 0);
    }
};
_ts_decorate$15([
    dto({
        number: true
    }),
    _ts_metadata$15("design:type", Number)
], CartDeliveryCartInfoPickupShopCoordinatesResponseDTO$1.prototype, "latitude", void 0);
_ts_decorate$15([
    dto({
        number: true
    }),
    _ts_metadata$15("design:type", Number)
], CartDeliveryCartInfoPickupShopCoordinatesResponseDTO$1.prototype, "longitude", void 0);
let CartDeliveryCartInfoPickupShopResponseDTO$1 = class CartDeliveryCartInfoPickupShopResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$15(this, "id", void 0), _define_property$15(this, "cityName", void 0), _define_property$15(this, "address", void 0), _define_property$15(this, "coordinates", void 0);
    }
};
_ts_decorate$15([
    dto({
        string: true
    }),
    _ts_metadata$15("design:type", String)
], CartDeliveryCartInfoPickupShopResponseDTO$1.prototype, "id", void 0);
_ts_decorate$15([
    dto({
        string: true
    }),
    _ts_metadata$15("design:type", String)
], CartDeliveryCartInfoPickupShopResponseDTO$1.prototype, "cityName", void 0);
_ts_decorate$15([
    dto({
        string: true
    }),
    _ts_metadata$15("design:type", String)
], CartDeliveryCartInfoPickupShopResponseDTO$1.prototype, "address", void 0);
_ts_decorate$15([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupShopCoordinatesResponseDTO$1
        },
        optional: {
            allowNull: true
        }
    }),
    _ts_metadata$15("design:type", Object)
], CartDeliveryCartInfoPickupShopResponseDTO$1.prototype, "coordinates", void 0);
class CartV2DeliveryCartInfoPickupResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$15(this, "type", void 0), _define_property$15(this, "typeId", void 0), _define_property$15(this, "typeName", void 0), _define_property$15(this, "shop", void 0);
    }
}
_ts_decorate$15([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$15("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], CartV2DeliveryCartInfoPickupResponseDTO.prototype, "type", void 0);
_ts_decorate$15([
    dto({
        string: true
    }),
    _ts_metadata$15("design:type", String)
], CartV2DeliveryCartInfoPickupResponseDTO.prototype, "typeId", void 0);
_ts_decorate$15([
    dto({
        string: true
    }),
    _ts_metadata$15("design:type", String)
], CartV2DeliveryCartInfoPickupResponseDTO.prototype, "typeName", void 0);
_ts_decorate$15([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupShopResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$15("design:type", Object)
], CartV2DeliveryCartInfoPickupResponseDTO.prototype, "shop", void 0);

function _define_property$14(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$14(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$14(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let CartDeliveryCartInfoPickupPointCoordinatesResponseDTO$1 = class CartDeliveryCartInfoPickupPointCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$14(this, "latitude", void 0), _define_property$14(this, "longitude", void 0);
    }
};
_ts_decorate$14([
    dto({
        number: true
    }),
    _ts_metadata$14("design:type", Number)
], CartDeliveryCartInfoPickupPointCoordinatesResponseDTO$1.prototype, "latitude", void 0);
_ts_decorate$14([
    dto({
        number: true
    }),
    _ts_metadata$14("design:type", Number)
], CartDeliveryCartInfoPickupPointCoordinatesResponseDTO$1.prototype, "longitude", void 0);
let CartDeliveryCartInfoPickupPointCityResponseDTO$1 = class CartDeliveryCartInfoPickupPointCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$14(this, "id", void 0), _define_property$14(this, "name", void 0);
    }
};
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartDeliveryCartInfoPickupPointCityResponseDTO$1.prototype, "id", void 0);
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartDeliveryCartInfoPickupPointCityResponseDTO$1.prototype, "name", void 0);
let CartDeliveryCartInfoPickupPointAddressResponseDTO$1 = class CartDeliveryCartInfoPickupPointAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$14(this, "id", void 0), _define_property$14(this, "name", void 0), _define_property$14(this, "coordinates", void 0);
    }
};
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartDeliveryCartInfoPickupPointAddressResponseDTO$1.prototype, "id", void 0);
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartDeliveryCartInfoPickupPointAddressResponseDTO$1.prototype, "name", void 0);
_ts_decorate$14([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointCoordinatesResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$14("design:type", Object)
], CartDeliveryCartInfoPickupPointAddressResponseDTO$1.prototype, "coordinates", void 0);
let CartDeliveryCartInfoPickupPointVariantResponseDTO$1 = class CartDeliveryCartInfoPickupPointVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$14(this, "id", void 0), _define_property$14(this, "name", void 0);
    }
};
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartDeliveryCartInfoPickupPointVariantResponseDTO$1.prototype, "id", void 0);
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartDeliveryCartInfoPickupPointVariantResponseDTO$1.prototype, "name", void 0);
class CartV2DeliveryCartInfoPickupPointResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$14(this, "type", void 0), _define_property$14(this, "typeId", void 0), _define_property$14(this, "typeName", void 0), _define_property$14(this, "city", void 0), _define_property$14(this, "address", void 0), _define_property$14(this, "variant", void 0);
    }
}
_ts_decorate$14([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$14("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], CartV2DeliveryCartInfoPickupPointResponseDTO.prototype, "type", void 0);
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartV2DeliveryCartInfoPickupPointResponseDTO.prototype, "typeId", void 0);
_ts_decorate$14([
    dto({
        string: true
    }),
    _ts_metadata$14("design:type", String)
], CartV2DeliveryCartInfoPickupPointResponseDTO.prototype, "typeName", void 0);
_ts_decorate$14([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointCityResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$14("design:type", Object)
], CartV2DeliveryCartInfoPickupPointResponseDTO.prototype, "city", void 0);
_ts_decorate$14([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointAddressResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$14("design:type", Object)
], CartV2DeliveryCartInfoPickupPointResponseDTO.prototype, "address", void 0);
_ts_decorate$14([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointVariantResponseDTO$1
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$14("design:type", Object)
], CartV2DeliveryCartInfoPickupPointResponseDTO.prototype, "variant", void 0);

function _define_property$13(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$13(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$13(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let CartDeliveryCalculationInfoVariantResponseDTO$1 = class CartDeliveryCalculationInfoVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$13(this, "name", void 0), _define_property$13(this, "id", void 0);
    }
};
_ts_decorate$13([
    dto({
        string: true
    }),
    _ts_metadata$13("design:type", String)
], CartDeliveryCalculationInfoVariantResponseDTO$1.prototype, "name", void 0);
_ts_decorate$13([
    dto({
        string: true
    }),
    _ts_metadata$13("design:type", String)
], CartDeliveryCalculationInfoVariantResponseDTO$1.prototype, "id", void 0);
class CartV2DeliveryCalculationInfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$13(this, "variants", void 0);
    }
}
_ts_decorate$13([
    dto({
        object: {
            type: CartDeliveryCalculationInfoVariantResponseDTO$1
        },
        array: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$13("design:type", Object)
], CartV2DeliveryCalculationInfoResponseDTO.prototype, "variants", void 0);

function _define_property$12(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$12(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$12(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2DeliveryResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$12(this, "calculation", void 0), _define_property$12(this, "info", void 0), _define_property$12(this, "cartInfo", void 0);
    }
}
_ts_decorate$12([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV2DeliveryCalculationResponseStatus.SUCCESS,
                        value: CartV2DeliveryCalculationSuccessResponseDTO
                    },
                    {
                        name: CartV2DeliveryCalculationResponseStatus.ERROR,
                        value: CartV2DeliveryCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$12("design:type", Object)
], CartV2DeliveryResponseDTO.prototype, "calculation", void 0);
_ts_decorate$12([
    dto({
        object: {
            type: CartV2DeliveryCalculationInfoResponseDTO
        }
    }),
    _ts_metadata$12("design:type", typeof CartV2DeliveryCalculationInfoResponseDTO === "undefined" ? Object : CartV2DeliveryCalculationInfoResponseDTO)
], CartV2DeliveryResponseDTO.prototype, "info", void 0);
_ts_decorate$12([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: CartV2DeliveryCartInfoCourierResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: CartV2DeliveryCartInfoPickupResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: CartV2DeliveryCartInfoPickupPointResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$12("design:type", Object)
], CartV2DeliveryResponseDTO.prototype, "cartInfo", void 0);

var CartV2PaymentCalculationResponseStatus = /*#__PURE__*/ function(CartV2PaymentCalculationResponseStatus) {
    CartV2PaymentCalculationResponseStatus["SUCCESS"] = "success";
    CartV2PaymentCalculationResponseStatus["ERROR"] = "error";
    return CartV2PaymentCalculationResponseStatus;
}({});

function _define_property$11(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$11(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$11(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2PaymentCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$11(this, "status", void 0), _define_property$11(this, "message", void 0);
    }
}
_ts_decorate$11([
    dto({
        equals: CartV2PaymentCalculationResponseStatus.ERROR
    }),
    _ts_metadata$11("design:type", typeof CartV2PaymentCalculationResponseStatus === "undefined" || typeof CartV2PaymentCalculationResponseStatus.ERROR === "undefined" ? Object : CartV2PaymentCalculationResponseStatus.ERROR)
], CartV2PaymentCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$11([
    dto({
        string: true
    }),
    _ts_metadata$11("design:type", String)
], CartV2PaymentCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$10(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$10(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$10(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2PaymentCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$10(this, "status", void 0);
    }
}
_ts_decorate$10([
    dto({
        equals: CartV2PaymentCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$10("design:type", typeof CartV2PaymentCalculationResponseStatus === "undefined" || typeof CartV2PaymentCalculationResponseStatus.SUCCESS === "undefined" ? Object : CartV2PaymentCalculationResponseStatus.SUCCESS)
], CartV2PaymentCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$$(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$$(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2PaymentCartInfoCashResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$$(this, "type", void 0), _define_property$$(this, "typeId", void 0), _define_property$$(this, "typeName", void 0);
    }
}
_ts_decorate$$([
    dto({
        equals: IntegrationCartConfigurationPaymentType.CASH
    }),
    _ts_metadata$$("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.CASH === "undefined" ? Object : IntegrationCartConfigurationPaymentType.CASH)
], CartV2PaymentCartInfoCashResponseDTO.prototype, "type", void 0);
_ts_decorate$$([
    dto({
        string: true
    }),
    _ts_metadata$$("design:type", String)
], CartV2PaymentCartInfoCashResponseDTO.prototype, "typeId", void 0);
_ts_decorate$$([
    dto({
        string: true
    }),
    _ts_metadata$$("design:type", String)
], CartV2PaymentCartInfoCashResponseDTO.prototype, "typeName", void 0);

function _define_property$_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$_(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$_(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2PaymentCartInfoOnlineResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$_(this, "type", void 0), _define_property$_(this, "typeId", void 0), _define_property$_(this, "typeName", void 0);
    }
}
_ts_decorate$_([
    dto({
        equals: IntegrationCartConfigurationPaymentType.ONLINE
    }),
    _ts_metadata$_("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.ONLINE === "undefined" ? Object : IntegrationCartConfigurationPaymentType.ONLINE)
], CartV2PaymentCartInfoOnlineResponseDTO.prototype, "type", void 0);
_ts_decorate$_([
    dto({
        string: true
    }),
    _ts_metadata$_("design:type", String)
], CartV2PaymentCartInfoOnlineResponseDTO.prototype, "typeId", void 0);
_ts_decorate$_([
    dto({
        string: true
    }),
    _ts_metadata$_("design:type", String)
], CartV2PaymentCartInfoOnlineResponseDTO.prototype, "typeName", void 0);

function _define_property$Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$Z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$Z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2PaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$Z(this, "calculation", void 0), _define_property$Z(this, "cartInfo", void 0);
    }
}
_ts_decorate$Z([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV2PaymentCalculationResponseStatus.SUCCESS,
                        value: CartV2PaymentCalculationSuccessResponseDTO
                    },
                    {
                        name: CartV2PaymentCalculationResponseStatus.ERROR,
                        value: CartV2PaymentCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$Z("design:type", Object)
], CartV2PaymentResponseDTO.prototype, "calculation", void 0);
_ts_decorate$Z([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationPaymentType.CASH,
                        value: CartV2PaymentCartInfoCashResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationPaymentType.ONLINE,
                        value: CartV2PaymentCartInfoOnlineResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$Z("design:type", Object)
], CartV2PaymentResponseDTO.prototype, "cartInfo", void 0);

function _define_property$Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$Y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$Y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$Y(this, "products", void 0), _define_property$Y(this, "info", void 0), _define_property$Y(this, "delivery", void 0), _define_property$Y(this, "payment", void 0), _define_property$Y(this, "lastValidatedHash", void 0), _define_property$Y(this, "applyBonuses", void 0);
    }
}
_ts_decorate$Y([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV2ProductStatus.SUCCESS,
                        value: CartV2ProductSuccessDTO
                    },
                    {
                        name: CartV2ProductStatus.ERROR,
                        value: CartV2ProductErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$Y("design:type", Array)
], CartV2DTO.prototype, "products", void 0);
_ts_decorate$Y([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV2InfoStatus.SUCCESS,
                        value: CartV2InfoSuccessDTO
                    },
                    {
                        name: CartV2InfoStatus.ERROR,
                        value: CartV2InfoErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$Y("design:type", Object)
], CartV2DTO.prototype, "info", void 0);
_ts_decorate$Y([
    dto({
        object: {
            type: CartV2DeliveryResponseDTO
        }
    }),
    _ts_metadata$Y("design:type", typeof CartV2DeliveryResponseDTO === "undefined" ? Object : CartV2DeliveryResponseDTO)
], CartV2DTO.prototype, "delivery", void 0);
_ts_decorate$Y([
    dto({
        object: {
            type: CartV2PaymentResponseDTO
        }
    }),
    _ts_metadata$Y("design:type", typeof CartV2PaymentResponseDTO === "undefined" ? Object : CartV2PaymentResponseDTO)
], CartV2DTO.prototype, "payment", void 0);
_ts_decorate$Y([
    dto({
        string: true
    }),
    _ts_metadata$Y("design:type", String)
], CartV2DTO.prototype, "lastValidatedHash", void 0);
_ts_decorate$Y([
    dto({
        boolean: true
    }),
    _ts_metadata$Y("design:type", Boolean)
], CartV2DTO.prototype, "applyBonuses", void 0);

function _define_property$X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$X(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$X(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartSingleAddProductAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$X(this, "id", void 0), _define_property$X(this, "value", void 0);
    }
}
_ts_decorate$X([
    dto({
        string: true
    }),
    _ts_metadata$X("design:type", String)
], CartSingleAddProductAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$X([
    dto({
        string: true
    }),
    _ts_metadata$X("design:type", String)
], CartSingleAddProductAttributeRequestDTO.prototype, "value", void 0);
class CartV2SingleAddProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$X(this, "originalProductId", void 0), _define_property$X(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$X([
    dto({
        string: true
    }),
    _ts_metadata$X("design:type", String)
], CartV2SingleAddProductRequestDTO.prototype, "originalProductId", void 0);
_ts_decorate$X([
    dto({
        array: true,
        object: {
            type: CartSingleAddProductAttributeRequestDTO
        }
    }),
    _ts_metadata$X("design:type", Array)
], CartV2SingleAddProductRequestDTO.prototype, "requiredAttributes", void 0);

function _define_property$W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$W(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$W(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2SingleRemoveProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$W(this, "id", void 0);
    }
}
_ts_decorate$W([
    dto({
        string: true
    }),
    _ts_metadata$W("design:type", String)
], CartV2SingleRemoveProductRequestDTO.prototype, "id", void 0);

function _define_property$V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$V(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$V(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2SingleUpdateProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$V(this, "id", void 0), _define_property$V(this, "applied", void 0), _define_property$V(this, "count", void 0);
    }
}
_ts_decorate$V([
    dto({
        string: true
    }),
    _ts_metadata$V("design:type", String)
], CartV2SingleUpdateProductRequestDTO.prototype, "id", void 0);
_ts_decorate$V([
    dto({
        boolean: true
    }),
    _ts_metadata$V("design:type", Boolean)
], CartV2SingleUpdateProductRequestDTO.prototype, "applied", void 0);
_ts_decorate$V([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$V("design:type", Number)
], CartV2SingleUpdateProductRequestDTO.prototype, "count", void 0);

function _define_property$U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$U(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$U(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartDeliveryCourierAddressRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$U(this, "street", void 0), _define_property$U(this, "houseNumber", void 0), _define_property$U(this, "appartmentNumber", void 0);
    }
}
_ts_decorate$U([
    dto({
        string: true
    }),
    _ts_metadata$U("design:type", String)
], UpdateCartDeliveryCourierAddressRequestDTO.prototype, "street", void 0);
_ts_decorate$U([
    dto({
        string: true
    }),
    _ts_metadata$U("design:type", String)
], UpdateCartDeliveryCourierAddressRequestDTO.prototype, "houseNumber", void 0);
_ts_decorate$U([
    dto({
        string: true
    }),
    _ts_metadata$U("design:type", String)
], UpdateCartDeliveryCourierAddressRequestDTO.prototype, "appartmentNumber", void 0);
class UpdateCartV2DeliveryCourierRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$U(this, "type", void 0), _define_property$U(this, "deliveryTypeId", void 0), _define_property$U(this, "cityId", void 0), _define_property$U(this, "address", void 0), _define_property$U(this, "variantId", void 0), _define_property$U(this, "comment", void 0);
    }
}
_ts_decorate$U([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$U("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], UpdateCartV2DeliveryCourierRequestDTO.prototype, "type", void 0);
_ts_decorate$U([
    dto({
        string: true
    }),
    _ts_metadata$U("design:type", String)
], UpdateCartV2DeliveryCourierRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$U([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$U("design:type", Object)
], UpdateCartV2DeliveryCourierRequestDTO.prototype, "cityId", void 0);
_ts_decorate$U([
    dto({
        object: {
            type: UpdateCartDeliveryCourierAddressRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$U("design:type", Object)
], UpdateCartV2DeliveryCourierRequestDTO.prototype, "address", void 0);
_ts_decorate$U([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$U("design:type", Object)
], UpdateCartV2DeliveryCourierRequestDTO.prototype, "variantId", void 0);
_ts_decorate$U([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$U("design:type", Object)
], UpdateCartV2DeliveryCourierRequestDTO.prototype, "comment", void 0);

function _define_property$T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$T(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$T(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartV2DeliveryPickupRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$T(this, "type", void 0), _define_property$T(this, "deliveryTypeId", void 0), _define_property$T(this, "shopId", void 0);
    }
}
_ts_decorate$T([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$T("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], UpdateCartV2DeliveryPickupRequestDTO.prototype, "type", void 0);
_ts_decorate$T([
    dto({
        string: true
    }),
    _ts_metadata$T("design:type", String)
], UpdateCartV2DeliveryPickupRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$T([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$T("design:type", Object)
], UpdateCartV2DeliveryPickupRequestDTO.prototype, "shopId", void 0);

function _define_property$S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$S(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$S(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartV2DeliveryPickupPointRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$S(this, "type", void 0), _define_property$S(this, "deliveryTypeId", void 0), _define_property$S(this, "cityId", void 0), _define_property$S(this, "addressId", void 0), _define_property$S(this, "variantId", void 0);
    }
}
_ts_decorate$S([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$S("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], UpdateCartV2DeliveryPickupPointRequestDTO.prototype, "type", void 0);
_ts_decorate$S([
    dto({
        string: true
    }),
    _ts_metadata$S("design:type", String)
], UpdateCartV2DeliveryPickupPointRequestDTO.prototype, "deliveryTypeId", void 0);
_ts_decorate$S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$S("design:type", Object)
], UpdateCartV2DeliveryPickupPointRequestDTO.prototype, "cityId", void 0);
_ts_decorate$S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$S("design:type", Object)
], UpdateCartV2DeliveryPickupPointRequestDTO.prototype, "addressId", void 0);
_ts_decorate$S([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$S("design:type", Object)
], UpdateCartV2DeliveryPickupPointRequestDTO.prototype, "variantId", void 0);

function _define_property$R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$R(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$R(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartV2PaymentOnlineRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$R(this, "type", void 0), _define_property$R(this, "paymentTypeId", void 0);
    }
}
_ts_decorate$R([
    dto({
        equals: IntegrationCartConfigurationPaymentType.ONLINE
    }),
    _ts_metadata$R("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.ONLINE === "undefined" ? Object : IntegrationCartConfigurationPaymentType.ONLINE)
], UpdateCartV2PaymentOnlineRequestDTO.prototype, "type", void 0);
_ts_decorate$R([
    dto({
        string: true
    }),
    _ts_metadata$R("design:type", String)
], UpdateCartV2PaymentOnlineRequestDTO.prototype, "paymentTypeId", void 0);

function _define_property$Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$Q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$Q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartV2PaymentCashRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$Q(this, "type", void 0), _define_property$Q(this, "paymentTypeId", void 0);
    }
}
_ts_decorate$Q([
    dto({
        equals: IntegrationCartConfigurationPaymentType.CASH
    }),
    _ts_metadata$Q("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.CASH === "undefined" ? Object : IntegrationCartConfigurationPaymentType.CASH)
], UpdateCartV2PaymentCashRequestDTO.prototype, "type", void 0);
_ts_decorate$Q([
    dto({
        string: true
    }),
    _ts_metadata$Q("design:type", String)
], UpdateCartV2PaymentCashRequestDTO.prototype, "paymentTypeId", void 0);

function _define_property$P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$P(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$P(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV2SingleUpdateRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$P(this, "applyBonuses", void 0), _define_property$P(this, "promoCodes", void 0), _define_property$P(this, "delivery", void 0), _define_property$P(this, "payment", void 0);
    }
}
_ts_decorate$P([
    dto({
        boolean: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$P("design:type", Object)
], CartV2SingleUpdateRequestDTO.prototype, "applyBonuses", void 0);
_ts_decorate$P([
    dto({
        array: true,
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$P("design:type", Object)
], CartV2SingleUpdateRequestDTO.prototype, "promoCodes", void 0);
_ts_decorate$P([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: UpdateCartV2DeliveryCourierRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: UpdateCartV2DeliveryPickupRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: UpdateCartV2DeliveryPickupPointRequestDTO
                    }
                ]
            }
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$P("design:type", Object)
], CartV2SingleUpdateRequestDTO.prototype, "delivery", void 0);
_ts_decorate$P([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationPaymentType.ONLINE,
                        value: UpdateCartV2PaymentOnlineRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationPaymentType.CASH,
                        value: UpdateCartV2PaymentCashRequestDTO
                    }
                ]
            }
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$P("design:type", Object)
], CartV2SingleUpdateRequestDTO.prototype, "payment", void 0);

function _define_property$O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$O(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$O(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartProductRequiredAttributeRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$O(this, "id", void 0), _define_property$O(this, "value", void 0);
    }
}
_ts_decorate$O([
    dto({
        string: true
    }),
    _ts_metadata$O("design:type", String)
], UpdateCartProductRequiredAttributeRequestDTO.prototype, "id", void 0);
_ts_decorate$O([
    dto({
        string: true
    }),
    _ts_metadata$O("design:type", String)
], UpdateCartProductRequiredAttributeRequestDTO.prototype, "value", void 0);
class UpdateCartV2ProductRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$O(this, "id", void 0), _define_property$O(this, "originalProductId", void 0), _define_property$O(this, "count", void 0), _define_property$O(this, "applied", void 0), _define_property$O(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$O([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$O("design:type", Object)
], UpdateCartV2ProductRequestDTO.prototype, "id", void 0);
_ts_decorate$O([
    dto({
        optional: true,
        string: true
    }),
    _ts_metadata$O("design:type", Object)
], UpdateCartV2ProductRequestDTO.prototype, "originalProductId", void 0);
_ts_decorate$O([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$O("design:type", Number)
], UpdateCartV2ProductRequestDTO.prototype, "count", void 0);
_ts_decorate$O([
    dto({
        boolean: true
    }),
    _ts_metadata$O("design:type", Boolean)
], UpdateCartV2ProductRequestDTO.prototype, "applied", void 0);
_ts_decorate$O([
    dto({
        array: true,
        object: {
            type: UpdateCartProductRequiredAttributeRequestDTO
        }
    }),
    _ts_metadata$O("design:type", Array)
], UpdateCartV2ProductRequestDTO.prototype, "requiredAttributes", void 0);

function _define_property$N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$N(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$N(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class UpdateCartV2RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$N(this, "products", void 0), _define_property$N(this, "applyBonuses", void 0), _define_property$N(this, "promoCodes", void 0), _define_property$N(this, "delivery", void 0), _define_property$N(this, "payment", void 0);
    }
}
_ts_decorate$N([
    dto({
        array: true,
        object: {
            type: UpdateCartV2ProductRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$N("design:type", Object)
], UpdateCartV2RequestDTO.prototype, "products", void 0);
_ts_decorate$N([
    dto({
        boolean: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$N("design:type", Object)
], UpdateCartV2RequestDTO.prototype, "applyBonuses", void 0);
_ts_decorate$N([
    dto({
        array: true,
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$N("design:type", Object)
], UpdateCartV2RequestDTO.prototype, "promoCodes", void 0);
_ts_decorate$N([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: UpdateCartV2DeliveryCourierRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: UpdateCartV2DeliveryPickupRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: UpdateCartV2DeliveryPickupPointRequestDTO
                    }
                ]
            }
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$N("design:type", Object)
], UpdateCartV2RequestDTO.prototype, "delivery", void 0);
_ts_decorate$N([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationPaymentType.ONLINE,
                        value: UpdateCartV2PaymentOnlineRequestDTO
                    },
                    {
                        name: IntegrationCartConfigurationPaymentType.CASH,
                        value: UpdateCartV2PaymentCashRequestDTO
                    }
                ]
            }
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$N("design:type", Object)
], UpdateCartV2RequestDTO.prototype, "payment", void 0);

function _define_property$M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$M(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$M(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CartV3InfoStatus = /*#__PURE__*/ function(CartV3InfoStatus) {
    CartV3InfoStatus["SUCCESS"] = "success";
    CartV3InfoStatus["ERROR"] = "error";
    return CartV3InfoStatus;
}({});
class CartV3InfoPriceCalculationDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$M(this, "name", void 0), _define_property$M(this, "description", void 0), _define_property$M(this, "value", void 0), _define_property$M(this, "measure", void 0);
    }
}
_ts_decorate$M([
    dto({
        string: true
    }),
    _ts_metadata$M("design:type", String)
], CartV3InfoPriceCalculationDTO.prototype, "name", void 0);
_ts_decorate$M([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$M("design:type", Object)
], CartV3InfoPriceCalculationDTO.prototype, "description", void 0);
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoPriceCalculationDTO.prototype, "value", void 0);
_ts_decorate$M([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$M("design:type", Object)
], CartV3InfoPriceCalculationDTO.prototype, "measure", void 0);
class CartV3InfoPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$M(this, "base", void 0), _define_property$M(this, "result", void 0), _define_property$M(this, "discount", void 0), _define_property$M(this, "calculation", void 0);
    }
}
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoPriceDTO.prototype, "base", void 0);
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoPriceDTO.prototype, "result", void 0);
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoPriceDTO.prototype, "discount", void 0);
_ts_decorate$M([
    dto({
        object: {
            type: CartV3InfoPriceCalculationDTO
        },
        array: true
    }),
    _ts_metadata$M("design:type", Array)
], CartV3InfoPriceDTO.prototype, "calculation", void 0);
var CartV3InfoBonusesWalletType = /*#__PURE__*/ function(CartV3InfoBonusesWalletType) {
    CartV3InfoBonusesWalletType["CASHBACK"] = "cashback";
    CartV3InfoBonusesWalletType["BONUS"] = "bonus";
    return CartV3InfoBonusesWalletType;
}({});
class CartV3InfoBonusesWalletCashbackDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$M(this, "type", void 0), _define_property$M(this, "willUse", void 0), _define_property$M(this, "willAdd", void 0), _define_property$M(this, "current", void 0);
    }
}
_ts_decorate$M([
    dto({
        equals: "cashback"
    }),
    _ts_metadata$M("design:type", typeof CartV3InfoBonusesWalletType === "undefined" || "string" === "undefined" ? Object : "cashback")
], CartV3InfoBonusesWalletCashbackDTO.prototype, "type", void 0);
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoBonusesWalletCashbackDTO.prototype, "willUse", void 0);
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoBonusesWalletCashbackDTO.prototype, "willAdd", void 0);
_ts_decorate$M([
    dto({
        number: true
    }),
    _ts_metadata$M("design:type", Number)
], CartV3InfoBonusesWalletCashbackDTO.prototype, "current", void 0);
class CartV3InfoBonusesWalletBonusDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$M(this, "type", void 0);
    }
}
_ts_decorate$M([
    dto({
        equals: "bonus"
    }),
    _ts_metadata$M("design:type", typeof CartV3InfoBonusesWalletType === "undefined" || "string" === "undefined" ? Object : "bonus")
], CartV3InfoBonusesWalletBonusDTO.prototype, "type", void 0);
class CartV3InfoSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$M(this, "status", void 0), _define_property$M(this, "price", void 0), _define_property$M(this, "bonusesWallet", void 0);
    }
}
_ts_decorate$M([
    dto({
        equals: "success"
    }),
    _ts_metadata$M("design:type", typeof CartV3InfoStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CartV3InfoSuccessDTO.prototype, "status", void 0);
_ts_decorate$M([
    dto({
        object: {
            type: CartV3InfoPriceDTO
        }
    }),
    _ts_metadata$M("design:type", typeof CartV3InfoPriceDTO === "undefined" ? Object : CartV3InfoPriceDTO)
], CartV3InfoSuccessDTO.prototype, "price", void 0);
_ts_decorate$M([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "cashback",
                        value: CartV3InfoBonusesWalletCashbackDTO
                    },
                    {
                        name: "bonus",
                        value: CartV3InfoBonusesWalletBonusDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$M("design:type", Object)
], CartV3InfoSuccessDTO.prototype, "bonusesWallet", void 0);
class CartV3InfoErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$M(this, "status", void 0), _define_property$M(this, "message", void 0);
    }
}
_ts_decorate$M([
    dto({
        equals: "error"
    }),
    _ts_metadata$M("design:type", typeof CartV3InfoStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CartV3InfoErrorDTO.prototype, "status", void 0);
_ts_decorate$M([
    dto({
        string: true
    }),
    _ts_metadata$M("design:type", String)
], CartV3InfoErrorDTO.prototype, "message", void 0);

function _define_property$L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$L(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$L(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$L(this, "base", void 0), _define_property$L(this, "result", void 0), _define_property$L(this, "discount", void 0);
    }
}
_ts_decorate$L([
    dto({
        number: true
    }),
    _ts_metadata$L("design:type", Number)
], CartProductPriceDTO.prototype, "base", void 0);
_ts_decorate$L([
    dto({
        number: true
    }),
    _ts_metadata$L("design:type", Number)
], CartProductPriceDTO.prototype, "result", void 0);
_ts_decorate$L([
    dto({
        number: true
    }),
    _ts_metadata$L("design:type", Number)
], CartProductPriceDTO.prototype, "discount", void 0);
class CartProductRequiredAttributeValueDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$L(this, "id", void 0), _define_property$L(this, "name", void 0);
    }
}
_ts_decorate$L([
    dto({
        string: true
    }),
    _ts_metadata$L("design:type", String)
], CartProductRequiredAttributeValueDTO.prototype, "id", void 0);
_ts_decorate$L([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$L("design:type", Object)
], CartProductRequiredAttributeValueDTO.prototype, "name", void 0);
class CartProductRequiredAttributeDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$L(this, "id", void 0), _define_property$L(this, "name", void 0), _define_property$L(this, "measure", void 0), _define_property$L(this, "value", void 0);
    }
}
_ts_decorate$L([
    dto({
        string: true
    }),
    _ts_metadata$L("design:type", String)
], CartProductRequiredAttributeDTO.prototype, "id", void 0);
_ts_decorate$L([
    dto({
        string: true
    }),
    _ts_metadata$L("design:type", String)
], CartProductRequiredAttributeDTO.prototype, "name", void 0);
_ts_decorate$L([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$L("design:type", Object)
], CartProductRequiredAttributeDTO.prototype, "measure", void 0);
_ts_decorate$L([
    dto({
        object: {
            type: CartProductRequiredAttributeValueDTO
        }
    }),
    _ts_metadata$L("design:type", typeof CartProductRequiredAttributeValueDTO === "undefined" ? Object : CartProductRequiredAttributeValueDTO)
], CartProductRequiredAttributeDTO.prototype, "value", void 0);
class CartProductOriginalDTO extends ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$L(this, "attributes", void 0), _define_property$L(this, "parts", void 0);
    }
}
_ts_decorate$L([
    dto({
        array: true,
        object: {
            type: ProductAttributeDTO
        }
    }),
    _ts_metadata$L("design:type", Array)
], CartProductOriginalDTO.prototype, "attributes", void 0);
_ts_decorate$L([
    dto({
        array: true,
        object: {
            type: ProductPartDTO
        }
    }),
    _ts_metadata$L("design:type", Array)
], CartProductOriginalDTO.prototype, "parts", void 0);
var CartV3ProductStatus = /*#__PURE__*/ function(CartV3ProductStatus) {
    CartV3ProductStatus["SUCCESS"] = "success";
    CartV3ProductStatus["ERROR"] = "error";
    return CartV3ProductStatus;
}({});
class CartV3ProductBaseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$L(this, "id", void 0), _define_property$L(this, "name", void 0), _define_property$L(this, "count", void 0), _define_property$L(this, "applied", void 0), _define_property$L(this, "originalProduct", void 0), _define_property$L(this, "maxCount", void 0), _define_property$L(this, "requiredAttributes", void 0);
    }
}
_ts_decorate$L([
    dto({
        string: true
    }),
    _ts_metadata$L("design:type", String)
], CartV3ProductBaseDTO.prototype, "id", void 0);
_ts_decorate$L([
    dto({
        string: true
    }),
    _ts_metadata$L("design:type", String)
], CartV3ProductBaseDTO.prototype, "name", void 0);
_ts_decorate$L([
    dto({
        number: true
    }),
    _ts_metadata$L("design:type", Number)
], CartV3ProductBaseDTO.prototype, "count", void 0);
_ts_decorate$L([
    dto({
        boolean: true
    }),
    _ts_metadata$L("design:type", Boolean)
], CartV3ProductBaseDTO.prototype, "applied", void 0);
_ts_decorate$L([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: CartProductOriginalDTO
        }
    }),
    _ts_metadata$L("design:type", Object)
], CartV3ProductBaseDTO.prototype, "originalProduct", void 0);
_ts_decorate$L([
    dto({
        number: {
            maxDecimalPlaces: 0
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$L("design:type", Object)
], CartV3ProductBaseDTO.prototype, "maxCount", void 0);
_ts_decorate$L([
    dto({
        array: true,
        object: {
            type: CartProductRequiredAttributeDTO
        }
    }),
    _ts_metadata$L("design:type", Array)
], CartV3ProductBaseDTO.prototype, "requiredAttributes", void 0);
class CartV3ProductSuccessDTO extends CartV3ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$L(this, "status", void 0), _define_property$L(this, "price", void 0);
    }
}
_ts_decorate$L([
    dto({
        equals: "success"
    }),
    _ts_metadata$L("design:type", typeof CartV3ProductStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CartV3ProductSuccessDTO.prototype, "status", void 0);
_ts_decorate$L([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: CartProductPriceDTO
        }
    }),
    _ts_metadata$L("design:type", Object)
], CartV3ProductSuccessDTO.prototype, "price", void 0);
class CartV3ProductErrorDTO extends CartV3ProductBaseDTO {
    constructor(...args){
        super(...args), _define_property$L(this, "status", void 0), _define_property$L(this, "message", void 0);
    }
}
_ts_decorate$L([
    dto({
        equals: "error"
    }),
    _ts_metadata$L("design:type", typeof CartV3ProductStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CartV3ProductErrorDTO.prototype, "status", void 0);
_ts_decorate$L([
    dto({
        string: true
    }),
    _ts_metadata$L("design:type", String)
], CartV3ProductErrorDTO.prototype, "message", void 0);

var CartV3DeliveryCalculationResponseStatus = /*#__PURE__*/ function(CartV3DeliveryCalculationResponseStatus) {
    CartV3DeliveryCalculationResponseStatus["SUCCESS"] = "success";
    CartV3DeliveryCalculationResponseStatus["ERROR"] = "error";
    return CartV3DeliveryCalculationResponseStatus;
}({});

function _define_property$K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$K(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$K(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3DeliveryCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$K(this, "status", void 0), _define_property$K(this, "message", void 0);
    }
}
_ts_decorate$K([
    dto({
        equals: CartV3DeliveryCalculationResponseStatus.ERROR
    }),
    _ts_metadata$K("design:type", typeof CartV3DeliveryCalculationResponseStatus === "undefined" || typeof CartV3DeliveryCalculationResponseStatus.ERROR === "undefined" ? Object : CartV3DeliveryCalculationResponseStatus.ERROR)
], CartV3DeliveryCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$K([
    dto({
        string: true
    }),
    _ts_metadata$K("design:type", String)
], CartV3DeliveryCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$J(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$J(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3DeliveryCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$J(this, "status", void 0);
    }
}
_ts_decorate$J([
    dto({
        equals: CartV3DeliveryCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$J("design:type", typeof CartV3DeliveryCalculationResponseStatus === "undefined" || typeof CartV3DeliveryCalculationResponseStatus.SUCCESS === "undefined" ? Object : CartV3DeliveryCalculationResponseStatus.SUCCESS)
], CartV3DeliveryCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$I(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$I(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartDeliveryCartInfoCourierAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$I(this, "street", void 0), _define_property$I(this, "houseNumber", void 0), _define_property$I(this, "appartmentNumber", void 0);
    }
}
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierAddressResponseDTO.prototype, "street", void 0);
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierAddressResponseDTO.prototype, "houseNumber", void 0);
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierAddressResponseDTO.prototype, "appartmentNumber", void 0);
class CartDeliveryCartInfoCourierCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$I(this, "id", void 0), _define_property$I(this, "name", void 0);
    }
}
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierCityResponseDTO.prototype, "id", void 0);
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierCityResponseDTO.prototype, "name", void 0);
class CartDeliveryCartInfoCourierVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$I(this, "id", void 0), _define_property$I(this, "name", void 0);
    }
}
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierVariantResponseDTO.prototype, "id", void 0);
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartDeliveryCartInfoCourierVariantResponseDTO.prototype, "name", void 0);
class CartV3DeliveryCartInfoCourierResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$I(this, "type", void 0), _define_property$I(this, "typeId", void 0), _define_property$I(this, "typeName", void 0), _define_property$I(this, "city", void 0), _define_property$I(this, "address", void 0), _define_property$I(this, "variant", void 0), _define_property$I(this, "comment", void 0);
    }
}
_ts_decorate$I([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.COURIER
    }),
    _ts_metadata$I("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.COURIER === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.COURIER)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "type", void 0);
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "typeId", void 0);
_ts_decorate$I([
    dto({
        string: true
    }),
    _ts_metadata$I("design:type", String)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "typeName", void 0);
_ts_decorate$I([
    dto({
        object: {
            type: CartDeliveryCartInfoCourierCityResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$I("design:type", Object)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "city", void 0);
_ts_decorate$I([
    dto({
        object: {
            type: CartDeliveryCartInfoCourierAddressResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$I("design:type", Object)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "address", void 0);
_ts_decorate$I([
    dto({
        object: {
            type: CartDeliveryCartInfoCourierVariantResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$I("design:type", Object)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "variant", void 0);
_ts_decorate$I([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$I("design:type", Object)
], CartV3DeliveryCartInfoCourierResponseDTO.prototype, "comment", void 0);

function _define_property$H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$H(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$H(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartDeliveryCartInfoPickupShopCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$H(this, "latitude", void 0), _define_property$H(this, "longitude", void 0);
    }
}
_ts_decorate$H([
    dto({
        number: true
    }),
    _ts_metadata$H("design:type", Number)
], CartDeliveryCartInfoPickupShopCoordinatesResponseDTO.prototype, "latitude", void 0);
_ts_decorate$H([
    dto({
        number: true
    }),
    _ts_metadata$H("design:type", Number)
], CartDeliveryCartInfoPickupShopCoordinatesResponseDTO.prototype, "longitude", void 0);
class CartDeliveryCartInfoPickupShopResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$H(this, "id", void 0), _define_property$H(this, "cityName", void 0), _define_property$H(this, "address", void 0), _define_property$H(this, "coordinates", void 0);
    }
}
_ts_decorate$H([
    dto({
        string: true
    }),
    _ts_metadata$H("design:type", String)
], CartDeliveryCartInfoPickupShopResponseDTO.prototype, "id", void 0);
_ts_decorate$H([
    dto({
        string: true
    }),
    _ts_metadata$H("design:type", String)
], CartDeliveryCartInfoPickupShopResponseDTO.prototype, "cityName", void 0);
_ts_decorate$H([
    dto({
        string: true
    }),
    _ts_metadata$H("design:type", String)
], CartDeliveryCartInfoPickupShopResponseDTO.prototype, "address", void 0);
_ts_decorate$H([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupShopCoordinatesResponseDTO
        },
        optional: {
            allowNull: true
        }
    }),
    _ts_metadata$H("design:type", Object)
], CartDeliveryCartInfoPickupShopResponseDTO.prototype, "coordinates", void 0);
class CartV3DeliveryCartInfoPickupResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$H(this, "type", void 0), _define_property$H(this, "typeId", void 0), _define_property$H(this, "typeName", void 0), _define_property$H(this, "shop", void 0);
    }
}
_ts_decorate$H([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP
    }),
    _ts_metadata$H("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP)
], CartV3DeliveryCartInfoPickupResponseDTO.prototype, "type", void 0);
_ts_decorate$H([
    dto({
        string: true
    }),
    _ts_metadata$H("design:type", String)
], CartV3DeliveryCartInfoPickupResponseDTO.prototype, "typeId", void 0);
_ts_decorate$H([
    dto({
        string: true
    }),
    _ts_metadata$H("design:type", String)
], CartV3DeliveryCartInfoPickupResponseDTO.prototype, "typeName", void 0);
_ts_decorate$H([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupShopResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$H("design:type", Object)
], CartV3DeliveryCartInfoPickupResponseDTO.prototype, "shop", void 0);

function _define_property$G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$G(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$G(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartDeliveryCartInfoPickupPointCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$G(this, "latitude", void 0), _define_property$G(this, "longitude", void 0);
    }
}
_ts_decorate$G([
    dto({
        number: true
    }),
    _ts_metadata$G("design:type", Number)
], CartDeliveryCartInfoPickupPointCoordinatesResponseDTO.prototype, "latitude", void 0);
_ts_decorate$G([
    dto({
        number: true
    }),
    _ts_metadata$G("design:type", Number)
], CartDeliveryCartInfoPickupPointCoordinatesResponseDTO.prototype, "longitude", void 0);
class CartDeliveryCartInfoPickupPointCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$G(this, "id", void 0), _define_property$G(this, "name", void 0);
    }
}
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartDeliveryCartInfoPickupPointCityResponseDTO.prototype, "id", void 0);
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartDeliveryCartInfoPickupPointCityResponseDTO.prototype, "name", void 0);
class CartDeliveryCartInfoPickupPointAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$G(this, "id", void 0), _define_property$G(this, "name", void 0), _define_property$G(this, "coordinates", void 0);
    }
}
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartDeliveryCartInfoPickupPointAddressResponseDTO.prototype, "id", void 0);
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartDeliveryCartInfoPickupPointAddressResponseDTO.prototype, "name", void 0);
_ts_decorate$G([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointCoordinatesResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$G("design:type", Object)
], CartDeliveryCartInfoPickupPointAddressResponseDTO.prototype, "coordinates", void 0);
class CartDeliveryCartInfoPickupPointVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$G(this, "id", void 0), _define_property$G(this, "name", void 0);
    }
}
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartDeliveryCartInfoPickupPointVariantResponseDTO.prototype, "id", void 0);
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartDeliveryCartInfoPickupPointVariantResponseDTO.prototype, "name", void 0);
class CartV3DeliveryCartInfoPickupPointResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$G(this, "type", void 0), _define_property$G(this, "typeId", void 0), _define_property$G(this, "typeName", void 0), _define_property$G(this, "city", void 0), _define_property$G(this, "address", void 0), _define_property$G(this, "variant", void 0);
    }
}
_ts_decorate$G([
    dto({
        equals: IntegrationCartConfigurationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$G("design:type", typeof IntegrationCartConfigurationDeliveryType === "undefined" || typeof IntegrationCartConfigurationDeliveryType.PICKUP_POINT === "undefined" ? Object : IntegrationCartConfigurationDeliveryType.PICKUP_POINT)
], CartV3DeliveryCartInfoPickupPointResponseDTO.prototype, "type", void 0);
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartV3DeliveryCartInfoPickupPointResponseDTO.prototype, "typeId", void 0);
_ts_decorate$G([
    dto({
        string: true
    }),
    _ts_metadata$G("design:type", String)
], CartV3DeliveryCartInfoPickupPointResponseDTO.prototype, "typeName", void 0);
_ts_decorate$G([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointCityResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$G("design:type", Object)
], CartV3DeliveryCartInfoPickupPointResponseDTO.prototype, "city", void 0);
_ts_decorate$G([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointAddressResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$G("design:type", Object)
], CartV3DeliveryCartInfoPickupPointResponseDTO.prototype, "address", void 0);
_ts_decorate$G([
    dto({
        object: {
            type: CartDeliveryCartInfoPickupPointVariantResponseDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$G("design:type", Object)
], CartV3DeliveryCartInfoPickupPointResponseDTO.prototype, "variant", void 0);

function _define_property$F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$F(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$F(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartDeliveryCalculationInfoVariantResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$F(this, "name", void 0), _define_property$F(this, "id", void 0);
    }
}
_ts_decorate$F([
    dto({
        string: true
    }),
    _ts_metadata$F("design:type", String)
], CartDeliveryCalculationInfoVariantResponseDTO.prototype, "name", void 0);
_ts_decorate$F([
    dto({
        string: true
    }),
    _ts_metadata$F("design:type", String)
], CartDeliveryCalculationInfoVariantResponseDTO.prototype, "id", void 0);
class CartV3DeliveryCalculationInfoResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$F(this, "variants", void 0);
    }
}
_ts_decorate$F([
    dto({
        object: {
            type: CartDeliveryCalculationInfoVariantResponseDTO
        },
        array: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$F("design:type", Object)
], CartV3DeliveryCalculationInfoResponseDTO.prototype, "variants", void 0);

function _define_property$E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$E(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$E(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3DeliveryResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$E(this, "calculation", void 0), _define_property$E(this, "info", void 0), _define_property$E(this, "cartInfo", void 0);
    }
}
_ts_decorate$E([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV3DeliveryCalculationResponseStatus.SUCCESS,
                        value: CartV3DeliveryCalculationSuccessResponseDTO
                    },
                    {
                        name: CartV3DeliveryCalculationResponseStatus.ERROR,
                        value: CartV3DeliveryCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$E("design:type", Object)
], CartV3DeliveryResponseDTO.prototype, "calculation", void 0);
_ts_decorate$E([
    dto({
        object: {
            type: CartV3DeliveryCalculationInfoResponseDTO
        }
    }),
    _ts_metadata$E("design:type", typeof CartV3DeliveryCalculationInfoResponseDTO === "undefined" ? Object : CartV3DeliveryCalculationInfoResponseDTO)
], CartV3DeliveryResponseDTO.prototype, "info", void 0);
_ts_decorate$E([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationDeliveryType.COURIER,
                        value: CartV3DeliveryCartInfoCourierResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP,
                        value: CartV3DeliveryCartInfoPickupResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationDeliveryType.PICKUP_POINT,
                        value: CartV3DeliveryCartInfoPickupPointResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$E("design:type", Object)
], CartV3DeliveryResponseDTO.prototype, "cartInfo", void 0);

var CartV3PaymentCalculationResponseStatus = /*#__PURE__*/ function(CartV3PaymentCalculationResponseStatus) {
    CartV3PaymentCalculationResponseStatus["SUCCESS"] = "success";
    CartV3PaymentCalculationResponseStatus["ERROR"] = "error";
    return CartV3PaymentCalculationResponseStatus;
}({});

function _define_property$D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$D(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$D(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3PaymentCalculationErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$D(this, "status", void 0), _define_property$D(this, "message", void 0);
    }
}
_ts_decorate$D([
    dto({
        equals: CartV3PaymentCalculationResponseStatus.ERROR
    }),
    _ts_metadata$D("design:type", typeof CartV3PaymentCalculationResponseStatus === "undefined" || typeof CartV3PaymentCalculationResponseStatus.ERROR === "undefined" ? Object : CartV3PaymentCalculationResponseStatus.ERROR)
], CartV3PaymentCalculationErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$D([
    dto({
        string: true
    }),
    _ts_metadata$D("design:type", String)
], CartV3PaymentCalculationErrorResponseDTO.prototype, "message", void 0);

function _define_property$C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$C(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$C(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3PaymentCalculationSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$C(this, "status", void 0);
    }
}
_ts_decorate$C([
    dto({
        equals: CartV3PaymentCalculationResponseStatus.SUCCESS
    }),
    _ts_metadata$C("design:type", typeof CartV3PaymentCalculationResponseStatus === "undefined" || typeof CartV3PaymentCalculationResponseStatus.SUCCESS === "undefined" ? Object : CartV3PaymentCalculationResponseStatus.SUCCESS)
], CartV3PaymentCalculationSuccessResponseDTO.prototype, "status", void 0);

function _define_property$B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$B(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$B(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3PaymentCartInfoCashResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$B(this, "type", void 0), _define_property$B(this, "typeId", void 0), _define_property$B(this, "typeName", void 0);
    }
}
_ts_decorate$B([
    dto({
        equals: IntegrationCartConfigurationPaymentType.CASH
    }),
    _ts_metadata$B("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.CASH === "undefined" ? Object : IntegrationCartConfigurationPaymentType.CASH)
], CartV3PaymentCartInfoCashResponseDTO.prototype, "type", void 0);
_ts_decorate$B([
    dto({
        string: true
    }),
    _ts_metadata$B("design:type", String)
], CartV3PaymentCartInfoCashResponseDTO.prototype, "typeId", void 0);
_ts_decorate$B([
    dto({
        string: true
    }),
    _ts_metadata$B("design:type", String)
], CartV3PaymentCartInfoCashResponseDTO.prototype, "typeName", void 0);

function _define_property$A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$A(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$A(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3PaymentCartInfoOnlineResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$A(this, "type", void 0), _define_property$A(this, "typeId", void 0), _define_property$A(this, "typeName", void 0);
    }
}
_ts_decorate$A([
    dto({
        equals: IntegrationCartConfigurationPaymentType.ONLINE
    }),
    _ts_metadata$A("design:type", typeof IntegrationCartConfigurationPaymentType === "undefined" || typeof IntegrationCartConfigurationPaymentType.ONLINE === "undefined" ? Object : IntegrationCartConfigurationPaymentType.ONLINE)
], CartV3PaymentCartInfoOnlineResponseDTO.prototype, "type", void 0);
_ts_decorate$A([
    dto({
        string: true
    }),
    _ts_metadata$A("design:type", String)
], CartV3PaymentCartInfoOnlineResponseDTO.prototype, "typeId", void 0);
_ts_decorate$A([
    dto({
        string: true
    }),
    _ts_metadata$A("design:type", String)
], CartV3PaymentCartInfoOnlineResponseDTO.prototype, "typeName", void 0);

function _define_property$z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$z(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$z(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3PaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$z(this, "calculation", void 0), _define_property$z(this, "cartInfo", void 0);
    }
}
_ts_decorate$z([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV3PaymentCalculationResponseStatus.SUCCESS,
                        value: CartV3PaymentCalculationSuccessResponseDTO
                    },
                    {
                        name: CartV3PaymentCalculationResponseStatus.ERROR,
                        value: CartV3PaymentCalculationErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$z("design:type", Object)
], CartV3PaymentResponseDTO.prototype, "calculation", void 0);
_ts_decorate$z([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: IntegrationCartConfigurationPaymentType.CASH,
                        value: CartV3PaymentCartInfoCashResponseDTO
                    },
                    {
                        name: IntegrationCartConfigurationPaymentType.ONLINE,
                        value: CartV3PaymentCartInfoOnlineResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$z("design:type", Object)
], CartV3PaymentResponseDTO.prototype, "cartInfo", void 0);

function _define_property$y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$y(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$y(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var CartV3PromoCodeStatus = /*#__PURE__*/ function(CartV3PromoCodeStatus) {
    CartV3PromoCodeStatus["SUCCESS"] = "success";
    CartV3PromoCodeStatus["ERROR"] = "error";
    return CartV3PromoCodeStatus;
}({});
class CartV3PromoCodeSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$y(this, "status", void 0), _define_property$y(this, "name", void 0), _define_property$y(this, "description", void 0), _define_property$y(this, "category", void 0);
    }
}
_ts_decorate$y([
    dto({
        equals: "success"
    }),
    _ts_metadata$y("design:type", typeof CartV3PromoCodeStatus === "undefined" || "string" === "undefined" ? Object : "success")
], CartV3PromoCodeSuccessDTO.prototype, "status", void 0);
_ts_decorate$y([
    dto({
        string: true
    }),
    _ts_metadata$y("design:type", String)
], CartV3PromoCodeSuccessDTO.prototype, "name", void 0);
_ts_decorate$y([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$y("design:type", Object)
], CartV3PromoCodeSuccessDTO.prototype, "description", void 0);
_ts_decorate$y([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$y("design:type", Object)
], CartV3PromoCodeSuccessDTO.prototype, "category", void 0);
class CartV3PromoCodeErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$y(this, "status", void 0), _define_property$y(this, "message", void 0);
    }
}
_ts_decorate$y([
    dto({
        equals: "error"
    }),
    _ts_metadata$y("design:type", typeof CartV3PromoCodeStatus === "undefined" || "string" === "undefined" ? Object : "error")
], CartV3PromoCodeErrorDTO.prototype, "status", void 0);
_ts_decorate$y([
    dto({
        string: true
    }),
    _ts_metadata$y("design:type", String)
], CartV3PromoCodeErrorDTO.prototype, "message", void 0);
class CartV3PromoCodeResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$y(this, "code", void 0), _define_property$y(this, "data", void 0);
    }
}
_ts_decorate$y([
    dto({
        string: true
    }),
    _ts_metadata$y("design:type", String)
], CartV3PromoCodeResponseDTO.prototype, "code", void 0);
_ts_decorate$y([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: CartV3PromoCodeSuccessDTO
                    },
                    {
                        name: "error",
                        value: CartV3PromoCodeErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$y("design:type", Object)
], CartV3PromoCodeResponseDTO.prototype, "data", void 0);
class CartV3PromoCodesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$y(this, "list", void 0), _define_property$y(this, "limit", void 0);
    }
}
_ts_decorate$y([
    dto({
        array: true,
        object: {
            type: CartV3PromoCodeResponseDTO
        }
    }),
    _ts_metadata$y("design:type", Array)
], CartV3PromoCodesResponseDTO.prototype, "list", void 0);
_ts_decorate$y([
    dto({
        number: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$y("design:type", Object)
], CartV3PromoCodesResponseDTO.prototype, "limit", void 0);

function _define_property$x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$x(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$x(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV3DTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$x(this, "products", void 0), _define_property$x(this, "info", void 0), _define_property$x(this, "delivery", void 0), _define_property$x(this, "payment", void 0), _define_property$x(this, "promoCodes", void 0), _define_property$x(this, "lastValidatedHash", void 0), _define_property$x(this, "applyBonuses", void 0);
    }
}
_ts_decorate$x([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV3ProductStatus.SUCCESS,
                        value: CartV3ProductSuccessDTO
                    },
                    {
                        name: CartV3ProductStatus.ERROR,
                        value: CartV3ProductErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$x("design:type", Array)
], CartV3DTO.prototype, "products", void 0);
_ts_decorate$x([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: CartV3InfoStatus.SUCCESS,
                        value: CartV3InfoSuccessDTO
                    },
                    {
                        name: CartV3InfoStatus.ERROR,
                        value: CartV3InfoErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$x("design:type", Object)
], CartV3DTO.prototype, "info", void 0);
_ts_decorate$x([
    dto({
        object: {
            type: CartV3DeliveryResponseDTO
        }
    }),
    _ts_metadata$x("design:type", typeof CartV3DeliveryResponseDTO === "undefined" ? Object : CartV3DeliveryResponseDTO)
], CartV3DTO.prototype, "delivery", void 0);
_ts_decorate$x([
    dto({
        object: {
            type: CartV3PaymentResponseDTO
        }
    }),
    _ts_metadata$x("design:type", typeof CartV3PaymentResponseDTO === "undefined" ? Object : CartV3PaymentResponseDTO)
], CartV3DTO.prototype, "payment", void 0);
_ts_decorate$x([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: CartV3PromoCodesResponseDTO
        }
    }),
    _ts_metadata$x("design:type", Object)
], CartV3DTO.prototype, "promoCodes", void 0);
_ts_decorate$x([
    dto({
        string: true
    }),
    _ts_metadata$x("design:type", String)
], CartV3DTO.prototype, "lastValidatedHash", void 0);
_ts_decorate$x([
    dto({
        boolean: true
    }),
    _ts_metadata$x("design:type", Boolean)
], CartV3DTO.prototype, "applyBonuses", void 0);

function _define_property$w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$w(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$w(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CartV4DTO extends CartV3DTO {
    constructor(...args){
        super(...args), _define_property$w(this, "deliveryTypes", void 0), _define_property$w(this, "paymentTypes", void 0);
    }
}
_ts_decorate$w([
    dto({
        array: true,
        object: {
            type: CartConfigurationDeliveryTypeResponseDTO
        }
    }),
    _ts_metadata$w("design:type", Array)
], CartV4DTO.prototype, "deliveryTypes", void 0);
_ts_decorate$w([
    dto({
        array: true,
        object: {
            type: CartGetConfigurationPaymentTypeResponseDTO
        }
    }),
    _ts_metadata$w("design:type", Array)
], CartV4DTO.prototype, "paymentTypes", void 0);

function _define_property$v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$v(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$v(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ExternalIntegrationCaptchaValidateStatus = /*#__PURE__*/ function(ExternalIntegrationCaptchaValidateStatus) {
    ExternalIntegrationCaptchaValidateStatus["OK"] = "ok";
    ExternalIntegrationCaptchaValidateStatus["FAILED"] = "failed";
    return ExternalIntegrationCaptchaValidateStatus;
}({});
class ExternalIntegrationCaptchaValidateResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$v(this, "status", void 0), _define_property$v(this, "message", void 0);
    }
}
_ts_decorate$v([
    dto({
        enum: ExternalIntegrationCaptchaValidateStatus
    }),
    _ts_metadata$v("design:type", String)
], ExternalIntegrationCaptchaValidateResponseDTO.prototype, "status", void 0);
_ts_decorate$v([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$v("design:type", String)
], ExternalIntegrationCaptchaValidateResponseDTO.prototype, "message", void 0);

var ExternalIntegrationDeliveryType = /*#__PURE__*/ function(ExternalIntegrationDeliveryType) {
    ExternalIntegrationDeliveryType["PICKUP_POINT"] = "pickup_point";
    ExternalIntegrationDeliveryType["COURIER"] = "courier";
    return ExternalIntegrationDeliveryType;
}({});

function _define_property$u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$u(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$u(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$u(this, "type", void 0), _define_property$u(this, "pickupPointId", void 0);
    }
}
_ts_decorate$u([
    dto({
        equals: ExternalIntegrationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$u("design:type", typeof ExternalIntegrationDeliveryType === "undefined" || typeof ExternalIntegrationDeliveryType.PICKUP_POINT === "undefined" ? Object : ExternalIntegrationDeliveryType.PICKUP_POINT)
], ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO.prototype, "type", void 0);
_ts_decorate$u([
    dto({
        string: true
    }),
    _ts_metadata$u("design:type", String)
], ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO.prototype, "pickupPointId", void 0);
class ExternalIntegrationDeliveryCalculateRequestDeliveryToPickupPointDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$u(this, "type", void 0), _define_property$u(this, "pickupPointId", void 0);
    }
}
_ts_decorate$u([
    dto({
        equals: ExternalIntegrationDeliveryType.PICKUP_POINT
    }),
    _ts_metadata$u("design:type", typeof ExternalIntegrationDeliveryType === "undefined" || typeof ExternalIntegrationDeliveryType.PICKUP_POINT === "undefined" ? Object : ExternalIntegrationDeliveryType.PICKUP_POINT)
], ExternalIntegrationDeliveryCalculateRequestDeliveryToPickupPointDTO.prototype, "type", void 0);
_ts_decorate$u([
    dto({
        string: true
    }),
    _ts_metadata$u("design:type", String)
], ExternalIntegrationDeliveryCalculateRequestDeliveryToPickupPointDTO.prototype, "pickupPointId", void 0);
class ExternalIntegrationDeliveryCalculateRequestDeliveryToCourierDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$u(this, "type", void 0), _define_property$u(this, "cityId", void 0), _define_property$u(this, "street", void 0), _define_property$u(this, "houseNumber", void 0);
    }
}
_ts_decorate$u([
    dto({
        equals: ExternalIntegrationDeliveryType.COURIER
    }),
    _ts_metadata$u("design:type", typeof ExternalIntegrationDeliveryType === "undefined" || typeof ExternalIntegrationDeliveryType.COURIER === "undefined" ? Object : ExternalIntegrationDeliveryType.COURIER)
], ExternalIntegrationDeliveryCalculateRequestDeliveryToCourierDTO.prototype, "type", void 0);
_ts_decorate$u([
    dto({
        string: true
    }),
    _ts_metadata$u("design:type", String)
], ExternalIntegrationDeliveryCalculateRequestDeliveryToCourierDTO.prototype, "cityId", void 0);
_ts_decorate$u([
    dto({
        string: true
    }),
    _ts_metadata$u("design:type", String)
], ExternalIntegrationDeliveryCalculateRequestDeliveryToCourierDTO.prototype, "street", void 0);
_ts_decorate$u([
    dto({
        string: true
    }),
    _ts_metadata$u("design:type", String)
], ExternalIntegrationDeliveryCalculateRequestDeliveryToCourierDTO.prototype, "houseNumber", void 0);
class ExternalIntegrationDeliveryCalculateRequestPackageDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$u(this, "weightG", void 0);
    }
}
_ts_decorate$u([
    dto({
        number: true
    }),
    _ts_metadata$u("design:type", Number)
], ExternalIntegrationDeliveryCalculateRequestPackageDTO.prototype, "weightG", void 0);
class ExternalIntegrationDeliveryCalculateRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$u(this, "deliveryFrom", void 0), _define_property$u(this, "deliveryTo", void 0), _define_property$u(this, "packages", void 0);
    }
}
_ts_decorate$u([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: ExternalIntegrationDeliveryType.PICKUP_POINT,
                        value: ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$u("design:type", typeof ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO === "undefined" ? Object : ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO)
], ExternalIntegrationDeliveryCalculateRequestDTO.prototype, "deliveryFrom", void 0);
_ts_decorate$u([
    dto({
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: ExternalIntegrationDeliveryType.PICKUP_POINT,
                        value: ExternalIntegrationDeliveryCalculateRequestDeliveryFromPickupPointDTO
                    },
                    {
                        name: ExternalIntegrationDeliveryType.COURIER,
                        value: ExternalIntegrationDeliveryCalculateRequestDeliveryToCourierDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$u("design:type", Object)
], ExternalIntegrationDeliveryCalculateRequestDTO.prototype, "deliveryTo", void 0);
_ts_decorate$u([
    dto({
        array: true,
        object: {
            type: ExternalIntegrationDeliveryCalculateRequestPackageDTO
        }
    }),
    _ts_metadata$u("design:type", Array)
], ExternalIntegrationDeliveryCalculateRequestDTO.prototype, "packages", void 0);

function _define_property$t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$t(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$t(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryCalculateResponseSuccessDataInfoDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$t(this, "id", void 0), _define_property$t(this, "name", void 0);
    }
}
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseSuccessDataInfoDTO.prototype, "id", void 0);
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseSuccessDataInfoDTO.prototype, "name", void 0);
class ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$t(this, "id", void 0), _define_property$t(this, "name", void 0), _define_property$t(this, "price", void 0), _define_property$t(this, "deliveryDaysMin", void 0), _define_property$t(this, "deliveryDaysMax", void 0), _define_property$t(this, "info", void 0);
    }
}
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO.prototype, "id", void 0);
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO.prototype, "name", void 0);
_ts_decorate$t([
    dto({
        number: true
    }),
    _ts_metadata$t("design:type", Number)
], ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO.prototype, "price", void 0);
_ts_decorate$t([
    dto({
        number: true
    }),
    _ts_metadata$t("design:type", Number)
], ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO.prototype, "deliveryDaysMin", void 0);
_ts_decorate$t([
    dto({
        number: true
    }),
    _ts_metadata$t("design:type", Number)
], ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO.prototype, "deliveryDaysMax", void 0);
_ts_decorate$t([
    dto({
        object: {
            type: ExternalIntegrationDeliveryCalculateResponseSuccessDataInfoDTO
        }
    }),
    _ts_metadata$t("design:type", typeof ExternalIntegrationDeliveryCalculateResponseSuccessDataInfoDTO === "undefined" ? Object : ExternalIntegrationDeliveryCalculateResponseSuccessDataInfoDTO)
], ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO.prototype, "info", void 0);
var ExternalIntegrationDeliveryCalculateResponseStatus = /*#__PURE__*/ function(ExternalIntegrationDeliveryCalculateResponseStatus) {
    ExternalIntegrationDeliveryCalculateResponseStatus["SUCCESS"] = "success";
    ExternalIntegrationDeliveryCalculateResponseStatus["ERROR"] = "error";
    return ExternalIntegrationDeliveryCalculateResponseStatus;
}({});
class ExternalIntegrationDeliveryCalculateResponseSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$t(this, "status", void 0), _define_property$t(this, "variants", void 0);
    }
}
_ts_decorate$t([
    dto({
        equals: "success"
    }),
    _ts_metadata$t("design:type", typeof ExternalIntegrationDeliveryCalculateResponseStatus === "undefined" || "string" === "undefined" ? Object : "success")
], ExternalIntegrationDeliveryCalculateResponseSuccessDTO.prototype, "status", void 0);
_ts_decorate$t([
    dto({
        object: {
            type: ExternalIntegrationDeliveryCalculateResponseSuccessVariantDTO
        },
        array: true
    }),
    _ts_metadata$t("design:type", Array)
], ExternalIntegrationDeliveryCalculateResponseSuccessDTO.prototype, "variants", void 0);
class ExternalIntegrationDeliveryCalculateResponseErrorExternalDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$t(this, "code", void 0), _define_property$t(this, "message", void 0);
    }
}
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseErrorExternalDTO.prototype, "code", void 0);
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseErrorExternalDTO.prototype, "message", void 0);
class ExternalIntegrationDeliveryCalculateResponseErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$t(this, "status", void 0), _define_property$t(this, "code", void 0), _define_property$t(this, "message", void 0), // Коды и тексты службы доставки — по ним потребитель отличает «неверный город» от «направление закрыто».
        _define_property$t(this, "external", void 0);
    }
}
_ts_decorate$t([
    dto({
        equals: "error"
    }),
    _ts_metadata$t("design:type", typeof ExternalIntegrationDeliveryCalculateResponseStatus === "undefined" || "string" === "undefined" ? Object : "error")
], ExternalIntegrationDeliveryCalculateResponseErrorDTO.prototype, "status", void 0);
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseErrorDTO.prototype, "code", void 0);
_ts_decorate$t([
    dto({
        string: true
    }),
    _ts_metadata$t("design:type", String)
], ExternalIntegrationDeliveryCalculateResponseErrorDTO.prototype, "message", void 0);
_ts_decorate$t([
    dto({
        optional: true,
        array: true,
        object: {
            type: ExternalIntegrationDeliveryCalculateResponseErrorExternalDTO
        }
    }),
    _ts_metadata$t("design:type", Array)
], ExternalIntegrationDeliveryCalculateResponseErrorDTO.prototype, "external", void 0);
class ExternalIntegrationDeliveryCalculateResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$t(this, "info", void 0);
    }
}
_ts_decorate$t([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: ExternalIntegrationDeliveryCalculateResponseSuccessDTO
                    },
                    {
                        name: "error",
                        value: ExternalIntegrationDeliveryCalculateResponseErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$t("design:type", Object)
], ExternalIntegrationDeliveryCalculateResponseDTO.prototype, "info", void 0);

function _define_property$s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$s(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$s(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryCourierCitiesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$s(this, "search", void 0);
    }
}
_ts_decorate$s([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$s("design:type", Object)
], ExternalIntegrationDeliveryCourierCitiesRequestDTO.prototype, "search", void 0);

function _define_property$r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$r(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$r(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryCourierCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$r(this, "id", void 0), _define_property$r(this, "name", void 0);
    }
}
_ts_decorate$r([
    dto({
        string: true
    }),
    _ts_metadata$r("design:type", String)
], ExternalIntegrationDeliveryCourierCityResponseDTO.prototype, "id", void 0);
_ts_decorate$r([
    dto({
        string: true
    }),
    _ts_metadata$r("design:type", String)
], ExternalIntegrationDeliveryCourierCityResponseDTO.prototype, "name", void 0);
class ExternalIntegrationDeliveryCourierCitiesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$r(this, "cities", void 0);
    }
}
_ts_decorate$r([
    dto({
        array: true,
        object: {
            type: ExternalIntegrationDeliveryCourierCityResponseDTO
        }
    }),
    _ts_metadata$r("design:type", Array)
], ExternalIntegrationDeliveryCourierCitiesResponseDTO.prototype, "cities", void 0);

var ExternalIntegrationDeliveryPickupPointAddressesMode = /*#__PURE__*/ function(ExternalIntegrationDeliveryPickupPointAddressesMode) {
    ExternalIntegrationDeliveryPickupPointAddressesMode["PARCEL_TERMINAL"] = "parcel_terminal";
    ExternalIntegrationDeliveryPickupPointAddressesMode["PICKUP_POINT"] = "pickup_point";
    return ExternalIntegrationDeliveryPickupPointAddressesMode;
}({});

function _define_property$q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$q(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$q(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryPickupPointAddressesPackageRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$q(this, "weightG", void 0), _define_property$q(this, "widthSM", void 0), _define_property$q(this, "heightSM", void 0), _define_property$q(this, "lengthSM", void 0);
    }
}
_ts_decorate$q([
    dto({
        number: true
    }),
    _ts_metadata$q("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressesPackageRequestDTO.prototype, "weightG", void 0);
_ts_decorate$q([
    dto({
        number: true
    }),
    _ts_metadata$q("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressesPackageRequestDTO.prototype, "widthSM", void 0);
_ts_decorate$q([
    dto({
        number: true
    }),
    _ts_metadata$q("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressesPackageRequestDTO.prototype, "heightSM", void 0);
_ts_decorate$q([
    dto({
        number: true
    }),
    _ts_metadata$q("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressesPackageRequestDTO.prototype, "lengthSM", void 0);
var ExternalIntegrationDeliveryPickupPointAddressesSelector = /*#__PURE__*/ function(ExternalIntegrationDeliveryPickupPointAddressesSelector) {
    ExternalIntegrationDeliveryPickupPointAddressesSelector["SEARCH"] = "search";
    ExternalIntegrationDeliveryPickupPointAddressesSelector["COORDINATES"] = "coordinates";
    return ExternalIntegrationDeliveryPickupPointAddressesSelector;
}({});
class ExternalIntegrationDeliveryPickupPointAddressesSelectorSearchRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$q(this, "type", void 0), _define_property$q(this, "search", void 0);
    }
}
_ts_decorate$q([
    dto({
        equals: "search"
    }),
    _ts_metadata$q("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressesSelector === "undefined" || "string" === "undefined" ? Object : "search")
], ExternalIntegrationDeliveryPickupPointAddressesSelectorSearchRequestDTO.prototype, "type", void 0);
_ts_decorate$q([
    dto({
        string: true
    }),
    _ts_metadata$q("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressesSelectorSearchRequestDTO.prototype, "search", void 0);
class ExternalIntegrationDeliveryPickupPointAddressesSelectorCoordinatesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$q(this, "type", void 0), _define_property$q(this, "latitude", void 0), _define_property$q(this, "longitude", void 0);
    }
}
_ts_decorate$q([
    dto({
        equals: "coordinates"
    }),
    _ts_metadata$q("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressesSelector === "undefined" || "string" === "undefined" ? Object : "coordinates")
], ExternalIntegrationDeliveryPickupPointAddressesSelectorCoordinatesRequestDTO.prototype, "type", void 0);
_ts_decorate$q([
    dto({
        number: true
    }),
    _ts_metadata$q("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressesSelectorCoordinatesRequestDTO.prototype, "latitude", void 0);
_ts_decorate$q([
    dto({
        number: true
    }),
    _ts_metadata$q("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressesSelectorCoordinatesRequestDTO.prototype, "longitude", void 0);
class ExternalIntegrationDeliveryPickupPointAddressesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$q(this, "cityId", void 0), _define_property$q(this, "mode", void 0), _define_property$q(this, "packages", void 0), _define_property$q(this, "selector", void 0);
    }
}
_ts_decorate$q([
    dto({
        string: true
    }),
    _ts_metadata$q("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressesRequestDTO.prototype, "cityId", void 0);
_ts_decorate$q([
    dto({
        enum: ExternalIntegrationDeliveryPickupPointAddressesMode,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$q("design:type", Object)
], ExternalIntegrationDeliveryPickupPointAddressesRequestDTO.prototype, "mode", void 0);
_ts_decorate$q([
    dto({
        array: true,
        object: {
            type: ExternalIntegrationDeliveryPickupPointAddressesPackageRequestDTO
        }
    }),
    _ts_metadata$q("design:type", Array)
], ExternalIntegrationDeliveryPickupPointAddressesRequestDTO.prototype, "packages", void 0);
_ts_decorate$q([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "search",
                        value: ExternalIntegrationDeliveryPickupPointAddressesSelectorSearchRequestDTO
                    },
                    {
                        name: "coordinates",
                        value: ExternalIntegrationDeliveryPickupPointAddressesSelectorCoordinatesRequestDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$q("design:type", Object)
], ExternalIntegrationDeliveryPickupPointAddressesRequestDTO.prototype, "selector", void 0);

var ExternalIntegrationDeliveryPickupPointAddressesStatus = /*#__PURE__*/ function(ExternalIntegrationDeliveryPickupPointAddressesStatus) {
    ExternalIntegrationDeliveryPickupPointAddressesStatus["SUCCESS"] = "success";
    ExternalIntegrationDeliveryPickupPointAddressesStatus["ERROR"] = "error";
    return ExternalIntegrationDeliveryPickupPointAddressesStatus;
}({});

function _define_property$p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$p(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$p(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryPickupPointAddressesErrorResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$p(this, "status", void 0), _define_property$p(this, "message", void 0), _define_property$p(this, "code", void 0);
    }
}
_ts_decorate$p([
    dto({
        equals: ExternalIntegrationDeliveryPickupPointAddressesStatus.ERROR
    }),
    _ts_metadata$p("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressesStatus === "undefined" || typeof ExternalIntegrationDeliveryPickupPointAddressesStatus.ERROR === "undefined" ? Object : ExternalIntegrationDeliveryPickupPointAddressesStatus.ERROR)
], ExternalIntegrationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "status", void 0);
_ts_decorate$p([
    dto({
        string: true
    }),
    _ts_metadata$p("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "message", void 0);
_ts_decorate$p([
    dto({
        string: true
    }),
    _ts_metadata$p("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressesErrorResponseDTO.prototype, "code", void 0);

function _define_property$o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$o(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$o(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryPickupPointAddressCoordinatesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$o(this, "latitude", void 0), _define_property$o(this, "longitude", void 0);
    }
}
_ts_decorate$o([
    dto({
        number: true
    }),
    _ts_metadata$o("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressCoordinatesResponseDTO.prototype, "latitude", void 0);
_ts_decorate$o([
    dto({
        number: true
    }),
    _ts_metadata$o("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressCoordinatesResponseDTO.prototype, "longitude", void 0);
class ExternalIntegrationDeliveryPickupPointAddressWorkingHoursListItemResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$o(this, "day", void 0), _define_property$o(this, "time", void 0);
    }
}
_ts_decorate$o([
    dto({
        number: true
    }),
    _ts_metadata$o("design:type", Number)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursListItemResponseDTO.prototype, "day", void 0);
_ts_decorate$o([
    dto({
        string: true
    }),
    _ts_metadata$o("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursListItemResponseDTO.prototype, "time", void 0);
class ExternalIntegrationDeliveryPickupPointAddressWorkingHoursExceptionsListItemResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$o(this, "date", void 0), _define_property$o(this, "time", void 0), _define_property$o(this, "work", void 0);
    }
}
_ts_decorate$o([
    dto({
        string: true
    }),
    _ts_metadata$o("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursExceptionsListItemResponseDTO.prototype, "date", void 0);
_ts_decorate$o([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$o("design:type", Object)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursExceptionsListItemResponseDTO.prototype, "time", void 0);
_ts_decorate$o([
    dto({
        boolean: true
    }),
    _ts_metadata$o("design:type", Boolean)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursExceptionsListItemResponseDTO.prototype, "work", void 0);
class ExternalIntegrationDeliveryPickupPointAddressWorkingHoursResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$o(this, "list", void 0), _define_property$o(this, "exceptions", void 0);
    }
}
_ts_decorate$o([
    dto({
        object: {
            type: ExternalIntegrationDeliveryPickupPointAddressWorkingHoursListItemResponseDTO
        },
        array: true
    }),
    _ts_metadata$o("design:type", Array)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursResponseDTO.prototype, "list", void 0);
_ts_decorate$o([
    dto({
        object: {
            type: ExternalIntegrationDeliveryPickupPointAddressWorkingHoursExceptionsListItemResponseDTO
        },
        array: true
    }),
    _ts_metadata$o("design:type", Array)
], ExternalIntegrationDeliveryPickupPointAddressWorkingHoursResponseDTO.prototype, "exceptions", void 0);
class ExternalIntegrationDeliveryPickupPointAddressResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$o(this, "id", void 0), _define_property$o(this, "fullAddress", void 0), _define_property$o(this, "mode", void 0), _define_property$o(this, "coordinates", void 0), _define_property$o(this, "workingHours", void 0);
    }
}
_ts_decorate$o([
    dto({
        string: true
    }),
    _ts_metadata$o("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressResponseDTO.prototype, "id", void 0);
_ts_decorate$o([
    dto({
        string: true
    }),
    _ts_metadata$o("design:type", String)
], ExternalIntegrationDeliveryPickupPointAddressResponseDTO.prototype, "fullAddress", void 0);
_ts_decorate$o([
    dto({
        enum: ExternalIntegrationDeliveryPickupPointAddressesMode
    }),
    _ts_metadata$o("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressesMode === "undefined" ? Object : ExternalIntegrationDeliveryPickupPointAddressesMode)
], ExternalIntegrationDeliveryPickupPointAddressResponseDTO.prototype, "mode", void 0);
_ts_decorate$o([
    dto({
        object: {
            type: ExternalIntegrationDeliveryPickupPointAddressCoordinatesResponseDTO
        }
    }),
    _ts_metadata$o("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressCoordinatesResponseDTO === "undefined" ? Object : ExternalIntegrationDeliveryPickupPointAddressCoordinatesResponseDTO)
], ExternalIntegrationDeliveryPickupPointAddressResponseDTO.prototype, "coordinates", void 0);
_ts_decorate$o([
    dto({
        object: {
            type: ExternalIntegrationDeliveryPickupPointAddressWorkingHoursResponseDTO
        }
    }),
    _ts_metadata$o("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressWorkingHoursResponseDTO === "undefined" ? Object : ExternalIntegrationDeliveryPickupPointAddressWorkingHoursResponseDTO)
], ExternalIntegrationDeliveryPickupPointAddressResponseDTO.prototype, "workingHours", void 0);
class ExternalIntegrationDeliveryPickupPointAddressesSuccessResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$o(this, "status", void 0), _define_property$o(this, "addresses", void 0);
    }
}
_ts_decorate$o([
    dto({
        equals: ExternalIntegrationDeliveryPickupPointAddressesStatus.SUCCESS
    }),
    _ts_metadata$o("design:type", typeof ExternalIntegrationDeliveryPickupPointAddressesStatus === "undefined" || typeof ExternalIntegrationDeliveryPickupPointAddressesStatus.SUCCESS === "undefined" ? Object : ExternalIntegrationDeliveryPickupPointAddressesStatus.SUCCESS)
], ExternalIntegrationDeliveryPickupPointAddressesSuccessResponseDTO.prototype, "status", void 0);
_ts_decorate$o([
    dto({
        array: true,
        object: {
            type: ExternalIntegrationDeliveryPickupPointAddressResponseDTO
        }
    }),
    _ts_metadata$o("design:type", Array)
], ExternalIntegrationDeliveryPickupPointAddressesSuccessResponseDTO.prototype, "addresses", void 0);

function _define_property$n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$n(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$n(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryPickupPointAddressesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$n(this, "info", void 0);
    }
}
_ts_decorate$n([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: ExternalIntegrationDeliveryPickupPointAddressesStatus.SUCCESS,
                        value: ExternalIntegrationDeliveryPickupPointAddressesSuccessResponseDTO
                    },
                    {
                        name: ExternalIntegrationDeliveryPickupPointAddressesStatus.ERROR,
                        value: ExternalIntegrationDeliveryPickupPointAddressesErrorResponseDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$n("design:type", Object)
], ExternalIntegrationDeliveryPickupPointAddressesResponseDTO.prototype, "info", void 0);

function _define_property$m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$m(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$m(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryPickupPointCitiesRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$m(this, "search", void 0);
    }
}
_ts_decorate$m([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$m("design:type", Object)
], ExternalIntegrationDeliveryPickupPointCitiesRequestDTO.prototype, "search", void 0);

function _define_property$l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$l(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$l(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationDeliveryPickupPointCityResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$l(this, "id", void 0), _define_property$l(this, "name", void 0);
    }
}
_ts_decorate$l([
    dto({
        string: true
    }),
    _ts_metadata$l("design:type", String)
], ExternalIntegrationDeliveryPickupPointCityResponseDTO.prototype, "id", void 0);
_ts_decorate$l([
    dto({
        string: true
    }),
    _ts_metadata$l("design:type", String)
], ExternalIntegrationDeliveryPickupPointCityResponseDTO.prototype, "name", void 0);
class ExternalIntegrationDeliveryPickupPointCitiesResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$l(this, "cities", void 0);
    }
}
_ts_decorate$l([
    dto({
        array: true,
        object: {
            type: ExternalIntegrationDeliveryPickupPointCityResponseDTO
        }
    }),
    _ts_metadata$l("design:type", Array)
], ExternalIntegrationDeliveryPickupPointCitiesResponseDTO.prototype, "cities", void 0);

function _define_property$k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$k(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$k(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationPaymentCancelRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$k(this, "id", void 0);
    }
}
_ts_decorate$k([
    dto({
        string: true
    }),
    _ts_metadata$k("design:type", String)
], ExternalIntegrationPaymentCancelRequestDTO.prototype, "id", void 0);

var ExternalIntegrationPaymentStatus = /*#__PURE__*/ function(ExternalIntegrationPaymentStatus) {
    ExternalIntegrationPaymentStatus["WAITING_FOR_PAYMENT"] = "waiting_for_payment";
    ExternalIntegrationPaymentStatus["COMPLETED"] = "completed";
    ExternalIntegrationPaymentStatus["CANCELED"] = "canceled";
    ExternalIntegrationPaymentStatus["REFUND"] = "refund";
    return ExternalIntegrationPaymentStatus;
}({});

function _define_property$j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$j(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$j(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ExternalIntegrationPaymentCancelResponseStatus = /*#__PURE__*/ function(ExternalIntegrationPaymentCancelResponseStatus) {
    ExternalIntegrationPaymentCancelResponseStatus["SUCCESS"] = "success";
    ExternalIntegrationPaymentCancelResponseStatus["ERROR"] = "error";
    return ExternalIntegrationPaymentCancelResponseStatus;
}({});
class ExternalIntegrationPaymentCancelResponseSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$j(this, "status", void 0), _define_property$j(this, "paymentStatus", void 0);
    }
}
_ts_decorate$j([
    dto({
        equals: "success"
    }),
    _ts_metadata$j("design:type", typeof ExternalIntegrationPaymentCancelResponseStatus === "undefined" || "string" === "undefined" ? Object : "success")
], ExternalIntegrationPaymentCancelResponseSuccessDTO.prototype, "status", void 0);
_ts_decorate$j([
    dto({
        enum: ExternalIntegrationPaymentStatus
    }),
    _ts_metadata$j("design:type", typeof ExternalIntegrationPaymentStatus === "undefined" ? Object : ExternalIntegrationPaymentStatus)
], ExternalIntegrationPaymentCancelResponseSuccessDTO.prototype, "paymentStatus", void 0);
class ExternalIntegrationPaymentCancelResponseErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$j(this, "status", void 0), _define_property$j(this, "message", void 0), _define_property$j(this, "code", void 0);
    }
}
_ts_decorate$j([
    dto({
        equals: "error"
    }),
    _ts_metadata$j("design:type", typeof ExternalIntegrationPaymentCancelResponseStatus === "undefined" || "string" === "undefined" ? Object : "error")
], ExternalIntegrationPaymentCancelResponseErrorDTO.prototype, "status", void 0);
_ts_decorate$j([
    dto({
        string: true
    }),
    _ts_metadata$j("design:type", String)
], ExternalIntegrationPaymentCancelResponseErrorDTO.prototype, "message", void 0);
_ts_decorate$j([
    dto({
        string: true
    }),
    _ts_metadata$j("design:type", String)
], ExternalIntegrationPaymentCancelResponseErrorDTO.prototype, "code", void 0);
class ExternalIntegrationPaymentCancelResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$j(this, "payment", void 0);
    }
}
_ts_decorate$j([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: ExternalIntegrationPaymentCancelResponseSuccessDTO
                    },
                    {
                        name: "error",
                        value: ExternalIntegrationPaymentCancelResponseErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$j("design:type", Object)
], ExternalIntegrationPaymentCancelResponseDTO.prototype, "payment", void 0);

function _define_property$i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$i(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$i(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ExternalIntegrationPaymentVat = /*#__PURE__*/ function(ExternalIntegrationPaymentVat) {
    ExternalIntegrationPaymentVat["NO"] = "no";
    ExternalIntegrationPaymentVat["VAT_0"] = "0";
    ExternalIntegrationPaymentVat["VAT_10"] = "10";
    ExternalIntegrationPaymentVat["VAT_20"] = "20";
    ExternalIntegrationPaymentVat["VAT_10_110"] = "10_110";
    ExternalIntegrationPaymentVat["VAT_20_120"] = "20_120";
    ExternalIntegrationPaymentVat["VAT_5"] = "5";
    ExternalIntegrationPaymentVat["VAT_7"] = "7";
    ExternalIntegrationPaymentVat["VAT_5_105"] = "5_105";
    ExternalIntegrationPaymentVat["VAT_7_107"] = "7_107";
    ExternalIntegrationPaymentVat["VAT_22"] = "22";
    ExternalIntegrationPaymentVat["VAT_22_122"] = "22_122";
    return ExternalIntegrationPaymentVat;
}({});
var ExternalIntegrationPaymentTaxSystem = /*#__PURE__*/ function(ExternalIntegrationPaymentTaxSystem) {
    ExternalIntegrationPaymentTaxSystem["OBSHAYA"] = "obshaya";
    ExternalIntegrationPaymentTaxSystem["USN_DOHOD"] = "usn_dohod";
    ExternalIntegrationPaymentTaxSystem["USN_DOHOD_MINUS_RASHOD"] = "usn_dohod_minus_rashod";
    ExternalIntegrationPaymentTaxSystem["ENVD"] = "envd";
    ExternalIntegrationPaymentTaxSystem["ESN"] = "esn";
    ExternalIntegrationPaymentTaxSystem["PATENT"] = "patent";
    return ExternalIntegrationPaymentTaxSystem;
}({});
class ExternalIntegrationPaymentCreateAmountRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Два знака — формат суммы, общий для обоих провайдеров: третий знак молча срезало бы округление
        // в копейки, и 1С о расхождении не узнавала бы. Нижних границ здесь нет — они правила провайдера.
        _define_property$i(this, "value", void 0);
    }
}
_ts_decorate$i([
    dto({
        number: {
            maxDecimalPlaces: 2
        }
    }),
    _ts_metadata$i("design:type", Number)
], ExternalIntegrationPaymentCreateAmountRequestDTO.prototype, "value", void 0);
class ExternalIntegrationPaymentCreateAuthorRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "id", void 0), _define_property$i(this, "ip", void 0), _define_property$i(this, "name", void 0), _define_property$i(this, "phone", void 0);
    }
}
_ts_decorate$i([
    dto({
        string: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateAuthorRequestDTO.prototype, "id", void 0);
_ts_decorate$i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateAuthorRequestDTO.prototype, "ip", void 0);
_ts_decorate$i([
    dto({
        string: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateAuthorRequestDTO.prototype, "name", void 0);
_ts_decorate$i([
    dto({
        phone: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateAuthorRequestDTO.prototype, "phone", void 0);
class ExternalIntegrationPaymentCreateItemAmountRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "value", void 0), _define_property$i(this, "vat", void 0);
    }
}
_ts_decorate$i([
    dto({
        number: {
            maxDecimalPlaces: 2
        }
    }),
    _ts_metadata$i("design:type", Number)
], ExternalIntegrationPaymentCreateItemAmountRequestDTO.prototype, "value", void 0);
_ts_decorate$i([
    dto({
        enum: ExternalIntegrationPaymentVat
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateItemAmountRequestDTO.prototype, "vat", void 0);
// https://yookassa.ru/developers/payment-acceptance/receipts/54fz/parameters-values
class ExternalIntegrationPaymentCreateItemYooKassaRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "paymentSubject", void 0), _define_property$i(this, "paymentMode", void 0);
    }
}
_ts_decorate$i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateItemYooKassaRequestDTO.prototype, "paymentSubject", void 0);
_ts_decorate$i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateItemYooKassaRequestDTO.prototype, "paymentMode", void 0);
// https://api.cdekfin.ru/ — признаки предмета расчёта и способа расчёта.
// Блок необязателен на уровне DTO (мерчанты на ЮKassa его не шлют), но для CDEK Pay обязателен:
// отсутствие отбивает сам провайдер кодом invalidPayment.
class ExternalIntegrationPaymentCreateItemCdekPayRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Номер позиции в чеке. Уникален внутри чека: по нему CDEK сопоставляет оплаченное и возвращаемое,
        // и по нему же 1С называет возвращаемую позицию. Угадывать его за 1С мы не умеем.
        // Целое: номер уходит в id позиции строкой, и дробный номер дал бы в чеке id вида "1.5".
        _define_property$i(this, "receiptProductIndex", void 0), // Признак предмета расчёта по 54-ФЗ: что именно продаём — товар, работу, услугу, ставку и так далее.
        // Числовой код из справочника CDEK: https://api.cdekfin.ru/#tag/payment_objects
        // Обычный товар — 1. Значение вне справочника отбивает сам CDEK.
        _define_property$i(this, "paymentObject", void 0), // Признак способа расчёта по 54-ФЗ: полная оплата, предоплата, рассрочка и так далее.
        // Строковый код из справочника CDEK: https://api.cdekfin.ru/#tag/payment_methods
        // Не передан — CDEK берёт full_payment (полный расчёт).
        _define_property$i(this, "paymentMethod", void 0);
    }
}
_ts_decorate$i([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$i("design:type", Number)
], ExternalIntegrationPaymentCreateItemCdekPayRequestDTO.prototype, "receiptProductIndex", void 0);
_ts_decorate$i([
    dto({
        number: {
            maxDecimalPlaces: 0
        }
    }),
    _ts_metadata$i("design:type", Number)
], ExternalIntegrationPaymentCreateItemCdekPayRequestDTO.prototype, "paymentObject", void 0);
_ts_decorate$i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateItemCdekPayRequestDTO.prototype, "paymentMethod", void 0);
class ExternalIntegrationPaymentCreateItemRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "description", void 0), // Количество бывает дробным (весовой товар), но у CDEK дробная часть — не больше трёх знаков, а
        // четвёртый уехал бы в CDEK как есть и вернулся невнятной ошибкой вместо понятного отказа.
        _define_property$i(this, "count", void 0), _define_property$i(this, "amount", void 0), _define_property$i(this, "yooKassa", void 0), _define_property$i(this, "cdekPay", void 0);
    }
}
_ts_decorate$i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateItemRequestDTO.prototype, "description", void 0);
_ts_decorate$i([
    dto({
        number: {
            maxDecimalPlaces: 3
        }
    }),
    _ts_metadata$i("design:type", Number)
], ExternalIntegrationPaymentCreateItemRequestDTO.prototype, "count", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateItemAmountRequestDTO
        }
    }),
    _ts_metadata$i("design:type", typeof ExternalIntegrationPaymentCreateItemAmountRequestDTO === "undefined" ? Object : ExternalIntegrationPaymentCreateItemAmountRequestDTO)
], ExternalIntegrationPaymentCreateItemRequestDTO.prototype, "amount", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateItemYooKassaRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateItemRequestDTO.prototype, "yooKassa", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateItemCdekPayRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateItemRequestDTO.prototype, "cdekPay", void 0);
class ExternalIntegrationPaymentCreateMetadataItemRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "key", void 0), _define_property$i(this, "value", void 0);
    }
}
_ts_decorate$i([
    dto({
        string: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateMetadataItemRequestDTO.prototype, "key", void 0);
_ts_decorate$i([
    dto({
        string: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateMetadataItemRequestDTO.prototype, "value", void 0);
// https://yookassa.ru/developers/payment-acceptance/receipts/54fz/parameters-values
class ExternalIntegrationPaymentCreateYooKassaRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "taxSystem", void 0);
    }
}
_ts_decorate$i([
    dto({
        enum: ExternalIntegrationPaymentTaxSystem,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateYooKassaRequestDTO.prototype, "taxSystem", void 0);
class ExternalIntegrationPaymentCreateRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$i(this, "id", void 0), _define_property$i(this, "metadata", void 0), _define_property$i(this, "redirectUrl", void 0), _define_property$i(this, "description", void 0), _define_property$i(this, "amount", void 0), _define_property$i(this, "author", void 0), _define_property$i(this, "yooKassa", void 0), // Пустой чек делает платёж невозвратным: позиции уезжают в строку платежа, и компенсационный
        // возврат при отказе 1С умирает на проверке позиций — деньги списаны, а заказа нет.
        _define_property$i(this, "items", void 0);
    }
}
_ts_decorate$i([
    dto({
        string: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "id", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateMetadataItemRequestDTO
        },
        array: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "metadata", void 0);
_ts_decorate$i([
    dto({
        string: true
    }),
    _ts_metadata$i("design:type", String)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "redirectUrl", void 0);
_ts_decorate$i([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "description", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateAmountRequestDTO
        }
    }),
    _ts_metadata$i("design:type", typeof ExternalIntegrationPaymentCreateAmountRequestDTO === "undefined" ? Object : ExternalIntegrationPaymentCreateAmountRequestDTO)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "amount", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateAuthorRequestDTO
        }
    }),
    _ts_metadata$i("design:type", typeof ExternalIntegrationPaymentCreateAuthorRequestDTO === "undefined" ? Object : ExternalIntegrationPaymentCreateAuthorRequestDTO)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "author", void 0);
_ts_decorate$i([
    dto({
        object: {
            type: ExternalIntegrationPaymentCreateYooKassaRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$i("design:type", Object)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "yooKassa", void 0);
_ts_decorate$i([
    dto({
        array: {
            notEmpty: true
        },
        object: {
            type: ExternalIntegrationPaymentCreateItemRequestDTO
        }
    }),
    _ts_metadata$i("design:type", Array)
], ExternalIntegrationPaymentCreateRequestDTO.prototype, "items", void 0);

function _define_property$h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$h(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$h(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ExternalIntegrationPaymentCreateResponseStatus = /*#__PURE__*/ function(ExternalIntegrationPaymentCreateResponseStatus) {
    ExternalIntegrationPaymentCreateResponseStatus["SUCCESS"] = "success";
    ExternalIntegrationPaymentCreateResponseStatus["ERROR"] = "error";
    return ExternalIntegrationPaymentCreateResponseStatus;
}({});
class ExternalIntegrationPaymentCreateResponseSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$h(this, "status", void 0), _define_property$h(this, "paymentStatus", void 0), _define_property$h(this, "redirectUrl", void 0), _define_property$h(this, "externalSystemId", void 0);
    }
}
_ts_decorate$h([
    dto({
        equals: "success"
    }),
    _ts_metadata$h("design:type", typeof ExternalIntegrationPaymentCreateResponseStatus === "undefined" || "string" === "undefined" ? Object : "success")
], ExternalIntegrationPaymentCreateResponseSuccessDTO.prototype, "status", void 0);
_ts_decorate$h([
    dto({
        enum: ExternalIntegrationPaymentStatus
    }),
    _ts_metadata$h("design:type", typeof ExternalIntegrationPaymentStatus === "undefined" ? Object : ExternalIntegrationPaymentStatus)
], ExternalIntegrationPaymentCreateResponseSuccessDTO.prototype, "paymentStatus", void 0);
_ts_decorate$h([
    dto({
        string: true
    }),
    _ts_metadata$h("design:type", String)
], ExternalIntegrationPaymentCreateResponseSuccessDTO.prototype, "redirectUrl", void 0);
_ts_decorate$h([
    dto({
        string: true
    }),
    _ts_metadata$h("design:type", String)
], ExternalIntegrationPaymentCreateResponseSuccessDTO.prototype, "externalSystemId", void 0);
class ExternalIntegrationPaymentCreateResponseErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$h(this, "status", void 0), _define_property$h(this, "message", void 0), _define_property$h(this, "code", void 0), // Текст исходной ошибки при serviceError — для разбора, контракт на него не завязывать.
        _define_property$h(this, "originalError", void 0);
    }
}
_ts_decorate$h([
    dto({
        equals: "error"
    }),
    _ts_metadata$h("design:type", typeof ExternalIntegrationPaymentCreateResponseStatus === "undefined" || "string" === "undefined" ? Object : "error")
], ExternalIntegrationPaymentCreateResponseErrorDTO.prototype, "status", void 0);
_ts_decorate$h([
    dto({
        string: true
    }),
    _ts_metadata$h("design:type", String)
], ExternalIntegrationPaymentCreateResponseErrorDTO.prototype, "message", void 0);
_ts_decorate$h([
    dto({
        string: true
    }),
    _ts_metadata$h("design:type", String)
], ExternalIntegrationPaymentCreateResponseErrorDTO.prototype, "code", void 0);
_ts_decorate$h([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$h("design:type", Object)
], ExternalIntegrationPaymentCreateResponseErrorDTO.prototype, "originalError", void 0);
class ExternalIntegrationPaymentCreateResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$h(this, "payment", void 0);
    }
}
_ts_decorate$h([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: ExternalIntegrationPaymentCreateResponseSuccessDTO
                    },
                    {
                        name: "error",
                        value: ExternalIntegrationPaymentCreateResponseErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$h("design:type", Object)
], ExternalIntegrationPaymentCreateResponseDTO.prototype, "payment", void 0);

function _define_property$g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$g(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$g(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationPaymentResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$g(this, "externalId", void 0), _define_property$g(this, "externalPaymentJSON", void 0), _define_property$g(this, "status", void 0), _define_property$g(this, "url", void 0);
    }
}
_ts_decorate$g([
    dto({
        string: true
    }),
    _ts_metadata$g("design:type", String)
], ExternalIntegrationPaymentResponseDTO.prototype, "externalId", void 0);
_ts_decorate$g([
    dto({
        string: true
    }),
    _ts_metadata$g("design:type", String)
], ExternalIntegrationPaymentResponseDTO.prototype, "externalPaymentJSON", void 0);
_ts_decorate$g([
    dto({
        enum: ExternalIntegrationPaymentStatus
    }),
    _ts_metadata$g("design:type", typeof ExternalIntegrationPaymentStatus === "undefined" ? Object : ExternalIntegrationPaymentStatus)
], ExternalIntegrationPaymentResponseDTO.prototype, "status", void 0);
_ts_decorate$g([
    dto({
        url: {
            allowAnyProtocol: true
        }
    }),
    _ts_metadata$g("design:type", String)
], ExternalIntegrationPaymentResponseDTO.prototype, "url", void 0);

function _define_property$f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$f(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$f(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationPaymentRefundAmountRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$f(this, "value", void 0);
    }
}
_ts_decorate$f([
    dto({
        number: {
            maxDecimalPlaces: 2
        }
    }),
    _ts_metadata$f("design:type", Number)
], ExternalIntegrationPaymentRefundAmountRequestDTO.prototype, "value", void 0);
// CDEK принимает возврат только вместе со списком возвращаемых товаров: без него чек возврата
// не сформировать. Позиция описывается так же, как при создании платежа.
class ExternalIntegrationPaymentRefundCdekPayRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$f(this, "reason", void 0), // notEmpty, как и у items создания: пустой массив — та же причина отказа, что и непереданный блок,
        // и разводить её на 400 из валидации и invalidPayment из сервиса в одном контракте незачем.
        _define_property$f(this, "items", void 0);
    }
}
_ts_decorate$f([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$f("design:type", Object)
], ExternalIntegrationPaymentRefundCdekPayRequestDTO.prototype, "reason", void 0);
_ts_decorate$f([
    dto({
        array: {
            notEmpty: true
        },
        object: {
            type: ExternalIntegrationPaymentCreateItemRequestDTO
        }
    }),
    _ts_metadata$f("design:type", Array)
], ExternalIntegrationPaymentRefundCdekPayRequestDTO.prototype, "items", void 0);
class ExternalIntegrationPaymentRefundRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$f(this, "id", void 0), _define_property$f(this, "amount", void 0), _define_property$f(this, "cdekPay", void 0);
    }
}
_ts_decorate$f([
    dto({
        string: true
    }),
    _ts_metadata$f("design:type", String)
], ExternalIntegrationPaymentRefundRequestDTO.prototype, "id", void 0);
_ts_decorate$f([
    dto({
        object: {
            type: ExternalIntegrationPaymentRefundAmountRequestDTO
        }
    }),
    _ts_metadata$f("design:type", typeof ExternalIntegrationPaymentRefundAmountRequestDTO === "undefined" ? Object : ExternalIntegrationPaymentRefundAmountRequestDTO)
], ExternalIntegrationPaymentRefundRequestDTO.prototype, "amount", void 0);
_ts_decorate$f([
    dto({
        object: {
            type: ExternalIntegrationPaymentRefundCdekPayRequestDTO
        },
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$f("design:type", Object)
], ExternalIntegrationPaymentRefundRequestDTO.prototype, "cdekPay", void 0);

function _define_property$e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$e(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$e(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var ExternalIntegrationPaymentRefundResponseStatus = /*#__PURE__*/ function(ExternalIntegrationPaymentRefundResponseStatus) {
    ExternalIntegrationPaymentRefundResponseStatus["SUCCESS"] = "success";
    ExternalIntegrationPaymentRefundResponseStatus["ERROR"] = "error";
    return ExternalIntegrationPaymentRefundResponseStatus;
}({});
class ExternalIntegrationPaymentRefundResponseSuccessDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$e(this, "status", void 0), _define_property$e(this, "paymentStatus", void 0);
    }
}
_ts_decorate$e([
    dto({
        equals: "success"
    }),
    _ts_metadata$e("design:type", typeof ExternalIntegrationPaymentRefundResponseStatus === "undefined" || "string" === "undefined" ? Object : "success")
], ExternalIntegrationPaymentRefundResponseSuccessDTO.prototype, "status", void 0);
_ts_decorate$e([
    dto({
        enum: ExternalIntegrationPaymentStatus
    }),
    _ts_metadata$e("design:type", typeof ExternalIntegrationPaymentStatus === "undefined" ? Object : ExternalIntegrationPaymentStatus)
], ExternalIntegrationPaymentRefundResponseSuccessDTO.prototype, "paymentStatus", void 0);
class ExternalIntegrationPaymentRefundResponseErrorDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$e(this, "status", void 0), _define_property$e(this, "message", void 0), _define_property$e(this, "code", void 0);
    }
}
_ts_decorate$e([
    dto({
        equals: "error"
    }),
    _ts_metadata$e("design:type", typeof ExternalIntegrationPaymentRefundResponseStatus === "undefined" || "string" === "undefined" ? Object : "error")
], ExternalIntegrationPaymentRefundResponseErrorDTO.prototype, "status", void 0);
_ts_decorate$e([
    dto({
        string: true
    }),
    _ts_metadata$e("design:type", String)
], ExternalIntegrationPaymentRefundResponseErrorDTO.prototype, "message", void 0);
_ts_decorate$e([
    dto({
        string: true
    }),
    _ts_metadata$e("design:type", String)
], ExternalIntegrationPaymentRefundResponseErrorDTO.prototype, "code", void 0);
class ExternalIntegrationPaymentRefundResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$e(this, "payment", void 0);
    }
}
_ts_decorate$e([
    dto({
        object: {
            discriminator: {
                property: "status",
                subTypes: [
                    {
                        name: "success",
                        value: ExternalIntegrationPaymentRefundResponseSuccessDTO
                    },
                    {
                        name: "error",
                        value: ExternalIntegrationPaymentRefundResponseErrorDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$e("design:type", Object)
], ExternalIntegrationPaymentRefundResponseDTO.prototype, "payment", void 0);

function _define_property$d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$d(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$d(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ExternalIntegrationPaymentSyncRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$d(this, "id", void 0), _define_property$d(this, "redirectUrl", void 0), _define_property$d(this, "status", void 0);
    }
}
_ts_decorate$d([
    dto({
        string: true
    }),
    _ts_metadata$d("design:type", String)
], ExternalIntegrationPaymentSyncRequestDTO.prototype, "id", void 0);
_ts_decorate$d([
    dto({
        string: true
    }),
    _ts_metadata$d("design:type", String)
], ExternalIntegrationPaymentSyncRequestDTO.prototype, "redirectUrl", void 0);
_ts_decorate$d([
    dto({
        enum: ExternalIntegrationPaymentStatus
    }),
    _ts_metadata$d("design:type", typeof ExternalIntegrationPaymentStatus === "undefined" ? Object : ExternalIntegrationPaymentStatus)
], ExternalIntegrationPaymentSyncRequestDTO.prototype, "status", void 0);

const fileExtensionPresets = {
    image: new Set([
        ".webp",
        ".jpg",
        ".jpeg",
        ".png",
        ".bmp",
        ".svg",
        ".gif"
    ]),
    video: new Set([
        ".mp4",
        ".avi",
        ".mov",
        ".mpeg",
        ".webm",
        ".mkv"
    ]),
    html: new Set([
        ".html"
    ]),
    pdf: new Set([
        ".pdf"
    ]),
    html_or_pdf: new Set([
        ".html",
        ".pdf"
    ])
};
const resizableImageExtensionPreset = new Set(fileExtensionPresets.image);
resizableImageExtensionPreset.delete(".svg");
resizableImageExtensionPreset.delete(".gif");

function _define_property$c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$c(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$c(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Справочник состояний сотрудника и их исходов ведётся в 1С. Один и тот же формат 1С отдаёт серверу
// (GET /v1/merchant-employee/config), а сервер — приложению (GET merchant-employee/config), поэтому
// DTO одни на обе стороны. Все id — непрозрачные строки 1С: сервер их только хранит.
var MerchantEmployeeOutcomeKind = /*#__PURE__*/ function(MerchantEmployeeOutcomeKind) {
    MerchantEmployeeOutcomeKind["DEAL"] = "deal";
    MerchantEmployeeOutcomeKind["REFUSAL"] = "refusal";
    MerchantEmployeeOutcomeKind["NEUTRAL"] = "neutral";
    return MerchantEmployeeOutcomeKind;
}({});
// Узел дерева исходов: папка (только на первом уровне) или элемент, который выбирает сотрудник.
var MerchantEmployeeOutcomeType = /*#__PURE__*/ function(MerchantEmployeeOutcomeType) {
    MerchantEmployeeOutcomeType["GROUP"] = "group";
    MerchantEmployeeOutcomeType["ITEM"] = "item";
    return MerchantEmployeeOutcomeType;
}({});
var MerchantEmployeeQuestionnaireFieldType = /*#__PURE__*/ function(MerchantEmployeeQuestionnaireFieldType) {
    MerchantEmployeeQuestionnaireFieldType["SELECT"] = "select";
    MerchantEmployeeQuestionnaireFieldType["NUMBER"] = "number";
    MerchantEmployeeQuestionnaireFieldType["TEXT"] = "text";
    return MerchantEmployeeQuestionnaireFieldType;
}({});
class MerchantEmployeeQuestionnaireOptionDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), _define_property$c(this, "name", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireOptionDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireOptionDTO.prototype, "name", void 0);
// Правила, которые DTO не выразить (у select options есть и непустой, id поля уникален в анкете), проверяет
// сервис справочника. options у других типов не читаются.
class MerchantEmployeeQuestionnaireFieldDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), _define_property$c(this, "name", void 0), _define_property$c(this, "type", void 0), _define_property$c(this, "required", void 0), // Только у select: сотрудник выбирает несколько вариантов, ответ приходит в optionIds.
        _define_property$c(this, "multiple", void 0), _define_property$c(this, "options", void 0), _define_property$c(this, "unit", void 0), _define_property$c(this, "placeholder", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "name", void 0);
_ts_decorate$c([
    dto({
        enum: MerchantEmployeeQuestionnaireFieldType
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "type", void 0);
_ts_decorate$c([
    dto({
        boolean: true
    }),
    _ts_metadata$c("design:type", Boolean)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "required", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "multiple", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: MerchantEmployeeQuestionnaireOptionDTO
        }
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "options", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "unit", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeQuestionnaireFieldDTO.prototype, "placeholder", void 0);
// Анкета «что спрашивал клиент» — заполняется к результату (например, к отказу «Нет модели»).
class MerchantEmployeeQuestionnaireDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), // Коротко — для списков и итогов.
        _define_property$c(this, "name", void 0), // Заголовок карточки в приложении.
        _define_property$c(this, "title", void 0), // У шаблона есть схема подбора: по этой анкете работает «Подобрать» (merchant-employee/pick).
        _define_property$c(this, "pick", void 0), _define_property$c(this, "fields", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireDTO.prototype, "name", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeQuestionnaireDTO.prototype, "title", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeQuestionnaireDTO.prototype, "pick", void 0);
_ts_decorate$c([
    dto({
        array: {
            notEmpty: true
        },
        object: {
            type: MerchantEmployeeQuestionnaireFieldDTO
        }
    }),
    _ts_metadata$c("design:type", Array)
], MerchantEmployeeQuestionnaireDTO.prototype, "fields", void 0);
class MerchantEmployeeRefusalReasonDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), _define_property$c(this, "name", void 0), // Своя анкета причины — главнее анкеты исхода.
        _define_property$c(this, "questionnaireId", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeRefusalReasonDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeRefusalReasonDTO.prototype, "name", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeRefusalReasonDTO.prototype, "questionnaireId", void 0);
// Тип документа 1С, к которому привязывается итог исхода (чек, скупка, залог…).
class MerchantEmployeeOutcomeDocumentDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "type", void 0), _define_property$c(this, "title", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeDocumentDTO.prototype, "type", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeDocumentDTO.prototype, "title", void 0);
// Элемент дерева исходов — то, что выбирает сотрудник.
class MerchantEmployeeOutcomeItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), _define_property$c(this, "name", void 0), _define_property$c(this, "type", void 0), _define_property$c(this, "kind", void 0), _define_property$c(this, "hint", void 0), _define_property$c(this, "icon", void 0), // Задан — сотрудник обязан выбрать документ из кандидатов 1С.
        _define_property$c(this, "document", void 0), // Непустой — сотрудник обязан выбрать причину из списка.
        _define_property$c(this, "refusalReasons", void 0), _define_property$c(this, "commentRequired", void 0), // Анкета исхода по умолчанию: нужна результату, если у выбранной причины нет своей.
        _define_property$c(this, "questionnaireId", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeItemDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeItemDTO.prototype, "name", void 0);
_ts_decorate$c([
    dto({
        equals: "item"
    }),
    _ts_metadata$c("design:type", typeof MerchantEmployeeOutcomeType === "undefined" || "string" === "undefined" ? Object : "item")
], MerchantEmployeeOutcomeItemDTO.prototype, "type", void 0);
_ts_decorate$c([
    dto({
        enum: MerchantEmployeeOutcomeKind
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeItemDTO.prototype, "kind", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeOutcomeItemDTO.prototype, "hint", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeOutcomeItemDTO.prototype, "icon", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: MerchantEmployeeOutcomeDocumentDTO
        }
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeOutcomeItemDTO.prototype, "document", void 0);
_ts_decorate$c([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeRefusalReasonDTO
        }
    }),
    _ts_metadata$c("design:type", Array)
], MerchantEmployeeOutcomeItemDTO.prototype, "refusalReasons", void 0);
_ts_decorate$c([
    dto({
        boolean: true
    }),
    _ts_metadata$c("design:type", Boolean)
], MerchantEmployeeOutcomeItemDTO.prototype, "commentRequired", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeOutcomeItemDTO.prototype, "questionnaireId", void 0);
// Папка первого уровня: выбрать её нельзя, внутри — только элементы. Так дерево и не глубже двух уровней.
class MerchantEmployeeOutcomeGroupDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), _define_property$c(this, "name", void 0), _define_property$c(this, "type", void 0), _define_property$c(this, "hint", void 0), _define_property$c(this, "icon", void 0), _define_property$c(this, "children", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeGroupDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeOutcomeGroupDTO.prototype, "name", void 0);
_ts_decorate$c([
    dto({
        equals: "group"
    }),
    _ts_metadata$c("design:type", typeof MerchantEmployeeOutcomeType === "undefined" || "string" === "undefined" ? Object : "group")
], MerchantEmployeeOutcomeGroupDTO.prototype, "type", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeOutcomeGroupDTO.prototype, "hint", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeOutcomeGroupDTO.prototype, "icon", void 0);
_ts_decorate$c([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeOutcomeItemDTO
        }
    }),
    _ts_metadata$c("design:type", Array)
], MerchantEmployeeOutcomeGroupDTO.prototype, "children", void 0);
class MerchantEmployeeStateDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$c(this, "id", void 0), _define_property$c(this, "name", void 0), _define_property$c(this, "isBase", void 0), // Имя иконки; фронт мапит его сам.
        _define_property$c(this, "icon", void 0), _define_property$c(this, "color", void 0), _define_property$c(this, "sort", void 0), // Пустой — состояние завершается без исхода.
        _define_property$c(this, "outcomes", void 0);
    }
}
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeStateDTO.prototype, "id", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeStateDTO.prototype, "name", void 0);
_ts_decorate$c([
    dto({
        boolean: true
    }),
    _ts_metadata$c("design:type", Boolean)
], MerchantEmployeeStateDTO.prototype, "isBase", void 0);
_ts_decorate$c([
    dto({
        string: true
    }),
    _ts_metadata$c("design:type", String)
], MerchantEmployeeStateDTO.prototype, "icon", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeStateDTO.prototype, "color", void 0);
_ts_decorate$c([
    dto({
        number: true
    }),
    _ts_metadata$c("design:type", Number)
], MerchantEmployeeStateDTO.prototype, "sort", void 0);
_ts_decorate$c([
    dto({
        array: true,
        object: {
            discriminator: {
                property: "type",
                subTypes: [
                    {
                        name: "group",
                        value: MerchantEmployeeOutcomeGroupDTO
                    },
                    {
                        name: "item",
                        value: MerchantEmployeeOutcomeItemDTO
                    }
                ]
            }
        }
    }),
    _ts_metadata$c("design:type", Array)
], MerchantEmployeeStateDTO.prototype, "outcomes", void 0);
class MerchantEmployeeConfigDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Нет — анкет нет.
        _define_property$c(this, "questionnaires", void 0), _define_property$c(this, "states", void 0), // Настройки мерчанта для приложения: спрашивать клиента и примерку сразу при входе в консультацию.
        // Сервер их не читает. Нет — решает приложение.
        _define_property$c(this, "askCustomerOnEnter", void 0), _define_property$c(this, "askFittingOnEnter", void 0);
    }
}
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        array: true,
        object: {
            type: MerchantEmployeeQuestionnaireDTO
        }
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeConfigDTO.prototype, "questionnaires", void 0);
_ts_decorate$c([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeStateDTO
        }
    }),
    _ts_metadata$c("design:type", Array)
], MerchantEmployeeConfigDTO.prototype, "states", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeConfigDTO.prototype, "askCustomerOnEnter", void 0);
_ts_decorate$c([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$c("design:type", Object)
], MerchantEmployeeConfigDTO.prototype, "askFittingOnEnter", void 0);

function _define_property$b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$b(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Кандидаты для привязки итога к документу 1С. Формат один в ответе 1С и в ответе приложению.
class MerchantEmployeeDocumentItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$b(this, "name", void 0), _define_property$b(this, "article", void 0), _define_property$b(this, "quantity", void 0);
    }
}
_ts_decorate$b([
    dto({
        string: true
    }),
    _ts_metadata$b("design:type", String)
], MerchantEmployeeDocumentItemDTO.prototype, "name", void 0);
_ts_decorate$b([
    dto({
        string: true
    }),
    _ts_metadata$b("design:type", String)
], MerchantEmployeeDocumentItemDTO.prototype, "article", void 0);
_ts_decorate$b([
    dto({
        number: true
    }),
    _ts_metadata$b("design:type", Number)
], MerchantEmployeeDocumentItemDTO.prototype, "quantity", void 0);
class MerchantEmployeeDocumentDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$b(this, "id", void 0), _define_property$b(this, "type", void 0), // Готовая подпись для списка.
        _define_property$b(this, "title", void 0), _define_property$b(this, "date", void 0), // Сумма документа, ₽.
        _define_property$b(this, "amount", void 0), _define_property$b(this, "items", void 0), // 1С считает документ наиболее вероятным.
        _define_property$b(this, "suggested", void 0);
    }
}
_ts_decorate$b([
    dto({
        string: true
    }),
    _ts_metadata$b("design:type", String)
], MerchantEmployeeDocumentDTO.prototype, "id", void 0);
_ts_decorate$b([
    dto({
        string: true
    }),
    _ts_metadata$b("design:type", String)
], MerchantEmployeeDocumentDTO.prototype, "type", void 0);
_ts_decorate$b([
    dto({
        string: true
    }),
    _ts_metadata$b("design:type", String)
], MerchantEmployeeDocumentDTO.prototype, "title", void 0);
_ts_decorate$b([
    dto({
        date: true
    }),
    _ts_metadata$b("design:type", typeof Date === "undefined" ? Object : Date)
], MerchantEmployeeDocumentDTO.prototype, "date", void 0);
_ts_decorate$b([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$b("design:type", Object)
], MerchantEmployeeDocumentDTO.prototype, "amount", void 0);
_ts_decorate$b([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeDocumentItemDTO
        }
    }),
    _ts_metadata$b("design:type", Array)
], MerchantEmployeeDocumentDTO.prototype, "items", void 0);
_ts_decorate$b([
    dto({
        optional: {
            allowNull: false
        },
        boolean: true
    }),
    _ts_metadata$b("design:type", Object)
], MerchantEmployeeDocumentDTO.prototype, "suggested", void 0);
class MerchantEmployeeDocumentsResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$b(this, "documents", void 0);
    }
}
_ts_decorate$b([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeDocumentDTO
        }
    }),
    _ts_metadata$b("design:type", Array)
], MerchantEmployeeDocumentsResponseDTO.prototype, "documents", void 0);
class MerchantEmployeeGetDocumentsRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$b(this, "outcomeId", void 0);
    }
}
_ts_decorate$b([
    dto({
        string: true
    }),
    _ts_metadata$b("design:type", String)
], MerchantEmployeeGetDocumentsRequestDTO.prototype, "outcomeId", void 0);

function _define_property$a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$a(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$a(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
// Что отсканировано в текущей консультации. Журнал сканов ведёт 1С, формат один в её ответе и в ответе
// приложению: сервер только проксирует.
class MerchantEmployeeScanCustomerDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$a(this, "name", void 0), _define_property$a(this, "cardMasked", void 0), _define_property$a(this, "phoneMasked", void 0), _define_property$a(this, "bonuses", void 0), // false — телефон ввели, но карты по нему нет: клиент новый.
        _define_property$a(this, "found", void 0);
    }
}
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanCustomerDTO.prototype, "name", void 0);
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanCustomerDTO.prototype, "cardMasked", void 0);
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanCustomerDTO.prototype, "phoneMasked", void 0);
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanCustomerDTO.prototype, "bonuses", void 0);
_ts_decorate$a([
    dto({
        boolean: true
    }),
    _ts_metadata$a("design:type", Boolean)
], MerchantEmployeeScanCustomerDTO.prototype, "found", void 0);
class MerchantEmployeeScanItemDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$a(this, "scanId", void 0), _define_property$a(this, "title", void 0), _define_property$a(this, "subtitle", void 0), _define_property$a(this, "scannedAt", void 0);
    }
}
_ts_decorate$a([
    dto({
        string: true
    }),
    _ts_metadata$a("design:type", String)
], MerchantEmployeeScanItemDTO.prototype, "scanId", void 0);
_ts_decorate$a([
    dto({
        string: true
    }),
    _ts_metadata$a("design:type", String)
], MerchantEmployeeScanItemDTO.prototype, "title", void 0);
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanItemDTO.prototype, "subtitle", void 0);
_ts_decorate$a([
    dto({
        date: true
    }),
    _ts_metadata$a("design:type", typeof Date === "undefined" ? Object : Date)
], MerchantEmployeeScanItemDTO.prototype, "scannedAt", void 0);
class MerchantEmployeeActivityScansDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Нет — клиента не сканировали.
        _define_property$a(this, "customer", void 0), _define_property$a(this, "items", void 0);
    }
}
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        object: {
            type: MerchantEmployeeScanCustomerDTO
        }
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeActivityScansDTO.prototype, "customer", void 0);
_ts_decorate$a([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeScanItemDTO
        }
    }),
    _ts_metadata$a("design:type", Array)
], MerchantEmployeeActivityScansDTO.prototype, "items", void 0);
// Скан без камеры: телефон клиента из шторки или штрихкод, введённый руками. Ровно одно из двух —
// это проверяет сервис: в DTO нет условия между полями.
class MerchantEmployeeScanRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Как набрал продавец: сервер приводит к 79XXXXXXXXX.
        _define_property$a(this, "phone", void 0), _define_property$a(this, "barcode", void 0);
    }
}
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanRequestDTO.prototype, "phone", void 0);
_ts_decorate$a([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    MinLength(1),
    MaxLength(100),
    _ts_metadata$a("design:type", Object)
], MerchantEmployeeScanRequestDTO.prototype, "barcode", void 0);

function _define_property$9(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$9(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$9(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class MerchantEmployeeShiftStatsStateDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$9(this, "stateId", void 0), _define_property$9(this, "stateName", void 0), _define_property$9(this, "seconds", void 0), _define_property$9(this, "count", void 0);
    }
}
_ts_decorate$9([
    dto({
        string: true
    }),
    _ts_metadata$9("design:type", String)
], MerchantEmployeeShiftStatsStateDTO.prototype, "stateId", void 0);
_ts_decorate$9([
    dto({
        string: true
    }),
    _ts_metadata$9("design:type", String)
], MerchantEmployeeShiftStatsStateDTO.prototype, "stateName", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsStateDTO.prototype, "seconds", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsStateDTO.prototype, "count", void 0);
class MerchantEmployeeShiftStatsOutcomesDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$9(this, "deal", void 0), _define_property$9(this, "refusal", void 0), _define_property$9(this, "neutral", void 0);
    }
}
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsOutcomesDTO.prototype, "deal", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsOutcomesDTO.prototype, "refusal", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsOutcomesDTO.prototype, "neutral", void 0);
class MerchantEmployeeShiftStatsDealDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$9(this, "outcomeName", void 0), // Из снимка привязанного документа; у сделки без документа их нет.
        _define_property$9(this, "title", void 0), _define_property$9(this, "amount", void 0), _define_property$9(this, "finishedAt", void 0);
    }
}
_ts_decorate$9([
    dto({
        string: true
    }),
    _ts_metadata$9("design:type", String)
], MerchantEmployeeShiftStatsDealDTO.prototype, "outcomeName", void 0);
_ts_decorate$9([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$9("design:type", Object)
], MerchantEmployeeShiftStatsDealDTO.prototype, "title", void 0);
_ts_decorate$9([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$9("design:type", Object)
], MerchantEmployeeShiftStatsDealDTO.prototype, "amount", void 0);
_ts_decorate$9([
    dto({
        date: true
    }),
    _ts_metadata$9("design:type", typeof Date === "undefined" ? Object : Date)
], MerchantEmployeeShiftStatsDealDTO.prototype, "finishedAt", void 0);
class MerchantEmployeeShiftStatsRefusalReasonDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$9(this, "reasonName", void 0), _define_property$9(this, "count", void 0);
    }
}
_ts_decorate$9([
    dto({
        string: true
    }),
    _ts_metadata$9("design:type", String)
], MerchantEmployeeShiftStatsRefusalReasonDTO.prototype, "reasonName", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsRefusalReasonDTO.prototype, "count", void 0);
class MerchantEmployeeShiftStatsDTO extends BaseEntity {
    constructor(...args){
        super(...args), // Нет — считать не по чему: ни открытой смены, ни закрытой сегодня.
        _define_property$9(this, "shiftOpenedAt", void 0), _define_property$9(this, "shiftClosedAt", void 0), _define_property$9(this, "onShiftSeconds", void 0), _define_property$9(this, "byState", void 0), // Консультации, у которых есть хоть один результат, — «обслужено». Исходов у них может быть больше.
        _define_property$9(this, "consultations", void 0), _define_property$9(this, "outcomes", void 0), // По консультациям: со сделкой / со сделкой или отказом; нет, если таких консультаций нет.
        _define_property$9(this, "conversion", void 0), _define_property$9(this, "dealsAmount", void 0), _define_property$9(this, "deals", void 0), _define_property$9(this, "refusalReasons", void 0);
    }
}
_ts_decorate$9([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$9("design:type", Object)
], MerchantEmployeeShiftStatsDTO.prototype, "shiftOpenedAt", void 0);
_ts_decorate$9([
    dto({
        optional: {
            allowNull: false
        },
        date: true
    }),
    _ts_metadata$9("design:type", Object)
], MerchantEmployeeShiftStatsDTO.prototype, "shiftClosedAt", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsDTO.prototype, "onShiftSeconds", void 0);
_ts_decorate$9([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeShiftStatsStateDTO
        }
    }),
    _ts_metadata$9("design:type", Array)
], MerchantEmployeeShiftStatsDTO.prototype, "byState", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsDTO.prototype, "consultations", void 0);
_ts_decorate$9([
    dto({
        object: {
            type: MerchantEmployeeShiftStatsOutcomesDTO
        }
    }),
    _ts_metadata$9("design:type", typeof MerchantEmployeeShiftStatsOutcomesDTO === "undefined" ? Object : MerchantEmployeeShiftStatsOutcomesDTO)
], MerchantEmployeeShiftStatsDTO.prototype, "outcomes", void 0);
_ts_decorate$9([
    dto({
        optional: {
            allowNull: false
        },
        number: true
    }),
    _ts_metadata$9("design:type", Object)
], MerchantEmployeeShiftStatsDTO.prototype, "conversion", void 0);
_ts_decorate$9([
    dto({
        number: true
    }),
    _ts_metadata$9("design:type", Number)
], MerchantEmployeeShiftStatsDTO.prototype, "dealsAmount", void 0);
_ts_decorate$9([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeShiftStatsDealDTO
        }
    }),
    _ts_metadata$9("design:type", Array)
], MerchantEmployeeShiftStatsDTO.prototype, "deals", void 0);
_ts_decorate$9([
    dto({
        array: true,
        object: {
            type: MerchantEmployeeShiftStatsRefusalReasonDTO
        }
    }),
    _ts_metadata$9("design:type", Array)
], MerchantEmployeeShiftStatsDTO.prototype, "refusalReasons", void 0);

function _define_property$8(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$8(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$8(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class NotificationPushSetTokenRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$8(this, "deviceId", void 0), _define_property$8(this, "token", void 0);
    }
}
_ts_decorate$8([
    dto({
        string: true
    }),
    _ts_metadata$8("design:type", String)
], NotificationPushSetTokenRequestDTO.prototype, "deviceId", void 0);
_ts_decorate$8([
    dto({
        string: true,
        optional: {
            allowNull: false
        }
    }),
    _ts_metadata$8("design:type", Object)
], NotificationPushSetTokenRequestDTO.prototype, "token", void 0);

function _define_property$7(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$7(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$7(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class CreateOrderV1RequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$7(this, "lastValidatedHash", void 0), _define_property$7(this, "comment", void 0);
    }
}
_ts_decorate$7([
    dto({
        string: true
    }),
    _ts_metadata$7("design:type", String)
], CreateOrderV1RequestDTO.prototype, "lastValidatedHash", void 0);
_ts_decorate$7([
    dto({
        string: true,
        optional: true
    }),
    _ts_metadata$7("design:type", Object)
], CreateOrderV1RequestDTO.prototype, "comment", void 0);

function _define_property$6(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$6(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$6(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrdersV1ListRequestDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$6(this, "status", void 0);
    }
}
_ts_decorate$6([
    dto({
        optional: {
            allowNull: false
        },
        enum: OrderStatus
    }),
    _ts_metadata$6("design:type", Object)
], OrdersV1ListRequestDTO.prototype, "status", void 0);

function _define_property$5(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$5(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$5(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderV1ProductPriceDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5(this, "resultPriceForUser", void 0);
    }
}
_ts_decorate$5([
    dto({
        number: true
    }),
    _ts_metadata$5("design:type", Number)
], OrderV1ProductPriceDTO.prototype, "resultPriceForUser", void 0);
class OrderV1BaseProductDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$5(this, "id", void 0), _define_property$5(this, "name", void 0), _define_property$5(this, "count", void 0), _define_property$5(this, "articleNumber", void 0), _define_property$5(this, "price", void 0);
    }
}
_ts_decorate$5([
    dto({
        string: true
    }),
    _ts_metadata$5("design:type", String)
], OrderV1BaseProductDTO.prototype, "id", void 0);
_ts_decorate$5([
    dto({
        string: true
    }),
    _ts_metadata$5("design:type", String)
], OrderV1BaseProductDTO.prototype, "name", void 0);
_ts_decorate$5([
    dto({
        number: true
    }),
    _ts_metadata$5("design:type", Number)
], OrderV1BaseProductDTO.prototype, "count", void 0);
_ts_decorate$5([
    dto({
        optional: {
            allowUndefined: false
        },
        string: true
    }),
    _ts_metadata$5("design:type", Object)
], OrderV1BaseProductDTO.prototype, "articleNumber", void 0);
_ts_decorate$5([
    dto({
        object: {
            type: OrderV1ProductPriceDTO
        }
    }),
    _ts_metadata$5("design:type", typeof OrderV1ProductPriceDTO === "undefined" ? Object : OrderV1ProductPriceDTO)
], OrderV1BaseProductDTO.prototype, "price", void 0);
class OrderV1ListProductDTO extends OrderV1BaseProductDTO {
    constructor(...args){
        super(...args), _define_property$5(this, "media", void 0);
    }
}
_ts_decorate$5([
    dto({
        optional: true,
        array: true,
        object: {
            type: ProductMediaDTO
        }
    }),
    _ts_metadata$5("design:type", Object)
], OrderV1ListProductDTO.prototype, "media", void 0);

function _define_property$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$4(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$4(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderV1PriceResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4(this, "result", void 0);
    }
}
_ts_decorate$4([
    dto({
        number: true
    }),
    _ts_metadata$4("design:type", Number)
], OrderV1PriceResponseDTO.prototype, "result", void 0);
class OrderV1BaseResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$4(this, "id", void 0), _define_property$4(this, "number", void 0), _define_property$4(this, "comment", void 0), _define_property$4(this, "createdAt", void 0), _define_property$4(this, "status", void 0), _define_property$4(this, "price", void 0);
    }
}
_ts_decorate$4([
    dto({
        string: true
    }),
    _ts_metadata$4("design:type", String)
], OrderV1BaseResponseDTO.prototype, "id", void 0);
_ts_decorate$4([
    dto({
        string: true
    }),
    _ts_metadata$4("design:type", String)
], OrderV1BaseResponseDTO.prototype, "number", void 0);
_ts_decorate$4([
    dto({
        string: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata$4("design:type", Object)
], OrderV1BaseResponseDTO.prototype, "comment", void 0);
_ts_decorate$4([
    dto({
        date: true
    }),
    _ts_metadata$4("design:type", typeof Date === "undefined" ? Object : Date)
], OrderV1BaseResponseDTO.prototype, "createdAt", void 0);
_ts_decorate$4([
    dto({
        enum: OrderStatus
    }),
    _ts_metadata$4("design:type", typeof OrderStatus === "undefined" ? Object : OrderStatus)
], OrderV1BaseResponseDTO.prototype, "status", void 0);
_ts_decorate$4([
    dto({
        object: {
            type: OrderV1PriceResponseDTO
        }
    }),
    _ts_metadata$4("design:type", typeof OrderV1PriceResponseDTO === "undefined" ? Object : OrderV1PriceResponseDTO)
], OrderV1BaseResponseDTO.prototype, "price", void 0);
class OrderV1ResponseDTO extends OrderV1BaseResponseDTO {
    constructor(...args){
        super(...args), _define_property$4(this, "products", void 0);
    }
}
_ts_decorate$4([
    dto({
        array: true,
        object: {
            type: OrderV1ListProductDTO
        }
    }),
    _ts_metadata$4("design:type", Array)
], OrderV1ResponseDTO.prototype, "products", void 0);

function _define_property$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$3(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$3(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderV1ProductDetailDTO extends OrderV1BaseProductDTO {
    constructor(...args){
        super(...args), _define_property$3(this, "originalProduct", void 0);
    }
}
_ts_decorate$3([
    dto({
        optional: true,
        object: {
            type: ProductBaseDTO
        }
    }),
    _ts_metadata$3("design:type", Object)
], OrderV1ProductDetailDTO.prototype, "originalProduct", void 0);

function _define_property$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$2(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$2(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class OrderV1DetailShopResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$2(this, "address", void 0), _define_property$2(this, "cityName", void 0);
    }
}
_ts_decorate$2([
    dto({
        string: true
    }),
    _ts_metadata$2("design:type", String)
], OrderV1DetailShopResponseDTO.prototype, "address", void 0);
_ts_decorate$2([
    dto({
        string: true
    }),
    _ts_metadata$2("design:type", String)
], OrderV1DetailShopResponseDTO.prototype, "cityName", void 0);
class OrderV1DetailResponseDTO extends OrderV1BaseResponseDTO {
    constructor(...args){
        super(...args), _define_property$2(this, "products", void 0), _define_property$2(this, "shop", void 0), _define_property$2(this, "managerComment", void 0);
    }
}
_ts_decorate$2([
    dto({
        array: true,
        object: {
            type: OrderV1ProductDetailDTO
        }
    }),
    _ts_metadata$2("design:type", Array)
], OrderV1DetailResponseDTO.prototype, "products", void 0);
_ts_decorate$2([
    dto({
        object: {
            type: OrderV1DetailShopResponseDTO
        }
    }),
    _ts_metadata$2("design:type", typeof OrderV1DetailShopResponseDTO === "undefined" ? Object : OrderV1DetailShopResponseDTO)
], OrderV1DetailResponseDTO.prototype, "shop", void 0);
_ts_decorate$2([
    dto({
        optional: {
            allowNull: false
        },
        string: true
    }),
    _ts_metadata$2("design:type", Object)
], OrderV1DetailResponseDTO.prototype, "managerComment", void 0);

function _define_property$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate$1(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata$1(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ResponseIdDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property$1(this, "id", void 0);
    }
}
_ts_decorate$1([
    dto({
        string: true
    }),
    _ts_metadata$1("design:type", String)
], ResponseIdDTO.prototype, "id", void 0);

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
class ShopResponseDTO extends BaseEntity {
    constructor(...args){
        super(...args), _define_property(this, "id", void 0), _define_property(this, "cityName", void 0), _define_property(this, "address", void 0), _define_property(this, "latitude", void 0), _define_property(this, "longitude", void 0);
    }
}
_ts_decorate([
    dto({
        string: true
    }),
    _ts_metadata("design:type", String)
], ShopResponseDTO.prototype, "id", void 0);
_ts_decorate([
    dto({
        string: true
    }),
    _ts_metadata("design:type", String)
], ShopResponseDTO.prototype, "cityName", void 0);
_ts_decorate([
    dto({
        string: true
    }),
    _ts_metadata("design:type", String)
], ShopResponseDTO.prototype, "address", void 0);
_ts_decorate([
    dto({
        number: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata("design:type", Object)
], ShopResponseDTO.prototype, "latitude", void 0);
_ts_decorate([
    dto({
        number: true,
        optional: {
            allowUndefined: false
        }
    }),
    _ts_metadata("design:type", Object)
], ShopResponseDTO.prototype, "longitude", void 0);

var SortingTypes = /*#__PURE__*/ function(SortingTypes) {
    SortingTypes["ASC"] = "ASC";
    SortingTypes["DESC"] = "DESC";
    return SortingTypes;
}({});

export { AdminCreateMerchantRequestDTO, AdminEditMerchantRequestDTO, AdminGetMerchantResponseDTO, AppConfigCaptchaResponseDTO, AppConfigOtelResponseDTO, AppConfigRequestDTO, AppConfigResponseDTO, AppVersionDTO, ApplyReferralCodeCurrentUserCustomerRequestDTO, ApplyReferralCodeErrorResponseDTO, ApplyReferralCodeResponseDTO, ApplyReferralCodeStatus, ApplyReferralCodeSuccessResponseDTO, AuthAdminEmailRequestDTO, AuthAnonymousRequestDTO, AuthAnonymousResponseDTO, AuthMerchantEmailRequestDTO, AuthMerchantEmployeeByKeyRequestDTO, AuthPhoneFinishRequestDTO, AuthPhoneFinishResponse401DTO, AuthPhoneFinishResponseDTO, AuthPhoneStartRequestDTO, AuthPhoneStartResponse429DTO, AuthPhoneStartResponseDTO, AuthPhoneStartV2ResponseDTO, AuthRefreshResponseDTO, BannerActionType, BannerActionTypeV1, BannerDetailResponseDTO, BannerDetailResponseV1DTO, BannerDetailResponseV2DTO, BannerGroupListResponseDTO, BannerGroupListResponseV1DTO, BannerGroupListResponseV2DTO, BannerGroupPlace, BannerGroupPlaceV1, BannerGroupRequestDTO, BannerGroupRequestV1DTO, BannerGroupResponseDTO, BannerGroupResponseV1DTO, BannerGroupResponseV2DTO, BannerMediaDTO, BannerMediaType, CameraScanHtmlResultDTO, CameraScanRequestDTO, CameraScanResponseDTO, CameraScanResultType, CameraScanSourceDTO, CameraScanSourceType, CancelOrderErrorResponseDTO, CancelOrderRequestDTO, CancelOrderResponseDTO, CancelOrderStatus, CancelOrderSuccessResponseDTO, CartConfigurationDeliveryTypeResponseDTO, CartGetConfigurationPaymentTypeResponseDTO, CartV1DTO, CartV1ProductDTO, CartV1SingleAddProductRequestDTO, CartV1SingleRemoveProductRequestDTO, CartV1SingleUpdateProductRequestDTO, CartV1SingleUpdateRequestDTO, CartV2ConfigurationDeliveryCourierCitiesRequestDTO, CartV2ConfigurationDeliveryCourierCitiesResponseDTO, CartV2ConfigurationDeliveryPickupPointAddressesQueryType, CartV2ConfigurationDeliveryPickupPointAddressesRequestDTO, CartV2ConfigurationDeliveryPickupPointAddressesResponseDTO, CartV2ConfigurationDeliveryPickupPointCitiesRequestDTO, CartV2ConfigurationDeliveryPickupPointCitiesResponseDTO, CartV2ConfigurationDeliveryPickupShopResponseDTO, CartV2ConfigurationDeliveryTypesResponseDTO, CartV2DTO, CartV2DeliveryCalculationResponseStatus, CartV2GetConfigurationPaymentTypesRequestDTO, CartV2GetConfigurationPaymentTypesResponseDTO, CartV2InfoStatus, CartV2PaymentCalculationResponseStatus, CartV2ProductStatus, CartV2SingleAddProductRequestDTO, CartV2SingleRemoveProductRequestDTO, CartV2SingleUpdateProductRequestDTO, CartV2SingleUpdateRequestDTO, CartV3DTO, CartV3InfoBonusesWalletBonusDTO, CartV3InfoBonusesWalletCashbackDTO, CartV3InfoBonusesWalletType, CartV3InfoErrorDTO, CartV3InfoStatus, CartV3InfoSuccessDTO, CartV3PromoCodeStatus, CartV3PromoCodesResponseDTO, CartV4DTO, CategoryDTO, CategoryGroupDTO, CategoryHierarchyItemDTO, CategoryHierarchyItemType, ContentBlockActiveOrdersDataDTO, ContentBlockActiveOrdersResponseDTO, ContentBlockBannerActionType, ContentBlockBannerAspectRatio, ContentBlockBannerDTO, ContentBlockBannerMediaType, ContentBlockBannersDataDTO, ContentBlockBannersResponseDTO, ContentBlockBonusCardDataDTO, ContentBlockBonusCardResponseDTO, ContentBlockComponent, ContentBlockPageRequestDTO, ContentBlockPageResponseDTO, ContentBlockProductsDataDTO, ContentBlockProductsResponseDTO, ContentBlockProductsView, ContentBlockResponseComponent, ContentBlockSearchDataDTO, ContentBlockSearchResponseDTO, ContentBlockShortcutActionType, ContentBlockShortcutDTO, ContentBlockShortcutsDataDTO, ContentBlockShortcutsResponseDTO, ContentBlockShortcutsStyle, CreateOrderErrorResponseDTO, CreateOrderRequestDTO, CreateOrderResponseDTO, CreateOrderStatus, CreateOrderSuccessResponseDTO, CreateOrderV1RequestDTO, CreateProductReviewComplaintRequestDTO, CreateProductReviewComplaintResponseDTO, CreateProductReviewMessageRequestDTO, CreateProductReviewRequestDTO, CreateProductsListSearchHistoryRequestDTO, CustomerAllowedNotificationLevel, CustomerProfileWalletV1EventResponseDTO, CustomerProfileWalletV1EventType, CustomerProfileWalletV1ResponseDTO, CustomerProfileWalletV3BonusResponseDTO, CustomerProfileWalletV3CashbackResponseDTO, CustomerProfileWalletV3EventType, CustomerProfileWalletV3ResponseDTO, CustomerProfileWalletV3Type, DefaultConnectionUtilRequestDTO, DefaultConnectionUtilResponseDTO, ExternalIntegrationCaptchaValidateResponseDTO, ExternalIntegrationCaptchaValidateStatus, ExternalIntegrationDeliveryCalculateRequestDTO, ExternalIntegrationDeliveryCalculateResponseDTO, ExternalIntegrationDeliveryCalculateResponseStatus, ExternalIntegrationDeliveryCourierCitiesRequestDTO, ExternalIntegrationDeliveryCourierCitiesResponseDTO, ExternalIntegrationDeliveryPickupPointAddressesMode, ExternalIntegrationDeliveryPickupPointAddressesRequestDTO, ExternalIntegrationDeliveryPickupPointAddressesResponseDTO, ExternalIntegrationDeliveryPickupPointAddressesSelector, ExternalIntegrationDeliveryPickupPointAddressesStatus, ExternalIntegrationDeliveryPickupPointCitiesRequestDTO, ExternalIntegrationDeliveryPickupPointCitiesResponseDTO, ExternalIntegrationDeliveryType, ExternalIntegrationPaymentCancelRequestDTO, ExternalIntegrationPaymentCancelResponseDTO, ExternalIntegrationPaymentCancelResponseStatus, ExternalIntegrationPaymentCreateItemRequestDTO, ExternalIntegrationPaymentCreateRequestDTO, ExternalIntegrationPaymentCreateResponseDTO, ExternalIntegrationPaymentCreateResponseStatus, ExternalIntegrationPaymentRefundRequestDTO, ExternalIntegrationPaymentRefundResponseDTO, ExternalIntegrationPaymentRefundResponseStatus, ExternalIntegrationPaymentResponseDTO, ExternalIntegrationPaymentStatus, ExternalIntegrationPaymentSyncRequestDTO, ExternalIntegrationPaymentTaxSystem, ExternalIntegrationPaymentVat, Gender, GetBannerProductsCatalogFiltersRequestDTO, GetBannerProductsCatalogFiltersResponseDTO, GetBannerProductsCatalogRequestDTO, GetContentBlockBannerProductsCatalogFiltersRequestDTO, GetContentBlockBannerProductsCatalogFiltersResponseDTO, GetContentBlockBannerProductsCatalogRequestDTO, GetCurrentUserCustomerSettingsResponseDTO, GetMostSearchableProductsListRequestDTO, GetProductReviewListItemResponseDTO, GetProductReviewRequestDTO, GetProductReviewResponseDTO, GetProductsListFiltersByCategoryRequestDTO, GetProductsListPriceRequestDTO, GetProductsListRequestDTO, GetProductsListSearchHistoryResponseDTO, GetProductsListSortingFields, GetProductsListViewHistoryRequestDTO, GetUserPromoCodeStatus, GetUserReferralCodeStatus, HomeInfoLinkHTMLResponseDTO, HomeInfoLinkResponseDTO, HomeInfoLinkType, INTEGRATION_NOTIFICATION_PUSH_AUDIENCE_REASONS, IntegrationApplyUserReferralCodeErrorResponseDTO, IntegrationApplyUserReferralCodeRequestDTO, IntegrationApplyUserReferralCodeResponseDTO, IntegrationApplyUserReferralCodeStatus, IntegrationApplyUserReferralCodeSuccessResponseDTO, IntegrationCancelOrderErrorResponseDTO, IntegrationCancelOrderRequestDTO, IntegrationCancelOrderResponseDTO, IntegrationCancelOrderResponseStatus, IntegrationCancelOrderSuccessResponseDTO, IntegrationCartConfigurationDeliveryCourierCitiesRequestDTO, IntegrationCartConfigurationDeliveryCourierCitiesResponseDTO, IntegrationCartConfigurationDeliveryPickupPointAddressesRequestDTO, IntegrationCartConfigurationDeliveryPickupPointAddressesResponseDTO, IntegrationCartConfigurationDeliveryPickupPointAddressesStatus, IntegrationCartConfigurationDeliveryPickupPointCitiesRequestDTO, IntegrationCartConfigurationDeliveryPickupPointCitiesResponseDTO, IntegrationCartConfigurationDeliveryType, IntegrationCartConfigurationDeliveryTypeResponseDTO, IntegrationCartConfigurationDeliveryTypesResponseDTO, IntegrationCartConfigurationPaymentType, IntegrationCartGetConfigurationPaymentTypeResponseDTO, IntegrationCartGetConfigurationPaymentTypesRequestDTO, IntegrationCartGetConfigurationPaymentTypesResponseDTO, IntegrationCategoriesGetIdsResponseDTO, IntegrationContentBlockBannerActionHtmlDTO, IntegrationContentBlockBannerActionProductDTO, IntegrationContentBlockBannerActionProductsCatalogDTO, IntegrationContentBlockBannerDTO, IntegrationContentBlockBannerHtmlDTO, IntegrationContentBlockBannerMediaDTO, IntegrationContentBlockBannersDTO, IntegrationContentBlockDTO, IntegrationContentBlockProductsDTO, IntegrationContentBlockSearchDTO, IntegrationContentBlockShortcutDTO, IntegrationContentBlockShortcutHtmlDTO, IntegrationContentBlockShortcutsDTO, IntegrationCreateOrUpdateUserRequestDTO, IntegrationCreateOrUpdateUserResponseDTO, IntegrationCreateOrderErrorCode, IntegrationCreateOrderErrorResponseDTO, IntegrationCreateOrderRequestDTO, IntegrationCreateOrderRequestV1DTO, IntegrationCreateOrderResponseDTO, IntegrationCreateOrderResponseStatus, IntegrationCreateOrderResponseV1DTO, IntegrationCreateOrderSuccessResponseDTO, IntegrationCreateProductReviewForCustomerRequestDTO, IntegrationCreateProductReviewForCustomerResponseDTO, IntegrationCreateProductReviewMessageFileRequestDTO, IntegrationCreateProductReviewMessageForCustomerRequestDTO, IntegrationCreateProductReviewMessageForCustomerResponseDTO, IntegrationCreateProductReviewMessageForManagerRequestDTO, IntegrationDefaultConnectionUtilRequestDTO, IntegrationDefaultConnectionUtilResponseDTO, IntegrationDeleteContentBlockDTO, IntegrationDeleteHomeInfoLinkDTO, IntegrationDeleteMerchantBannerDTO, IntegrationDeleteMerchantCategoryDTO, IntegrationDeleteMerchantIntegrationDTO, IntegrationDeleteMerchantShopDTO, IntegrationDeleteProductDTO, IntegrationDeleteProductInfoSectionDTO, IntegrationDeletePromoSplashDTO, IntegrationDeleteUserRequestDTO, IntegrationDeleteUserResponseDTO, IntegrationDeleteUserStatus, IntegrationGetCategoryHierarchyResponseDTO, IntegrationGetMerchantApiVersionDTO, IntegrationGetMerchantEmployeeActivityScansRequestDTO, IntegrationGetMerchantEmployeeDocumentsRequestDTO, IntegrationGetMerchantIntegrationsDTO, IntegrationGetProductMediaHashRequestDTO, IntegrationGetUnknownUserByPhoneCodes, IntegrationGetUnknownUserByPhoneExistsResponseDTO, IntegrationGetUnknownUserByPhoneNotExistsResponseDTO, IntegrationGetUnknownUserByPhoneRequestDTO, IntegrationGetUnknownUserByPhoneResponseDTO, IntegrationGetUserPromoCodesDisabledResponseDTO, IntegrationGetUserPromoCodesEnabledResponseDTO, IntegrationGetUserPromoCodesRequestDTO, IntegrationGetUserPromoCodesResponseDTO, IntegrationGetUserReferralCodeDisabledResponseDTO, IntegrationGetUserReferralCodeEnabledResponseDTO, IntegrationGetUserReferralCodeRequestDTO, IntegrationGetUserReferralCodeResponseDTO, IntegrationGetUserWalletV1EventResponseDTO, IntegrationGetUserWalletV1RequestDTO, IntegrationGetUserWalletV1ResponseDTO, IntegrationGetUserWalletV3RequestDTO, IntegrationGetUserWalletV3ResponseDTO, IntegrationGetUserWalletV3Type, IntegrationHomeInfoLinkCameraScanRequestDTO, IntegrationHomeInfoLinkCameraScanResponseDTO, IntegrationInitializeHomeInfoLinkDTO, IntegrationInitializeHomeInfoLinkDataDTO, IntegrationInitializeMerchantApiVersionDTO, IntegrationInitializeMerchantBannerActionHtmlDTO, IntegrationInitializeMerchantBannerActionProductDTO, IntegrationInitializeMerchantBannerActionProductsCatalogDTO, IntegrationInitializeMerchantBannerDTO, IntegrationInitializeMerchantBannerImageDTO, IntegrationInitializeMerchantBannerMediaDTO, IntegrationInitializeMerchantBannerV1DTO, IntegrationInitializeMerchantCategoryDTO, IntegrationInitializeMerchantCategoryImageDTO, IntegrationInitializeMerchantCustomerAuthRequiredLegalFilesDTO, IntegrationInitializeMerchantMetaDTO, IntegrationInitializeMerchantMetaLegalFileDTO, IntegrationInitializeMerchantShopDTO, IntegrationInitializeProductDTO, IntegrationInitializeProductInfoSectionDTO, IntegrationInitializeProductInfoSectionImageDTO, IntegrationInitializeProductMediaDTO, IntegrationInitializeProductV1DTO, IntegrationInitializePromoSplashDTO, IntegrationInitializePromoSplashInfoDTO, IntegrationInitializePromoSplashInfoImageDTO, IntegrationMerchantEmployeeActivityEvent, IntegrationMerchantEmployeePickNotForPickDTO, IntegrationMerchantEmployeePickQuestionnaireInvalidDTO, IntegrationMerchantEmployeePickRequestDTO, IntegrationMerchantEmployeePickResponseDTO, IntegrationMerchantEmployeePickStatus, IntegrationMerchantEmployeePickSuccessDTO, IntegrationMerchantEmployeePickTimeoutDTO, IntegrationNotificationPushAudienceRequestDTO, IntegrationNotificationPushAudienceResponseDTO, IntegrationNotificationPushLevel, IntegrationNotificationPushNotDeliveredReason, IntegrationNotificationPushRequestDTO, IntegrationNotificationPushResponseDTO, IntegrationProductDisplayStatusRequestDTO, IntegrationProductDisplayStatusResponseDTO, IntegrationProductMediaHashResponseDTO, IntegrationProductsGetIdsResponseDTO, IntegrationSendMerchantEmployeeActivityActivityDTO, IntegrationSendMerchantEmployeeActivityRequestDTO, IntegrationSetCategoryGroupImageDTO, IntegrationSetCategoryHierarchyDTO, IntegrationSetMerchantIntegrationDTO, IntegrationSyncMerchantEmployeeShiftActivityRequestDTO, IntegrationSyncMerchantEmployeeShiftV2RequestDTO, IntegrationSyncOrderBaseRequestDTO, IntegrationSyncOrderProductRequestDTO, IntegrationSyncOrderRequestDTO, IntegrationSyncOrderRequestV1DTO, IntegrationSyncProductReviewRequestDTO, IntegrationTempUserTrySendTelegramBotMessageActionRequestDTO, IntegrationTempUserTrySendTelegramBotMessageRequestDTO, IntegrationTempUserTrySendTelegramBotMessageResponseDTO, IntegrationTempUserTrySendTelegramBotMessageStatus, IntegrationValidateCartV2DeliveryCalculationResponseStatus, IntegrationValidateCartV2InfoResponseStatus, IntegrationValidateCartV2PaymentCalculationResponseStatus, IntegrationValidateCartV2ProductResponseStatus, IntegrationValidateCartV2RequestDTO, IntegrationValidateCartV2ResponseDTO, IntegrationValidateCartV3DeliveryCalculationResponseStatus, IntegrationValidateCartV3DeliveryResponseDTO, IntegrationValidateCartV3InfoBonusesWalletBonusResponseDTO, IntegrationValidateCartV3InfoBonusesWalletCashbackResponseDTO, IntegrationValidateCartV3InfoBonusesWalletType, IntegrationValidateCartV3InfoErrorResponseDTO, IntegrationValidateCartV3InfoResponseDTO, IntegrationValidateCartV3InfoResponseStatus, IntegrationValidateCartV3PaymentCalculationResponseStatus, IntegrationValidateCartV3PaymentResponseDTO, IntegrationValidateCartV3ProductResponseErrorDTO, IntegrationValidateCartV3ProductResponseStatus, IntegrationValidateCartV3ProductResponseSuccessDTO, IntegrationValidateCartV3PromoCodeStatus, IntegrationValidateCartV3RequestDTO, IntegrationValidateCartV3ResponseDTO, IntegrationValidateCartV4RequestDTO, IntegrationValidateCartV4ResponseDTO, IntegrationValidateOrderRequestV1DTO, IntegrationValidateOrderResponseV1DTO, MERCHANT_INTEGRATION_SELF_SETTABLE_TYPES, MERCHANT_INTEGRATION_UNIQUE_TYPES, MerchantEmployeeActivityScansDTO, MerchantEmployeeConfigDTO, MerchantEmployeeCurrentActivityDTO, MerchantEmployeeDocumentDTO, MerchantEmployeeDocumentItemDTO, MerchantEmployeeDocumentsResponseDTO, MerchantEmployeeEnterStateRequestDTO, MerchantEmployeeFilledQuestionnaireDTO, MerchantEmployeeFinishStateRequestDTO, MerchantEmployeeGetDocumentsRequestDTO, MerchantEmployeeOutcomeDocumentDTO, MerchantEmployeeOutcomeGroupDTO, MerchantEmployeeOutcomeItemDTO, MerchantEmployeeOutcomeKind, MerchantEmployeeOutcomeType, MerchantEmployeePickRequestDTO, MerchantEmployeePickResponseDTO, MerchantEmployeePickScope, MerchantEmployeePickSummaryDTO, MerchantEmployeeQuestionnaireAnswerDTO, MerchantEmployeeQuestionnaireDTO, MerchantEmployeeQuestionnaireFieldDTO, MerchantEmployeeQuestionnaireFieldType, MerchantEmployeeQuestionnaireOptionDTO, MerchantEmployeeRefusalReasonDTO, MerchantEmployeeResultDTO, MerchantEmployeeScanCustomerDTO, MerchantEmployeeScanItemDTO, MerchantEmployeeScanRequestDTO, MerchantEmployeeShiftDTO, MerchantEmployeeShiftStatsDTO, MerchantEmployeeShiftStatsDealDTO, MerchantEmployeeShiftStatsOutcomesDTO, MerchantEmployeeShiftStatsRefusalReasonDTO, MerchantEmployeeShiftStatsStateDTO, MerchantEmployeeShiftStatusDTO, MerchantEmployeeStateDTO, MerchantIntegrationCdekPayValueDTO, MerchantIntegrationCdekValueDTO, MerchantIntegrationDataProviderAuthType, MerchantIntegrationDataProviderBackendKeyValueDTO, MerchantIntegrationDataProviderCredentialsAuthDTO, MerchantIntegrationDataProviderKeyValueDTO, MerchantIntegrationDataProviderTokenAuthDTO, MerchantIntegrationDataProviderUrlValueDTO, MerchantIntegrationFirebaseValueDTO, MerchantIntegrationMaxmaValueDTO, MerchantIntegrationSmsProviderName, MerchantIntegrationSmsProviderProstorSmsDTO, MerchantIntegrationSmsProviderSmsIntRuDTO, MerchantIntegrationSmsProviderSmsRuDTO, MerchantIntegrationSmsProviderSmsUslugiRuDTO, MerchantIntegrationSmsProviderSmscRuDTO, MerchantIntegrationSmsProviderValueDTO, MerchantIntegrationTelegramBotSupportLinkValueDTO, MerchantIntegrationTelegramBotTokenValueDTO, MerchantIntegrationTypes, MerchantIntegrationYandexMapsValueDTO, MerchantIntegrationYooKassaValueDTO, MerchantMetaCustomerAuthRequiredLegalFilesResponseDTO, MerchantMetaLegalFilePlace, MerchantMetaLegalFileResponseDTO, MerchantMetaLegalFileType, MerchantMetaLinkType, MerchantMetaResponseDTO, MerchantResponseDTO, MostSearchableProductNameDTO, NotificationPushSetTokenRequestDTO, OneCInitializeProductAttributeValueNumberDTO, OneCInitializeProductAttributeValueStringArrayDTO, OneCInitializeProductAttributeValueStringArrayV1DTO, OneCInitializeProductAttributeValueStringDTO, OneCInitializeProductAttributeValueStringV1DTO, OrderBaseProductDTO, OrderBaseResponseDTO, OrderDetailResponseDTO, OrderListProductDTO, OrderPriceResponseDTO, OrderProductDetailDTO, OrderProductPriceDTO, OrderProductStatus, OrderResponseDTO, OrderStatus, OrderV1BaseProductDTO, OrderV1BaseResponseDTO, OrderV1DetailResponseDTO, OrderV1ListProductDTO, OrderV1ProductDetailDTO, OrderV1ResponseDTO, OrdersListRequestDTO, OrdersV1ListRequestDTO, ProductAttributeDTO, ProductAttributeRequiredToOrderMode, ProductAttributeType, ProductAttributeValueType, ProductBadgeDTO, ProductBadgeIconType, ProductBaseDTO, ProductDTO, ProductDetailDTO, ProductFiltersBaseDTO, ProductFiltersBaseSortingDTO, ProductFiltersByCategoryDTO, ProductInfoSectionDTO, ProductInfoSectionLinkDTO, ProductInfoSectionTextDTO, ProductInfoSectionType, ProductMediaDTO, ProductMediaType, ProductPartDTO, ProductReviewMessageMediaType, ProductReviewStatus, PromoSplashInfoType, PromoSplashResponseDTO, ResponseIdDTO, SetProductFavouriteRequestDTO, ShopResponseDTO, SortingTypes, StorageFileDTO, StorageFileProcessingStatus, UpdateCartV2RequestDTO, UpdateCurrentUserCustomerRequestDTO, UpdateCurrentUserCustomerSettingsRequestDTO, UpdateCurrentUserRequestCustomerShopDTO, UpdateV1CartProductRequestDTO, UpdateV1CartRequestDTO, UserDetailAdminDTO, UserDetailCustomerDTO, UserDetailCustomerV1DTO, UserExternalInterfaceDTO, UserExternalRole, UserPromoCodesCustomerDTO, UserPromoCodesDisabledCustomerDTO, UserPromoCodesEnabledCustomerDTO, UserReferralCustomerDTO, UserReferralDisabledCustomerDTO, UserReferralEnabledCustomerDTO, UserRole, dto, dtoMessageIsDefined, dtoMessageIsNotEmptyString, dtoMessageIsPhone, dtoMessageIsValidValue, fileExtensionPresets, merchantIntegrationSelfSettableValueDiscriminator, merchantIntegrationValueDiscriminator, resizableImageExtensionPreset };
//# sourceMappingURL=main.mjs.map
